import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "../api/axiosInstance";
import { useNavigate, useSearchParams } from "react-router-dom";
import { createReservationTranslator, getReservationLocale, translateReservationStatus, translateReservationModificationBlock } from "./reservationI18n";
import { reservationGroupMessages } from "./reservationGroupMessages";
import ReservationDraftPopup from "./ReservationDraftPopup";
import ReservationAttendanceDialog from "./ReservationAttendanceDialog";
import RoomUnassignOption from "./RoomUnassignOption";
import { attendanceMessages } from "./reservationAttendanceMessages";
import GuestSelectorPopup from "../Components/GuestSelectorPopup";
import BookerCustomerCreatePopup from "./BookerCustomerCreatePopup";
import BookerSelectorPopup from "./BookerSelectorPopup";
import { getBookerDisplayName, isCompanyBooker, requiresCompanyBookerContact } from "./bookerPresentation";
import MainGuestCustomerCreatePopup from "./MainGuestCustomerCreatePopup";
import AccompanyingCustomerCreatePopup from "./AccompanyingCustomerCreatePopup";
import ChildCustomerCreatePopup from "./ChildCustomerCreatePopup";
import GroupDraftExtrasModal from "./GroupDraftExtrasModal";
import GroupPendingExtrasModal from "./GroupPendingExtrasModal";
import GroupSpecialRate from "../Components/GroupSpecialRate";
import OverridePopup from "../Components/OverridePopup";
import ReservationExtrasModal from "../Components/ReservationExtrasModal";
//10.02.2026 - Pop up para pagos por Stripe
import ManagePaymentsPopup from "./ManagePaymentsPopup";
import PmsConfirmDialog from "../Components/PmsConfirmDialog";
import { usePmsTheme } from "../Components/PmsThemeContext";
import { useCurrencyDisplay } from "../currency/CurrencyDisplayContext";
import {
  currencyAmountStep,
  formatCurrencyAmount,
  getCurrencyMinorUnits,
  hasValidCurrencyPrecision,
  quantizeCurrencyAmount,
} from "../currency/currencyPrecision";
import ReservationGroupCommentsPopup from "./ReservationGroupCommentsPopup";
import { DEMO_BUSINESS_UNIT_ID, isDemoMode, readDemoUser } from "../demo/demoMode";
import { demoReferenceDate } from "../demo/demoSnapshot";
import { readStoredUser } from "../utils/sessionUser";
import "./ReservationGroupView.css";
import {
  getReservationModificationBlock,
  normalizeReservationManualModificationPolicy,
} from "./reservationModificationPolicy";
import {
  buildCategoryMoveOverrideCurrencyFields,
  createReservationChargeOperationId,
  getActiveBusinessUnitReservationError,
  getGroupCancellationGate,
  getReservationModificationErrorMessage,
  isWorkflowOnlyReservationChargeType,
  readRatesPricesDeepLink,
  reservationChargeCreatePath,
  reservationChargeIdempotencyConfig,
  resolveCategoryMoveBaseCurrency,
} from "./reservationGroupViewCompat";
import { submitWaivedTerminalWorkflow } from "./terminalReservationWorkflow";
import {
  buildDraftGroupAddRoomsPayload,
  buildGroupExtraAssignmentFeedback,
  canAddDraftGroupRooms,
  canManageGroupExtrasForContext,
  getDraftGroupApiErrorMessage,
  getGroupExtraAssignmentPersistenceDecision,
  getGroupRateTargetReservations,
} from "./groupDraftUiContracts";
import { reconcileDraftGroupRoomOptions } from "./roomNumberPickerAvailability";
import {
  FiAlertTriangle,
  FiCalendar,
  FiCheck,
  FiCreditCard,
  FiDollarSign,
  FiFileText,
  FiHash,
  FiHome,
  FiLogIn,
  FiLogOut,
  FiLock,
  FiMinus,
  FiMoreHorizontal,
  FiPackage,
  FiPlus,
  FiRefreshCcw,
  FiSend,
  FiShuffle,
  FiUsers,
  FiX,
} from "react-icons/fi";

// Channex/OTAs can mix operational metadata and the guest's own message in one notes field.
const CHANNEL_DETAIL_LABELS = [
  "Meal Plan",
  "Smoking Preference",
  "Payment Collect",
  "Payment Type",
  "OTA Commission",
  "Commission",
  "Guest name",
  "Guarantee",
  "Card",
  "Policies",
  "Cancellation Policy",
  "Deposit Policy",
  "Children and Extra Bed Policy",
  "Taxes",
  "Services",
  "Rate Plan",
  "Rate Plan Code",
  "Room Type Code",
  "Room Remarks",
  "Bed Preferences",
  "Listing Base Price",
  "Total Paid Amount",
  "Transient Occupancy Tax Paid Amount",
  "Listing Security Price",
  "Listing Cancellation Payout",
  "Listing Cancellation Host Fee",
  "Occupancy Tax Amount Paid To Host",
];

const GUEST_REQUEST_LABELS = [
  "Guest Request",
  "Guest Requests",
  "Special Request",
  "Special Requests",
  "Customer Request",
  "Customer Requests",
  "Guest Comment",
  "Guest Comments",
  "Customer Comment",
  "Customer Comments",
  "Comment",
  "Comments",
  "Message",
  "Free Text",
  "Notes",
];

const SPECIAL_REQUEST_SPLIT_LABELS = [
  ...GUEST_REQUEST_LABELS,
  ...CHANNEL_DETAIL_LABELS,
];

const escapeRegExp = (value) =>
  String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const SPANISH_UI_MESSAGE_PATTERNS = Object.values(reservationGroupMessages).map((message) =>
  new RegExp(`^${String(message).split(/\{\w+\}/g).map(escapeRegExp).join("[\\s\\S]*")}$`)
);

const dedupeTextLines = (lines) => {
  const seen = new Set();
  return lines.filter((line) => {
    const normalized = String(line || "").trim().replace(/\s+/g, " ").toLowerCase();
    if (!normalized || seen.has(normalized)) return false;
    seen.add(normalized);
    return true;
  });
};

const startsWithAnySpecialRequestLabel = (line, labels) =>
  labels.some((label) =>
    new RegExp(`^${escapeRegExp(label)}\\s*:`, "i").test(line)
  );

const stripSpecialRequestLabel = (line, labels) => {
  for (const label of labels) {
    const re = new RegExp(`^${escapeRegExp(label)}\\s*:\\s*`, "i");
    if (re.test(line)) return line.replace(re, "").trim();
  }
  return line;
};

const getBusinessUnitIdFromReservations = (reservationList) => {
  const match = (Array.isArray(reservationList) ? reservationList : []).find(
    (reservation) =>
      reservation?.business_unit_id !== undefined &&
      reservation?.business_unit_id !== null &&
      reservation?.business_unit_id !== ""
  );

  return match?.business_unit_id ?? null;
};

const toDateInputValue = (value) => {
  if (!value) return "";
  const text = String(value);
  const isoMatch = text.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoMatch) return isoMatch[1];

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";

  const y = parsed.getFullYear();
  const m = String(parsed.getMonth() + 1).padStart(2, "0");
  const d = String(parsed.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const getLocalArrivalParts = (date = new Date()) => {
  const pad = (value) => String(value).padStart(2, "0");
  return {
    arrivalDate: `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`,
    arrivalTime: `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`,
  };
};

const parseDateInputAsUtc = (value) => {
  const text = toDateInputValue(value);
  if (!text) return null;

  const [year, month, day] = text.split("-").map(Number);
  if (!year || !month || !day) return null;

  return new Date(Date.UTC(year, month - 1, day));
};

const countDateInputNights = (checkInDate, checkOutDate) => {
  const start = parseDateInputAsUtc(checkInDate);
  const end = parseDateInputAsUtc(checkOutDate);
  if (!start || !end) return 0;

  const diff = end.getTime() - start.getTime();
  if (diff <= 0) return 0;

  return Math.round(diff / (1000 * 60 * 60 * 24));
};

const addDaysToDateInput = (value, days) => {
  const date = parseDateInputAsUtc(value);
  if (!date) return "";
  date.setUTCDate(date.getUTCDate() + Number(days || 0));
  return date.toISOString().slice(0, 10);
};

const buildStayDateOptions = (checkInDate, checkOutDate) => {
  const start = parseDateInputAsUtc(checkInDate);
  const end = parseDateInputAsUtc(checkOutDate);
  if (!start || !end || end <= start) return [];

  const options = [];
  const cursor = new Date(start.getTime());
  while (cursor < end && options.length < 370) {
    options.push(cursor.toISOString().slice(0, 10));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return options;
};

const normalizeNumericId = (...values) => {
  for (const value of values) {
    if (value === undefined || value === null || value === "") continue;
    const numeric = Number(value);
    if (Number.isFinite(numeric) && numeric > 0) return numeric;
  }
  return null;
};

const normalizeCapacityLimit = (...values) => {
  for (const value of values) {
    if (value === undefined || value === null || value === "") continue;
    const numeric = Number(value);
    if (Number.isFinite(numeric) && numeric >= 0) return numeric;
  }
  return null;
};

const listOptionNames = (items, fallbackLabel) => {
  const names = (Array.isArray(items) ? items : [])
    .map((item) => String(item?.name || item?.rate_name || item?.label || "").trim())
    .filter(Boolean);

  if (!names.length) return fallbackLabel;
  return names.slice(0, 5).join(", ");
};

const formatNoRatesReason = (data, t = (text) => text) => {
  if (!data) return t("No room rates available.");

  if (t("No room rates available.") !== "No room rates available.") {
    const code = data.reason_code || data.code;
    return [
      t("No valid room rates. Review the selected dates, occupancy and room category."),
      code ? t("Reference: {code}", { code }) : "",
    ].filter(Boolean).join(" ");
  }

  const reason =
    data.reason ||
    data.reason_message ||
    data.message ||
    data.error ||
    null;

  const code = data.reason_code || data.code || null;

  if (code && reason) return t("No valid room rates ({p0}): {p1}", { p0: code, p1: reason });
  if (reason) return t("No valid room rates: {p0}", { p0: reason });
  if (code) return t("No valid room rates ({p0}).", { p0: code });
  if (data.skip) return t("No valid room rates. Details: {p0}", { p0: JSON.stringify(data.skip) });

  return t("No room rates available.");
};

const formatReservationHistoryDate = (value, t = (text) => text, locale = "en-GB") => {
  if (!value) return t("Unknown date");
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);

  return new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(parsed);
};

const formatReservationHistoryValue = (value, t = (text) => text) => {
  if (value === undefined || value === null || value === "") return "—";
  if (typeof value === "boolean") return value ? t("Yes") : t("No");
  if (typeof value === "number") return Number.isFinite(value) ? String(value) : "—";
  if (Array.isArray(value)) {
    const labels = value.map((item) => formatReservationHistoryValue(item, t)).filter((item) => item !== "—");
    return labels.length ? labels.join(", ") : "—";
  }
  if (typeof value === "object") {
    const label =
      value.label ??
      value.name ??
      value.display_value ??
      value.room_number ??
      value.value;
    return label === undefined || label === null
      ? "—"
      : formatReservationHistoryValue(label, t);
  }
  return String(value);
};

const humanizeReservationHistoryField = (value, t = (text) => text) =>
  t(String(value || "Change")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase()));

const CHANNEL_MANAGER_SOURCE_MARKERS = [
  "channex",
  "channelmanager",
  "lighthouse",
  "logismanager",
  "bookingcom",
  "expedia",
  "airbnb",
  "agoda",
  "hotelscom",
];

const normalizeChannelManagerSource = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");

const isChannelManagerSourceValue = (value) => {
  const normalized = normalizeChannelManagerSource(value);
  return (
    normalized.length > 0 &&
    CHANNEL_MANAGER_SOURCE_MARKERS.some((marker) =>
      normalized.includes(marker)
    )
  );
};

const hasExternalSourceAttribution = (record) =>
  record?.source_attribution?.has_external_source === true ||
  record?.source_attribution?.has_external_source === 1 ||
  record?.source_attribution?.is_external === true ||
  String(record?.source_attribution?.has_external_source || "").toLowerCase() ===
    "true";

const getExplicitReservationOriginType = (record) => {
  const rawValue =
    record?.origin_type ??
    record?.source_attribution?.origin_type ??
    record?.source_attribution?.source_type;
  if (rawValue === undefined || rawValue === null) return "";
  return normalizeChannelManagerSource(rawValue);
};

const isChannelManagerReservationRecord = (record) => {
  if (!record) return false;
  const explicitOriginType = getExplicitReservationOriginType(record);
  if (explicitOriginType) {
    return ["channelmanager", "ota", "external", "externalapi"].includes(
      explicitOriginType
    );
  }

  // Compatibility only for reservations created before origin_type existed.
  if (hasExternalSourceAttribution(record)) return true;

  const sourceType = normalizeChannelManagerSource(
    record?.source_attribution?.source_type ||
      record?.source_attribution?.source_category ||
      record?.source_attribution?.origin
  );
  if (sourceType === "ota" || sourceType === "channelmanager") return true;

  const sourceValues = [
    record.created_by,
    record.updated_by,
    record.external_agency_name,
    record.source_system,
    record.source,
  ];
  if (sourceValues.some(isChannelManagerSourceValue)) return true;

  const hasExternalLocator = Boolean(
    String(
      record.external_reservation_id ||
        record.external_partner_locator ||
        ""
    ).trim()
  );
  const hasExternalContext = Boolean(
    String(
      record.external_creator_id ||
        record.external_hotel_id ||
        record.external_agency_name ||
        ""
    ).trim()
  );
  return hasExternalLocator && hasExternalContext;
};

const getReservationVersionState = (version) =>
  version?.snapshot ||
  version?.state ||
  version?.reservation ||
  version?.data ||
  version ||
  null;

const readReservationVersionPath = (value, path) => {
  const parts = String(path || "").split(".");
  let current = value;
  for (const part of parts) {
    if (current === undefined || current === null) return undefined;
    current = current[part];
  }
  return current;
};

const firstReservationVersionValue = (version, paths) => {
  const state = getReservationVersionState(version);
  for (const path of paths) {
    const value = readReservationVersionPath(state, path);
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return null;
};

const formatReservationVersionNight = (night, t = (text) => text) => {
  if (!night) return "—";
  const price = night.effective_price ?? night.base_price;
  const assignedCategory =
    night.room_category_name || night.assigned_room_category_name;
  const bookedCategory =
    night.booked_room_category_name || assignedCategory;
  return [
    bookedCategory
      ? t("Booked: {p0}", { p0: bookedCategory })
      : null,
    assignedCategory
      ? t("Assigned: {p0}", { p0: assignedCategory })
      : null,
    night.room_rate_name || night.room_rate_code,
    price == null
      ? null
      : `${formatReservationHistoryValue(price)}${night.currency ? ` ${night.currency}` : ""}`,
    night.rate_price_id ? t("Rate price #{p0}", { p0: night.rate_price_id }) : t("Rate price unlinked"),
  ]
    .filter(Boolean)
    .join(" · ");
};

const formatReservationVersionGuest = (guest, t = (text) => text) => {
  if (!guest) return "—";
  const customer = guest.customer_snapshot || guest.customer || {};
  const name = [
    customer.first_name,
    customer.middle_name,
    customer.last_name,
    customer.second_last_name,
  ]
    .filter(Boolean)
    .join(" ");
  const role = guest.is_main_guest
    ? t("Main guest")
    : guest.is_child
      ? t("Child")
      : t(guest.relationship || "Accompanying guest");
  const contact = customer.email || customer.phone || null;
  return [name || t("Customer #{p0}", { p0: guest.customer_id || "—" }), role, contact]
    .filter(Boolean)
    .join(" · ");
};

const formatReservationHistoryListValue = (value, field, t = (text) => text) => {
  if (field === "guests" && Array.isArray(value)) {
    const guests = value
      .map((guest) => formatReservationVersionGuest(guest, t))
      .filter((guest) => guest !== "—");
    return guests.length ? guests.join("; ") : "—";
  }

  const formattedValue = formatReservationHistoryValue(value, t);
  if (formattedValue !== "—" || !value || typeof value !== "object") {
    return formattedValue;
  }

  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const buildReservationHistoryListRows = (events, reservationLabel, t = (text) => text) => {
  const rows = [];
  let sequence = 0;

  const addRow = ({
    event,
    field,
    fieldLabel,
    before,
    after,
    key,
  }) => {
    const occurredAt = event?.occurred_at || null;
    const parsedTime = new Date(occurredAt || "").getTime();
    rows.push({
      key,
      occurredAt,
      reservationLabel,
      field,
      fieldLabel,
      before,
      after,
      actor:
        event?.actor_name ||
        event?.actor?.name ||
        event?.actor?.email ||
        t("System"),
      sortTime: Number.isNaN(parsedTime) ? Number.MAX_SAFE_INTEGER : parsedTime,
      sequence,
    });
    sequence += 1;
  };

  (Array.isArray(events) ? events : []).forEach((event, eventIndex) => {
    const eventKey = event?.id || event?.occurred_at || `event-${eventIndex}`;
    const changes = Array.isArray(event?.changes) ? event.changes : [];
    const nights = Array.isArray(event?.nights) ? event.nights : [];

    changes.forEach((change, changeIndex) => {
      const field = change?.field || "change";
      addRow({
        event,
        field,
        fieldLabel: change?.label ? t(change.label) : humanizeReservationHistoryField(field, t),
        before: change?.before ?? change?.before_value,
        after: change?.after ?? change?.after_value,
        key: `${eventKey}-change-${field}-${changeIndex}`,
      });
    });

    nights.forEach((night, nightIndex) => {
      const stayDate =
        night?.stay_date || night?.date || t("Night {p0}", { p0: nightIndex + 1 });
      const nightChanges = Array.isArray(night?.changes) ? night.changes : [];

      if (nightChanges.length > 0) {
        nightChanges.forEach((change, changeIndex) => {
          const field = change?.field || t("night");
          const label = change?.label ? t(change.label) : humanizeReservationHistoryField(field, t);
          addRow({
            event,
            field,
            fieldLabel: `${stayDate} · ${label}`,
            before: change?.before ?? change?.before_value,
            after: change?.after ?? change?.after_value,
            key: `${eventKey}-night-${stayDate}-${field}-${changeIndex}`,
          });
        });
        return;
      }

      const before = night?.before ?? night?.old_price ?? night?.previous_price;
      const after = night?.after ?? night?.new_price ?? night?.price;

      addRow({
        event,
        field: "night",
        fieldLabel: t("Night {p0}", { p0: stayDate }),
        before:
          before && typeof before === "object"
            ? formatReservationVersionNight(before, t)
            : before,
        after:
          after && typeof after === "object"
            ? formatReservationVersionNight(after, t)
            : after,
        key: `${eventKey}-night-${stayDate}-${nightIndex}`,
      });
    });
  });

  return rows.sort(
    (left, right) =>
      left.sortTime - right.sortTime || left.sequence - right.sequence
  );
};

const RESERVATION_VERSION_COMPARISON_FIELDS = [
  { label: "Status", paths: ["status"] },
  { label: "Check-in", paths: ["check_in_date", "checkInDate"] },
  { label: "Check-out", paths: ["check_out_date", "checkOutDate"] },
  { label: "Adults", paths: ["adults"] },
  { label: "Children", paths: ["children"] },
  {
    label: "Room category",
    paths: [
      "room_category_name",
      "room_category_name_snapshot",
      "room_category.name",
      "roomCategory.name",
      "category_name",
    ],
  },
  {
    label: "Room rate",
    paths: [
      "rate_name",
      "room_rate_name",
      "room_rate_name_snapshot",
      "rate.name",
      "roomRate.name",
    ],
  },
  {
    label: "Room",
    paths: ["room_number", "room_number_snapshot", "room.room_number", "room.name"],
  },
  {
    label: "Room total",
    paths: ["total_price", "room_total", "total"],
  },
];

const firstRoomMetaValue = (...values) => {
  for (const value of values) {
    if (value == null) continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return "-";
};

const getRoomMetadataLines = (room) => [
  {
    label: "Floor",
    value: firstRoomMetaValue(room?.floor, room?.room_floor),
  },
  {
    label: "Bed type",
    value: firstRoomMetaValue(room?.bed_type, room?.bedType, room?.room_bed_type),
  },
  {
    label: "View",
    value: firstRoomMetaValue(room?.view, room?.views, room?.room_view),
  },
];

const sortRoomsByNumber = (rooms = []) =>
  [...rooms].sort((a, b) => {
    const numA = parseInt(a?.room_number, 10);
    const numB = parseInt(b?.room_number, 10);
    if (!Number.isNaN(numA) && !Number.isNaN(numB)) return numA - numB;
    return String(a?.room_number || "").localeCompare(String(b?.room_number || ""));
  });

const getDisplayGroupLocator = (value, fallback = "") => {
  const raw = String(value || "").trim();
  if (!raw) return fallback;
  return raw.split(/__(?:EDIT|ADD)__/)[0] || fallback;
};

const normalizeRoomsToAdd = (value) => {
  const parsed = parseInt(value, 10);
  if (!Number.isFinite(parsed) || parsed < 1) return 1;
  return parsed;
};

const normalizeSpecialRequestSegments = (rawValue) => {
  const text = String(rawValue || "").replace(/\r/g, "\n").trim();
  if (!text) return [];

  const labelPattern = [...SPECIAL_REQUEST_SPLIT_LABELS]
    .sort((a, b) => b.length - a.length)
    .map(escapeRegExp)
    .join("|");

  const withBreaks = text.replace(
    new RegExp(`\\s+(${labelPattern})\\s*:`, "gi"),
    "\n$1:"
  );

  return withBreaks
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean);
};

const parseSpecialRequests = (rawValue) => {
  const segments = normalizeSpecialRequestSegments(rawValue);
  const guestRequests = [];
  const channelDetails = [];

  segments.forEach((segment) => {
    if (startsWithAnySpecialRequestLabel(segment, GUEST_REQUEST_LABELS)) {
      const cleaned = stripSpecialRequestLabel(segment, GUEST_REQUEST_LABELS);
      if (cleaned) guestRequests.push(cleaned);
      return;
    }

    if (startsWithAnySpecialRequestLabel(segment, CHANNEL_DETAIL_LABELS)) {
      channelDetails.push(segment);
      return;
    }

    guestRequests.push(segment);
  });

  return {
    hasContent: segments.length > 0,
    guestRequests: dedupeTextLines(guestRequests),
    channelDetails: dedupeTextLines(channelDetails),
  };
};

const formatSpecialRequestSentence = (parts) =>
  parts
    .map((part) => String(part || "").trim().replace(/\s+/g, " "))
    .filter(Boolean)
    .map((part) => (/[.!?]$/.test(part) ? part : `${part}.`))
    .join(" ");

const SpecialRequestsPanel = ({ value }) => {
  const { languageId } = usePmsTheme();
  const t = useMemo(() => createReservationTranslator(languageId, reservationGroupMessages), [languageId]);
  const parsed = parseSpecialRequests(value);
  const guestText = formatSpecialRequestSentence(parsed.guestRequests);
  const channelText = formatSpecialRequestSentence(parsed.channelDetails.map((detail) => {
    const label = CHANNEL_DETAIL_LABELS.find((candidate) =>
      new RegExp(`^${escapeRegExp(candidate)}\\s*:`, "i").test(detail)
    );
    // Translate only the metadata heading; preserve the guest's original message.
    return label ? `${t(label)}${detail.slice(label.length)}` : detail;
  }));

  if (!parsed.hasContent) {
    return (
      <div className="rgv-panelMuted"> {t("No special requests for this reservation.")} </div>
    );
  }

  return (
    <div className="rgv-specialRequests">
      {guestText && (
        <span className="rgv-specialRequestGuestText">{guestText}</span>
      )}
      {guestText && channelText && " "}
      {channelText && <span>{channelText}</span>}
    </div>
  );
};

const SpecialRequestsPopup = ({ isOpen, onClose, rooms }) => {
  const { languageId } = usePmsTheme();
  const t = useMemo(() => createReservationTranslator(languageId, reservationGroupMessages), [languageId]);
  if (!isOpen) return null;

  return (
    <>
      <div className="rgv-modalOverlay" onClick={onClose} />
      <div className="rgv-specialRequestsModal" role="dialog" aria-modal="true">
        <div className="rgv-specialRequestsModalHeader">
          <div>
            <div className="rgv-panelTitle">{t("Special requests")}</div>
            <h5>{t("Guest requests by room")}</h5>
          </div>
          <button type="button" className="rgv-specialRequestsModalClose" onClick={onClose} aria-label={t("Close special requests")}>
            <FiX size={18} aria-hidden="true" />
          </button>
        </div>

        <div className="rgv-specialRequestsModalBody">
          {rooms.length ? (
            rooms.map((room, index) => (
              <div key={room.id || index} className="rgv-specialRequestsRoomBlock">
                <div className="rgv-specialRequestsRoomTitle">{room.label}</div>
                <SpecialRequestsPanel value={room.specialRequests} />
              </div>
            ))
          ) : (
            <div className="rgv-panelMuted">{t("No special requests for this group.")}</div>
          )}
        </div>
      </div>
    </>
  );
};

const CONFIRMED_RESERVATION_WORKFLOW_ACTIONS = [
  { key: "modify_stay", title: "Modify Stay Dates", icon: FiCalendar },
  { key: "room_category", title: "Change Room Category Assigned", icon: FiHome },
  { key: "assign_room_number", title: "Assign Room Number", icon: FiHash },
  { key: "guests_occupancy", title: "Change Number of Adults/Children", icon: FiUsers },
  { key: "rates_prices", title: "Change Room Rate or Price", icon: FiDollarSign },
  { key: "extras", title: "Manage Extras", icon: FiPackage },
  { key: "charges", title: "Manage Charges", icon: FiDollarSign },
  { key: "payments", title: "Payments", icon: FiCreditCard, handler: "payments" },
  { key: "send_confirmation", title: "Send Confirmation", icon: FiSend, handler: "send_confirmation" },
];

const IN_HOUSE_RESERVATION_WORKFLOW_ACTIONS = [
  { key: "room_move", title: "Room Move", icon: FiShuffle },
  { key: "room_category", title: "Room Category Move", icon: FiHome },
  { key: "modify_stay", title: "Modify Stay", icon: FiCalendar },
  { key: "guests_occupancy", title: "Guests & Occupancy", icon: FiUsers },
  { key: "price_adjustment", title: "Price Adjustment", icon: FiDollarSign },
  { key: "extras", title: "Manage Extras", icon: FiPackage },
  { key: "charges", title: "Manage Charges", icon: FiDollarSign },
  { key: "payments", title: "Payments", icon: FiCreditCard, handler: "payments" },
  { key: "registration_form", title: "Registration Form", icon: FiFileText },
];

const CHECKED_OUT_RESERVATION_WORKFLOW_ACTIONS = [
  { key: "charges", title: "Manage Charges", icon: FiDollarSign },
  { key: "payments", title: "Payments", icon: FiCreditCard, handler: "payments" },
];

const resolveReservationActionMode = (reservation) => {
  const normalizedStatus = String(reservation?.status || "").trim().toLowerCase();
  if (normalizedStatus === "checked in" || normalizedStatus === "in house") return "in_house";
  if (normalizedStatus === "confirmed") return "confirmed";
  if (
    normalizedStatus === "checked out" ||
    normalizedStatus === "no show" ||
    normalizedStatus === "noshow" ||
    normalizedStatus === "cancelled" ||
    normalizedStatus === "canceled"
  ) {
    return "checked_out";
  }
  return "status_only";
};

const getExpandedRoomRowsStorageKey = (reservationGroupId, mode) =>
  `palantir:reservation-group:${String(reservationGroupId || "")}:${String(mode || "")}:expanded-rooms`;

const readExpandedRoomRows = (reservationGroupId, mode) => {
  if (typeof window === "undefined") return {};

  try {
    const parsed = JSON.parse(
      window.sessionStorage.getItem(
        getExpandedRoomRowsStorageKey(reservationGroupId, mode)
      ) || "{}"
    );

    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};

    return Object.entries(parsed).reduce((rows, [reservationId, isExpanded]) => {
      if (isExpanded === true) rows[reservationId] = true;
      return rows;
    }, {});
  } catch (_err) {
    return {};
  }
};

const writeExpandedRoomRows = (reservationGroupId, mode, rows) => {
  if (typeof window === "undefined") return;

  try {
    const expandedRows = Object.entries(rows || {}).reduce(
      (result, [reservationId, isExpanded]) => {
        if (isExpanded === true) result[reservationId] = true;
        return result;
      },
      {}
    );

    window.sessionStorage.setItem(
      getExpandedRoomRowsStorageKey(reservationGroupId, mode),
      JSON.stringify(expandedRows)
    );
  } catch (_err) {
    // The UI state still works when session storage is unavailable.
  }
};

const DEMO_ACTION_HINT = "Solo consulta: esta acción está desactivada en la demo.";
const DEMO_DATA_UNAVAILABLE = "Datos no incluidos en la demo.";

const ReservationGroupView = ({
  reservationGroupId: embeddedReservationGroupId = null,
  initialReservationActionsId = null,
  actionsOnly = false,
  readOnlyPreview = false,
  onActionsOpen,
  onActionsClose,
  onActionsError,
  onReservationChange,
} = {}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { reservation_group_id: routeReservationGroupId } = useParams();
  const reservation_group_id = embeddedReservationGroupId || routeReservationGroupId;
  const demoReadOnlyActive = isDemoMode() || readOnlyPreview;
  const defaultAvatarUrl = demoReadOnlyActive
    ? `${String(process.env.PUBLIC_URL || "").replace(/\/+$/, "")}/default-avatar.png`
    : "/default-avatar.png";
  const mode = demoReadOnlyActive || actionsOnly ? "view" : searchParams.get("mode") || "edit_draft"; // default actual

  const isViewMode = mode === "view";

  const [showCancelPopup, setShowCancelPopup] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  // ✅ cancelación individual (room)
  const [showRoomCancelPopup, setShowRoomCancelPopup] = useState(false);
  const [roomCancelReason, setRoomCancelReason] = useState("");
  const [roomCancelTarget, setRoomCancelTarget] = useState(null);

  const [showManagePaymentsPopup, setShowManagePaymentsPopup] = useState(false);
  const [paymentTargetReservation, setPaymentTargetReservation] = useState(null);
  const [showCommentsPopup, setShowCommentsPopup] = useState(false);
  const [showSpecialRequestsPopup, setShowSpecialRequestsPopup] = useState(false);
  const [commentsInitialResId, setCommentsInitialResId] = useState(null);
  const [commentsData, setCommentsData] = useState([]);
  const commentsCount = commentsData.length;
  const [showDeleteGroupDialog, setShowDeleteGroupDialog] = useState(false);
  const [showConfirmGroupDialog, setShowConfirmGroupDialog] = useState(false);
  const [confirmGroupError, setConfirmGroupError] = useState(null);
  const [discardGroupError, setDiscardGroupError] = useState(null);
  const [isSendingConfirmation, setIsSendingConfirmation] = useState(false);
  const [showSendConfirmationPopup, setShowSendConfirmationPopup] = useState(false);
  const [confirmationEmailFields, setConfirmationEmailFields] = useState([""]);


  const navigate = useNavigate();
  // A preview must never inherit permissions from a real PMS login in this browser.
  const currentUser = demoReadOnlyActive ? readDemoUser() : readStoredUser();
  const activeBusinessUnitId = Number(currentUser?.active_business_unit_id) || null;
  const permissions = currentUser?.permissions || {};
  const canManageReservationCharges = !demoReadOnlyActive && Boolean(
    permissions["reservations.manage_charges"] ||
      permissions["reservations.update"]
  );
  const canUpdateReservations = !demoReadOnlyActive && Boolean(permissions["reservations.update"]);
  const canRegularizeStays = !demoReadOnlyActive && (
    Number(currentUser?.id) === 1 || permissions["reservations.regularize_stay"] === true
  );
  const canAddRoomsToGroup = !demoReadOnlyActive && canAddDraftGroupRooms(permissions);
  const canApplyGroupSpecialRate = !demoReadOnlyActive && Boolean(
    permissions["reservations.override_rates"] || canUpdateReservations
  );
  const canViewReservationPayments = Boolean(permissions["reservations.view_payments"]);
  const canEditNonStripePayments = !demoReadOnlyActive && Boolean(permissions["reservations.add_payment"]);
  const canEditStripePayments = !demoReadOnlyActive && Boolean(permissions["stripe_transactions.manage"]);

  const { themeId, languageId } = usePmsTheme();
  const t = useMemo(() => createReservationTranslator(languageId, reservationGroupMessages), [languageId]);
  const reservationLocale = getReservationLocale(languageId);
  const formatGroupError = useCallback((message, fallback = "Could not complete this action. Please review the reservation and try again.") => {
    if (!message) return t(fallback);
    const translated = t(message);
    if (languageId !== "es" || translated !== message || SPANISH_UI_MESSAGE_PATTERNS.some((pattern) => pattern.test(String(message)))) {
      return translated;
    }
    return t(fallback);
  }, [languageId, t]);
  const { baseCurrency, formatFromBase, currencyCatalog = [] } = useCurrencyDisplay();
  const quantizeBaseAmount = (amount, currencyCode = baseCurrency) =>
    quantizeCurrencyAmount(amount, currencyCode, currencyCatalog) ?? 0;
  const isLightTheme = themeId === "light";

  const businessUnitId = demoReadOnlyActive ? DEMO_BUSINESS_UNIT_ID :
    searchParams.get("business_unit_id") ||
    currentUser?.active_business_unit_id ||
    "1";
  const goToReservationsList = () =>
    navigate(`/reservations/list?business_unit_id=${businessUnitId}`);
  const goToReservationGroupView = (targetGroupId) => {
    const resolvedGroupId = Number(targetGroupId);
    if (!Number.isFinite(resolvedGroupId) || resolvedGroupId <= 0) {
      goToReservationsList();
      return;
    }

    navigate(`/reservation-group/${resolvedGroupId}?mode=view&business_unit_id=${businessUnitId}`);
  };
  const goToGroupAccount = () => {
    if (demoReadOnlyActive) return;
    navigate(`/reservation-group/${reservation_group_id}/account?business_unit_id=${businessUnitId}`);
  };
  const goToReservationAccount = (reservationId) => {
    if (demoReadOnlyActive) return;
    const resolvedReservationId = Number(reservationId);
    if (!Number.isFinite(resolvedReservationId) || resolvedReservationId <= 0) {
      goToGroupAccount();
      return;
    }

    navigate(
      `/reservation-group/${reservation_group_id}/account?business_unit_id=${businessUnitId}&reservation_id=${resolvedReservationId}`
    );
  };

  const [group, setGroup] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [reservations, setReservations] = useState([]);
  const effectiveBusinessUnitId =
    group?.business_unit_id ??
    getBusinessUnitIdFromReservations(reservations) ??
    businessUnitId;
  const [reservationModificationPolicy, setReservationModificationPolicy] =
    useState(() => normalizeReservationManualModificationPolicy(null));
  const isUpdateDraft = Boolean(group?.original_group_id);
  const isAddRoomsDraft = !isViewMode && group?.workflow_mode === "add_rooms";
  const groupRateLocked = group?.group_rate_locked === true || Boolean(group?.group_rate_locked_at) ||
    (reservations || []).some((reservation) =>
      ["checkedin", "checkedout"].includes(
        String(reservation?.status || "").toLowerCase().replace(/[\s_-]+/g, "")
      )
    );
  const groupExtrasPermissionContext = !group
    ? "unknown"
    : !isViewMode && !isUpdateDraft
      ? "new_draft"
      : "existing_group";
  const canManageGroupExtras = !demoReadOnlyActive && canManageGroupExtrasForContext({
    permissions,
    context: groupExtrasPermissionContext,
  });
  const discardDraftLabel = isUpdateDraft ? t("Discard Update") : t("Discard Draft");
  const displayGroupLocator = getDisplayGroupLocator(group?.group_locator);
  const [loading, setLoading] = useState(true);
  const [demoLoadError, setDemoLoadError] = useState("");
  const [isDraftPopupOpen, setIsDraftPopupOpen] = useState(false);
  const [activeDraftId, setActiveDraftId] = useState(null);
  const [deletingDraftId, setDeletingDraftId] = useState(null);
  const [isBookerSelectorOpen, setIsBookerSelectorOpen] = useState(false);
  const [isAddBookerOpen, setIsAddBookerOpen] = useState(false);
  const [, setBookerSelectorDraftId] = useState(null);
  const [bookerEditorMode, setBookerEditorMode] = useState("create"); // "create" | "edit"
  const [bookerEditorInitialData, setBookerEditorInitialData] = useState(null);
  const skipNextAutoOpenRef = useRef(false);
  const [isMainGuestSelectorOpen, setIsMainGuestSelectorOpen] = useState(false);
  const [mainGuestSelectorDraftId, setMainGuestSelectorDraftId] = useState(null);

  const [isAccompanyingSelectorOpen, setIsAccompanyingSelectorOpen] = useState(false);
  const [accompanyingSelectorDraftId, setAccompanyingSelectorDraftId] = useState(null);

  const [isAddMainGuestOpen, setIsAddMainGuestOpen] = useState(false);
  const [mainGuestEditorMode, setMainGuestEditorMode] = useState("create"); // "create" | "edit"
  const [mainGuestEditorInitialData, setMainGuestEditorInitialData] = useState(null);
  const [mainGuestEditorDraftId, setMainGuestEditorDraftId] = useState(null);
 
  const [isAddAccompanyingOpen, setIsAddAccompanyingOpen] = useState(false);
  const [accompanyingEditorMode, setAccompanyingEditorMode] = useState("create"); // "create" | "edit"
  const [accompanyingEditorInitialData, setAccompanyingEditorInitialData] = useState(null);
  const [accompanyingEditorDraftId, setAccompanyingEditorDraftId] = useState(null);
  const [accompanyingEditorGuestIndex, setAccompanyingEditorGuestIndex] = useState(null);

  const [isChildSelectorOpen, setIsChildSelectorOpen] = useState(false);
  const [childSelectorDraftId, setChildSelectorDraftId] = useState(null);
  const [childSelectorGuestIndex, setChildSelectorGuestIndex] = useState(null);

  const [isAddChildOpen, setIsAddChildOpen] = useState(false);
  const [childEditorMode, setChildEditorMode] = useState("create"); // "create" | "edit"
  const [childEditorInitialData, setChildEditorInitialData] = useState(null);
  const [childEditorDraftId, setChildEditorDraftId] = useState(null);
  const [childEditorGuestIndex, setChildEditorGuestIndex] = useState(null);

  const [roomsToAdd, setRoomsToAdd] = useState(1);
  const normalizedRoomsToAdd = normalizeRoomsToAdd(roomsToAdd);
  const handleRoomsToAddChange = (event) => {
    const nextValue = event.target.value;
    setRoomsToAdd(nextValue === "" ? "" : normalizeRoomsToAdd(nextValue));
  };

  const [showCopyPanel, setShowCopyPanel] = useState(false);
  const [copyFrom, setCopyFrom] = useState("");
  const [copyTo, setCopyTo] = useState("");
  const [isCopying, setIsCopying] = useState(false);
  const [hasAnyRoomRate, setHasAnyRoomRate] = useState(false);
  const [copyFromOptions, setCopyFromOptions] = useState([]);
  const [copyToOptions, setCopyToOptions] = useState([]);
  const canUseCopyInfo =
    !isViewMode &&
    (reservations?.length || 0) > 1 &&
    hasAnyRoomRate;

  const [showGroupExtras, setShowGroupExtras] = useState(false);
  const [showSpecialRate, setShowSpecialRate] = useState(false);
  const [backgroundGroupDraft, setBackgroundGroupDraft] = useState(null);
  const backgroundGroupDraftRef = useRef(null);
  const setBackgroundGroupDraftState = (nextDraft) => {
    backgroundGroupDraftRef.current = nextDraft;
    setBackgroundGroupDraft(nextDraft);
  };
  const [backgroundGroupActionLoading, setBackgroundGroupActionLoading] = useState(false);
  const [expandedRoomRows, setExpandedRoomRows] = useState(() =>
    readExpandedRoomRows(reservation_group_id, mode)
  );
  const [roomMetaIndexByReservationId, setRoomMetaIndexByReservationId] = useState({});
  const [nightlyRowsByReservationId, setNightlyRowsByReservationId] = useState({});
  const [nightlyLoadingByReservationId, setNightlyLoadingByReservationId] = useState({});
  const [nightlyErrorByReservationId, setNightlyErrorByReservationId] = useState({});
  const nightlyRequestsRef = useRef({});
  const nightlyGroupContextRef = useRef(null);
  const clearNightlyBreakdownCache = useCallback(() => {
    nightlyRequestsRef.current = {};
    setNightlyRowsByReservationId({});
    setNightlyLoadingByReservationId({});
    setNightlyErrorByReservationId({});
  }, []);
  const clearNightlyBreakdownCacheFor = useCallback((reservationId) => {
    const id = Number(reservationId);
    if (!id) return;
    delete nightlyRequestsRef.current[id];

    setNightlyRowsByReservationId((prev) => {
      if (!Object.prototype.hasOwnProperty.call(prev, id)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setNightlyLoadingByReservationId((prev) => {
      if (!Object.prototype.hasOwnProperty.call(prev, id)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setNightlyErrorByReservationId((prev) => {
      if (!Object.prototype.hasOwnProperty.call(prev, id)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);
  const normalizeNightlyRows = useCallback((rows) =>
    (Array.isArray(rows) ? rows : [])
      .map((r) => ({
        id: r?.id ?? null,
        date: r?.date ? String(r.date).slice(0, 10) : null,
        basePrice: Number(r?.price || 0),
        price: Number(r?.override?.final_price ?? r?.final_price ?? r?.price ?? 0),
        hasOverride: r?.override != null || r?.final_price != null,
        overrideType: r?.override?.override_type ?? r?.override_type ?? null,
        overrideOperation: r?.override?.operation ?? r?.operation ?? null,
        overrideAmount: r?.override?.override_amount ?? r?.override_amount ?? null,
        overrideReason: r?.override?.reason ?? r?.reason ?? null,
      }))
      .filter((r) => !!r.date)
      .sort((a, b) => String(a.date).localeCompare(String(b.date))), []);

  const loadNightlyBreakdownFor = useCallback(async (reservationId) => {
    const id = Number(reservationId);
    if (!id) return;

    const hasRows = Object.prototype.hasOwnProperty.call(nightlyRowsByReservationId, id);
    if (hasRows || nightlyRequestsRef.current[id]) return;

    const request = Symbol();
    nightlyRequestsRef.current[id] = request;

    setNightlyLoadingByReservationId((prev) => ({ ...prev, [id]: true }));
    setNightlyErrorByReservationId((prev) => ({ ...prev, [id]: null }));

    try {
      const endpoint = isViewMode
        ? `/api/reservations/${id}/nightly-prices`
        : `/api/reservation-drafts/${id}/nightly-prices`;

      const { data } = await axios.get(endpoint);
      if (nightlyRequestsRef.current[id] !== request) return;
      const normalized = normalizeNightlyRows(data);
      setNightlyRowsByReservationId((prev) => ({ ...prev, [id]: normalized }));
    } catch (err) {
      if (nightlyRequestsRef.current[id] !== request) return;
      const status = Number(err?.response?.status || 0);

      if (status === 404) {
        setNightlyRowsByReservationId((prev) => ({ ...prev, [id]: [] }));
      } else {
        console.warn("[RGV][NIGHTLY][LOAD][ERR]", { reservationId: id, err });
        setNightlyRowsByReservationId((prev) => ({ ...prev, [id]: [] }));
        setNightlyErrorByReservationId((prev) => ({
          ...prev,
          [id]: err?.response?.data?.error || t("Failed to load nightly breakdown."),
        }));
      }
    } finally {
      if (nightlyRequestsRef.current[id] === request) {
        delete nightlyRequestsRef.current[id];
        setNightlyLoadingByReservationId((prev) => ({ ...prev, [id]: false }));
      }
    }
  }, [isViewMode, nightlyRowsByReservationId, normalizeNightlyRows, t]);

  useEffect(() => {
    if (nightlyGroupContextRef.current !== `${mode}:${reservation_group_id}`) return;
    reservations.forEach((reservation) => {
      if (expandedRoomRows[reservation.id]) void loadNightlyBreakdownFor(reservation.id);
    });
  }, [expandedRoomRows, reservations, mode, reservation_group_id, loadNightlyBreakdownFor]);

  const [roomCatalogById, setRoomCatalogById] = useState({});
  const [roomCatalogBusinessUnitId, setRoomCatalogBusinessUnitId] = useState(null);
  const roomNumberPickerSavingRef = useRef(false);
  const [roomNumberPicker, setRoomNumberPicker] = useState({
    show: false,
    mode: "assignment",
    reservation: null,
    availableRooms: [],
    occupiedRooms: [],
    loading: false,
    error: "",
    savingRoomId: null,
    effectiveStayDate: "",
    effectiveEndDate: "",
    currentRoomId: null,
    currentRoomLabel: "",
    allowOccupiedSelection: true,
    title: t("Select Suggested Room"),
    subtitle: t("Review available and occupied rooms before applying the suggestion."),
  });
  const [arrivalActionLoadingByReservationId, setArrivalActionLoadingByReservationId] = useState({});
  const [roomCancellationLoadingId, setRoomCancellationLoadingId] = useState(null);
  const [pendingNoShowReservation, setPendingNoShowReservation] = useState(null);
  const [attendanceDialog, setAttendanceDialog] = useState(null);
  const [pendingRevertNoShowReservation, setPendingRevertNoShowReservation] = useState(null);
  const [pendingReverseCheckInReservation, setPendingReverseCheckInReservation] = useState(null);
  const [pendingCheckOutReservation, setPendingCheckOutReservation] = useState(null);
  const [earlyCheckOutDialog, setEarlyCheckOutDialog] = useState(null);
  const [pendingReinstateReservation, setPendingReinstateReservation] = useState(null);
  const [reinstateLoadingId, setReinstateLoadingId] = useState(null);
  const [reinstateDialogState, setReinstateDialogState] = useState(null);
  const [checkInDialogState, setCheckInDialogState] = useState(null);
  const [todayIso, setTodayIso] = useState(() =>
    (demoReadOnlyActive && demoReferenceDate()) || getLocalArrivalParts().arrivalDate
  );
  const [reservationActionsDialog, setReservationActionsDialog] = useState(null);
  const [groupActionsDialogOpen, setGroupActionsDialogOpen] = useState(false);
  const [reservationActionFeedbackState, setReservationActionFeedbackState] = useState(null);
  const [modifyStayDialog, setModifyStayDialog] = useState(null);
  const [guestOccupancyDialog, setGuestOccupancyDialog] = useState(null);
  const [roomCategoryDialog, setRoomCategoryDialog] = useState(null);
  const roomCategoryRateSectionRef = useRef(null);
  const roomCategoryRoomSectionRef = useRef(null);
  const roomCategoryReviewSectionRef = useRef(null);
  const [ratesPricesDialog, setRatesPricesDialog] = useState(null);
  const [reservationExtrasDialog, setReservationExtrasDialog] = useState(null);
  const [reservationChargesDialog, setReservationChargesDialog] = useState(null);
  const [ratesOverridePopupOpen, setRatesOverridePopupOpen] = useState(false);
  const [roomCategoryOverridePopupOpen, setRoomCategoryOverridePopupOpen] = useState(false);
  const [reservationChangeHistory, setReservationChangeHistory] = useState({
    summary: null,
    reservations: [],
  });
  const [reservationChangeHistoryLoading, setReservationChangeHistoryLoading] = useState(false);
  const [reservationChangeHistoryError, setReservationChangeHistoryError] = useState("");
  const [reservationHistoryDialogId, setReservationHistoryDialogId] = useState(null);
  const [reservationHistoryActiveTab, setReservationHistoryActiveTab] = useState("overview");
  const [reservationCheckTimeSavingByKey, setReservationCheckTimeSavingByKey] = useState({});
  const [reservationCheckTimeErrorById, setReservationCheckTimeErrorById] = useState({});
  const embeddedActionsOpenedRef = useRef("");
  const embeddedInitialLoadCompleteRef = useRef(false);
  const ratesPricesDeepLinkHandledRef = useRef("");
  const openRatesPricesDialogRef = useRef(null);

  //07.02.2026 - integrar información de pago en la reserva

  // 🆕 Payments (saved cards) - DB is the source of truth
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentMethodsLoading, setPaymentMethodsLoading] = useState(false);
  const [, setPaymentMethodsErr] = useState(null);
  const [summaryPaymentBalance, setSummaryPaymentBalance] = useState(null);
  const [summaryPaymentBalanceLoading, setSummaryPaymentBalanceLoading] = useState(false);

  useEffect(() => {
    const scopedThemeClass = isLightTheme
      ? "rgv-brand-scope--light"
      : "rgv-brand-scope--dark";
    document.body.classList.add("rgv-brand-scope", scopedThemeClass);
    return () => {
      document.body.classList.remove("rgv-brand-scope", scopedThemeClass);
    };
  }, [isLightTheme]);

  useEffect(() => {
    const syncToday = () => {
      const nextToday = (demoReadOnlyActive && demoReferenceDate()) || getLocalArrivalParts().arrivalDate;
      setTodayIso((prev) => (prev === nextToday ? prev : nextToday));
    };

    syncToday();
    const timer = window.setInterval(syncToday, 60 * 1000);
    return () => window.clearInterval(timer);
  }, [demoReadOnlyActive]);

  useEffect(() => {
    const resolvedBusinessUnitId = Number(
      effectiveBusinessUnitId || businessUnitId
    );
    if (!resolvedBusinessUnitId) {
      setReservationModificationPolicy(
        normalizeReservationManualModificationPolicy(null)
      );
      return undefined;
    }

    const controller = new AbortController();
    axios
      .get("/api/hotel-settings", {
        params: { business_unit_id: resolvedBusinessUnitId },
        signal: controller.signal,
      })
      .then(({ data }) => {
        setReservationModificationPolicy(
          normalizeReservationManualModificationPolicy(
            data?.reservation_manual_modification_policy
          )
        );
      })
      .catch((error) => {
        if (error?.code === "ERR_CANCELED") return;
        if (error?.response?.status !== 404) {
          console.warn(
            "[ReservationGroupView][MODIFICATION_POLICY][LOAD][ERR]",
            error?.response?.data || error
          );
        }
        setReservationModificationPolicy(
          normalizeReservationManualModificationPolicy(null)
        );
      });

    return () => controller.abort();
  }, [businessUnitId, effectiveBusinessUnitId]);

  const isCanceledError = (err) =>
    err?.code === "ERR_CANCELED" ||
    err?.name === "CanceledError" ||
    err?.message === "canceled";

  const isOtaReservation = isChannelManagerReservationRecord;

  const getManualModificationBlock = (reservation, actions) =>
    translateReservationModificationBlock(getReservationModificationBlock({
      reservation,
      policy: reservationModificationPolicy,
      actions,
    }), languageId);

  const showManualModificationBlockedMsg = (reservation, actions) => {
    const block = getManualModificationBlock(reservation, actions);
    if (!block) return false;
    setReservationActionFeedbackState({
      title: t("Manual change not allowed"),
      message: block.message,
      tone: "warning",
    });
    return true;
  };






  useEffect(() => {
    console.log("[STATE][RESERVATIONS_UPDATED]");
    console.log("[STATE][RES_LEN]", reservations?.length);
    console.log(
      "[STATE][FIRST_GUESTS_LEN]",
      reservations?.[0]?.guests?.length
    );
    console.log(
      "[STATE][FIRST_GUESTS]",
      reservations?.[0]?.guests
    );
  }, [reservations]);



  useEffect(() => {
    console.log("[GROUP][BOOKER][ADD_POPUP][STATE]", { isAddBookerOpen });
  }, [isAddBookerOpen]);


  console.log("[ReservationGroupView][Render]", { reservation_group_id });

  const hydrateGuests = async (reservationsList) => {
    const enriched = await Promise.all(
      (reservationsList || []).map(async (r) => {
        try {
          const { data } = await axios.get(
            `/api/reservation-drafts/draft-guests/by-draft/${r.id}`
          );

          const rows = Array.isArray(data) ? data : [];

          const main_guest = rows.find((g) => g.is_main_guest)?.customer || null;

          const accompanying_guests = rows
            .filter((g) => !g.is_main_guest && !g.is_child)
            .map((g) => ({ guest_index: g.guest_index ?? null, customer: g.customer || null }))
            .sort((a, b) => (Number(a.guest_index ?? 9999) - Number(b.guest_index ?? 9999)));

          const children_guests = rows
            .filter((g) => g.is_child)
            .map((g) => ({ guest_index: g.guest_index ?? null, customer: g.customer || null }))
            .sort((a, b) => (Number(a.guest_index ?? 9999) - Number(b.guest_index ?? 9999)));

          return {
            ...r,
            main_guest,
            accompanying_guests,
            children_guests,
          };
        } catch (err) {
          console.warn("[GROUP][GUESTS][HYDRATE][ERR]", { draftId: r.id, err });
          return {
            ...r,
            main_guest: r.main_guest || null,
            accompanying_guests: r.accompanying_guests || [],
            children_guests: r.children_guests || [],
          };
        }
      })
    );

    return enriched;
  };

  const loadCopyOptions = async () => {
    try {
      const { data } = await axios.get(
        `/api/reservation-draft-groups/${reservation_group_id}/copy-info-options`
      );

      setHasAnyRoomRate(!!data?.has_any_room_rate);
      setCopyFromOptions(Array.isArray(data?.copy_from_options) ? data.copy_from_options : []);
      setCopyToOptions(Array.isArray(data?.copy_to_options) ? data.copy_to_options : []);
    } catch (err) {
      console.warn("[GROUP][COPY_OPTIONS][ERR]", err);
      setHasAnyRoomRate(false);
      setCopyFromOptions([]);
      setCopyToOptions([]);
    }
  };

  const closeCopyInfoModal = () => {
    setCopyFrom("");
    setCopyTo("");
    setShowCopyPanel(false);
  };

  const openCopyInfoModal = () => {
    setCopyFrom("");
    setCopyTo("");
    setShowCopyPanel(true);
  };

  const handleCopyInfoSubmit = async () => {
    if (!copyFrom || !copyTo || isCopying) return;

    try {
      setIsCopying(true);

      console.log("[GROUP][COPY_INFO][CLICK]", {
        reservation_group_id,
        copyFrom,
        copyTo,
      });

      const { data } = await axios.post(
        `/api/reservation-draft-groups/${reservation_group_id}/copy/${copyFrom}/to/${copyTo}`
      );

      const copiedTo = Array.isArray(data?.copied_to) ? data.copied_to : [];
      const skipped = Array.isArray(data?.skipped) ? data.skipped : [];

      const labelByDraftId = (id) => {
        const opt = (copyToOptions || []).find((option) => Number(option.id) === Number(id));
        if (opt?.label) return opt.label;
        const reservation = (reservations || []).find((item) => Number(item.id) === Number(id));
        return reservation?.locator || `#${id}`;
      };

      const copiedCount = copiedTo.length;
      const skippedCount = skipped.length;

      console.log("[GROUP][COPY_INFO][RESULT]", {
        copiedCount,
        skippedCount,
        copiedTo,
        skipped,
      });

      if (copiedCount === 0) {
        const details = skipped
          .slice(0, 5)
          .map((item) => t("{p0}: no availability", { p0: labelByDraftId(item.reservation_draft_id) }))
          .join("\n");

        alert(
          formatGroupError(t("No rooms were copied.\n") +
          t("Skipped: {p0}\n", { p0: skippedCount }) +
          (details ? t("\nDetails:\n{p0}", { p0: details }) : ""))
        );
        return;
      }

      if (skippedCount > 0) {
        const details = skipped
          .slice(0, 5)
          .map((item) => t("{p0}: no availability", { p0: labelByDraftId(item.reservation_draft_id) }))
          .join("\n");

        alert(
          formatGroupError(t("Copied: {p0}\n", { p0: copiedCount }) +
          t("Skipped: {p0}\n", { p0: skippedCount }) +
          (details ? t("\nDetails:\n{p0}", { p0: details }) : ""))
        );
      } else {
        alert(formatGroupError(t("Copied: {p0}", { p0: copiedCount })));
      }

      closeCopyInfoModal();
      await loadGroup();
    } catch (err) {
      const backendError = err?.response?.data;
      console.error("[GROUP][COPY_INFO][ERR]", {
        status: err?.response?.status,
        backendError,
        err,
      });

      const copiedTo = Array.isArray(backendError?.copied_to) ? backendError.copied_to : [];
      const skipped = Array.isArray(backendError?.skipped) ? backendError.skipped : [];

      if (copiedTo.length === 0 && skipped.length > 0) {
        alert(formatGroupError(t("No rooms were copied.\nSkipped: {p0}", { p0: skipped.length })));
        return;
      }

      alert(formatGroupError(backendError?.error || t("Error copying info.")));
    } finally {
      setIsCopying(false);
    }
  };

  // 🆕 Payments (saved cards): reads reservation_group_payment_methods
  const loadPaymentMethods = async (options = {}) => {
    if (demoReadOnlyActive) return;
    if (!canViewReservationPayments) {
      setPaymentMethods([]);
      setPaymentMethodsErr(t("You do not have permission to view reservation payments."));
      setPaymentMethodsLoading(false);
      return;
    }

    try {
      setPaymentMethodsLoading(true);
      setPaymentMethodsErr(null);

      console.log("[GROUP][PAYMENTS][PM][LOAD][START]", { reservation_group_id });

      const { data } = await axios.get(
        `/api/stripe-transactions/groups/${reservation_group_id}/payment-methods`,
        options.signal ? { signal: options.signal } : undefined
      );

      const rows = Array.isArray(data) ? data : [];
      setPaymentMethods(rows);

      console.log("[GROUP][PAYMENTS][PM][LOAD][OK]", {
        reservation_group_id,
        count: rows.length,
      });
    } catch (err) {
      if (isCanceledError(err)) return;
      console.warn("[GROUP][PAYMENTS][PM][LOAD][ERR]", err?.response?.data || err);
      setPaymentMethods([]);
      setPaymentMethodsErr(err?.response?.data?.error || t("Failed to load saved cards."));
    } finally {
      setPaymentMethodsLoading(false);
    }
  };

  const loadSummaryPaymentBalance = async (options = {}) => {
    if (demoReadOnlyActive) return;
    if (!isViewMode || !canViewReservationPayments) {
      setSummaryPaymentBalance(null);
      setSummaryPaymentBalanceLoading(false);
      return;
    }

    try {
      setSummaryPaymentBalanceLoading(true);
      const { data } = await axios.get(
        `/api/reservation-groups/${reservation_group_id}/payment-balance`,
        options.signal ? { signal: options.signal } : undefined
      );
      setSummaryPaymentBalance(data || null);
    } catch (err) {
      if (isCanceledError(err)) return;
      console.warn("[GROUP][SUMMARY][PAYMENT_BALANCE][ERR]", err?.response?.data || err);
      setSummaryPaymentBalance(null);
    } finally {
      setSummaryPaymentBalanceLoading(false);
    }
  };

  const loadRoomCatalog = async (businessUnitIdValue, options = {}) => {
    const bu = Number(businessUnitIdValue);
    if (!Number.isFinite(bu) || bu <= 0) return;

    if (
      Number(roomCatalogBusinessUnitId) === bu &&
      Object.keys(roomCatalogById || {}).length > 0
    ) {
      return;
    }

    try {
      const { data } = await axios.get(
        "/api/rooms",
        options.signal
          ? { params: { business_unit_id: bu }, signal: options.signal }
          : { params: { business_unit_id: bu } }
      );

      const rows = Array.isArray(data) ? data : [];
      const nextMap = {};

      rows.forEach((room) => {
        const id = Number(room?.id);
        if (!Number.isFinite(id) || id <= 0) return;

        nextMap[id] = {
          room_number: room?.room_number ?? null,
          name: room?.name ?? null,
          room_category_id: room?.room_category_id ?? room?.RoomCategory?.id ?? null,
          room_category_name: room?.RoomCategory?.name ?? room?.room_category?.name ?? null,
          floor: room?.floor ?? null,
          bed_type: room?.bed_type ?? null,
          view: room?.view ?? null,
          description: room?.description ?? null,
        };
      });

      setRoomCatalogById(nextMap);
      setRoomCatalogBusinessUnitId(bu);
    } catch (err) {
      if (isCanceledError(err)) return;
      console.warn("[ReservationGroupView][ROOM_CATALOG][LOAD][ERR]", {
        business_unit_id: bu,
        err,
      });
    }
  };

  const loadReservationChangeHistory = async (options = {}) => {
    if (demoReadOnlyActive) return;
    if (!isViewMode || !reservation_group_id) {
      setReservationChangeHistory({ summary: null, reservations: [] });
      setReservationChangeHistoryError("");
      setReservationChangeHistoryLoading(false);
      return;
    }

    try {
      setReservationChangeHistoryLoading(true);
      setReservationChangeHistoryError("");
      const { data } = await axios.get(
        `/api/reservation-groups/${reservation_group_id}/change-history`,
        options.signal ? { signal: options.signal } : undefined
      );

      setReservationChangeHistory({
        summary: data?.summary || null,
        reservations: Array.isArray(data?.reservations) ? data.reservations : [],
      });
    } catch (err) {
      if (isCanceledError(err)) return;
      console.warn("[ReservationGroupView][CHANGE_HISTORY][LOAD][ERR]", err?.response?.data || err);
      setReservationChangeHistory({ summary: null, reservations: [] });
      setReservationChangeHistoryError(
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        t("Reservation history could not be loaded.")
      );
    } finally {
      setReservationChangeHistoryLoading(false);
    }
  };

  const loadGroup = async (options = {}) => {
  console.log("[ReservationGroupView][LOAD]", {
    reservation_group_id,
    mode,
  });

  try {
    const endpoint =
      mode === "view"
        ? `/api/reservation-groups/${reservation_group_id}/full`
        : `/api/reservation-draft-groups/${reservation_group_id}`;



    console.log("[ReservationGroupView][LOAD][GET]", endpoint);

    const { data } = await axios.get(
      endpoint,
      options.signal ? { signal: options.signal } : undefined
    );

    if (mode === "view" && !demoReadOnlyActive) {
      void loadReservationChangeHistory(options);
    } else {
      setReservationChangeHistory({ summary: null, reservations: [] });
      setReservationChangeHistoryError("");
      setReservationHistoryDialogId(null);
    }

    console.log("[LOAD][RAW_DATA]", data);
    console.log("[LOAD][RAW_KEYS]", Object.keys(data || {}));
    console.log("[LOAD][RAW_RES_LEN]", data?.reservations?.length);
    console.log(
      "[LOAD][RAW_FIRST_GUESTS_LEN]",
      data?.reservations?.[0]?.guests?.length
    );
    console.log(
      "[LOAD][RAW_FIRST_GUESTS]",
      data?.reservations?.[0]?.guests
    );

    

    let normalizedData = data;

    // 🔒 VIEW → el endpoint devuelve SOLO un array de reservas
    if (mode === "view" && Array.isArray(data)) {
      normalizedData = {
        group: {
          id: reservation_group_id,
        },
        reservations: data,
      };
    }

    setGroup(normalizedData.group);

    const roomCatalogBuId =
      normalizedData?.group?.business_unit_id ??
      getBusinessUnitIdFromReservations(normalizedData?.reservations) ??
      businessUnitId ??
      null;
    void loadRoomCatalog(roomCatalogBuId, options);

    console.log("[LOAD][NORMALIZED]", normalizedData);

    console.log(
      "[LOAD][NORMALIZED_FIRST_GUESTS_LEN]",
      normalizedData?.reservations?.[0]?.guests?.length
    );


    if (mode === "view") {
      const mapped = (normalizedData.reservations || []).map((r) => {
        const rows = Array.isArray(r.guests) ? r.guests : [];

        const main_guest =
          rows.find((g) => g.is_main_guest)?.customer || null;

        const accompanying_guests = rows
          .filter((g) => !g.is_main_guest && !g.is_child)
          .map((g) => ({
            guest_index: g.guest_index ?? null,
            customer: g.customer || null,
          }))
          .sort((a, b) => Number(a.guest_index ?? 9999) - Number(b.guest_index ?? 9999));

        const children_guests = rows
          .filter((g) => g.is_child)
          .map((g) => ({
            guest_index: g.guest_index ?? null,
            customer: g.customer || null,
          }))
          .sort((a, b) => Number(a.guest_index ?? 9999) - Number(b.guest_index ?? 9999));

        return {
          ...r,
          main_guest,
          accompanying_guests,
          children_guests,
        };
      });

      console.log("[VIEW][MAPPED_GUESTS]", {
        reservations: mapped.length,
        first: {
          main_guest: mapped[0]?.main_guest,
          acc: mapped[0]?.accompanying_guests?.length,
          children: mapped[0]?.children_guests?.length,
        },
      });

      if (options.signal?.aborted) return null;
      nightlyGroupContextRef.current = `${mode}:${reservation_group_id}`;
      clearNightlyBreakdownCache();
      setReservations(mapped);
    }
 else {
      // ✏️ DRAFT: comportamiento actual intacto
      const enrichedReservations = await hydrateGuests(normalizedData.reservations || []);
      if (options.signal?.aborted) return null;
      nightlyGroupContextRef.current = `${mode}:${reservation_group_id}`;
      clearNightlyBreakdownCache();
      setReservations(enrichedReservations);

      // 🔥 Auto-open popup SOLO en draft
      if (skipNextAutoOpenRef.current) {
        console.log("[ReservationGroupView][AUTOPOPUP] skipped once (manual close)");
        skipNextAutoOpenRef.current = false;
      } else if (
        Array.isArray(normalizedData.reservations) &&
        normalizedData.reservations.length === 1 &&
        !normalizedData.reservations[0].check_in_date
      ) {
        console.log("[ReservationGroupView][AUTOPOPUP] opening first reservation popup", {
          reservation_draft_id: normalizedData.reservations[0].id,
        });

        setActiveDraftId(normalizedData.reservations[0].id);
        setIsDraftPopupOpen(true);
      }

      await loadCopyOptions();
    }

    // No bloquear el primer render por carga secundaria de tarjetas.
    if (canViewReservationPayments && !demoReadOnlyActive) {
      void loadPaymentMethods(options);
      void loadSummaryPaymentBalance(options);
    } else {
      setPaymentMethods([]);
      setPaymentMethodsErr(t("You do not have permission to view reservation payments."));
      setSummaryPaymentBalance(null);
      setSummaryPaymentBalanceLoading(false);
    }

    // Carga de comentarios (no bloquea el render principal)
    const commentsGroupParam = isViewMode ? "reservation_group_id" : "reservation_group_draft_id";
    if (!demoReadOnlyActive) {
      axios.get(`/api/reservation-group-comments?${commentsGroupParam}=${reservation_group_id}`)
        .then(({ data }) => setCommentsData(data))
        .catch(() => {});
    }

    if (actionsOnly) {
      if (
        embeddedInitialLoadCompleteRef.current &&
        typeof onReservationChange === "function"
      ) {
        onReservationChange();
      }
      embeddedInitialLoadCompleteRef.current = true;
    }

    return normalizedData;
  } catch (err) {
    if (isCanceledError(err)) return null;
    console.error("[ReservationGroupView][LOAD][ERR]", err);
    if (demoReadOnlyActive) {
      setDemoLoadError(Number(err?.response?.status) === 404
        ? "Esta reserva no está incluida en la demo."
        : "No se ha podido cargar el detalle de la demo.");
    }
    if (actionsOnly && typeof onActionsError === "function") {
      onActionsError(err);
    }
    return null;
  } finally {
    setLoading(false);
  }
};


  useEffect(() => {
    if (!reservation_group_id) return;

    const controller = new AbortController();
    setExpandedRoomRows(readExpandedRoomRows(reservation_group_id, mode));
    nightlyGroupContextRef.current = null;
    clearNightlyBreakdownCache();
    setLoading(true);
    setDemoLoadError("");
    if (demoReadOnlyActive) setGroup(null);
    loadGroup({ signal: controller.signal });

    return () => {
      controller.abort();
      nightlyRequestsRef.current = {};
    };
    // loadGroup intentionally follows the selected group/mode lifecycle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reservation_group_id, mode]);

  useEffect(() => {
    const targetReservationId = Number(initialReservationActionsId);
    if (
      !actionsOnly ||
      loading ||
      !group ||
      !Number.isFinite(targetReservationId) ||
      targetReservationId <= 0
    ) {
      return;
    }

    const sessionKey = `${reservation_group_id}:${targetReservationId}`;
    if (embeddedActionsOpenedRef.current === sessionKey) return;

    const reservation = (reservations || []).find(
      (item) => Number(item?.id) === targetReservationId
    );
    if (!reservation) {
      embeddedActionsOpenedRef.current = sessionKey;
      if (typeof onActionsError === "function") {
        onActionsError(new Error(t("The selected reservation is not part of this reservation group.")));
      }
      return;
    }

    embeddedActionsOpenedRef.current = sessionKey;
    setReservationActionsDialog({
      reservation,
      mode: resolveReservationActionMode(reservation),
    });
    if (typeof onActionsOpen === "function") {
      onActionsOpen(reservation);
    }
  }, [
    actionsOnly,
    group,
    initialReservationActionsId,
    loading,
    onActionsError,
    onActionsOpen,
    reservation_group_id,
    reservations,
    t,
  ]);

  useEffect(() => {
    const deepLink = readRatesPricesDeepLink(searchParams);
    if (!deepLink) {
      ratesPricesDeepLinkHandledRef.current = "";
      return;
    }
    if (demoReadOnlyActive || actionsOnly || loading || !group) return;

    const requestKey = `${reservation_group_id}:${deepLink.reservationId || "invalid"}`;
    if (ratesPricesDeepLinkHandledRef.current === requestKey) return;
    ratesPricesDeepLinkHandledRef.current = requestKey;

    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete("open_action");
    nextSearchParams.delete("reservation_id");
    setSearchParams(nextSearchParams, { replace: true });

    if (!isViewMode) {
      setReservationActionFeedbackState({
        title: t("Rates & Prices"),
        message: t("Rates & Prices is available from the confirmed reservation view."),
        tone: "warning",
      });
      return;
    }

    if (!deepLink.reservationId) {
      setReservationActionFeedbackState({
        title: t("Rates & Prices"),
        message: t("The requested reservation is not valid."),
        tone: "warning",
      });
      return;
    }

    const reservation = (reservations || []).find(
      (item) => Number(item?.id) === deepLink.reservationId
    );
    if (!reservation) {
      setReservationActionFeedbackState({
        title: t("Rates & Prices"),
        message: t("The requested reservation is not part of this reservation group."),
        tone: "warning",
      });
      return;
    }

    const openDialog = openRatesPricesDialogRef.current;
    if (typeof openDialog !== "function") {
      setReservationActionFeedbackState({
        title: t("Rates & Prices"),
        message: t("Rates & Prices could not be opened. Refresh the reservation and try again."),
        tone: "warning",
      });
      return;
    }

    void openDialog(reservation, "rates_prices");
  }, [
    demoReadOnlyActive,
    actionsOnly,
    group,
    isViewMode,
    loading,
    reservation_group_id,
    reservations,
    searchParams,
    setSearchParams,
    t,
  ]);

  if (loading) return demoReadOnlyActive && !actionsOnly ? <p role="status">Cargando detalle de la demo...</p> : null;
  if (!group) return demoReadOnlyActive && !actionsOnly ? (
    <div className="rgv-page rgv-page--light">
      <p role="alert">{demoLoadError || "Esta reserva no está incluida en la demo."}</p>
      <button type="button" className="btn rgv-statActionBtn" onClick={goToReservationsList}>Volver a reservas</button>
    </div>
  ) : null;

  const refreshDraftGuestsFor = async (draftId) => {
    const { data } = await axios.get(`/api/reservation-drafts/draft-guests/by-draft/${draftId}`);
    const rows = Array.isArray(data) ? data : [];

    const main_guest = rows.find((g) => g.is_main_guest)?.customer || null;

    const accompanying_guests = rows
      .filter((g) => !g.is_main_guest && !g.is_child)
      .map((g) => ({ guest_index: g.guest_index ?? null, customer: g.customer || null }))
      .sort((a, b) => (Number(a.guest_index ?? 9999) - Number(b.guest_index ?? 9999)));

    const children_guests = rows
      .filter((g) => g.is_child)
      .map((g) => ({ guest_index: g.guest_index ?? null, customer: g.customer || null }))
      .sort((a, b) => (Number(a.guest_index ?? 9999) - Number(b.guest_index ?? 9999)));

    setReservations((prev) =>
      prev.map((r) =>
        r.id === draftId
          ? { ...r, main_guest, accompanying_guests, children_guests }
          : r
      )
    );

    console.log(
      "[VIEW][DEBUG] guests in first reservation:",
      data.reservations?.[0]?.guests
    );

  };

  const loadFullCustomer = async (customerId) => {
    const id = Number(customerId);
    if (!id) return null;

    try {
      const { data } = await axios.get(`/api/customers/${id}`);
      return data || null;
    } catch (err) {
      console.warn("[CUSTOMER][FULL][GET][ERR]", { customerId: id, err });
      return null;
    }
  };

  const parseRequiredCustomerFields = (raw) => {
    if (typeof raw === "string") {
      try {
        return JSON.parse(raw);
      } catch (e) {
        console.warn("[REQUIRED_FIELDS][PARSE][ERR]", { raw });
        return null;
      }
    }
    return raw;
  };

  const getRequiredFieldsForRole = (parsed, roleKey) => {
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      if (roleKey === "accompanying") {
        if (Array.isArray(parsed.accompanying)) return parsed.accompanying;
        if (Array.isArray(parsed.accompanying_guest)) return parsed.accompanying_guest;
        if (Array.isArray(parsed.accompanying_guests)) return parsed.accompanying_guests;
        return [];
      }

      if (roleKey === "child") {
        if (Array.isArray(parsed.child)) return parsed.child;
        if (Array.isArray(parsed.children)) return parsed.children;
        return [];
      }

      return Array.isArray(parsed[roleKey]) ? parsed[roleKey] : [];
    }

    if (Array.isArray(parsed)) return parsed;
    return [];
  };

  const getMissingRequiredFields = (required, customerData) =>
    (required || []).filter((f) => {
      const v = customerData?.[f];
      return v == null || String(v).trim() === "";
    });

  const normalizeCheckInRoleKey = (value) => {
    const key = String(value || "").trim().toLowerCase();
    if (key === "mainguest" || key === "main_guest") return "main_guest";
    if (key === "accompanying_guest" || key === "accompanying_guests") return "accompanying";
    if (key === "children" || key === "children_guest" || key === "children_guests") return "child";
    if (key === "booker" || key === "accompanying" || key === "child") return key;
    return "";
  };

  const normalizeCheckInSettings = (rawSettings) => {
    const source = rawSettings || {};
    const rawRoles = Array.isArray(source.required_customer_roles_check_in)
      ? source.required_customer_roles_check_in
      : ["main_guest"];
    const rawFields = parseRequiredCustomerFields(source.required_customer_fields_check_in) || {};
    const fieldRuleRoles = ["booker", "main_guest", "accompanying", "child"].filter((role) => {
      const fields = getRequiredFieldsForRole(rawFields, role);
      return fields.length > 0;
    });
    const roles = [
      "main_guest",
      ...rawRoles
        .map(normalizeCheckInRoleKey)
        .filter((role) => role && role !== "main_guest"),
      ...fieldRuleRoles,
    ];

    return {
      required_customer_roles_check_in: [...new Set(roles)],
      required_customer_fields_check_in: {
        booker: [...new Set(getRequiredFieldsForRole(rawFields, "booker"))],
        main_guest: [...new Set(getRequiredFieldsForRole(rawFields, "main_guest"))],
        accompanying: [...new Set(getRequiredFieldsForRole(rawFields, "accompanying"))],
        child: [...new Set(getRequiredFieldsForRole(rawFields, "child"))],
      },
      require_full_payment_for_check_in:
        source.require_full_payment_for_check_in === true ||
        String(source.require_full_payment_for_check_in).toLowerCase() === "true",
    };
  };

  const amountShapeToNumber = (value) => {
    if (value && typeof value === "object" && value.cents != null) {
      const cents = Number(value.cents);
      return Number.isFinite(cents) ? cents / 100 : 0;
    }

    if (value && typeof value === "object" && value.amount != null) {
      const amount = Number(value.amount);
      return Number.isFinite(amount) ? amount : 0;
    }

    const amount = Number(value);
    return Number.isFinite(amount) ? amount : 0;
  };

  const normalizeReservationStatus = (status) => String(status || "").trim().toLowerCase();
  const getReservationCheckInDateIso = (value) => toDateInputValue(value);
  const getReservationArrivalDateIso = (value) => toDateInputValue(value);

  const isCheckedInReservation = (reservation) =>
    ["checked in", "checkedin"].includes(normalizeReservationStatus(reservation?.status));

  const canManageArrivalForReservation = (reservation) => {
    const checkInDateIso = getReservationCheckInDateIso(reservation?.check_in_date);
    return Boolean(checkInDateIso) && checkInDateIso <= todayIso;
  };

  const canCheckInReservation = (reservation) => {
    const normalizedStatus = normalizeReservationStatus(reservation?.status);

    return (
      canManageArrivalForReservation(reservation) &&
      normalizedStatus !== "checked in" &&
      normalizedStatus !== "checked out" &&
      normalizedStatus !== "cancelled" &&
      normalizedStatus !== "no show" &&
      normalizedStatus !== "noshow"
    );
  };

  const canMarkNoShowReservation = (reservation) => {
    const normalizedStatus = normalizeReservationStatus(reservation?.status);

    return (
      canManageArrivalForReservation(reservation) &&
      ["confirmed", "requested"].includes(normalizedStatus)
    );
  };

  const canReverseCheckInReservation = (reservation) =>
    isCheckedInReservation(reservation) &&
    getReservationArrivalDateIso(reservation?.arrival_date) === todayIso;

  const canCheckOutReservation = (reservation) =>
    isCheckedInReservation(reservation) &&
    !canReverseCheckInReservation(reservation);

  const canShowCancelReservationAction = (reservation) => {
    const normalizedStatus = normalizeReservationStatus(reservation?.status);

    return (
      isViewMode &&
      normalizedStatus !== "checked in" &&
      normalizedStatus !== "cancelled" &&
      normalizedStatus !== "checked out" &&
      normalizedStatus !== "no show" &&
      normalizedStatus !== "noshow"
    );
  };

  const canCancelReservationAction = (reservation) =>
    canShowCancelReservationAction(reservation);

  const getReservationActionPolicyActions = (actionKey) => {
    if (actionKey === "cancel") return ["cancellation"];
    if (["modify_stay", "late_arrival", "regularize_stay"].includes(actionKey)) return ["dates", "rate_prices"];
    if (actionKey === "guests_occupancy") return ["guests", "rate_prices"];
    if (actionKey === "room_category") return ["room_category", "rate_prices"];
    if (actionKey === "rates_prices" || actionKey === "price_adjustment") {
      return ["rate_prices"];
    }
    return [];
  };

  const applyReservationModificationPolicy = (action, reservation) => {
    if (demoReadOnlyActive) {
      return { ...action, disabled: true, hint: DEMO_ACTION_HINT, policyBlocked: false };
    }
    if (action.disabled) return action;
    const policyActions = getReservationActionPolicyActions(action?.key);
    if (!policyActions.length) return action;
    const block = getManualModificationBlock(reservation, policyActions);
    if (!block) return action;
    return {
      ...action,
      disabled: true,
      hint: block.message,
      policyBlocked: true,
    };
  };

  const getReservationActionMode = (reservation) =>
    resolveReservationActionMode(reservation);

  const getReservationStatusActionItems = (reservation) => {
    const normalizedStatus = normalizeReservationStatus(reservation?.status);
    const actions = [];
    const attendanceEligible = ["confirmed", "no show", "noshow"].includes(normalizedStatus);
    const pastArrival = toDateInputValue(reservation?.check_in_date) < todayIso;
    const attendanceLabels = attendanceMessages(languageId);
    if (attendanceEligible && pastArrival) {
      if (canUpdateReservations && toDateInputValue(reservation?.check_out_date) > todayIso) {
        actions.push({ key: "late_arrival", title: attendanceLabels.late_arrival, icon: FiLogIn, tone: "success" });
      }
      if (!demoReadOnlyActive) {
        actions.push({
          key: "regularize_stay", title: attendanceLabels.regularize_stay, icon: FiRefreshCcw, tone: "warning",
          disabled: !canRegularizeStays,
          hint: canRegularizeStays ? undefined : attendanceLabels.regularizePermissionRequired,
        });
      }
    }
    if (reservation.attendance_resolution && (canUpdateReservations || canRegularizeStays)) {
      actions.push({ key: "attendance_review", title: attendanceLabels.review, icon: FiRefreshCcw });
    }

    if (canCheckInReservation(reservation) && !(attendanceEligible && pastArrival)) {
      actions.push({
        key: "check_in",
        title: t("Check In"),
        icon: FiLogIn,
        handler: "check_in",
        tone: "success",
      });
    }

    if (canMarkNoShowReservation(reservation)) {
      actions.push({
        key: "no_show",
        title: t("Mark as No Show"),
        icon: FiAlertTriangle,
        handler: "no_show",
        tone: "warning",
      });
    }

    if (canReverseCheckInReservation(reservation)) {
      actions.push({
        key: "reverse_check_in",
        title: t("Reverse Check-In"),
        icon: FiRefreshCcw,
        handler: "reverse_check_in",
        tone: "warning",
      });
    } else if (canCheckOutReservation(reservation)) {
      actions.push({
        key: "check_out",
        title: t("Check-Out"),
        icon: FiLogOut,
        handler: "check_out",
        tone: "success",
      });
    }

    if (canCancelReservationAction(reservation)) {
      actions.push({
        key: "cancel",
        title: t("Cancel Reservation"),
        icon: FiX,
        handler: "cancel",
        tone: "danger",
      });
    }

    if (normalizedStatus === "no show" || normalizedStatus === "noshow") {
      actions.push({
        key: "revert_no_show",
        title: t("Revert No Show"),
        icon: FiRefreshCcw,
        handler: "revert_no_show",
        tone: "success",
      });
    }

    if (normalizedStatus === "cancelled") {
      actions.push({
        key: "reinstate",
        title: t("Reinstate"),
        icon: FiRefreshCcw,
        handler: "reinstate",
        tone: "success",
      });
    }

    return actions;
  };

  const getReservationWorkflowActionItems = (mode) => {
    if (mode === "in_house") return IN_HOUSE_RESERVATION_WORKFLOW_ACTIONS;
    if (mode === "confirmed") return CONFIRMED_RESERVATION_WORKFLOW_ACTIONS;
    if (mode === "checked_out") return CHECKED_OUT_RESERVATION_WORKFLOW_ACTIONS;
    return [];
  };

  const getReservationActionItems = (mode, reservation) => [
    ...getReservationStatusActionItems(reservation),
    ...getReservationWorkflowActionItems(mode),
  ].map((action) =>
    applyReservationModificationPolicy(action, reservation)
  );

  const getReservationActionTitle = (mode) =>
    mode === "in_house"
      ? t("In-House Actions")
      : mode === "checked_out"
        ? t("Post-Stay Actions")
        : t("Reservation Actions");

  const openReservationActionsDialog = (reservation) => {
    if (!reservation?.id) return;
    setReservationActionsDialog({
      reservation,
      mode: getReservationActionMode(reservation),
    });
  };

  const closeReservationActionsDialog = () => {
    setReservationActionsDialog(null);
    if (actionsOnly && typeof onActionsClose === "function") {
      onActionsClose();
    }
  };

  const updateModifyStayDialog = (updates) => {
    setModifyStayDialog((prev) => (prev ? { ...prev, ...updates } : prev));
  };

  const openModifyStayDialog = (reservation) => {
    if (
      showManualModificationBlockedMsg(reservation, [
        "dates",
        "rate_prices",
      ])
    ) {
      return;
    }
    if (!reservation?.id || !isViewMode) {
      setReservationActionFeedbackState({
        title: t("Modify Stay"),
        message: t("Modify Stay is available from the confirmed reservation view."),
        tone: "warning",
      });
      return;
    }

    const checkInDate = toDateInputValue(reservation.check_in_date);
    const checkOutDate = toDateInputValue(reservation.check_out_date);

    setModifyStayDialog({
      reservation,
      isInHouseStay: isCheckedInReservation(reservation),
      originalCheckInDate: checkInDate,
      originalCheckOutDate: checkOutDate,
      checkInDate,
      checkOutDate,
      loading: false,
      error: "",
      reason: "",
    });
  };

  const closeModifyStayDialog = () => {
    if (modifyStayDialog?.loading) return;
    const draftGroupId = modifyStayDialog?.priceReview?.draftGroupId;
    setModifyStayDialog(null);
    if (draftGroupId) {
      void cleanupModifyStayDraftGroup(draftGroupId);
    }
  };

  const getModifyStaySourceSnapshot = (reservation) => {
    const adults = Number(reservation?.adults ?? reservation?.num_adults);
    const children = Number(reservation?.children ?? reservation?.num_children ?? 0);
    const babies = Number(reservation?.babies ?? 0);

    return {
      adults,
      children: Number.isFinite(children) ? children : 0,
      babies: Number.isFinite(babies) ? babies : 0,
      childrenAges: reservation?.children_ages ?? reservation?.childrenAges ?? null,
      categoryId: normalizeNumericId(
        reservation?.room_category_id,
        reservation?.roomCategory?.id,
        reservation?.room_category?.id
      ),
      categoryMaxAdults: normalizeCapacityLimit(
        reservation?.roomCategory?.max_adults,
        reservation?.room_category?.max_adults
      ),
      categoryMaxChildren: normalizeCapacityLimit(
        reservation?.roomCategory?.max_children,
        reservation?.room_category?.max_children
      ),
      categoryMaxOccupancy: normalizeCapacityLimit(
        reservation?.roomCategory?.max_occupancy,
        reservation?.room_category?.max_occupancy
      ),
      rateId: normalizeNumericId(
        reservation?.rate_id,
        reservation?.room_rate_id,
        reservation?.rate?.id,
        reservation?.room_rate?.id
      ),
      groupRateId: normalizeNumericId(
        reservation?.group_rate_id,
        reservation?.groupRate?.id,
        reservation?.group_rate?.id
      ),
      roomId: normalizeNumericId(
        reservation?.effective_room_id,
        reservation?.room_id,
        reservation?.room?.id,
        reservation?.suggested_room_id,
        reservation?.suggested_room?.id
      ),
    };
  };

  const cleanupModifyStayDraftGroup = async (draftGroupId) => {
    if (!draftGroupId) return;

    try {
      await axios.delete(`/api/reservation-draft-groups/${draftGroupId}`);
    } catch (cleanupErr) {
      console.warn("[GROUP][MODIFY_STAY][CLEANUP][WARN]", cleanupErr?.response?.data || cleanupErr);
    }
  };

  const buildModifyStayErrorMessage = (err, fallback = t("Modify Stay could not be completed.")) => {
    return formatGroupError(getReservationModificationErrorMessage(err, fallback), fallback);
  };

  const findTargetDraftForReservation = (draftRows, reservationId) =>
    (Array.isArray(draftRows) ? draftRows : []).find(
      (draft) => Number(draft?.original_reservation_id) === Number(reservationId)
    ) || null;

  const ensureOptionIncludesId = ({ items, id, idKey = "id" }) =>
    (Array.isArray(items) ? items : []).some((item) => Number(item?.[idKey]) === Number(id));

  const restoreModifyStayRoom = async ({
    draftId,
    sourceReservation,
    sourceSnapshot,
    checkInDate,
    checkOutDate,
  }) => {
    const roomId = Number(sourceSnapshot.roomId);
    if (!Number.isFinite(roomId) || roomId <= 0) return;

    const { data } = await axios.get("/api/rooms/suggestion-options", {
      params: {
        business_unit_id:
          sourceReservation?.business_unit_id ??
          effectiveBusinessUnitId ??
          businessUnitId,
        room_category_id: sourceSnapshot.categoryId,
        check_in_date: checkInDate,
        check_out_date: checkOutDate,
        exclude_reservation_id: sourceReservation?.id,
        exclude_block_id: sourceReservation?.source_block_id || undefined,
      },
    });

    const availableRooms = Array.isArray(data?.available) ? data.available : [];
    const occupiedRooms = Array.isArray(data?.occupied) ? data.occupied : [];
    const roomIsAvailable = availableRooms.some((room) => Number(room?.id) === roomId);
    const roomIsOccupied = occupiedRooms.some((room) => Number(room?.id) === roomId);
    const sourceIsCheckedIn = normalizeReservationStatus(sourceReservation?.status) === "checked in";

    if (sourceIsCheckedIn && !roomIsAvailable) {
      throw new Error(
        t("The current room is not available for the modified stay. Use Room Move before changing these dates.")
      );
    }

    if (!roomIsAvailable && !roomIsOccupied) {
      return;
    }

    await axios.patch(`/api/reservation-drafts/${draftId}`, {
      room_id: roomId,
      updated_by: roomIsAvailable ? "ModifyStayRestoreRoom" : "ModifyStayRestoreRoomSuggestion",
    });
  };

  const resetModifyStayPreparedDraft = async () => {
    const draftGroupId = modifyStayDialog?.priceReview?.draftGroupId;
    if (!draftGroupId) {
      updateModifyStayDialog({ priceReview: null, loading: false, error: "" });
      return;
    }

    try {
      updateModifyStayDialog({ loading: true, error: "" });
      await cleanupModifyStayDraftGroup(draftGroupId);
      updateModifyStayDialog({ priceReview: null, loading: false, error: "" });
    } catch (err) {
      updateModifyStayDialog({
        loading: false,
        error: buildModifyStayErrorMessage(err),
      });
    }
  };

  const confirmModifyStayCommit = async () => {
    const dialog = modifyStayDialog;
    const priceReview = dialog?.priceReview;
    const draftGroupId = Number(priceReview?.draftGroupId);
    const sourceReservation = dialog?.reservation;
    const reservationId = Number(sourceReservation?.id);
    const reason = String(dialog?.reason || "").trim();

    if (!draftGroupId || !reservationId) return;
    if (isOtaReservation(sourceReservation) && !reason) {
      updateModifyStayDialog({
        error: t("Enter the reason for changing this OTA reservation."),
      });
      return;
    }

    let commitCompleted = false;

    try {
      updateModifyStayDialog({ loading: true, error: "" });
      await axios.post(`/api/reservation-draft-groups/${draftGroupId}/commit`, {
        reason: reason || undefined,
      });
      commitCompleted = true;

      setModifyStayDialog(null);
      try {
        await loadGroup();
      } catch (reloadErr) {
        console.warn("[GROUP][MODIFY_STAY][RELOAD][WARN]", reloadErr?.response?.data || reloadErr);
      }

      setReservationActionFeedbackState({
        title: t("Stay modified"),
        message: t("{p0} has been updated. Prices were recalculated from the selected room rate.", { p0: sourceReservation?.locator || t("Reservation #{p0}", { p0: reservationId }) }),
        tone: "primary",
      });
    } catch (err) {
      console.error("[GROUP][MODIFY_STAY][COMMIT][ERR]", err);
      if (commitCompleted) {
        setModifyStayDialog(null);
        return;
      }
      updateModifyStayDialog({
        loading: false,
        error: buildModifyStayErrorMessage(err),
      });
    }
  };

  const updateGuestOccupancyDialog = (updates) => {
    setGuestOccupancyDialog((prev) => (prev ? { ...prev, ...updates } : prev));
  };

  const openGuestOccupancyDialog = (reservation) => {
    if (
      showManualModificationBlockedMsg(reservation, [
        "guests",
        "rate_prices",
      ])
    ) {
      return;
    }
    if (!reservation?.id || !isViewMode) {
      setReservationActionFeedbackState({
        title: t("Guests & Occupancy"),
        message: t("Guests & Occupancy is available from the confirmed reservation view."),
        tone: "warning",
      });
      return;
    }

    const sourceSnapshot = getModifyStaySourceSnapshot(reservation);
    const adults = Number.isFinite(sourceSnapshot.adults) && sourceSnapshot.adults > 0
      ? sourceSnapshot.adults
      : 1;
    const children = Number.isFinite(sourceSnapshot.children) && sourceSnapshot.children >= 0
      ? sourceSnapshot.children
      : 0;

    setGuestOccupancyDialog({
      reservation,
      adults,
      children,
      originalAdults: adults,
      originalChildren: children,
      checkInDate: toDateInputValue(reservation.check_in_date),
      checkOutDate: toDateInputValue(reservation.check_out_date),
      loading: false,
      error: "",
      priceReview: null,
      reason: "",
    });
  };

  const closeGuestOccupancyDialog = () => {
    if (guestOccupancyDialog?.loading) return;
    const draftGroupId = guestOccupancyDialog?.priceReview?.draftGroupId;
    setGuestOccupancyDialog(null);
    if (draftGroupId) {
      void cleanupModifyStayDraftGroup(draftGroupId);
    }
  };

  const resetGuestOccupancyPreparedDraft = async () => {
    const draftGroupId = guestOccupancyDialog?.priceReview?.draftGroupId;
    if (!draftGroupId) {
      updateGuestOccupancyDialog({ priceReview: null, loading: false, error: "" });
      return;
    }

    try {
      updateGuestOccupancyDialog({ loading: true, error: "" });
      await cleanupModifyStayDraftGroup(draftGroupId);
      updateGuestOccupancyDialog({ priceReview: null, loading: false, error: "" });
    } catch (err) {
      updateGuestOccupancyDialog({
        loading: false,
        error: buildModifyStayErrorMessage(err),
      });
    }
  };

  const confirmGuestOccupancyCommit = async () => {
    const dialog = guestOccupancyDialog;
    const priceReview = dialog?.priceReview;
    const draftGroupId = Number(priceReview?.draftGroupId);
    const sourceReservation = dialog?.reservation;
    const reservationId = Number(sourceReservation?.id);
    const reason = String(dialog?.reason || "").trim();

    if (!draftGroupId || !reservationId) return;
    if (isOtaReservation(sourceReservation) && !reason) {
      updateGuestOccupancyDialog({
        error: t("Enter the reason for changing this OTA reservation."),
      });
      return;
    }

    let commitCompleted = false;

    try {
      updateGuestOccupancyDialog({ loading: true, error: "" });
      await axios.post(`/api/reservation-draft-groups/${draftGroupId}/commit`, {
        reason: reason || undefined,
      });
      commitCompleted = true;

      setGuestOccupancyDialog(null);
      try {
        await loadGroup();
      } catch (reloadErr) {
        console.warn("[GROUP][GUEST_OCCUPANCY][RELOAD][WARN]", reloadErr?.response?.data || reloadErr);
      }

      setReservationActionFeedbackState({
        title: t("Guests & occupancy updated"),
        message: t("{p0} has been updated. Prices were recalculated from the selected room rate.", { p0: sourceReservation?.locator || t("Reservation #{p0}", { p0: reservationId }) }),
        tone: "primary",
      });
    } catch (err) {
      console.error("[GROUP][GUEST_OCCUPANCY][COMMIT][ERR]", err);
      if (commitCompleted) {
        setGuestOccupancyDialog(null);
        return;
      }
      updateGuestOccupancyDialog({
        loading: false,
        error: buildModifyStayErrorMessage(err),
      });
    }
  };

  const updateRoomCategoryDialog = (updates) => {
    setRoomCategoryDialog((prev) => (prev ? { ...prev, ...updates } : prev));
  };

  const scrollRoomCategoryWorkflowTo = (targetRef) => {
    if (typeof window === "undefined") return;

    const behavior = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches
      ? "auto"
      : "smooth";

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        targetRef?.current?.scrollIntoView({
          behavior,
          block: "start",
          inline: "nearest",
        });
      });
    });
  };

  const loadRoomCategoryDialogRooms = async ({
    sourceReservation,
    categoryId,
    checkInDate,
    checkOutDate,
  }) => {
    const { data } = await axios.get("/api/rooms/suggestion-options", {
      params: {
        business_unit_id:
          sourceReservation?.business_unit_id ??
          effectiveBusinessUnitId ??
          businessUnitId,
        room_category_id: categoryId,
        check_in_date: checkInDate,
        check_out_date: checkOutDate,
        exclude_reservation_id: sourceReservation?.id,
        exclude_block_id: sourceReservation?.source_block_id || undefined,
      },
    });

    return {
      availableRooms: Array.isArray(data?.available) ? data.available : [],
      occupiedRooms: Array.isArray(data?.occupied) ? data.occupied : [],
    };
  };

  const openRoomCategoryDialog = async (reservation) => {
    if (showManualModificationBlockedMsg(reservation, ["room_category"])) {
      return;
    }
    if (!reservation?.id || !isViewMode) {
      setReservationActionFeedbackState({
        title: t("Room & Category"),
        message: t("Room & Category is available from the confirmed reservation view."),
        tone: "warning",
      });
      return;
    }

    const businessUnitScopeError = getActiveBusinessUnitReservationError({
      activeBusinessUnitId,
      reservationBusinessUnitId:
        reservation?.business_unit_id ?? group?.business_unit_id,
    });
    if (businessUnitScopeError) {
      setReservationActionFeedbackState({
        title: t("Room & Category"),
        message: businessUnitScopeError,
        tone: "warning",
      });
      return;
    }

    const sourceSnapshot = getModifyStaySourceSnapshot(reservation);
    const sourceIsCheckedIn = isCheckedInReservation(reservation);
    const originalCheckInDate = toDateInputValue(reservation.check_in_date);
    const checkInDate = sourceIsCheckedIn ? todayIso : originalCheckInDate;
    const checkOutDate = toDateInputValue(reservation.check_out_date);
    const nights = countDateInputNights(checkInDate, checkOutDate);

    if (!checkInDate || !checkOutDate || nights <= 0) {
      setReservationActionFeedbackState({
        title: t("Room & Category"),
        message: sourceIsCheckedIn
          ? t("There are no future stay nights available for a Room & Category move.")
          : t("The reservation needs valid stay dates before changing room category."),
        tone: "warning",
      });
      return;
    }

    if (!Number.isFinite(sourceSnapshot.adults) || sourceSnapshot.adults < 1) {
      setReservationActionFeedbackState({
        title: t("Room & Category"),
        message: t("The reservation needs a valid adults count before changing room category."),
        tone: "warning",
      });
      return;
    }

    const ratePriceBlock = getManualModificationBlock(reservation, [
      "rate_prices",
    ]);

    setRoomCategoryDialog({
      mode: "nightly_category_change",
      reservation,
      checkInDate,
      checkOutDate,
      reservationCheckInDate: originalCheckInDate,
      reservationCheckOutDate: checkOutDate,
      requiresRoom: sourceIsCheckedIn,
      nights,
      originalCheckInDate,
      draftGroupId: null,
      draftId: null,
      categories: [],
      rates: [],
      availableRooms: [],
      occupiedRooms: [],
      selectedCategory: null,
      selectedRate: null,
      selectedChangeType: null,
      upgradeChargeMode: "charge",
      businessUnitId: activeBusinessUnitId,
      baseCurrencyCode: resolveCategoryMoveBaseCurrency(baseCurrency),
      normalPriceReview: null,
      ratePriceChangeBlocked: Boolean(ratePriceBlock),
      ratePriceChangeBlockedMessage: ratePriceBlock?.message || "",
      selectedRoom: null,
      selectedRoomMode: null,
      priceReview: null,
      loading: true,
      savingRoomId: null,
      reason: "",
      error: "",
    });

    try {
      const { data } = await axios.get(
        `/api/reservations/${reservation.id}/category-move/options`,
        {
          params: {
            business_unit_id: activeBusinessUnitId,
            effective_stay_date: checkInDate,
            effective_end_date: checkOutDate,
          },
        }
      );
      const categories = (
        Array.isArray(data?.categories) ? data.categories : []
      ).filter(
        (category) =>
          Boolean(category?.upgrade_available) ||
          (!ratePriceBlock &&
            Boolean(category?.normal_change_available))
      );
      const effectiveDate =
        toDateInputValue(data?.effective_date) || checkInDate;
      const effectiveEndDate =
        toDateInputValue(data?.effective_end_date) || checkOutDate;
      const responseBaseCurrencyCode = resolveCategoryMoveBaseCurrency(
        data?.base_currency_code,
        data?.currency,
        baseCurrency
      );

      updateRoomCategoryDialog({
        checkInDate: effectiveDate,
        checkOutDate: effectiveEndDate,
        nights: countDateInputNights(effectiveDate, effectiveEndDate),
        baseCurrencyCode: responseBaseCurrencyCode,
        categories,
        loading: false,
        error: categories.length
          ? ""
          : t("No room categories are available for the selected nights."),
      });
    } catch (err) {
      console.error("[GROUP][ROOM_CATEGORY][NIGHTLY_OPEN][ERR]", err);
      updateRoomCategoryDialog({
        loading: false,
        error: buildModifyStayErrorMessage(
          err,
          t("Room Category Move options could not be loaded.")
        ),
      });
    }
  };

  const closeRoomCategoryDialog = () => {
    if (roomCategoryDialog?.loading) return;
    const draftGroupId = roomCategoryDialog?.draftGroupId;
    setRoomCategoryOverridePopupOpen(false);
    setRoomCategoryDialog(null);
    if (draftGroupId) {
      void cleanupModifyStayDraftGroup(draftGroupId);
    }
  };

  const requestRoomCategoryChangePreview = async ({
    dialog,
    category,
    changeType,
    rate = null,
    upgradeChargeMode = "charge",
    overrides = [],
  }) => {
    const reservationId = Number(dialog?.reservation?.id);
    const categoryId = Number(category?.id);
    if (!reservationId || !categoryId || !changeType) {
      throw new Error(
        t("Select a room category and pricing option before reviewing the price.")
      );
    }

    const submittedOverrides =
      changeType === "normal" && Array.isArray(overrides) ? overrides : [];
    const requestBaseCurrencyCode = resolveCategoryMoveBaseCurrency(
      dialog?.baseCurrencyCode,
      baseCurrency
    );
    const overrideCurrencyFields =
      changeType === "normal"
        ? buildCategoryMoveOverrideCurrencyFields({
            overrides: submittedOverrides,
            baseCurrencyCode: requestBaseCurrencyCode,
          })
        : {};

    const { data } = await axios.post(
      `/api/reservations/${reservationId}/category-move/preview`,
      {
        effective_stay_date: dialog.checkInDate,
        effective_end_date: dialog.checkOutDate,
        room_category_id: categoryId,
        change_type: changeType,
        upgrade_charge_mode: upgradeChargeMode,
        room_rate_id:
          changeType === "normal" ? Number(rate?.id) || undefined : undefined,
        overrides:
          changeType === "normal" ? submittedOverrides : undefined,
        ...overrideCurrencyFields,
      },
      {
        params: {
          business_unit_id:
            dialog?.businessUnitId || activeBusinessUnitId,
        },
      }
    );
    const roomDifference = Number(data?.difference || 0);
    const upgradeChargeAmount = Number(data?.upgrade_charge?.amount || 0);
    const responseBaseCurrencyCode = resolveCategoryMoveBaseCurrency(
      data?.base_currency_code,
      data?.currency,
      requestBaseCurrencyCode
    );

    return {
      currentRoomPrice: Number(data?.current_future_price || 0),
      newRoomPrice: Number(data?.new_future_price || 0),
      roomDifference,
      difference: quantizeBaseAmount(
        roomDifference + upgradeChargeAmount,
        responseBaseCurrencyCode
      ),
      checkInDate: data?.effective_date || dialog.checkInDate,
      checkOutDate:
        data?.effective_end_date ||
        data?.check_out_date ||
        dialog.checkOutDate,
      nights: Array.isArray(data?.stay_dates)
        ? data.stay_dates.length
        : dialog.nights,
      futureOnly: true,
      baseCurrencyCode: responseBaseCurrencyCode,
      rateSourceCurrencyCode:
        resolveCategoryMoveBaseCurrency(
          data?.rate_source_currency_code
        ) || null,
      baseNewRoomPrice: Number(
        data?.base_new_future_price ?? data?.new_future_price ?? 0
      ),
      nightlyPrices: Array.isArray(data?.nightly_prices)
        ? data.nightly_prices
        : [],
      overrides: changeType === "normal" ? submittedOverrides : [],
      hasOverride:
        changeType === "normal" &&
        Boolean(data?.has_override ?? submittedOverrides.length),
      changeType,
      upgradeChargeMode:
        data?.upgrade_charge_mode || upgradeChargeMode,
      upgradeChargeAmount,
      upgradeNightlyCharges: Array.isArray(
        data?.upgrade_charge?.nightly_charges
      )
        ? data.upgrade_charge.nightly_charges
        : [],
    };
  };

  const changeRoomCategoryDateRange = async (field, value) => {
    const dialog = roomCategoryDialog;
    const reservationId = Number(dialog?.reservation?.id);
    if (!dialog || !reservationId) return;

    const nextCheckInDate =
      field === "checkInDate" ? value : dialog.checkInDate;
    const nextCheckOutDate =
      field === "checkOutDate" ? value : dialog.checkOutDate;
    if (
      !nextCheckInDate ||
      !nextCheckOutDate ||
      countDateInputNights(nextCheckInDate, nextCheckOutDate) <= 0
    ) {
      updateRoomCategoryDialog({
        [field]: value,
        selectedCategory: null,
        selectedRate: null,
        selectedChangeType: null,
        selectedRoom: null,
        priceReview: null,
        normalPriceReview: null,
        error:
          t("The end date must be later than the first affected night."),
      });
      return;
    }

    try {
      updateRoomCategoryDialog({
        [field]: value,
        loading: true,
        selectedCategory: null,
        selectedRate: null,
        selectedChangeType: null,
        selectedRoom: null,
        selectedRoomMode: null,
        priceReview: null,
        normalPriceReview: null,
        categories: [],
        rates: [],
        availableRooms: [],
        occupiedRooms: [],
        error: "",
      });
      const { data } = await axios.get(
        `/api/reservations/${reservationId}/category-move/options`,
        {
          params: {
            business_unit_id:
              dialog?.businessUnitId || activeBusinessUnitId,
            effective_stay_date: nextCheckInDate,
            effective_end_date: nextCheckOutDate,
          },
        }
      );
      const categories = (
        Array.isArray(data?.categories) ? data.categories : []
      ).filter(
        (category) =>
          Boolean(category?.upgrade_available) ||
          (!dialog.ratePriceChangeBlocked &&
            Boolean(category?.normal_change_available))
      );
      const effectiveDate =
        toDateInputValue(data?.effective_date) || nextCheckInDate;
      const effectiveEndDate =
        toDateInputValue(data?.effective_end_date) || nextCheckOutDate;
      const responseBaseCurrencyCode = resolveCategoryMoveBaseCurrency(
        data?.base_currency_code,
        data?.currency,
        dialog?.baseCurrencyCode,
        baseCurrency
      );

      updateRoomCategoryDialog({
        loading: false,
        checkInDate: effectiveDate,
        checkOutDate: effectiveEndDate,
        nights: countDateInputNights(effectiveDate, effectiveEndDate),
        baseCurrencyCode: responseBaseCurrencyCode,
        categories,
        error: categories.length
          ? ""
          : t("No room categories are available for the selected nights."),
      });
    } catch (err) {
      console.error("[GROUP][ROOM_CATEGORY][DATE_RANGE][ERR]", err);
      updateRoomCategoryDialog({
        loading: false,
        error: buildModifyStayErrorMessage(
          err,
          t("Room Category Move options could not be loaded for these dates.")
        ),
      });
    }
  };

  const selectRoomCategoryDialogCategory = async (category) => {
    const dialog = roomCategoryDialog;
    const draftId = Number(dialog?.draftId);
    const categoryId = Number(category?.id);
    const sourceReservation = dialog?.reservation;
    const isNightlyCategoryChange =
      dialog?.mode === "nightly_category_change";

    if (!categoryId || !sourceReservation) return;
    setRoomCategoryOverridePopupOpen(false);

    if (isNightlyCategoryChange) {
      try {
        const normalChangeAvailable =
          Boolean(category?.normal_change_available) &&
          !dialog.ratePriceChangeBlocked;
        const upgradeAvailable = Boolean(category?.upgrade_available);
        const selectedChangeType =
          normalChangeAvailable
            ? "normal"
            : upgradeAvailable
              ? "upgrade"
              : null;
        updateRoomCategoryDialog({
          loading: true,
          error: "",
          selectedCategory: category,
          selectedRate: null,
          selectedChangeType,
          selectedRoom: null,
          selectedRoomMode: null,
          rates: [],
          availableRooms: [],
          occupiedRooms: [],
          priceReview: null,
          normalPriceReview: null,
        });

        const roomOptions = await loadRoomCategoryDialogRooms({
          sourceReservation,
          categoryId,
          checkInDate: dialog.checkInDate,
          checkOutDate: dialog.checkOutDate,
        }).catch((roomErr) => {
          console.warn(
            "[GROUP][ROOM_CATEGORY][NIGHTLY_ROOMS][WARN]",
            roomErr?.response?.data || roomErr
          );
          return { availableRooms: [], occupiedRooms: [] };
        });
        const rates = normalChangeAvailable &&
          Array.isArray(category?.room_rates)
          ? category.room_rates
          : [];
        const priceReview =
          selectedChangeType === "upgrade"
            ? await requestRoomCategoryChangePreview({
                dialog,
                category,
                changeType: "upgrade",
                upgradeChargeMode: "charge",
              })
            : null;

        updateRoomCategoryDialog({
          loading: false,
          selectedCategory: category,
          selectedChangeType,
          rates,
          availableRooms: roomOptions.availableRooms,
          occupiedRooms: roomOptions.occupiedRooms,
          priceReview,
          normalPriceReview: null,
          error:
            normalChangeAvailable || upgradeAvailable
              ? ""
              : dialog.ratePriceChangeBlockedMessage ||
                t("No permitted category-change option is available."),
        });
        scrollRoomCategoryWorkflowTo(roomCategoryRateSectionRef);
      } catch (err) {
        console.error(
          "[GROUP][ROOM_CATEGORY][NIGHTLY_CATEGORY][ERR]",
          err
        );
        updateRoomCategoryDialog({
          loading: false,
          error: buildModifyStayErrorMessage(err, t("Room Category Move options could not be loaded.")),
        });
      }
      return;
    }

    if (!draftId) return;

    try {
      updateRoomCategoryDialog({
        loading: true,
        error: "",
        selectedCategory: category,
        selectedRate: null,
        selectedRoom: null,
        selectedRoomMode: null,
        rates: [],
        availableRooms: [],
        occupiedRooms: [],
        priceReview: null,
        normalPriceReview: null,
      });

      await axios.patch(`/api/reservation-drafts/${draftId}`, {
        room_category_id: categoryId,
        room_id: null,
        rate_id: null,
        group_rate_id: null,
        total_price: null,
        night_price: null,
        updated_by: "SelectRoomCategory",
      });
      await axios.post(`/api/reservation-drafts/${draftId}/rebuild-flags`);

      const [{ data: ratesData }, roomOptions] = await Promise.all([
        axios.get(`/api/reservation-drafts/${draftId}/valid-room-rates`),
        loadRoomCategoryDialogRooms({
          sourceReservation,
          categoryId,
          checkInDate: dialog.checkInDate,
          checkOutDate: dialog.checkOutDate,
        }).catch((roomErr) => {
          console.warn("[GROUP][ROOM_CATEGORY][ROOMS][WARN]", roomErr?.response?.data || roomErr);
          return { availableRooms: [], occupiedRooms: [] };
        }),
      ]);

      const rates = Array.isArray(ratesData?.room_rates) ? ratesData.room_rates : [];

      updateRoomCategoryDialog({
        loading: false,
        selectedCategory: category,
        rates,
        availableRooms: roomOptions.availableRooms,
        occupiedRooms: roomOptions.occupiedRooms,
        error: rates.length ? "" : formatNoRatesReason(ratesData, t),
      });
      scrollRoomCategoryWorkflowTo(roomCategoryRateSectionRef);
    } catch (err) {
      console.error("[GROUP][ROOM_CATEGORY][CATEGORY][ERR]", err);
      updateRoomCategoryDialog({
        loading: false,
        error: buildModifyStayErrorMessage(err),
      });
    }
  };

  const toggleRoomCategoryUpgradeRate = async (applyUpgradeRate) => {
    const dialog = roomCategoryDialog;
    const category = dialog?.selectedCategory;
    if (!dialog || !category) return;

    if (applyUpgradeRate && !category.upgrade_available) {
      updateRoomCategoryDialog({
        error: t("No upgrade rate is configured for this category route."),
      });
      return;
    }

    if (!applyUpgradeRate) {
      const normalPriceReview = dialog.normalPriceReview || null;
      if (normalPriceReview && dialog.selectedRate) {
        updateRoomCategoryDialog({
          selectedChangeType: "normal",
          priceReview: normalPriceReview,
          upgradeChargeMode: "charge",
          error: "",
        });
        return;
      }

      if (!dialog.selectedRate) {
        updateRoomCategoryDialog({
          selectedChangeType: category.normal_change_available
            ? "normal"
            : null,
          priceReview: null,
          upgradeChargeMode: "charge",
          error: "",
        });
        return;
      }
    }

    try {
      updateRoomCategoryDialog({
        loading: true,
        error: "",
      });
      const priceReview = await requestRoomCategoryChangePreview({
        dialog,
        category,
        changeType: applyUpgradeRate ? "upgrade" : "normal",
        upgradeChargeMode: "charge",
        rate: applyUpgradeRate ? null : dialog.selectedRate,
        overrides: applyUpgradeRate
          ? []
          : dialog.normalPriceReview?.overrides || [],
      });
      updateRoomCategoryDialog({
        loading: false,
        selectedChangeType: applyUpgradeRate ? "upgrade" : "normal",
        upgradeChargeMode: "charge",
        priceReview,
        normalPriceReview: applyUpgradeRate
          ? dialog.selectedChangeType === "normal"
            ? dialog.priceReview
            : dialog.normalPriceReview
          : priceReview,
        error: "",
      });
    } catch (err) {
      console.error("[GROUP][ROOM_CATEGORY][UPGRADE_RATE_TOGGLE][ERR]", err);
      updateRoomCategoryDialog({
        loading: false,
        error: buildModifyStayErrorMessage(
          err,
          applyUpgradeRate
            ? t("Upgrade rate could not be applied.")
            : t("Upgrade rate could not be removed.")
        ),
      });
    }
  };

  const selectRoomCategoryDialogRate = async (rate) => {
    const dialog = roomCategoryDialog;
    const draftId = Number(dialog?.draftId);
    const sourceReservation = dialog?.reservation;
    const rateId = Number(rate?.id);
    const hadSelectedRate = Boolean(dialog?.selectedRate);
    const isNightlyCategoryChange =
      dialog?.mode === "nightly_category_change";

    if (!rateId || !sourceReservation) return;
    setRoomCategoryOverridePopupOpen(false);

    if (isNightlyCategoryChange) {
      const selectedCategoryId = Number(dialog?.selectedCategory?.id);
      if (!selectedCategoryId) {
        updateRoomCategoryDialog({ error: t("Select a room category before selecting a room rate.") });
        return;
      }
      if (dialog.selectedChangeType !== "normal") {
        updateRoomCategoryDialog({
          error: t("Remove the upgrade rate before choosing a new room rate."),
        });
        return;
      }

      try {
        updateRoomCategoryDialog({ loading: true, error: "" });
        const priceReview = await requestRoomCategoryChangePreview({
          dialog,
          category: dialog.selectedCategory,
          changeType: "normal",
          rate,
        });

        updateRoomCategoryDialog({
          loading: false,
          selectedChangeType: "normal",
          selectedRate: rate,
          priceReview,
          normalPriceReview: priceReview,
        });
        scrollRoomCategoryWorkflowTo(roomCategoryRoomSectionRef);
      } catch (err) {
        console.error("[GROUP][ROOM_CATEGORY][NIGHTLY_RATE][ERR]", err);
        updateRoomCategoryDialog({
          loading: false,
          error: buildModifyStayErrorMessage(err, t("Room Category Move price preview could not be completed.")),
        });
      }
      return;
    }

    if (!draftId) return;

    try {
      updateRoomCategoryDialog({ loading: true, error: "" });

      await axios.patch(`/api/reservation-drafts/${draftId}`, {
        rate_id: rateId,
        group_rate_id: null,
        updated_by: "SelectRoomRate",
      });

      const { data: afterRateDraft } = await axios.get(`/api/reservation-drafts/${draftId}`);
      await axios.post(`/api/reservation-drafts/${draftId}/save-nightly-prices`, {
        room_rate_id: afterRateDraft.rate_id,
        check_in_date: afterRateDraft.check_in_date,
        check_out_date: afterRateDraft.check_out_date,
      });
      await axios.post(`/api/reservation-drafts/${draftId}/totals/sync-from-draft-nightly-prices`);

      if (!hadSelectedRate) {
        await axios.post("/api/reservation-drafts/draft-availability/apply-consumption", {
          reservation_group_id: afterRateDraft.reservation_group_id,
          business_unit_id: afterRateDraft.business_unit_id,
          reservation_draft_id: draftId,
        });
      }

      const { data: preparedDraft } = await axios.get(`/api/reservation-drafts/${draftId}`);
      const currentRoomPrice = Number(sourceReservation?.total_price || 0);
      const newRoomPrice = Number(preparedDraft?.total_price || 0);

      updateRoomCategoryDialog({
        loading: false,
        selectedRate: rate,
        priceReview: {
          draftGroupId: dialog.draftGroupId,
          draftId,
          currentRoomPrice,
          newRoomPrice,
          difference: quantizeBaseAmount(newRoomPrice - currentRoomPrice),
          hasOverride: Boolean(preparedDraft?.has_override),
        },
      });
      scrollRoomCategoryWorkflowTo(roomCategoryRoomSectionRef);
    } catch (err) {
      console.error("[GROUP][ROOM_CATEGORY][RATE][ERR]", err);
      updateRoomCategoryDialog({
        loading: false,
        error: buildModifyStayErrorMessage(err),
      });
    }
  };

  const refreshRoomCategoryDialogFromDraft = async () => {
    const dialog = roomCategoryDialog;
    const draftId = Number(dialog?.draftId);
    if (!draftId) return null;

    const { data: preparedDraft } = await axios.get(
      `/api/reservation-drafts/${draftId}`,
      { timeout: 30000 }
    );
    const currentRoomPrice = Number(dialog?.reservation?.total_price || 0);
    const newRoomPrice = Number(preparedDraft?.total_price || 0);

    updateRoomCategoryDialog({
      priceReview: {
        ...(dialog?.priceReview || {}),
        draftGroupId: dialog?.draftGroupId,
        draftId,
        currentRoomPrice,
        newRoomPrice,
        difference: quantizeBaseAmount(newRoomPrice - currentRoomPrice),
        hasOverride: Boolean(preparedDraft?.has_override),
      },
    });
    return preparedDraft;
  };

  const applyInHouseRoomCategoryOverride = async (payload) => {
    const dialog = roomCategoryDialog;
    const reservationId = Number(dialog?.reservation?.id);
    const selectedCategoryId = Number(dialog?.selectedCategory?.id);
    const selectedRateId = Number(dialog?.selectedRate?.id);
    if (!reservationId || !selectedCategoryId || !selectedRateId) {
      throw new Error(t("Select a room category and room rate before applying an override."));
    }

    if (dialog.selectedChangeType !== "normal") {
      throw new Error(
        t("Room-price overrides are only available for a normal category change.")
      );
    }
    const priceReview = await requestRoomCategoryChangePreview({
      dialog,
      category: dialog.selectedCategory,
      changeType: "normal",
      rate: dialog.selectedRate,
      overrides: payload?.overrides || [],
    });

    updateRoomCategoryDialog({
      priceReview,
      normalPriceReview: priceReview,
      error: "",
    });
    return priceReview;
  };

  const removeInHouseRoomCategoryOverride = async () => {
    const dialog = roomCategoryDialog;
    const selectedCategoryId = Number(dialog?.selectedCategory?.id);
    const selectedRateId = Number(dialog?.selectedRate?.id);
    if (!dialog || !selectedCategoryId || !selectedRateId) return;

    try {
      updateRoomCategoryDialog({
        loading: true,
        error: "",
      });
      const priceReview = await requestRoomCategoryChangePreview({
        dialog,
        category: dialog.selectedCategory,
        changeType: "normal",
        rate: dialog.selectedRate,
        overrides: [],
      });

      updateRoomCategoryDialog({
        loading: false,
        selectedChangeType: "normal",
        priceReview,
        normalPriceReview: priceReview,
        error: "",
      });
    } catch (err) {
      console.error("[GROUP][ROOM_CATEGORY][OVERRIDE_REMOVE][ERR]", err);
      updateRoomCategoryDialog({
        loading: false,
        error: buildModifyStayErrorMessage(
          err,
          t("Override could not be removed.")
        ),
      });
    }
  };

  const selectRoomCategoryDialogRoom = async (room, selectionMode = "available") => {
    const dialog = roomCategoryDialog;
    const draftId = Number(dialog?.draftId);
    const roomId = Number(room?.id);
    const isNightlyCategoryChange =
      dialog?.mode === "nightly_category_change";

    if (!roomId) return;

    if (isNightlyCategoryChange) {
      if (selectionMode === "occupied") {
        updateRoomCategoryDialog({
          error:
            t("The selected room must be available for every affected night."),
        });
        return;
      }
      updateRoomCategoryDialog({
        selectedRoom: room,
        selectedRoomMode: selectionMode,
        error: "",
      });
      scrollRoomCategoryWorkflowTo(roomCategoryReviewSectionRef);
      return;
    }

    if (!draftId) return;

    try {
      updateRoomCategoryDialog({ savingRoomId: roomId, error: "" });
      await axios.patch(`/api/reservation-drafts/${draftId}`, {
        room_id: roomId,
        updated_by: selectionMode === "occupied" ? "SelectRoomSuggestion" : "SelectRoom",
      });
      updateRoomCategoryDialog({
        selectedRoom: room,
        selectedRoomMode: selectionMode,
        savingRoomId: null,
      });
      scrollRoomCategoryWorkflowTo(roomCategoryReviewSectionRef);
    } catch (err) {
      console.error("[GROUP][ROOM_CATEGORY][ROOM][ERR]", err);
      updateRoomCategoryDialog({
        savingRoomId: null,
        error: buildModifyStayErrorMessage(err),
      });
    }
  };

  const clearRoomCategoryDialogRoom = async () => {
    const dialog = roomCategoryDialog;
    const draftId = Number(dialog?.draftId);
    const isNightlyCategoryChange =
      dialog?.mode === "nightly_category_change";

    if (isNightlyCategoryChange) {
      updateRoomCategoryDialog({
        selectedRoom: null,
        selectedRoomMode: null,
        savingRoomId: null,
        error: "",
      });
      return;
    }

    if (!draftId) return;

    try {
      updateRoomCategoryDialog({ savingRoomId: "clear", error: "" });
      await axios.patch(`/api/reservation-drafts/${draftId}`, {
        room_id: null,
        updated_by: "ClearRoomSuggestion",
      });
      updateRoomCategoryDialog({
        selectedRoom: null,
        selectedRoomMode: null,
        savingRoomId: null,
      });
    } catch (err) {
      console.error("[GROUP][ROOM_CATEGORY][ROOM_CLEAR][ERR]", err);
      updateRoomCategoryDialog({
        savingRoomId: null,
        error: buildModifyStayErrorMessage(err),
      });
    }
  };

  const confirmRoomCategoryCommit = async () => {
    const dialog = roomCategoryDialog;
    const priceReview = dialog?.priceReview;
    const isNightlyCategoryChange =
      dialog?.mode === "nightly_category_change";
    const draftGroupId = Number(dialog?.draftGroupId || priceReview?.draftGroupId);
    const sourceReservation = dialog?.reservation;
    const reservationId = Number(sourceReservation?.id);
    const reason = String(dialog?.reason || "").trim();

    if (!reservationId || !priceReview) {
      updateRoomCategoryDialog({
        error:
          t("Select a room category and complete the price review before confirming."),
      });
      return;
    }

    if (isOtaReservation(sourceReservation) && !reason) {
      updateRoomCategoryDialog({
        error: t("Enter the reason for changing this OTA reservation."),
      });
      return;
    }

    const sourceSnapshot = getModifyStaySourceSnapshot(sourceReservation);
    const selectedCategoryId = Number(dialog?.selectedCategory?.id);
    const selectedRateId = Number(dialog?.selectedRate?.id);
    const selectedRoomId = Number(dialog?.selectedRoom?.id);
    const hasRoomChange =
      Number.isFinite(selectedRoomId) &&
      selectedRoomId > 0 &&
      selectedRoomId !== Number(sourceSnapshot.roomId || 0);
    const hasCategoryOrRateChange =
      selectedCategoryId !== Number(sourceSnapshot.categoryId || 0) ||
      selectedRateId !== Number(sourceSnapshot.rateId || 0);

    if (isNightlyCategoryChange) {
      const changeType = dialog?.selectedChangeType;
      if (
        !selectedCategoryId ||
        !changeType ||
        (changeType === "normal" && !selectedRateId) ||
        (dialog.requiresRoom && !selectedRoomId)
      ) {
        updateRoomCategoryDialog({
          error: dialog.requiresRoom
            ? t("Complete the category and price selection, then choose an available room before confirming.")
            : t("Complete the category and price selection before confirming."),
        });
        return;
      }
      if (
        changeType === "normal" &&
        !hasCategoryOrRateChange &&
        !hasRoomChange &&
        !priceReview?.hasOverride
      ) {
        updateRoomCategoryDialog({
          error:
            t("Choose a different category, rate, room or price before confirming."),
        });
        return;
      }

      try {
        updateRoomCategoryDialog({ loading: true, error: "" });
        const submittedOverrides =
          changeType === "normal" && Array.isArray(priceReview?.overrides)
            ? priceReview.overrides
            : [];
        const responseBaseCurrencyCode = resolveCategoryMoveBaseCurrency(
          priceReview?.baseCurrencyCode,
          dialog?.baseCurrencyCode,
          baseCurrency
        );
        const overrideCurrencyFields =
          changeType === "normal"
            ? buildCategoryMoveOverrideCurrencyFields({
                overrides: submittedOverrides,
                baseCurrencyCode: responseBaseCurrencyCode,
              })
            : {};
        const { data } = await axios.post(
          `/api/reservations/${reservationId}/category-move`,
          {
            effective_stay_date: dialog.checkInDate,
            effective_end_date: dialog.checkOutDate,
            room_category_id: selectedCategoryId,
            change_type: changeType,
            upgrade_charge_mode:
              changeType === "upgrade" ? "charge" : undefined,
            room_rate_id:
              changeType === "normal" ? selectedRateId : undefined,
            room_id: selectedRoomId || undefined,
            reason: reason || undefined,
            updated_by: "ReservationGroupViewRoomCategoryMove",
            overrides:
              changeType === "normal" ? submittedOverrides : undefined,
            ...overrideCurrencyFields,
          },
          {
            params: {
              business_unit_id:
                dialog?.businessUnitId || activeBusinessUnitId,
            },
          }
        );
        const committedBaseCurrencyCode = resolveCategoryMoveBaseCurrency(
          data?.base_currency_code,
          data?.currency,
          responseBaseCurrencyCode
        );

        setRoomCategoryOverridePopupOpen(false);
        setRoomCategoryDialog(null);
        try {
          await loadGroup();
        } catch (reloadErr) {
          console.warn("[GROUP][ROOM_CATEGORY][IN_HOUSE_RELOAD][WARN]", reloadErr?.response?.data || reloadErr);
        }

        setReservationActionFeedbackState({
          title:
            changeType === "upgrade"
              ? t("Room upgrade completed")
              : t("Room category change completed"),
          message:
            changeType === "upgrade"
              ? t("{p0} was upgraded for the selected nights. Room price was preserved and the separate upgrade charge is {p1}.", { p0: sourceReservation?.locator ||
                  t("Reservation #{p0}", { p0: reservationId }), p1: formatCategoryMoveMoney(
                  data?.upgrade_charge_amount || 0,
                  committedBaseCurrencyCode
                ) })
              : t("{p0} was updated for the selected nights. Room price difference: {p1}.", { p0: sourceReservation?.locator ||
                  t("Reservation #{p0}", { p0: reservationId }), p1: formatCategoryMoveMoney(
                  data?.difference || 0,
                  committedBaseCurrencyCode
                ) }),
          tone: "primary",
        });
      } catch (err) {
        console.error("[GROUP][ROOM_CATEGORY][NIGHTLY_COMMIT][ERR]", err);
        updateRoomCategoryDialog({
          loading: false,
          error: buildModifyStayErrorMessage(err, t("Room Category Move could not be completed.")),
        });
      }
      return;
    }

    if (!draftGroupId) {
      updateRoomCategoryDialog({ error: t("Select a room category and room rate before confirming.") });
      return;
    }

    if (!hasCategoryOrRateChange && !hasRoomChange) {
      updateRoomCategoryDialog({ error: t("Choose a different category, rate or room before confirming.") });
      return;
    }

    let commitCompleted = false;

    try {
      updateRoomCategoryDialog({ loading: true, error: "" });
      await axios.post(`/api/reservation-draft-groups/${draftGroupId}/commit`, {
        reason: reason || undefined,
      });
      commitCompleted = true;

      setRoomCategoryOverridePopupOpen(false);
      setRoomCategoryDialog(null);
      try {
        await loadGroup();
      } catch (reloadErr) {
        console.warn("[GROUP][ROOM_CATEGORY][RELOAD][WARN]", reloadErr?.response?.data || reloadErr);
      }

      setReservationActionFeedbackState({
        title: t("Room & category updated"),
        message: t("{p0} has been updated. Prices were recalculated from the selected room rate.", { p0: sourceReservation?.locator || t("Reservation #{p0}", { p0: reservationId }) }),
        tone: "primary",
      });
    } catch (err) {
      console.error("[GROUP][ROOM_CATEGORY][COMMIT][ERR]", err);
      if (commitCompleted) {
        setRoomCategoryDialog(null);
        return;
      }
      updateRoomCategoryDialog({
        loading: false,
        error: buildModifyStayErrorMessage(err),
      });
    }
  };

  const updateRatesPricesDialog = (updates) => {
    setRatesPricesDialog((prev) => (prev ? { ...prev, ...updates } : prev));
  };

  const getReservationRateLabel = (reservation) =>
    reservation?.rate_name ||
    reservation?.rate?.name ||
    reservation?.room_rate?.name ||
    reservation?.group_rate_name ||
    reservation?.groupRate?.name ||
    reservation?.group_rate?.name ||
    "";

  const getRateOptionLabel = (rate) =>
    rate?.name || rate?.rate_name || rate?.label || (rate?.id ? t("Rate #{p0}", { p0: rate.id }) : t("Room rate"));

  const buildRatesPricesPriceReview = ({ dialog, preparedDraft, selectedRate, baseRateRoomPrice }) => {
    const sourceReservation = dialog?.reservation || {};
    const currentRoomPrice = Number(sourceReservation?.total_price || 0);
    const newRoomPrice = Number(preparedDraft?.total_price || 0);
    const selectedRateBasePrice = Number.isFinite(Number(baseRateRoomPrice))
      ? Number(baseRateRoomPrice)
      : newRoomPrice;
    const originalRateId = Number(dialog?.originalRateId || 0);
    const selectedRateId = normalizeNumericId(preparedDraft?.rate_id, selectedRate?.id);
    const hasOverride = Boolean(preparedDraft?.has_override);
    const originalHasOverride = Boolean(dialog?.originalHasOverride);
    const difference = quantizeBaseAmount(newRoomPrice - currentRoomPrice);
    const rateDifference = quantizeBaseAmount(selectedRateBasePrice - currentRoomPrice);
    const overrideDifference = quantizeBaseAmount(newRoomPrice - selectedRateBasePrice);
    const rateChanged =
      Number.isFinite(Number(selectedRateId)) &&
      Number(selectedRateId) > 0 &&
      Number(selectedRateId) !== Number(originalRateId || 0);
    const priceChanged = Math.abs(difference) >= 0.5 * (10 ** -getCurrencyMinorUnits(baseCurrency, currencyCatalog));
    const overrideChanged = hasOverride !== originalHasOverride;

    return {
      draftGroupId: dialog?.draftGroupId,
      draftId: dialog?.draftId,
      currentRoomPrice,
      selectedRateBasePrice,
      newRoomPrice,
      difference,
      rateDifference,
      overrideDifference,
      rateChanged,
      priceChanged,
      overrideChanged,
      hasAnyChange: rateChanged || priceChanged || overrideChanged,
    };
  };

  const loadRatesPricesBaseRateTotal = async (draftId) => {
    const { data } = await axios.get(`/api/reservation-drafts/${draftId}/nightly-prices`, {
      timeout: 30000,
    });
    return (Array.isArray(data) ? data : []).reduce(
      (total, night) => total + Number(night?.price || 0),
      0
    );
  };

  const refreshRatesPricesDialogFromDraft = async ({ selectedRate } = {}) => {
    const dialog = ratesPricesDialog;
    const draftId = Number(dialog?.draftId);
    if (!draftId) return null;

    const [{ data: preparedDraft }, baseRateRoomPrice] = await Promise.all([
      axios.get(`/api/reservation-drafts/${draftId}`, { timeout: 30000 }),
      loadRatesPricesBaseRateTotal(draftId),
    ]);
    const effectiveSelectedRate = selectedRate || dialog?.selectedRate || null;
    const priceReview = buildRatesPricesPriceReview({
      dialog,
      preparedDraft,
      selectedRate: effectiveSelectedRate,
      baseRateRoomPrice,
    });

    updateRatesPricesDialog({
      preparedDraft,
      selectedRate: effectiveSelectedRate,
      baseRateRoomPrice,
      priceReview,
      hasOverride: Boolean(preparedDraft?.has_override),
    });

    return preparedDraft;
  };

  const ensureRatesPricesNightlyPrices = async (draftId, draft) => {
    let hasNightlyPrices = false;

    try {
      const { data } = await axios.get(`/api/reservation-drafts/${draftId}/nightly-prices`);
      hasNightlyPrices = Array.isArray(data) && data.length > 0;
    } catch (err) {
      if (err?.response?.status !== 404) throw err;
    }

    if (hasNightlyPrices) return;

    if (!draft?.rate_id || !draft?.check_in_date || !draft?.check_out_date) {
      throw new Error(t("This reservation needs a standard room rate and valid stay dates before prices can be edited."));
    }

    await axios.post(`/api/reservation-drafts/${draftId}/save-nightly-prices`, {
      room_rate_id: draft.rate_id,
      check_in_date: draft.check_in_date,
      check_out_date: draft.check_out_date,
    });

    if (!draft?.has_override) {
      await axios.post(`/api/reservation-drafts/${draftId}/totals/sync-from-draft-nightly-prices`);
    }
  };

  const openRatesPricesDialog = async (reservation, actionKey = "rates_prices") => {
    const allowRateChange = actionKey === "rates_prices";
    const actionTitle = allowRateChange ? t("Rates & Prices") : t("Price Adjustment");
    if (showManualModificationBlockedMsg(reservation, ["rate_prices"])) {
      return;
    }
    if (!reservation?.id || !isViewMode) {
      setReservationActionFeedbackState({
        title: actionKey === "price_adjustment" ? t("Price Adjustment") : t("Rates & Prices"),
        message: t("Rates & Prices is available from the confirmed reservation view."),
        tone: "warning",
      });
      return;
    }

    const businessUnitScopeError = getActiveBusinessUnitReservationError({
      activeBusinessUnitId,
      reservationBusinessUnitId:
        reservation?.business_unit_id ?? group?.business_unit_id,
    });
    if (businessUnitScopeError) {
      setReservationActionFeedbackState({
        title: actionTitle,
        message: businessUnitScopeError,
        tone: "warning",
      });
      return;
    }

    const sourceSnapshot = getModifyStaySourceSnapshot(reservation);
    const checkInDate = toDateInputValue(reservation.check_in_date);
    const checkOutDate = toDateInputValue(reservation.check_out_date);
    const nights = countDateInputNights(checkInDate, checkOutDate);

    if (!checkInDate || !checkOutDate || nights <= 0) {
      setReservationActionFeedbackState({
        title: actionTitle,
        message: t("The reservation needs valid stay dates before editing rates or prices."),
        tone: "warning",
      });
      return;
    }

    if (sourceSnapshot.groupRateId && !sourceSnapshot.rateId) {
      setReservationActionFeedbackState({
        title: actionTitle,
        message: t("Rates & Prices cannot edit a Group Rate yet. Use the full update flow for this reservation."),
        tone: "warning",
      });
      return;
    }

    if (!sourceSnapshot.rateId) {
      setReservationActionFeedbackState({
        title: actionTitle,
        message: t("The reservation needs a standard room rate before prices can be edited."),
        tone: "warning",
      });
      return;
    }

    setRatesPricesDialog({
      mode: actionKey,
      title: actionTitle,
      allowRateChange,
      reservation,
      checkInDate,
      checkOutDate,
      nights,
      draftGroupId: null,
      draftId: null,
      rates: [],
      selectedRate: null,
      preparedDraft: null,
      originalRateId: sourceSnapshot.rateId,
      originalHasOverride: Boolean(reservation?.has_override),
      hasOverride: Boolean(reservation?.has_override),
      baseRateRoomPrice: null,
      priceReview: null,
      ratesMessage: "",
      loading: true,
      reason: "",
      error: "",
    });

    let draftGroupId = null;

    try {
      const { data: editData } = await axios.post(
        `/api/reservation-draft-groups/from-group/${reservation_group_id}/start-edit`,
        { modification_actions: ["rate_prices"] }
      );
      draftGroupId = Number(editData?.draft_group_id);

      if (!draftGroupId) {
        throw new Error(t("Could not create the rates-and-prices draft."));
      }

      const { data: draftRows } = await axios.get(
        `/api/reservation-drafts/draft-groups/${draftGroupId}/reservations`
      );
      const targetDraft = findTargetDraftForReservation(draftRows, reservation.id);
      const draftId = Number(targetDraft?.id);

      if (!draftId) {
        throw new Error(t("Could not find the reservation inside the rates-and-prices draft."));
      }

      await axios.patch(`/api/reservation-drafts/${draftId}`, {
        updated_by: "RatesPricesOpenReleaseSelfConsumption",
      });
      await axios.post(`/api/reservation-drafts/${draftId}/rebuild-flags`);

      const { data: draftAfterOpen } = await axios.get(`/api/reservation-drafts/${draftId}`);
      await ensureRatesPricesNightlyPrices(draftId, draftAfterOpen);
      const [{ data: preparedDraft }, baseRateRoomPrice] = await Promise.all([
        axios.get(`/api/reservation-drafts/${draftId}`),
        loadRatesPricesBaseRateTotal(draftId),
      ]);

      const { data: ratesData } = allowRateChange
        ? await axios.get(`/api/reservation-drafts/${draftId}/valid-room-rates`)
        : { data: { room_rates: [] } };
      const fetchedRates = Array.isArray(ratesData?.room_rates) ? ratesData.room_rates : [];
      const currentRateLabel = getReservationRateLabel(reservation) || t("Rate #{p0}", { p0: sourceSnapshot.rateId });
      const currentRate = {
        id: sourceSnapshot.rateId,
        name: currentRateLabel,
      };
      const rates = fetchedRates.some((rate) => Number(rate?.id) === Number(sourceSnapshot.rateId))
        ? fetchedRates
        : [currentRate, ...fetchedRates];
      const selectedRate =
        rates.find((rate) => Number(rate?.id) === Number(preparedDraft?.rate_id || sourceSnapshot.rateId)) ||
        currentRate;
      const baseDialog = {
        draftGroupId,
        draftId,
        reservation,
        originalRateId: sourceSnapshot.rateId,
        originalHasOverride: Boolean(targetDraft?.has_override ?? reservation?.has_override),
      };
      const priceReview = buildRatesPricesPriceReview({
        dialog: baseDialog,
        preparedDraft,
        selectedRate,
        baseRateRoomPrice,
      });

      updateRatesPricesDialog({
        draftGroupId,
        draftId,
        rates,
        selectedRate,
        preparedDraft,
        baseRateRoomPrice,
        originalHasOverride: Boolean(targetDraft?.has_override ?? reservation?.has_override),
        hasOverride: Boolean(preparedDraft?.has_override),
        priceReview,
        ratesMessage: fetchedRates.length ? "" : formatNoRatesReason(ratesData, t),
        loading: false,
        error: "",
      });
    } catch (err) {
      console.error("[GROUP][RATES_PRICES][OPEN][ERR]", err);
      await cleanupModifyStayDraftGroup(draftGroupId);
      updateRatesPricesDialog({
        loading: false,
        error: buildModifyStayErrorMessage(err),
      });
    }
  };

  openRatesPricesDialogRef.current = openRatesPricesDialog;

  const closeRatesPricesDialog = () => {
    if (ratesPricesDialog?.loading) return;
    const draftGroupId = ratesPricesDialog?.draftGroupId;
    setRatesOverridePopupOpen(false);
    setRatesPricesDialog(null);
    if (draftGroupId) {
      void cleanupModifyStayDraftGroup(draftGroupId);
    }
  };

  const selectRatesPricesDialogRate = async (rate) => {
    const dialog = ratesPricesDialog;
    const draftId = Number(dialog?.draftId);
    const rateId = Number(rate?.id);
    const currentRateId = normalizeNumericId(dialog?.preparedDraft?.rate_id, dialog?.selectedRate?.id);

    if (!draftId || !rateId) return;

    if (Number(currentRateId) === Number(rateId)) {
      updateRatesPricesDialog({ selectedRate: rate, error: "" });
      return;
    }

    try {
      updateRatesPricesDialog({ loading: true, error: "" });

      await axios.patch(`/api/reservation-drafts/${draftId}`, {
        rate_id: rateId,
        group_rate_id: null,
        updated_by: "RatesPricesSelectRoomRate",
      });

      const { data: afterRateDraft } = await axios.get(`/api/reservation-drafts/${draftId}`);
      await axios.post(`/api/reservation-drafts/${draftId}/save-nightly-prices`, {
        room_rate_id: afterRateDraft.rate_id,
        check_in_date: afterRateDraft.check_in_date,
        check_out_date: afterRateDraft.check_out_date,
      });
      await axios.post(`/api/reservation-drafts/${draftId}/totals/sync-from-draft-nightly-prices`);

      const [{ data: preparedDraft }, baseRateRoomPrice] = await Promise.all([
        axios.get(`/api/reservation-drafts/${draftId}`),
        loadRatesPricesBaseRateTotal(draftId),
      ]);
      const priceReview = buildRatesPricesPriceReview({
        dialog,
        preparedDraft,
        selectedRate: rate,
        baseRateRoomPrice,
      });

      updateRatesPricesDialog({
        loading: false,
        selectedRate: rate,
        preparedDraft,
        baseRateRoomPrice,
        hasOverride: Boolean(preparedDraft?.has_override),
        priceReview,
      });
    } catch (err) {
      console.error("[GROUP][RATES_PRICES][RATE][ERR]", err);
      updateRatesPricesDialog({
        loading: false,
        error: buildModifyStayErrorMessage(err),
      });
    }
  };

  const removeRatesPricesOverrides = async () => {
    const draftId = Number(ratesPricesDialog?.draftId);
    if (!draftId) return;

    try {
      updateRatesPricesDialog({ loading: true, error: "" });
      await axios.delete(`/api/reservation-drafts/${draftId}/overrides`);
      await refreshRatesPricesDialogFromDraft();
      updateRatesPricesDialog({ loading: false });
    } catch (err) {
      console.error("[GROUP][RATES_PRICES][REMOVE_OVERRIDE][ERR]", err);
      updateRatesPricesDialog({
        loading: false,
        error: buildModifyStayErrorMessage(err),
      });
    }
  };

  const confirmRatesPricesCommit = async () => {
    const dialog = ratesPricesDialog;
    const draftGroupId = Number(dialog?.draftGroupId);
    const sourceReservation = dialog?.reservation;
    const reservationId = Number(sourceReservation?.id);
    const priceReview = dialog?.priceReview;
    const reason = String(dialog?.reason || "").trim();

    if (!draftGroupId || !reservationId || !priceReview) {
      updateRatesPricesDialog({ error: t("Prepare the price change before confirming.") });
      return;
    }

    if (!priceReview.hasAnyChange) {
      updateRatesPricesDialog({ error: t("Choose a different rate, apply an override, or remove the current override before confirming.") });
      return;
    }

    if (isOtaReservation(sourceReservation) && !reason) {
      updateRatesPricesDialog({
        error: t("Enter the reason for changing this OTA reservation."),
      });
      return;
    }

    let commitCompleted = false;

    try {
      updateRatesPricesDialog({ loading: true, error: "" });
      await axios.post(`/api/reservation-draft-groups/${draftGroupId}/commit`, {
        reason: reason || undefined,
      });
      commitCompleted = true;

      setRatesOverridePopupOpen(false);
      setRatesPricesDialog(null);
      try {
        await loadGroup();
      } catch (reloadErr) {
        console.warn("[GROUP][RATES_PRICES][RELOAD][WARN]", reloadErr?.response?.data || reloadErr);
      }

      setReservationActionFeedbackState({
        title: dialog?.allowRateChange ? t("Rates & prices updated") : t("Price adjustment applied"),
        message: t("{p0} has been updated.", { p0: sourceReservation?.locator || t("Reservation #{p0}", { p0: reservationId }) }),
        tone: "primary",
      });
    } catch (err) {
      console.error("[GROUP][RATES_PRICES][COMMIT][ERR]", err);
      if (commitCompleted) {
        setRatesPricesDialog(null);
        return;
      }
      updateRatesPricesDialog({
        loading: false,
        error: buildModifyStayErrorMessage(err),
      });
    }
  };

  const updateReservationExtrasDialog = (updates) => {
    setReservationExtrasDialog((prev) => (prev ? { ...prev, ...updates } : prev));
  };

  const openReservationExtrasDialog = async (reservation) => {
    const actionTitle = t("Manage Extras");

    if (!reservation?.id || !isViewMode) {
      setReservationActionFeedbackState({
        title: actionTitle,
        message: t("Manage Extras is available from the confirmed reservation view."),
        tone: "warning",
      });
      return;
    }

    const sourceSnapshot = getModifyStaySourceSnapshot(reservation);
    const currentRoomPrice = Number(reservation?.total_price || 0);

    if (!sourceSnapshot.rateId || !currentRoomPrice) {
      setReservationActionFeedbackState({
        title: actionTitle,
        message: t("Select a room rate and room price before managing extras."),
        tone: "warning",
      });
      return;
    }

    const businessUnitForExtras =
      reservation?.business_unit_id ??
      effectiveBusinessUnitId ??
      businessUnitId;

    setReservationExtrasDialog({
      reservation,
      businessUnitId: businessUnitForExtras,
      draftGroupId: null,
      draftId: null,
      loading: true,
      saving: false,
      error: "",
    });

    let draftGroupId = null;

    try {
      const { data: editData } = await axios.post(
        `/api/reservation-draft-groups/from-group/${reservation_group_id}/start-edit`,
        { modification_actions: [] }
      );
      draftGroupId = Number(editData?.draft_group_id);

      if (!draftGroupId) {
        throw new Error(t("Could not create the extras draft."));
      }

      const { data: draftRows } = await axios.get(
        `/api/reservation-drafts/draft-groups/${draftGroupId}/reservations`
      );
      const targetDraft = findTargetDraftForReservation(draftRows, reservation.id);
      const draftId = Number(targetDraft?.id);

      if (!draftId) {
        throw new Error(t("Could not find the reservation inside the extras draft."));
      }

      updateReservationExtrasDialog({
        draftGroupId,
        draftId,
        businessUnitId: targetDraft?.business_unit_id ?? businessUnitForExtras,
        loading: false,
        error: "",
      });
    } catch (err) {
      console.error("[GROUP][EXTRAS][OPEN][ERR]", err);
      await cleanupModifyStayDraftGroup(draftGroupId);
      updateReservationExtrasDialog({
        loading: false,
        error: buildModifyStayErrorMessage(err, t("Manage Extras could not be opened.")),
      });
    }
  };

  const closeReservationExtrasDialog = () => {
    if (reservationExtrasDialog?.loading || reservationExtrasDialog?.saving) return;
    const draftGroupId = reservationExtrasDialog?.draftGroupId;
    setReservationExtrasDialog(null);
    if (draftGroupId) {
      void cleanupModifyStayDraftGroup(draftGroupId);
    }
  };

  const handleReservationExtrasSaved = async () => {
    const dialog = reservationExtrasDialog;
    const draftGroupId = Number(dialog?.draftGroupId);
    const sourceReservation = dialog?.reservation;
    const reservationId = Number(sourceReservation?.id);

    if (!draftGroupId || !reservationId) {
      throw new Error(t("Could not save extras because the draft was not prepared."));
    }

    let commitCompleted = false;

    try {
      updateReservationExtrasDialog({ saving: true, error: "" });
      await axios.post(`/api/reservation-draft-groups/${draftGroupId}/commit`);
      commitCompleted = true;

      setReservationExtrasDialog(null);
      try {
        await loadGroup();
      } catch (reloadErr) {
        console.warn("[GROUP][EXTRAS][RELOAD][WARN]", reloadErr?.response?.data || reloadErr);
      }

      setReservationActionFeedbackState({
        title: t("Extras updated"),
        message: t("{p0} extras have been updated.", { p0: sourceReservation?.locator || t("Reservation #{p0}", { p0: reservationId }) }),
        tone: "primary",
      });

      return { skipClose: true };
    } catch (err) {
      console.error("[GROUP][EXTRAS][COMMIT][ERR]", err);
      if (commitCompleted) {
        setReservationExtrasDialog(null);
        return { skipClose: true };
      }

      updateReservationExtrasDialog({
        saving: false,
        error: buildModifyStayErrorMessage(err, t("Manage Extras could not be saved.")),
      });
      throw err;
    }
  };

  const createReservationChargeDraft = (chargeType = null, overrides = {}) => ({
    clientOperationId: createReservationChargeOperationId(),
    editingChargeId: null,
    charge_type_id: chargeType?.id ? String(chargeType.id) : "",
    quantity: "1",
    unit_amount:
      chargeType?.default_amount !== undefined && chargeType?.default_amount !== null
        ? String(chargeType.default_amount || 0)
        : "",
    service_date: getLocalArrivalParts().arrivalDate,
    stay_date: "",
    description: "",
    ...overrides,
  });

  const updateReservationChargesDialog = (updates) => {
    setReservationChargesDialog((prev) => (prev ? { ...prev, ...updates } : prev));
  };

  const applyReservationChargeSummary = (payload) => {
    const rows = Array.isArray(payload?.rows) ? payload.rows : [];
    const totalsPayload = payload?.totals || {};
    updateReservationChargesDialog({
      rows,
      totals: {
        charges: Number(totalsPayload.charges || 0),
        posted_count: Number(totalsPayload.posted_count || 0),
        voided_count: Number(totalsPayload.voided_count || 0),
      },
    });
  };

  const openReservationChargesDialog = async (reservation, options = {}) => {
    const workflow = options.workflow || null;
    const actionTitle = options.title || workflow?.title || t("Manage Charges");
    const reservationId = Number(reservation?.id);

    if (!canManageReservationCharges) {
      setReservationActionFeedbackState({
        title: actionTitle,
        message: t("You do not have permission to manage reservation charges."),
        tone: "warning",
      });
      return;
    }

    if (!reservationId || !isViewMode) {
      setReservationActionFeedbackState({
        title: actionTitle,
        message: t("Manage Charges is available from the confirmed reservation view."),
        tone: "warning",
      });
      return;
    }

    const businessUnitForCharges =
      reservation?.business_unit_id ??
      effectiveBusinessUnitId ??
      businessUnitId;

    setReservationChargesDialog({
      reservation,
      businessUnitId: businessUnitForCharges,
      workflow,
      lockedChargeCode: options.lockedChargeCode || workflow?.chargeCode || null,
      types: [],
      rows: [],
      totals: { charges: Number(reservation?.total_charges || 0), posted_count: 0, voided_count: 0 },
      draft: createReservationChargeDraft(null, options.draft || workflow?.draft || {}),
      loading: true,
      saving: false,
      voidingChargeId: null,
      error: "",
    });

    try {
      const [typesResponse, chargesResponse] = await Promise.all([
        axios.get("/api/reservation-charges/types", {
          params: { business_unit_id: businessUnitForCharges },
        }),
        axios.get(`/api/reservation-charges/reservations/${reservationId}`),
      ]);

      const types = Array.isArray(typesResponse.data) ? typesResponse.data : [];
      const activeTypes = types.filter((type) => type?.is_active);
      const lockedChargeCode = options.lockedChargeCode || workflow?.chargeCode || null;
      const lockedType = lockedChargeCode
        ? activeTypes.find((type) => String(type?.code || "") === String(lockedChargeCode || ""))
        : null;
      const manualTypes = activeTypes.filter(
        (type) => !isWorkflowOnlyReservationChargeType(type)
      );
      const defaultType =
        lockedType ||
        manualTypes.find((type) => String(type?.code || "") === "minibar") ||
        manualTypes[0] ||
        null;
      const chargesPayload = chargesResponse.data || {};

      setReservationChargesDialog({
        reservation,
        businessUnitId: businessUnitForCharges,
        workflow,
        lockedChargeCode,
        types,
        rows: Array.isArray(chargesPayload.rows) ? chargesPayload.rows : [],
        totals: {
          charges: Number(chargesPayload?.totals?.charges || 0),
          posted_count: Number(chargesPayload?.totals?.posted_count || 0),
          voided_count: Number(chargesPayload?.totals?.voided_count || 0),
        },
        draft: createReservationChargeDraft(defaultType, options.draft || workflow?.draft || {}),
        loading: false,
        saving: false,
        voidingChargeId: null,
        error:
          lockedChargeCode && !lockedType
            ? t("The protected charge type for this action is not available in Settings.")
            : "",
      });
    } catch (err) {
      console.error("[GROUP][CHARGES][OPEN][ERR]", err);
      updateReservationChargesDialog({
        loading: false,
        error: buildModifyStayErrorMessage(err, t("Manage Charges could not be opened.")),
      });
    }
  };

  const closeReservationChargesDialog = () => {
    if (reservationChargesDialog?.loading || reservationChargesDialog?.saving) return;
    setReservationChargesDialog(null);
  };

  const updateReservationChargeDraft = (field, value) => {
    setReservationChargesDialog((prev) => {
      if (!prev) return prev;
      const nextDraft = { ...(prev.draft || {}), [field]: value };
      if (!prev.workflow && !nextDraft.editingChargeId) {
        // Editing the form starts a new logical attempt. Network retries without
        // edits keep the existing key and cannot duplicate the charge.
        nextDraft.clientOperationId = createReservationChargeOperationId();
      }

      if (field === "charge_type_id") {
        const type = (prev.types || []).find((item) => String(item.id) === String(value));
        if (type) {
          nextDraft.unit_amount = String(type.default_amount || 0);
          if (!nextDraft.description && type.description) {
            nextDraft.description = "";
          }
        }
      }

      return { ...prev, draft: nextDraft, error: "" };
    });
  };

  const startEditReservationCharge = (charge) => {
    if (!charge?.id || !charge?.is_editable || String(charge.status || "") !== "posted") return;

    setReservationChargesDialog((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        draft: {
          editingChargeId: charge.id,
          charge_type_id: charge.charge_type_id ? String(charge.charge_type_id) : "",
          quantity: String(charge.quantity || 1),
          unit_amount: String(charge.unit_amount || charge.amount || 0),
          original_quantity: String(charge.quantity || 1),
          original_unit_amount: String(charge.unit_amount || charge.amount || 0),
          original_amount: Number(
            charge.amount ??
              (Number(charge.quantity || 1) * Number(charge.unit_amount || 0))
          ),
          original_currency: String(charge.currency || "").trim().toUpperCase(),
          service_date: toDateInputValue(charge.service_date) || getLocalArrivalParts().arrivalDate,
          stay_date: toDateInputValue(charge.stay_date) || "",
          description: charge.description || "",
        },
        error: "",
      };
    });
  };

  const resetReservationChargeDraft = () => {
    setReservationChargesDialog((prev) => {
      if (!prev) return prev;
      const activeTypes = (prev.types || []).filter((type) => type?.is_active);
      const lockedType = prev.lockedChargeCode
        ? activeTypes.find((type) => String(type?.code || "") === String(prev.lockedChargeCode || ""))
        : null;
      const manualTypes = activeTypes.filter(
        (type) => !isWorkflowOnlyReservationChargeType(type)
      );
      const defaultType =
        lockedType ||
        manualTypes.find((type) => String(type?.code || "") === "minibar") ||
        manualTypes[0] ||
        null;
      return { ...prev, draft: createReservationChargeDraft(defaultType), error: "" };
    });
  };

  const buildReservationChargePayload = (dialog) => {
    const reservation = dialog?.reservation;
    const reservationId = Number(reservation?.id);
    const draft = dialog?.draft || {};
    const selectedType = (dialog?.types || []).find(
      (type) => String(type.id) === String(draft.charge_type_id)
    );

    if (!dialog || !reservationId || !selectedType) {
      return { error: t("Choose a charge type before saving.") };
    }

    if (
      dialog?.lockedChargeCode &&
      String(selectedType?.code || "") !== String(dialog.lockedChargeCode || "")
    ) {
      return { error: t("This action requires its protected charge type.") };
    }

    const quantity = Math.max(0, Number(draft.quantity || 1));
    const unitAmount = Number(draft.unit_amount || 0);
    if (!Number.isFinite(quantity) || quantity <= 0 || !Number.isFinite(unitAmount) || unitAmount < 0) {
      return { error: t("Enter a valid quantity and amount.") };
    }
    const chargeCurrency = String(selectedType.currency || baseCurrency || "").trim().toUpperCase();
    const monetaryFieldsUnchanged = Boolean(draft.editingChargeId) &&
      Number(draft.original_quantity) === quantity &&
      Number(draft.original_unit_amount) === unitAmount &&
      String(draft.original_currency || chargeCurrency).trim().toUpperCase() === chargeCurrency;
    if (
      !monetaryFieldsUnchanged &&
      !hasValidCurrencyPrecision(draft.unit_amount, chargeCurrency, currencyCatalog)
    ) {
      const minorUnits = getCurrencyMinorUnits(chargeCurrency, currencyCatalog);
      return {
        error: minorUnits === 0
          ? t("{currency} charge amounts accept whole units only.", { currency: chargeCurrency })
          : t("{currency} charge amounts accept up to {decimals} decimal places.", {
              currency: chargeCurrency,
              decimals: minorUnits,
            }),
      };
    }

    const stayDateOptions = buildStayDateOptions(
      toDateInputValue(reservation?.check_in_date),
      toDateInputValue(reservation?.check_out_date)
    );
    if (draft.stay_date && !stayDateOptions.includes(draft.stay_date)) {
      return { error: t("Choose a stay date within this reservation stay.") };
    }

    return {
      reservationId,
      selectedType,
      draft,
      payload: {
        charge_type_id: Number(selectedType.id),
        charge_code: selectedType.code,
        charge_name: selectedType.name,
        quantity,
        unit_amount: unitAmount,
        amount: monetaryFieldsUnchanged
          ? Number(draft.original_amount ?? quantity * unitAmount)
          : quantizeBaseAmount(quantity * unitAmount, chargeCurrency),
        currency: chargeCurrency,
        service_date: draft.service_date || getLocalArrivalParts().arrivalDate,
        stay_date: draft.stay_date || null,
        description: draft.description || null,
      },
    };
  };

  const openReservationChargeWorkflow = async (reservation, kind, workflowOptions = {}) => {
    const todayParts = getLocalArrivalParts();
    const workflowByKind = {
      cancel: {
        kind: "cancel",
        title: t("Cancellation Charge"),
        editorTitle: t("Cancellation charge"),
        chargeCode: "cancellation_penalty",
        source: "cancellation",
        requiresReason: true,
        reasonLabel: t("Reason for cancellation"),
        descriptionLabel: t("Charge note"),
        descriptionPlaceholder: t("Internal note for the charge"),
        successTitle: t("Reservation cancelled"),
      },
      no_show: {
        kind: "no_show",
        title: t("No-Show Charge"),
        editorTitle: t("No-show charge"),
        chargeCode: "no_show_penalty",
        source: "no_show",
        descriptionLabel: t("No-show note"),
        descriptionPlaceholder: t("Reason or internal note"),
        successTitle: t("Reservation marked No Show"),
      },
      early_checkout: {
        kind: "early_checkout",
        title: t("Early Check-Out Charge"),
        editorTitle: t("Early departure charge"),
        chargeCode: "early_departure_charge",
        source: "early_checkout",
        descriptionLabel: t("Early check-out note"),
        descriptionPlaceholder: t("Reason or internal note"),
        successTitle: t("Check-out completed"),
      },
    };
    const workflowConfig = workflowByKind[kind];
    if (!workflowConfig) return;

    const unitAmount =
      workflowOptions.unitAmount !== undefined && workflowOptions.unitAmount !== null
        ? String(workflowOptions.unitAmount || 0)
        : undefined;

    await openReservationChargesDialog(reservation, {
      title: workflowConfig.title,
      lockedChargeCode: workflowConfig.chargeCode,
      workflow: {
        ...workflowConfig,
        ...workflowOptions,
      },
      draft: {
        quantity: "1",
        ...(unitAmount !== undefined ? { unit_amount: unitAmount } : {}),
        service_date: workflowOptions.serviceDate || workflowOptions.departureDate || todayParts.arrivalDate,
        stay_date: workflowOptions.stayDate || "",
        cancellation_reason: workflowOptions.cancellationReason || "",
        description: workflowOptions.description || "",
      },
    });
  };

  const runReservationChargeWorkflowAction = async ({ dialog, chargePayload = null, waive = false }) => {
    const workflow = dialog?.workflow;
    const reservation = dialog?.reservation;
    const reservationId = Number(reservation?.id);
    const cancellationReason = String(dialog?.draft?.cancellation_reason || "").trim();
    if (!workflow || !reservationId) return;

    if (workflow.kind === "cancel") {
      await axios.post(
        `/api/reservations/${reservationId}/terminal-workflows/cancellation`,
        {
          waive_charge: Boolean(waive),
          ...(!waive && chargePayload ? { charge: chargePayload } : {}),
          cancellation_reason:
            cancellationReason ||
            (waive ? "Cancellation charge waived" : "Cancellation penalty applied"),
        }
      );
      setReservationActionFeedbackState({
        title: t("Reservation cancelled"),
        message: t("{p0} has been cancelled{p1}", { p0: reservation?.locator || t("Reservation #{p0}", { p0: reservationId }), p1: waive ? t(" without a cancellation charge.") : t(" with a cancellation charge.") }),
        tone: "primary",
      });
      return;
    }

    if (workflow.kind === "no_show") {
      await axios.post(
        `/api/reservations/${reservationId}/terminal-workflows/no_show`,
        {
          waive_charge: Boolean(waive),
          ...(!waive && chargePayload ? { charge: chargePayload } : {}),
        }
      );
      clearNightlyBreakdownCacheFor(reservationId);
      setReservationActionFeedbackState({
        title: t("Reservation marked No Show"),
        message: t("{p0} has been marked as No Show{p1}", { p0: reservation?.locator || t("Reservation #{p0}", { p0: reservationId }), p1: waive ? t(" without a no-show charge.") : t(" with a no-show charge.") }),
        tone: "primary",
      });
      return;
    }

    if (workflow.kind === "early_checkout") {
      const releasedDates = Array.isArray(workflow.releasedDates) ? workflow.releasedDates : [];

      if (releasedDates.length > 0) {
        await applyEarlyCheckOutRoomReleaseDraft(reservation, releasedDates);
        await axios.post(`/api/reservation-charges/reservations/${reservationId}/early-checkout`, {
          departure_date: workflow.departureDate,
          original_check_out_date: workflow.originalCheckOutDate,
          charges:
            waive || !chargePayload
              ? []
              : [
                  {
                    stay_date: chargePayload.stay_date || null,
                    service_date: chargePayload.service_date || workflow.departureDate,
                    amount: Number(chargePayload.amount || 0),
                    description:
                      chargePayload.description ||
                      `Early departure charge for ${workflow.departureDate}`,
                    metadata: {
                      pending_dates: releasedDates,
                    },
                  },
                ],
        });
      }

      await axios.put(`/api/reservations/${reservationId}`, {
        status: "Checked Out",
        departure_date: workflow.departureDate,
        departure_time: workflow.departureTime,
        updated_by: "ReservationGroupViewEarlyCheckOut",
      });

      setCheckInDialogState({
        title: t("Check-out completed"),
        message: t("{p0} has been checked out early{p1}", { p0: reservation?.locator || t("Reservation #{p0}", { p0: reservationId }), p1: waive ? t(" without an early departure charge.") : t(" with an early departure charge.") }),
        tone: "primary",
      });
    }
  };

  const completeReservationChargeWorkflow = async ({ waive = false } = {}) => {
    const dialog = reservationChargesDialog;
    const reservationId = Number(dialog?.reservation?.id);
    if (!dialog?.workflow || !reservationId) return;

    if (dialog.workflow.kind === "cancel" && !String(dialog?.draft?.cancellation_reason || "").trim()) {
      updateReservationChargesDialog({ error: t("Enter a cancellation reason before continuing.") });
      return;
    }

    const built = waive ? null : buildReservationChargePayload(dialog);
    if (!waive && built?.error) {
      updateReservationChargesDialog({ error: built.error });
      return;
    }

    try {
      updateReservationChargesDialog({ saving: true, error: "" });
      setArrivalActionLoadingByReservationId((prev) => ({ ...prev, [reservationId]: true }));
      await runReservationChargeWorkflowAction({
        dialog,
        chargePayload: waive ? null : built.payload,
        waive,
      });
      setReservationChargesDialog(null);
      await loadGroup();
    } catch (err) {
      console.error("[GROUP][CHARGES][WORKFLOW][ERR]", err);
      updateReservationChargesDialog({
        saving: false,
        error: buildModifyStayErrorMessage(err, t("The reservation action could not be completed.")),
      });
    } finally {
      setArrivalActionLoadingByReservationId((prev) => ({ ...prev, [reservationId]: false }));
    }
  };

  const saveReservationCharge = async () => {
    const dialog = reservationChargesDialog;
    if (dialog?.workflow) {
      await completeReservationChargeWorkflow({ waive: false });
      return;
    }

    const built = buildReservationChargePayload(dialog);
    if (built?.error) {
      updateReservationChargesDialog({ error: built.error });
      return;
    }

    const { reservationId, draft, payload } = built;

    try {
      updateReservationChargesDialog({ saving: true, error: "" });
      const response = draft.editingChargeId
        ? await axios.put(`/api/reservation-charges/charges/${draft.editingChargeId}`, payload)
        : await axios.post(
            reservationChargeCreatePath(reservationId, null),
            payload,
            reservationChargeIdempotencyConfig(draft.clientOperationId)
          );

      applyReservationChargeSummary(response.data || {});
      resetReservationChargeDraft();
      updateReservationChargesDialog({ saving: false, error: "" });
      await loadGroup();
    } catch (err) {
      console.error("[GROUP][CHARGES][SAVE][ERR]", err);
      updateReservationChargesDialog({
        saving: false,
        error: buildModifyStayErrorMessage(err, t("Reservation charge could not be saved.")),
      });
    }
  };

  const isVoidableProtectedReservationCharge = (charge) => {
    const chargeCode = String(charge?.charge_code || "").trim().toLowerCase();
    const source = String(charge?.source || "").trim().toLowerCase();
    return (
      ["cancellation_penalty", "no_show_penalty", "early_departure_charge"].includes(chargeCode) ||
      ["cancellation", "no_show", "early_checkout"].includes(source)
    );
  };

  const voidReservationCharge = async (charge) => {
    const chargeId = Number(charge?.id);
    const canVoidCharge = Boolean(charge?.is_editable) || isVoidableProtectedReservationCharge(charge);
    if (!chargeId || !canVoidCharge || String(charge.status || "") !== "posted") return;

    try {
      updateReservationChargesDialog({ voidingChargeId: chargeId, error: "" });
      const response = await axios.delete(
        `/api/reservation-charges/charges/${chargeId}`
      );
      applyReservationChargeSummary(response.data || {});
      updateReservationChargesDialog({ voidingChargeId: null, error: "" });
      await loadGroup();
    } catch (err) {
      console.error("[GROUP][CHARGES][VOID][ERR]", err);
      updateReservationChargesDialog({
        voidingChargeId: null,
        error: buildModifyStayErrorMessage(err, t("Reservation charge could not be voided.")),
      });
    }
  };

  const runGuestOccupancyWorkflow = async () => {
    const dialog = guestOccupancyDialog;
    const sourceReservation = dialog?.reservation;
    const reservationId = Number(sourceReservation?.id);
    const targetAdults = Number(dialog?.adults);
    const targetChildren = Number(dialog?.children);
    const checkInDate = toDateInputValue(sourceReservation?.check_in_date);
    const checkOutDate = toDateInputValue(sourceReservation?.check_out_date);
    const nights = countDateInputNights(checkInDate, checkOutDate);

    if (!reservationId || !sourceReservation) return;

    if (!Number.isInteger(targetAdults) || targetAdults < 1) {
      updateGuestOccupancyDialog({ error: t("Adults must be at least 1.") });
      return;
    }

    if (!Number.isInteger(targetChildren) || targetChildren < 0) {
      updateGuestOccupancyDialog({ error: t("Children cannot be negative.") });
      return;
    }

    if (
      Number(targetAdults) === Number(dialog?.originalAdults) &&
      Number(targetChildren) === Number(dialog?.originalChildren)
    ) {
      updateGuestOccupancyDialog({ error: t("Choose a different occupancy before applying this action.") });
      return;
    }

    if (!checkInDate || !checkOutDate || nights <= 0) {
      updateGuestOccupancyDialog({ error: t("The reservation needs valid stay dates before changing occupancy.") });
      return;
    }

    const sourceSnapshot = getModifyStaySourceSnapshot(sourceReservation);

    if (!sourceSnapshot.categoryId) {
      updateGuestOccupancyDialog({ error: t("The reservation needs a room category before changing occupancy.") });
      return;
    }

    if (
      sourceSnapshot.categoryMaxAdults != null &&
      Number(targetAdults) > Number(sourceSnapshot.categoryMaxAdults)
    ) {
      updateGuestOccupancyDialog({
        error: t("This room category allows up to {p0} adult{p1}.", { p0: sourceSnapshot.categoryMaxAdults, p1: Number(sourceSnapshot.categoryMaxAdults) === 1 ? "" : "s" }),
      });
      return;
    }

    if (
      sourceSnapshot.categoryMaxChildren != null &&
      Number(targetChildren) > Number(sourceSnapshot.categoryMaxChildren)
    ) {
      updateGuestOccupancyDialog({
        error: t("This room category allows up to {p0} child{p1}.", { p0: sourceSnapshot.categoryMaxChildren, p1: Number(sourceSnapshot.categoryMaxChildren) === 1 ? "" : "ren" }),
      });
      return;
    }

    if (
      sourceSnapshot.categoryMaxOccupancy != null &&
      Number(sourceSnapshot.categoryMaxOccupancy) > 0 &&
      Number(targetAdults) + Number(targetChildren) > Number(sourceSnapshot.categoryMaxOccupancy)
    ) {
      updateGuestOccupancyDialog({
        error: t("This room category allows up to {p0} total guest{p1} across adults and children.", { p0: sourceSnapshot.categoryMaxOccupancy, p1: Number(sourceSnapshot.categoryMaxOccupancy) === 1 ? "" : "s" }),
      });
      return;
    }

    if (sourceSnapshot.groupRateId && !sourceSnapshot.rateId) {
      updateGuestOccupancyDialog({
        error: t("Guests & Occupancy cannot recalculate a Group Rate yet. Use the full update flow for this reservation."),
      });
      return;
    }

    if (!sourceSnapshot.rateId) {
      updateGuestOccupancyDialog({
        error: t("The reservation needs a standard room rate before Guests & Occupancy can recalculate prices."),
      });
      return;
    }

    let draftGroupId = null;

    try {
      updateGuestOccupancyDialog({ loading: true, error: "", priceReview: null });

      const { data: editData } = await axios.post(
        `/api/reservation-draft-groups/from-group/${reservation_group_id}/start-edit`,
        { modification_actions: ["guests", "rate_prices"] }
      );
      draftGroupId = Number(editData?.draft_group_id);

      if (!draftGroupId) {
        throw new Error(t("Could not create the guests-and-occupancy draft."));
      }

      const { data: draftRows } = await axios.get(
        `/api/reservation-drafts/draft-groups/${draftGroupId}/reservations`
      );
      const targetDraft = findTargetDraftForReservation(draftRows, reservationId);
      const draftId = Number(targetDraft?.id);

      if (!draftId) {
        throw new Error(t("Could not find the reservation inside the guests-and-occupancy draft."));
      }

      await axios.patch(`/api/reservation-drafts/${draftId}`, {
        updated_by: "AdultsOpenResetToDatesOnly",
      });

      const { data: afterResetDraft } = await axios.get(`/api/reservation-drafts/${draftId}`);
      const { data: populateData } = await axios.post("/api/reservation-drafts/draft-availability/populate", {
        reservation_draft_id: draftId,
        reservation_group_id: afterResetDraft.reservation_group_id,
        business_unit_id: afterResetDraft.business_unit_id,
        start_date: checkInDate,
        end_date: checkOutDate,
      });

      if (populateData?.has_availability !== true) {
        throw new Error(populateData?.reason || t("No availability for the selected occupancy."));
      }

      await axios.post(`/api/reservation-drafts/${draftId}/rebuild-flags`);

      const { data: adultsData } = await axios.get(`/api/reservation-drafts/${draftId}/valid-adults`);
      const validAdults = Array.isArray(adultsData?.valid_adults) ? adultsData.valid_adults : [];
      if (!validAdults.map(Number).includes(Number(targetAdults))) {
        throw new Error(
          t("The selected adults count is not valid for this stay. Available adults: {p0}.", { p0: validAdults.length ? validAdults.join(", ") : t("none") })
        );
      }

      await axios.patch(`/api/reservation-drafts/${draftId}`, {
        adults: targetAdults,
        updated_by: "SelectAdults",
      });
      await axios.post(`/api/reservation-drafts/${draftId}/rebuild-flags`);

      const { data: childrenData } = await axios.get(`/api/reservation-drafts/${draftId}/valid-children`);
      const validChildren = Array.isArray(childrenData?.valid_children) ? childrenData.valid_children : [];
      if (!validChildren.map(Number).includes(Number(targetChildren))) {
        throw new Error(
          t("The selected children count is not valid for this stay. Available children: {p0}.", { p0: validChildren.length ? validChildren.join(", ") : t("none") })
        );
      }

      const nextChildrenAges =
        Number(targetChildren) === Number(sourceSnapshot.children)
          ? sourceSnapshot.childrenAges
          : null;

      await axios.patch(`/api/reservation-drafts/${draftId}`, {
        children: targetChildren,
        babies: sourceSnapshot.babies,
        children_ages: nextChildrenAges,
        updated_by: "SelectChildren",
      });
      await axios.post(`/api/reservation-drafts/${draftId}/rebuild-flags`);

      const { data: categoryData } = await axios.get(
        `/api/reservation-drafts/${draftId}/valid-room-categories`
      );
      const categories = Array.isArray(categoryData?.categories) ? categoryData.categories : [];
      if (!ensureOptionIncludesId({ items: categories, id: sourceSnapshot.categoryId })) {
        throw new Error(
          t("The previous room category is not available for the selected occupancy. Available categories: {p0}.", { p0: listOptionNames(categories, t("none")) })
        );
      }

      await axios.patch(`/api/reservation-drafts/${draftId}`, {
        room_category_id: sourceSnapshot.categoryId,
        room_id: null,
        rate_id: null,
        group_rate_id: null,
        updated_by: "SelectRoomCategory",
      });
      await axios.post(`/api/reservation-drafts/${draftId}/rebuild-flags`);

      const { data: ratesData } = await axios.get(`/api/reservation-drafts/${draftId}/valid-room-rates`);
      const roomRates = Array.isArray(ratesData?.room_rates) ? ratesData.room_rates : [];
      if (!ensureOptionIncludesId({ items: roomRates, id: sourceSnapshot.rateId })) {
        const reason = ratesData?.reason || ratesData?.reason_message || ratesData?.message || ratesData?.error;
        throw new Error(
          t("The previous room rate is not valid for the selected occupancy. Available rates: {p0}.{p1}", { p0: listOptionNames(roomRates, t("none")), p1: reason ? ` ${reason}` : "" })
        );
      }

      await axios.patch(`/api/reservation-drafts/${draftId}`, {
        rate_id: sourceSnapshot.rateId,
        group_rate_id: null,
        updated_by: "SelectRoomRate",
      });

      const { data: afterRateDraft } = await axios.get(`/api/reservation-drafts/${draftId}`);
      await axios.post(`/api/reservation-drafts/${draftId}/save-nightly-prices`, {
        room_rate_id: afterRateDraft.rate_id,
        check_in_date: afterRateDraft.check_in_date,
        check_out_date: afterRateDraft.check_out_date,
      });
      await axios.post(`/api/reservation-drafts/${draftId}/totals/sync-from-draft-nightly-prices`);
      await axios.post("/api/reservation-drafts/draft-availability/apply-consumption", {
        reservation_group_id: afterRateDraft.reservation_group_id,
        business_unit_id: afterRateDraft.business_unit_id,
        reservation_draft_id: draftId,
      });

      await restoreModifyStayRoom({
        draftId,
        sourceReservation,
        sourceSnapshot,
        checkInDate,
        checkOutDate,
      });

      const { data: preparedDraft } = await axios.get(`/api/reservation-drafts/${draftId}`);
      const currentRoomPrice = Number(sourceReservation?.total_price || 0);
      const newRoomPrice = Number(preparedDraft?.total_price || 0);

      updateGuestOccupancyDialog({
        loading: false,
        error: "",
        priceReview: {
          draftGroupId,
          draftId,
          currentRoomPrice,
          newRoomPrice,
          difference: quantizeBaseAmount(newRoomPrice - currentRoomPrice),
          adults: targetAdults,
          children: targetChildren,
          checkInDate,
          checkOutDate,
          nights,
        },
      });
    } catch (err) {
      console.error("[GROUP][GUEST_OCCUPANCY][ERR]", err);
      await cleanupModifyStayDraftGroup(draftGroupId);
      updateGuestOccupancyDialog({
        loading: false,
        error: buildModifyStayErrorMessage(err),
      });
    }
  };

  const runModifyStayWorkflow = async () => {
    const dialog = modifyStayDialog;
    const sourceReservation = dialog?.reservation;
    const reservationId = Number(sourceReservation?.id);
    const checkInDate = toDateInputValue(dialog?.checkInDate);
    const checkOutDate = toDateInputValue(dialog?.checkOutDate);
    const originalCheckInDate = toDateInputValue(dialog?.originalCheckInDate);
    const originalCheckOutDate = toDateInputValue(dialog?.originalCheckOutDate);
    const nights = countDateInputNights(checkInDate, checkOutDate);
    const sourceIsCheckedIn = isCheckedInReservation(sourceReservation);

    if (!reservationId || !sourceReservation) return;

    if (!checkInDate || !checkOutDate || nights <= 0) {
      updateModifyStayDialog({ error: t("Check-out must be after check-in.") });
      return;
    }

    if (sourceIsCheckedIn && checkInDate !== originalCheckInDate) {
      updateModifyStayDialog({
        checkInDate: originalCheckInDate,
        error: t("Check-in is locked for in-house reservations. Modify Stay can only change check-out."),
      });
      return;
    }

    if (checkInDate === originalCheckInDate && checkOutDate === originalCheckOutDate) {
      updateModifyStayDialog({ error: t("Choose different dates before applying Modify Stay.") });
      return;
    }

    const sourceSnapshot = getModifyStaySourceSnapshot(sourceReservation);

    if (!Number.isFinite(sourceSnapshot.adults) || sourceSnapshot.adults < 1) {
      updateModifyStayDialog({ error: t("The reservation needs a valid adults count before Modify Stay.") });
      return;
    }

    if (!sourceSnapshot.categoryId) {
      updateModifyStayDialog({ error: t("The reservation needs a room category before Modify Stay.") });
      return;
    }

    if (sourceSnapshot.groupRateId && !sourceSnapshot.rateId) {
      updateModifyStayDialog({
        error: t("Modify Stay cannot recalculate a Group Rate yet. Use the full update flow for this reservation."),
      });
      return;
    }

    if (!sourceSnapshot.rateId) {
      updateModifyStayDialog({
        error: t("The reservation needs a standard room rate before Modify Stay can recalculate prices."),
      });
      return;
    }

    let draftGroupId = null;

    try {
      updateModifyStayDialog({ loading: true, error: "", priceReview: null });

      const { data: editData } = await axios.post(
        `/api/reservation-draft-groups/from-group/${reservation_group_id}/start-edit`,
        { modification_actions: ["dates", "rate_prices"] }
      );
      draftGroupId = Number(editData?.draft_group_id);

      if (!draftGroupId) {
        throw new Error(t("Could not create the modify-stay draft."));
      }

      const { data: draftRows } = await axios.get(
        `/api/reservation-drafts/draft-groups/${draftGroupId}/reservations`
      );
      const targetDraft = findTargetDraftForReservation(draftRows, reservationId);
      const draftId = Number(targetDraft?.id);

      if (!draftId) {
        throw new Error(t("Could not find the reservation inside the modify-stay draft."));
      }

      await axios.patch(`/api/reservation-drafts/${draftId}`, {
        check_in_date: checkInDate,
        check_out_date: checkOutDate,
        room_nights: nights,
        updated_by: "ModifyStayDates",
      });

      const { data: afterDatesDraft } = await axios.get(`/api/reservation-drafts/${draftId}`);
      const { data: populateData } = await axios.post("/api/reservation-drafts/draft-availability/populate", {
        reservation_draft_id: draftId,
        reservation_group_id: afterDatesDraft.reservation_group_id,
        business_unit_id: afterDatesDraft.business_unit_id,
        start_date: checkInDate,
        end_date: checkOutDate,
      });

      if (populateData?.has_availability !== true) {
        throw new Error(populateData?.reason || t("No availability for the selected dates."));
      }

      await axios.post(`/api/reservation-drafts/${draftId}/rebuild-flags`);

      const { data: adultsData } = await axios.get(`/api/reservation-drafts/${draftId}/valid-adults`);
      const validAdults = Array.isArray(adultsData?.valid_adults) ? adultsData.valid_adults : [];
      if (!validAdults.map(Number).includes(Number(sourceSnapshot.adults))) {
        throw new Error(
          t("The previous adults count is not valid for the selected dates. Available adults: {p0}.", { p0: validAdults.length ? validAdults.join(", ") : t("none") })
        );
      }

      await axios.patch(`/api/reservation-drafts/${draftId}`, {
        adults: sourceSnapshot.adults,
        updated_by: "ModifyStayRestoreAdults",
      });
      await axios.post(`/api/reservation-drafts/${draftId}/rebuild-flags`);

      const { data: childrenData } = await axios.get(`/api/reservation-drafts/${draftId}/valid-children`);
      const validChildren = Array.isArray(childrenData?.valid_children) ? childrenData.valid_children : [];
      if (!validChildren.map(Number).includes(Number(sourceSnapshot.children))) {
        throw new Error(
          t("The previous children count is not valid for the selected dates. Available children: {p0}.", { p0: validChildren.length ? validChildren.join(", ") : t("none") })
        );
      }

      await axios.patch(`/api/reservation-drafts/${draftId}`, {
        children: sourceSnapshot.children,
        babies: sourceSnapshot.babies,
        children_ages: sourceSnapshot.childrenAges,
        updated_by: "ModifyStayRestoreChildren",
      });
      await axios.post(`/api/reservation-drafts/${draftId}/rebuild-flags`);

      const { data: categoryData } = await axios.get(
        `/api/reservation-drafts/${draftId}/valid-room-categories`
      );
      const categories = Array.isArray(categoryData?.categories) ? categoryData.categories : [];
      if (!ensureOptionIncludesId({ items: categories, id: sourceSnapshot.categoryId })) {
        throw new Error(
          t("The previous room category is not available for the selected dates. Available categories: {p0}.", { p0: listOptionNames(categories, t("none")) })
        );
      }

      await axios.patch(`/api/reservation-drafts/${draftId}`, {
        room_category_id: sourceSnapshot.categoryId,
        room_id: null,
        rate_id: null,
        group_rate_id: null,
        updated_by: "SelectRoomCategory",
      });
      await axios.post(`/api/reservation-drafts/${draftId}/rebuild-flags`);

      const { data: ratesData } = await axios.get(`/api/reservation-drafts/${draftId}/valid-room-rates`);
      const roomRates = Array.isArray(ratesData?.room_rates) ? ratesData.room_rates : [];
      if (!ensureOptionIncludesId({ items: roomRates, id: sourceSnapshot.rateId })) {
        const reason = ratesData?.reason || ratesData?.reason_message || ratesData?.message || ratesData?.error;
        throw new Error(
          t("The previous room rate is not valid for the selected dates. Available rates: {p0}.{p1}", { p0: listOptionNames(roomRates, t("none")), p1: reason ? ` ${reason}` : "" })
        );
      }

      await axios.patch(`/api/reservation-drafts/${draftId}`, {
        rate_id: sourceSnapshot.rateId,
        group_rate_id: null,
        updated_by: "SelectRoomRate",
      });

      const { data: afterRateDraft } = await axios.get(`/api/reservation-drafts/${draftId}`);
      await axios.post(`/api/reservation-drafts/${draftId}/save-nightly-prices`, {
        room_rate_id: afterRateDraft.rate_id,
        check_in_date: afterRateDraft.check_in_date,
        check_out_date: afterRateDraft.check_out_date,
      });
      await axios.post(`/api/reservation-drafts/${draftId}/totals/sync-from-draft-nightly-prices`);
      await axios.post("/api/reservation-drafts/draft-availability/apply-consumption", {
        reservation_group_id: afterRateDraft.reservation_group_id,
        business_unit_id: afterRateDraft.business_unit_id,
        reservation_draft_id: draftId,
      });

      await restoreModifyStayRoom({
        draftId,
        sourceReservation,
        sourceSnapshot,
        checkInDate,
        checkOutDate,
      });

      const { data: preparedDraft } = await axios.get(`/api/reservation-drafts/${draftId}`);
      const currentRoomPrice = Number(sourceReservation?.total_price || 0);
      const newRoomPrice = Number(preparedDraft?.total_price || 0);

      updateModifyStayDialog({
        loading: false,
        error: "",
        priceReview: {
          draftGroupId,
          draftId,
          currentRoomPrice,
          newRoomPrice,
          difference: quantizeBaseAmount(newRoomPrice - currentRoomPrice),
          checkInDate,
          checkOutDate,
          nights,
        },
      });
    } catch (err) {
      console.error("[GROUP][MODIFY_STAY][ERR]", err);
      await cleanupModifyStayDraftGroup(draftGroupId);
      updateModifyStayDialog({
        loading: false,
        error: buildModifyStayErrorMessage(err),
      });
    }
  };

  const handleReservationActionClick = (action, reservation) => {
    if (demoReadOnlyActive) return;
    if (action?.disabled) {
      setReservationActionFeedbackState({
        title: t("Manual change not allowed"),
        message: action?.hint || t("This reservation action is disabled."),
        tone: "warning",
      });
      return;
    }
    setReservationActionsDialog(null);

    if (["late_arrival", "regularize_stay", "attendance_review"].includes(action?.key)) {
      setAttendanceDialog({ reservationId: reservation.id, mode: action.key === "attendance_review" ? "review" : action.key });
      return;
    }

    if (action?.key === "modify_stay") {
      openModifyStayDialog(reservation);
      return;
    }

    if (action?.key === "guests_occupancy") {
      openGuestOccupancyDialog(reservation);
      return;
    }

    if (action?.key === "room_category") {
      void openRoomCategoryDialog(reservation);
      return;
    }

    if (action?.key === "assign_room_number") {
      void openRoomNumberPicker(reservation, {
        mode: "assignment",
        definitiveOnly: true,
      });
      return;
    }

    if (action?.key === "room_move") {
      void openRoomNumberPicker(reservation, { mode: "room_move" });
      return;
    }

    if (action?.key === "rates_prices" || action?.key === "price_adjustment") {
      void openRatesPricesDialog(reservation, action.key);
      return;
    }

    if (action?.key === "extras") {
      void openReservationExtrasDialog(reservation);
      return;
    }

    if (action?.key === "charges") {
      void openReservationChargesDialog(reservation);
      return;
    }

    if (action?.handler === "check_in") {
      void handleCheckInReservation(reservation);
      return;
    }

    if (action?.handler === "no_show") {
      void openReservationChargeWorkflow(reservation, "no_show");
      return;
    }

    if (action?.handler === "reverse_check_in") {
      setPendingReverseCheckInReservation(reservation);
      return;
    }

    if (action?.handler === "check_out") {
      void openCheckOutFlow(reservation);
      return;
    }

    if (action?.handler === "cancel") {
      requestReservationCancellation(reservation);
      return;
    }

    if (action?.handler === "revert_no_show") {
      setPendingRevertNoShowReservation(reservation);
      return;
    }

    if (action?.handler === "reinstate") {
      setPendingReinstateReservation(reservation);
      return;
    }

    if (action?.handler === "payments") {
      setPaymentTargetReservation(reservation);
      setShowManagePaymentsPopup(true);
      return;
    }

    if (action?.handler === "send_confirmation") {
      handleOpenSendConfirmationPopup();
      return;
    }

    setReservationActionFeedbackState({
      title: t(action?.title || "Reservation action"),
      message: t("This action is ready in the menu. Its workflow will be connected next."),
      tone: "primary",
    });
  };

  const customerHasRequiredFields = (customer, requiredFields) => {
    const fields = Array.isArray(requiredFields) ? requiredFields : [];
    if (!fields.length) return true;
    return fields.every((fieldName) => {
      if (!customer) return false;
      const value = customer?.[fieldName];
      if (value === undefined || value === null) return false;
      if (typeof value === "boolean") return true;
      return String(value).trim().length > 0;
    });
  };

  const getReservationRoomIdForCheckIn = (reservation) =>
    reservation?.effective_room_id ||
    reservation?.room_id ||
    reservation?.room?.id ||
    null;

  const hasReservationRoomForCheckIn = (reservation) =>
    Boolean(
      getReservationRoomIdForCheckIn(reservation) ||
      reservation?.effective_room_number ||
      reservation?.room_number ||
      reservation?.room?.room_number
    );

  const loadCustomerForCheckInValidation = async (customer) => {
    if (!customer?.id) return customer || null;
    return (await loadFullCustomer(customer.id)) || customer;
  };

  const loadCheckInMissingItems = async (reservation) => {
    const missing = [];
    const addMissing = (key, label) => {
      if (!missing.some((item) => item.key === key)) missing.push({ key, label });
    };

    const { data: settingsData } = await axios.get("/api/hotel-settings", {
      params: { business_unit_id: effectiveBusinessUnitId || businessUnitId },
    });
    const checkInSettings = normalizeCheckInSettings(settingsData || {});
    const requiredRoles = new Set(checkInSettings.required_customer_roles_check_in);
    const requiredFields = checkInSettings.required_customer_fields_check_in;

    if (!hasReservationRoomForCheckIn(reservation)) {
      addMissing("room_number", t("Room Number"));
    }

    const checkInDate = getReservationCheckInDateIso(reservation?.check_in_date);
    const checkOutDate = getReservationCheckInDateIso(reservation?.check_out_date);
    if (!checkInDate || !checkOutDate || checkInDate >= checkOutDate) {
      addMissing("stay_dates", t("Stay dates"));
    }

    const adults = Number(reservation?.adults ?? reservation?.num_adults);
    const children = Number(reservation?.children ?? reservation?.num_children ?? 0);
    if (!Number.isFinite(adults) || adults < 1 || !Number.isFinite(children) || children < 0) {
      addMissing("occupancy", t("Occupancy"));
    }

    if (isCompanyBooker(group)) {
      if (!getBookerDisplayName(group)) addMissing("booker_data", t("Company booker data"));
      if (requiresCompanyBookerContact(settingsData) && !String(group?.booker_contact_name || "").trim()) {
        addMissing("booker_contact", t("Company booker contact name"));
      }
    } else {
      const booker = await loadCustomerForCheckInValidation(group?.booker || null);
      if (requiredRoles.has("booker") && !booker) {
        addMissing("booker", t("Booker"));
      } else if (booker && !customerHasRequiredFields(booker, requiredFields.booker)) {
        addMissing("booker_data", t("Booker data"));
      }
    }

    const mainGuest = await loadCustomerForCheckInValidation(reservation?.main_guest || null);
    if (requiredRoles.has("main_guest") && !mainGuest) {
      addMissing("main_guest", t("Main guest"));
    } else if (mainGuest && !customerHasRequiredFields(mainGuest, requiredFields.main_guest)) {
      addMissing("main_guest_data", t("Main guest data"));
    }

    const accompanyingGuests = await Promise.all(
      (reservation?.accompanying_guests || [])
        .map((item) => item?.customer || item)
        .filter(Boolean)
        .map(loadCustomerForCheckInValidation)
    );
    const expectedAccompanyingGuests = Number.isFinite(adults) ? Math.max(adults - 1, 0) : 0;
    if (requiredRoles.has("accompanying") && accompanyingGuests.length < expectedAccompanyingGuests) {
      addMissing("accompanying_guests", t("Accompanying guests"));
    }
    if (
      accompanyingGuests.length > 0 &&
      requiredFields.accompanying.length > 0 &&
      accompanyingGuests.some((customer) => !customerHasRequiredFields(customer, requiredFields.accompanying))
    ) {
      addMissing("accompanying_guest_data", t("Accompanying guest data"));
    }

    const childGuests = await Promise.all(
      (reservation?.children_guests || [])
        .map((item) => item?.customer || item)
        .filter(Boolean)
        .map(loadCustomerForCheckInValidation)
    );
    const expectedChildGuests = Number.isFinite(children) ? Math.max(children, 0) : 0;
    if (requiredRoles.has("child") && childGuests.length < expectedChildGuests) {
      addMissing("child_guests", t("Child guests"));
    }
    if (
      childGuests.length > 0 &&
      requiredFields.child.length > 0 &&
      childGuests.some((customer) => !customerHasRequiredFields(customer, requiredFields.child))
    ) {
      addMissing("child_guest_data", t("Child guest data"));
    }

    if (checkInSettings.require_full_payment_for_check_in && canViewReservationPayments) {
      try {
        const { data: balanceData } = await axios.get(`/api/reservation-groups/${reservation_group_id}/payment-balance`);
        const rows = Array.isArray(balanceData?.reservations) ? balanceData.reservations : [];
        const reservationBalance = rows.find((row) => Number(row.reservation_id) === Number(reservation?.id));
        if (amountShapeToNumber(reservationBalance?.balance_due) > 0.005) {
          addMissing("full_payment", t("Full payment"));
        }
      } catch (err) {
        console.warn("[GROUP][CHECK_IN][PAYMENT_BALANCE][WARN]", err?.response?.data || err);
      }
    }

    return missing;
  };

  const handleCheckInReservation = async (reservation) => {
    const reservationId = Number(reservation?.id);
    if (!reservationId || !isViewMode) return;
    if (toDateInputValue(reservation.check_in_date) < todayIso) {
      const ended = toDateInputValue(reservation.check_out_date) <= todayIso;
      if (!ended || canRegularizeStays) setAttendanceDialog({ reservationId, mode: ended ? "regularize_stay" : "late_arrival" });
      return;
    }

    try {
      setArrivalActionLoadingByReservationId((prev) => ({ ...prev, [reservationId]: true }));
      const missing = await loadCheckInMissingItems(reservation);

      if (missing.length > 0) {
        setCheckInDialogState({
          title: t("Required check-in data missing"),
          message: t("Please fill the required check-in data before completing check-in: {p0}.", { p0: missing
            .map((item) => item.label)
            .join(", ") }),
          tone: "warning",
        });
        return;
      }

      const { arrivalDate, arrivalTime } = getLocalArrivalParts();
      const roomId = getReservationRoomIdForCheckIn(reservation);
      await axios.put(`/api/reservations/${reservationId}`, {
        status: "Checked In",
        arrival_date: arrivalDate,
        arrival_time: arrivalTime,
        ...(roomId ? { room_id: roomId } : {}),
        updated_by: "ReservationGroupViewCheckIn",
      });
      await loadGroup();
      setCheckInDialogState({
        title: t("Check-in completed"),
        message: t("{p0} has been checked in.", { p0: reservation?.locator || t("Reservation #{p0}", { p0: reservationId }) }),
        tone: "primary",
      });
    } catch (err) {
      console.error("[GROUP][CHECK_IN][ERR]", { reservationId, err });
      setCheckInDialogState({
        title: t("Could not complete check-in"),
        message: err?.response?.data?.error || t("Could not complete this check-in."),
        tone: "warning",
      });
    } finally {
      setArrivalActionLoadingByReservationId((prev) => ({ ...prev, [reservationId]: false }));
    }
  };

  const openReservationStayEditor = (reservationId) => {
    if (!reservationId) return;
    setActiveDraftId(reservationId);
    setIsDraftPopupOpen(true);
  };

  const resetRoomCancelFlow = () => {
    setShowRoomCancelPopup(false);
    setRoomCancelTarget(null);
    setRoomCancelReason("");
  };

  const openRoomCancelDialog = (target) => {
    if (!target?.id || !target?.kind) return;
    if (target.kind === "reservation") {
      const sourceReservation = (reservations || []).find(
        (reservation) => Number(reservation?.id) === Number(target.id)
      );
      if (
        sourceReservation &&
        showManualModificationBlockedMsg(sourceReservation, ["cancellation"])
      ) {
        return;
      }
      void openReservationChargeWorkflow(
        sourceReservation || {
          id: Number(target.id),
          locator: target.locator || t("reservation #{p0}", { p0: target.id }),
          business_unit_id: effectiveBusinessUnitId,
        },
        "cancel"
      );
      return;
    }
    setRoomCancelTarget(target);
    setRoomCancelReason("");
    setShowRoomCancelPopup(true);
  };

  const requestReservationCancellation = (reservation) => {
    if (!reservation?.id) return;

    if (
      showManualModificationBlockedMsg(reservation, ["cancellation"])
    ) {
      return;
    }

    void openReservationChargeWorkflow(reservation, "cancel");
  };

  const requestDraftRoomRemoval = (reservation) => {
    if (!reservation?.id) return;

    if (
      showManualModificationBlockedMsg(reservation, ["cancellation"])
    ) {
      return;
    }

    openRoomCancelDialog({
      id: Number(reservation.id),
      kind: "draft",
      locator: reservation.locator || t("reservation #{p0}", { p0: reservation.id }),
    });
  };

  const handleConfirmRoomCancellation = async () => {
    const targetId = Number(roomCancelTarget?.id);
    const reason = String(roomCancelReason || "").trim();

    if (!targetId || !reason) return;

    try {
      setRoomCancellationLoadingId(targetId);

      if (roomCancelTarget.kind === "reservation") {
        await submitWaivedTerminalWorkflow({
          client: axios,
          reservationId: targetId,
          workflow: "cancellation",
          cancellationReason: reason,
        });
      } else {
        await axios.patch(`/api/reservation-drafts/${targetId}`, {
          is_cancelled: true,
          cancellation_reason: reason,
          updated_by: "UserCancelRoom",
        });

        const business_unit_id = effectiveBusinessUnitId ?? null;

        if (business_unit_id) {
          await axios.post(`/api/reservation-drafts/draft-availability/sync-group`, {
            reservation_group_id: Number(reservation_group_id),
            business_unit_id: Number(business_unit_id),
          });
        }
      }

      resetRoomCancelFlow();
      await loadGroup();
    } catch (err) {
      console.error("[GROUP][ROOM][CANCEL][ERR]", err);
      alert(formatGroupError(err?.response?.data?.error || t("Failed to cancel room reservation.")));
    } finally {
      setRoomCancellationLoadingId(null);
    }
  };

  const handleMarkReservationNoShow = async (reservation) => {
    const reservationId = Number(reservation?.id);
    if (!reservationId || !isViewMode) return;

    try {
      setArrivalActionLoadingByReservationId((prev) => ({ ...prev, [reservationId]: true }));
      await submitWaivedTerminalWorkflow({
        client: axios,
        reservationId,
        workflow: "no_show",
      });
      clearNightlyBreakdownCacheFor(reservationId);
      await loadGroup();
    } catch (err) {
      console.error("[GROUP][NO_SHOW][ERR]", { reservationId, err });
      alert(formatGroupError(err?.response?.data?.error || t("Error marking reservation as No Show.")));
    } finally {
      setArrivalActionLoadingByReservationId((prev) => ({ ...prev, [reservationId]: false }));
      setPendingNoShowReservation(null);
    }
  };

  const handleRevertReservationNoShow = async (reservation) => {
    const reservationId = Number(reservation?.id);
    if (!reservationId || !isViewMode) return;

    try {
      setArrivalActionLoadingByReservationId((prev) => ({ ...prev, [reservationId]: true }));
      await axios.post(`/api/reservations/${reservationId}/reinstate`, {
        updated_by: "ReservationGroupViewReinstateNoShow",
      });
      clearNightlyBreakdownCacheFor(reservationId);
      await loadGroup();
    } catch (err) {
      console.error("[GROUP][NO_SHOW_REVERT][ERR]", { reservationId, err });
      alert(formatGroupError(err?.response?.data?.error || t("Error reverting reservation from No Show.")));
    } finally {
      setArrivalActionLoadingByReservationId((prev) => ({ ...prev, [reservationId]: false }));
      setPendingRevertNoShowReservation(null);
    }
  };

  const handleReverseCheckInReservation = async (reservation) => {
    const reservationId = Number(reservation?.id);
    if (!reservationId || !isViewMode) return;

    if (!canReverseCheckInReservation(reservation)) {
      setPendingReverseCheckInReservation(null);
      setCheckInDialogState({
        title: t("Reverse check-in not available"),
        message: t("Check-in can only be reversed on the same date it was completed."),
        tone: "warning",
      });
      return;
    }

    try {
      setArrivalActionLoadingByReservationId((prev) => ({ ...prev, [reservationId]: true }));
      await axios.put(`/api/reservations/${reservationId}`, {
        status: "Confirmed",
        arrival_date: null,
        arrival_time: null,
        updated_by: "ReservationGroupViewReverseCheckIn",
      });
      await loadGroup();
      setCheckInDialogState({
        title: t("Check-in reversed"),
        message: t("{p0} has been moved back to Confirmed.", { p0: reservation?.locator || t("Reservation #{p0}", { p0: reservationId }) }),
        tone: "primary",
      });
    } catch (err) {
      console.error("[GROUP][REVERSE_CHECK_IN][ERR]", { reservationId, err });
      setCheckInDialogState({
        title: t("Could not reverse check-in"),
        message: err?.response?.data?.error || t("Could not reverse this check-in."),
        tone: "warning",
      });
    } finally {
      setArrivalActionLoadingByReservationId((prev) => ({ ...prev, [reservationId]: false }));
      setPendingReverseCheckInReservation(null);
    }
  };

  const handleCheckOutReservation = async (reservation) => {
    const reservationId = Number(reservation?.id);
    if (!reservationId || !isViewMode) return;

    if (!canCheckOutReservation(reservation)) {
      setPendingCheckOutReservation(null);
      setCheckInDialogState({
        title: t("Check-out not available"),
        message: canReverseCheckInReservation(reservation)
          ? t("This check-in can still be reversed today. Check-out becomes available from the following day.")
          : t("Only checked-in reservations can be checked out."),
        tone: "warning",
      });
      return;
    }

    try {
      setArrivalActionLoadingByReservationId((prev) => ({ ...prev, [reservationId]: true }));
      const { arrivalDate: departureDate, arrivalTime: departureTime } = getLocalArrivalParts();
      await axios.put(`/api/reservations/${reservationId}`, {
        status: "Checked Out",
        departure_date: departureDate,
        departure_time: departureTime,
        updated_by: "ReservationGroupViewCheckOut",
      });
      await loadGroup();
      setCheckInDialogState({
        title: t("Check-out completed"),
        message: t("{p0} has been checked out.", { p0: reservation?.locator || t("Reservation #{p0}", { p0: reservationId }) }),
        tone: "primary",
      });
    } catch (err) {
      console.error("[GROUP][CHECK_OUT][ERR]", { reservationId, err });
      setCheckInDialogState({
        title: t("Could not complete check-out"),
        message: err?.response?.data?.error || t("Could not complete this check-out."),
        tone: "warning",
      });
    } finally {
      setArrivalActionLoadingByReservationId((prev) => ({ ...prev, [reservationId]: false }));
      setPendingCheckOutReservation(null);
    }
  };

  const closeReinstateDialog = () => setReinstateDialogState(null);

  const buildReinstateErrorMessage = (payload) => {
    const blockedDates = Array.isArray(payload?.blocked_dates)
      ? payload.blocked_dates.filter(Boolean)
      : [];

    if (payload?.code === "REINSTATE_UNAVAILABLE" && blockedDates.length > 0) {
      return t("This reservation cannot be reinstated because these stay dates are no longer available: {p0}.", { p0: blockedDates.join(", ") });
    }

    return (
      payload?.error ||
      payload?.details ||
      t("This reservation could not be reinstated right now.")
    );
  };

  const handleReinstateReservation = async (reservation) => {
    const reservationId = Number(reservation?.id);
    if (!reservationId || !isViewMode) return;

    try {
      setReinstateLoadingId(reservationId);

      const { data } = await axios.post(`/api/reservations/${reservationId}/reinstate`, {
        updated_by: "UserReinstateReservation",
      });

      setPendingReinstateReservation(null);
      await loadGroup();

      if (data?.room_assignment_mode === "suggested" && reservation?.room_id) {
        const preferredRoomLabel =
          reservation?.room_number ||
          reservation?.room?.room_number ||
          reservation?.suggested_room_number ||
          reservation?.suggested_room?.room_number ||
          t("room #{p0}", { p0: reservation.room_id });
        setReinstateDialogState({
          title: t("Reservation reinstated with suggested room"),
          message: t("The reservation was reinstated successfully, but {p0} is no longer free. Pilcore kept it as a suggested room so you can assign another one when needed.", { p0: preferredRoomLabel }),
          tone: "primary",
        });
      }
    } catch (err) {
      console.error("[GROUP][REINSTATE][ERR]", { reservationId, err });
      const payload = err?.response?.data || {};
      setPendingReinstateReservation(null);
      setReinstateDialogState({
        title:
          payload?.code === "REINSTATE_UNAVAILABLE"
            ? t("Reinstatement unavailable")
            : t("Could not reinstate reservation"),
        message: buildReinstateErrorMessage(payload),
        tone: "warning",
      });
    } finally {
      setReinstateLoadingId(null);
    }
  };


  const companyBooker = isCompanyBooker(group);
  const bookerProfile = companyBooker ? group?.booker_company : group?.booker;
  const bookerId = companyBooker ? null : group?.booker?.id || null;

  const totals = (reservations || []).reduce(
    (acc, r) => {
      const room = Number(r?.total_price || 0);
      const extras = Number(r?.total_extras || 0);
      const charges = Number(r?.total_charges || 0);
      acc.room += room;
      acc.extras += extras;
      acc.charges += charges;
      acc.final += (room + extras + charges);
      return acc;
    },
    { room: 0, extras: 0, charges: 0, final: 0 }
  );

  const groupStats = (reservations || []).reduce(
    (acc, r) => {
      const isRoomCancelled =
        r?.is_cancelled === true ||
        r?.is_cancelled === 1 ||
        String(r?.is_cancelled || "").toLowerCase() === "true" ||
        String(r?.status || "").toLowerCase() === "cancelled";

      if (isRoomCancelled) {
        acc.cancelled += 1;
      } else {
        acc.rooms += 1;
        acc.adults += Number(r?.adults || 0);
        acc.children += Number(r?.children || 0);
      }

      return acc;
    },
    { rooms: 0, adults: 0, children: 0, cancelled: 0 }
  );

  const getReservationRoomLabel = (reservation) => {
    const roomMeta = Number.isFinite(Number(reservation?.room_id))
      ? roomCatalogById[Number(reservation.room_id)]
      : null;
    const roomNum =
      reservation?.room_number ||
      reservation?.room?.room_number ||
      roomMeta?.room_number ||
      roomMeta?.name ||
      null;
    const guestName = reservation?.main_guest
      ? `${reservation.main_guest.first_name || ""} ${reservation.main_guest.last_name || ""}`.trim()
      : "";

    if (roomNum && guestName) return t("Room {p0} - {p1}", { p0: roomNum, p1: guestName });
    if (roomNum) return t("Room {p0}", { p0: roomNum });
    if (guestName) return guestName;
    return reservation?.locator ? `Loc. ${reservation.locator}` : t("Reservation #{p0}", { p0: reservation?.id || "" });
  };

  const specialRequestRooms = (reservations || [])
    .map((reservation) => {
      const parsed = parseSpecialRequests(reservation?.special_requests);
      return {
        id: reservation?.id,
        label: getReservationRoomLabel(reservation),
        specialRequests: reservation?.special_requests,
        guestText: formatSpecialRequestSentence(parsed.guestRequests),
        channelText: formatSpecialRequestSentence(parsed.channelDetails),
        hasContent: parsed.hasContent,
      };
    })
    .filter((room) => room.hasContent);

  const specialRequestPreviewItems = [
    ...specialRequestRooms
      .filter((room) => room.guestText)
      .map((room) => ({ ...room, text: room.guestText, isImportant: true })),
    ...specialRequestRooms
      .filter((room) => room.channelText)
      .map((room) => ({ ...room, text: room.channelText, isImportant: false })),
  ];

  const formatMoney = (amount) => formatFromBase(amount);
  const formatCategoryMoveMoney = (amount, currencyCode) => {
    const responseBaseCurrencyCode = resolveCategoryMoveBaseCurrency(
      currencyCode,
      baseCurrency
    );
    const configuredBaseCurrencyCode =
      resolveCategoryMoveBaseCurrency(baseCurrency);
    if (
      !responseBaseCurrencyCode ||
      responseBaseCurrencyCode === configuredBaseCurrencyCode
    ) {
      return formatFromBase(amount);
    }

    const numericAmount = Number(amount);
    if (!Number.isFinite(numericAmount)) return "—";
    return formatCurrencyAmount(numericAmount, responseBaseCurrencyCode, {
      locale: reservationLocale,
      catalog: currencyCatalog,
    });
  };
  const summaryPaymentTotals = summaryPaymentBalance?.totals || null;
  const summaryPaidAmount = summaryPaymentTotals ? amountShapeToNumber(summaryPaymentTotals.paid_net) : 0;
  const summaryFinancials = {
    sales: totals.final,
    paid: summaryPaidAmount,
    balance: Math.max(0, totals.final - summaryPaidAmount),
  };
  const formatSummaryPaymentMoney = (amount) => {
    if (demoReadOnlyActive) return "—";
    if (isViewMode && !canViewReservationPayments) return t("No access");
    if (summaryPaymentBalanceLoading && !summaryPaymentBalance) return t("Loading...");
    return formatMoney(amount);
  };
  const hasSavedCard = (paymentMethods || []).length > 0;
  const hasActiveReservationsInView = (reservations || []).some((reservation) => {
    const normalizedStatus = String(reservation?.status || "").trim().toLowerCase();
    return normalizedStatus !== "cancelled" && normalizedStatus !== "no show" && normalizedStatus !== "noshow";
  });
  const hasCheckedInReservationsInView =
    isViewMode && (reservations || []).some((reservation) => isCheckedInReservation(reservation));
  const normalizedGroupStatus = String(group?.status || "").trim().toLowerCase();
  const isGroupCancelledInView =
    isViewMode && (normalizedGroupStatus === "cancelled" || !hasActiveReservationsInView);
  const groupDetailsLabel = demoReadOnlyActive
    ? t("Reservation details · Solo consulta")
    : isGroupCancelledInView
    ? t("Cancelled reservation details")
    : isViewMode
    ? t("Confirmed reservation details")
    : t("Draft reservation details");
  const canShowGroupBookerControls =
    !isAddRoomsDraft && (!isViewMode || (!isGroupCancelledInView && normalizedGroupStatus !== "checked out"));
  const canManageGroupBooker = !demoReadOnlyActive && canShowGroupBookerControls;

  const firstNonEmptyText = (...values) => {
    for (const v of values) {
      if (v == null) continue;
      const s = String(v).trim();
      if (s !== "") return s;
    }
    return null;
  };

  const getRoomAssignmentDate = (assignment) =>
    firstNonEmptyText(assignment?.stay_date, assignment?.date, assignment?.stayDate)?.slice(0, 10) || "";

  const getSortedRoomAssignments = (reservation) =>
    (Array.isArray(reservation?.room_assignments) ? reservation.room_assignments : [])
      .map((assignment) => ({
        ...assignment,
        __stayDate: getRoomAssignmentDate(assignment),
      }))
      .filter((assignment) => assignment.__stayDate)
      .sort((a, b) => {
        const dateCompare = String(a.__stayDate).localeCompare(String(b.__stayDate));
        if (dateCompare !== 0) return dateCompare;
        return Number(a?.id || 0) - Number(b?.id || 0);
      })
      .filter((assignment, index, assignments) => assignment.__stayDate !== assignments[index + 1]?.__stayDate);

  const getRoomCatalogMetaForRoomId = (roomId) => {
    const numericRoomId = Number(roomId);
    return Number.isFinite(numericRoomId) ? roomCatalogById[numericRoomId] || null : null;
  };

  const getRoomDetailsForAssignment = (reservation, assignment = null) => {
    const assignmentRoom = assignment?.room || null;
    const assignmentRoomId = assignment?.room_id ?? assignmentRoom?.id ?? null;
    const roomMeta = getRoomCatalogMetaForRoomId(assignmentRoomId);
    const reservationRoomMeta = getRoomCatalogMetaForRoomId(reservation?.room_id);
    const roomNumber = assignment?.room_id === null ? null : firstNonEmptyText(
      assignmentRoom?.room_number,
      roomMeta?.room_number,
      reservation?.effective_room_number,
      reservation?.room_number,
      reservation?.room?.room_number,
      reservationRoomMeta?.room_number
    );
    const categoryName = firstNonEmptyText(
      assignmentRoom?.RoomCategory?.name,
      assignmentRoom?.room_category?.name,
      roomMeta?.room_category_name,
      reservation?.room_category_name,
      reservation?.room_category?.name,
      reservation?.roomCategory?.name
    );

    return {
      roomNumber,
      roomCategoryName: categoryName,
      roomCategoryId: firstNonEmptyText(
        assignmentRoom?.room_category_id,
        assignmentRoom?.RoomCategory?.id,
        assignmentRoom?.room_category?.id,
        roomMeta?.room_category_id,
        reservation?.room_category_id,
        reservation?.room_category?.id,
        reservation?.roomCategory?.id
      ),
    };
  };

  const getRoomDetailsForDate = (reservation, date) => {
    const dateKey = String(date || "").slice(0, 10);
    const assignments = getSortedRoomAssignments(reservation);
    const assignment = assignments.find((row) => row.__stayDate === dateKey) || null;
    return getRoomDetailsForAssignment(reservation, assignment);
  };

  const buildRoomMoveNumberLabel = (reservation, fallbackLabel = "-") => {
    const assignments = getSortedRoomAssignments(reservation);
    if (!assignments.length) return fallbackLabel || "-";

    const sequence = [];
    assignments.forEach((assignment) => {
      const roomNumber = assignment?.room_id === null
        ? t("Room not assigned")
        : getRoomDetailsForAssignment(reservation, assignment).roomNumber;
      if (!roomNumber) return;
      if (sequence[sequence.length - 1] !== roomNumber) sequence.push(roomNumber);
    });

    return sequence.length > 0 ? sequence.join(" → ") : fallbackLabel || "-";
  };

  const buildRoomMetaCarouselCards = (reservation, fallbackCard) => {
    const assignments = getSortedRoomAssignments(reservation);
    const cards = [];

    assignments.forEach((assignment) => {
      const assignmentRoom = assignment?.room || null;
      const assignmentRoomId = assignment?.room_id ?? assignmentRoom?.id ?? null;
      const roomMeta = getRoomCatalogMetaForRoomId(assignmentRoomId);
      const roomNumber = firstNonEmptyText(assignmentRoom?.room_number, roomMeta?.room_number);

      if (!roomNumber) return;
      if (cards[cards.length - 1]?.roomNumber === roomNumber) return;

      cards.push({
        roomNumber,
        floor: firstNonEmptyText(assignmentRoom?.floor, roomMeta?.floor),
        bedType: firstNonEmptyText(
          assignmentRoom?.bed_type,
          assignmentRoom?.bedType,
          roomMeta?.bed_type,
          roomMeta?.bedType
        ),
        view: firstNonEmptyText(assignmentRoom?.view, assignmentRoom?.views, roomMeta?.view, roomMeta?.views),
      });
    });

    return cards.length > 0 ? cards : [fallbackCard].filter(Boolean);
  };

  const changeRoomMetaIndex = (reservationId, delta, cardCount) => {
    const total = Number(cardCount || 0);
    if (!reservationId || total <= 1) return;

    setRoomMetaIndexByReservationId((prev) => {
      const current = Number(prev[reservationId] || 0);
      const next = Math.max(0, Math.min(total - 1, current + delta));
      if (next === current) return prev;
      return { ...prev, [reservationId]: next };
    });
  };

  const buildNightlyDisplayRows = (reservation, nightlyRows) => {
    const stayNightByDate = new Map(
      (Array.isArray(reservation?.stay_nights)
        ? reservation.stay_nights
        : []
      ).map((night) => [
        String(night?.stay_date || "").slice(0, 10),
        night,
      ])
    );
    return (Array.isArray(nightlyRows) ? nightlyRows : []).map(
      (nightRow) => {
        const date = String(nightRow?.date || "").slice(0, 10);
        const stayNight = stayNightByDate.get(date) || null;
        const roomDetails = getRoomDetailsForDate(
          reservation,
          nightRow?.date
        );
        const assignedCategoryName = firstNonEmptyText(
          stayNight?.room_category?.name,
          roomDetails.roomCategoryName
        );
        const bookedCategoryName = firstNonEmptyText(
          stayNight?.booked_room_category?.name,
          assignedCategoryName
        );

        return {
          ...nightRow,
          ...roomDetails,
          roomNumber: firstNonEmptyText(
            stayNight?.room?.room_number,
            roomDetails.roomNumber
          ),
          roomCategoryName: assignedCategoryName,
          bookedRoomCategoryName: bookedCategoryName,
          roomRateName: firstNonEmptyText(
            stayNight?.room_rate?.name,
            stayNight?.room_rate?.rate_code
          ),
        };
      }
    );
  };

  const getRoomNights = (reservation) => {
    const explicitNights = Number(reservation?.room_nights || 0);
    if (Number.isFinite(explicitNights) && explicitNights > 0) return explicitNights;

    const checkIn = reservation?.check_in_date ? new Date(reservation.check_in_date) : null;
    const checkOut = reservation?.check_out_date ? new Date(reservation.check_out_date) : null;

    if (!checkIn || !checkOut) return 0;
    if (Number.isNaN(checkIn.getTime()) || Number.isNaN(checkOut.getTime())) return 0;

    const diffMs = checkOut.getTime() - checkIn.getTime();
    if (diffMs <= 0) return 0;

    return Math.round(diffMs / (1000 * 60 * 60 * 24));
  };

  const formatCheckTime = (t) => {
    if (!t) return "—";
    return String(t).substring(0, 5);
  };

  const toCheckTimeInputValue = (value) => {
    if (!value) return "";
    const normalized = String(value).slice(0, 5);
    return /^\d{2}:\d{2}$/.test(normalized) ? normalized : "";
  };

  const reservationCheckTimeKey = (reservationId, field) =>
    `${reservationId}:${field}`;

  const handleReservationCheckTimeBlur = async (
    reservation,
    field,
    input
  ) => {
    const reservationId = Number(reservation?.id);
    if (!reservationId || !input) return;

    const currentValue = toCheckTimeInputValue(reservation?.[field]);
    const nextValue = String(input.value || "").trim();
    if (nextValue === currentValue) return;

    if (nextValue && !/^\d{2}:\d{2}$/.test(nextValue)) {
      input.value = currentValue;
      setReservationCheckTimeErrorById((prev) => ({
        ...prev,
        [reservationId]: t("Enter a valid time."),
      }));
      return;
    }

    if (
      isViewMode &&
      showManualModificationBlockedMsg(reservation, ["dates"])
    ) {
      input.value = currentValue;
      return;
    }

    const savingKey = reservationCheckTimeKey(reservationId, field);
    if (reservationCheckTimeSavingByKey[savingKey]) return;

    setReservationCheckTimeSavingByKey((prev) => ({
      ...prev,
      [savingKey]: true,
    }));
    setReservationCheckTimeErrorById((prev) => {
      if (!Object.prototype.hasOwnProperty.call(prev, reservationId)) return prev;
      const next = { ...prev };
      delete next[reservationId];
      return next;
    });

    try {
      const payload = {
        [field]: nextValue ? `${nextValue}:00` : null,
        updated_by: "ReservationGroupViewCheckTimes",
      };

      if (isViewMode) {
        await axios.put(`/api/reservations/${reservationId}`, payload);
      } else {
        await axios.patch(`/api/reservation-drafts/${reservationId}`, payload);
      }

      await loadGroup();
    } catch (err) {
      console.error("[ReservationGroupView][CHECK_TIME][UPDATE][ERR]", {
        reservation_id: reservationId,
        field,
        err: err?.response?.data || err,
      });
      input.value = currentValue;
      setReservationCheckTimeErrorById((prev) => ({
        ...prev,
        [reservationId]:
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          t("The time could not be saved."),
      }));
    } finally {
      setReservationCheckTimeSavingByKey((prev) => {
        const next = { ...prev };
        delete next[savingKey];
        return next;
      });
    }
  };

  const canManageOperationalReservation = (reservation) => {
    if (demoReadOnlyActive) return false;
    if (!reservation?.id) return false;

    const normalizedStatus = normalizeReservationStatus(reservation?.status);
    const cancelled =
      reservation?.is_cancelled === true ||
      reservation?.is_cancelled === 1 ||
      String(reservation?.is_cancelled || "").toLowerCase() === "true" ||
      normalizedStatus === "cancelled";

    return (
      !cancelled &&
      normalizedStatus !== "checked out" &&
      normalizedStatus !== "no show" &&
      normalizedStatus !== "noshow"
    );
  };

  const refreshOperationalGuestsFor = async (reservationId) => {
    if (!reservationId) return;

    if (!isViewMode) {
      await refreshDraftGuestsFor(reservationId);
      return;
    }

    const { data } = await axios.get(`/api/reservation-guests/by-reservation/${reservationId}`);
    const rows = Array.isArray(data) ? data : [];

    const main_guest = rows.find((g) => g.is_main_guest)?.customer || null;
    const accompanying_guests = rows
      .filter((g) => !g.is_main_guest && !g.is_child)
      .map((g) => ({ guest_index: g.guest_index ?? null, customer: g.customer || null }))
      .sort((a, b) => Number(a.guest_index ?? 9999) - Number(b.guest_index ?? 9999));
    const children_guests = rows
      .filter((g) => g.is_child)
      .map((g) => ({ guest_index: g.guest_index ?? null, customer: g.customer || null }))
      .sort((a, b) => Number(a.guest_index ?? 9999) - Number(b.guest_index ?? 9999));

    setReservations((prev) =>
      prev.map((r) =>
        Number(r.id) === Number(reservationId)
          ? { ...r, main_guest, accompanying_guests, children_guests }
          : r
      )
    );
  };

  const assignGuestToReservationTarget = async ({
    targetId,
    customerId,
    isMainGuest = false,
    isChild = false,
    guestIndex = null,
    updatedBy = "ReservationGroupViewGuestAssign",
  }) => {
    if (!targetId || !customerId) {
      throw new Error(t("Missing reservation target or customer id."));
    }

    const payload = {
      customer_id: customerId,
      is_main_guest: Boolean(isMainGuest),
      is_child: Boolean(isChild),
      relationship: null,
      guest_index: guestIndex != null && !Number.isNaN(Number(guestIndex)) ? Number(guestIndex) : null,
      updated_by: updatedBy,
    };

    if (isViewMode) {
      await axios.post("/api/reservation-guests", {
        ...payload,
        reservation_id: targetId,
      });
      return;
    }

    await axios.post("/api/reservation-drafts/draft-guests", {
      ...payload,
      reservation_draft_id: targetId,
    });
  };

  const deleteGuestFromReservationTarget = async ({
    targetId,
    customerId = null,
    role,
    guestIndex = null,
  }) => {
    if (!targetId || !role) {
      throw new Error(t("Missing reservation target or guest role."));
    }

    if (role === "main_guest") {
      const url = isViewMode
        ? `/api/reservation-guests/by-reservation/${targetId}/main-guest`
        : `/api/reservation-drafts/draft-guests/by-draft/${targetId}/main-guest`;
      await axios.delete(url);
      return;
    }

    if (!customerId) {
      throw new Error(t("Missing customer id."));
    }

    const params =
      guestIndex != null && !Number.isNaN(Number(guestIndex))
        ? { guest_index: Number(guestIndex) }
        : {};
    const normalizedRole = role === "child" ? "child" : "accompanying";
    const url = isViewMode
      ? `/api/reservation-guests/by-reservation/${targetId}/by-customer/${customerId}/${normalizedRole}`
      : `/api/reservation-drafts/draft-guests/by-draft/${targetId}/by-customer/${customerId}/${normalizedRole}`;

    await axios.delete(url, { params });
  };

  const patchGroupBooker = async (bookerId, updatedBy) => {
    const endpoint = isViewMode
      ? `/api/reservation-groups/${reservation_group_id}`
      : `/api/reservation-draft-groups/${reservation_group_id}/booker`;

    await axios.patch(endpoint, {
      booker_id: bookerId,
      booker_company_id: null,
      booker_contact_name: null,
      updated_by: updatedBy,
    });
  };

  const getRoomMoveCurrentRoomId = (reservation) =>
    normalizeNumericId(
      reservation?.effective_room_id,
      reservation?.room_id,
      reservation?.room?.id
    );

  const getRoomMoveCurrentRoomLabel = (reservation) => {
    const currentRoomId = getRoomMoveCurrentRoomId(reservation);
    const roomMeta = currentRoomId ? roomCatalogById[Number(currentRoomId)] : null;
    const roomNumber =
      reservation?.effective_room_number ||
      reservation?.room_number ||
      reservation?.room?.room_number ||
      roomMeta?.room_number ||
      roomMeta?.name ||
      "";

    return roomNumber ? t("Room {p0}", { p0: roomNumber }) : t("Room not assigned");
  };

  const filterCurrentRoomFromPickerOptions = (rooms, currentRoomId) => {
    const normalizedCurrentRoomId = Number(currentRoomId);
    if (!Number.isFinite(normalizedCurrentRoomId) || normalizedCurrentRoomId <= 0) {
      return Array.isArray(rooms) ? rooms : [];
    }

    return (Array.isArray(rooms) ? rooms : []).filter(
      (room) => Number(room?.id) !== normalizedCurrentRoomId
    );
  };

  const prepareRoomNumberPickerOptions = ({ roomOptions, reservation, mode }) => {
    if (mode !== "room_move") {
      return roomOptions;
    }

    const currentRoomId = getRoomMoveCurrentRoomId(reservation);
    return {
      availableRooms: filterCurrentRoomFromPickerOptions(roomOptions?.availableRooms, currentRoomId),
      occupiedRooms: filterCurrentRoomFromPickerOptions(roomOptions?.occupiedRooms, currentRoomId),
    };
  };

  const closeRoomNumberPicker = () => {
    setRoomNumberPicker({
      show: false,
      mode: "assignment",
      reservation: null,
      availableRooms: [],
      occupiedRooms: [],
      loading: false,
      error: "",
      savingRoomId: null,
      effectiveStayDate: "",
      effectiveEndDate: "",
      currentRoomId: null,
      currentRoomLabel: "",
      allowOccupiedSelection: true,
      title: t("Select Suggested Room"),
      subtitle: t("Review available and occupied rooms before applying the suggestion."),
    });
  };

  const loadRoomNumberPickerOptions = async ({ reservation, checkInDate, checkOutDate }) => {
    const { data } = await axios.get(`${process.env.REACT_APP_API_URL}/api/rooms/suggestion-options`, {
      params: {
        business_unit_id:
          reservation?.business_unit_id ??
          effectiveBusinessUnitId ??
          businessUnitId,
        room_category_id: reservation.room_category_id,
        check_in_date: checkInDate,
        check_out_date: checkOutDate,
        exclude_reservation_id: isViewMode ? reservation.id : undefined,
        exclude_block_id: reservation?.source_block_id || undefined,
        reservation_draft_group_id: !isViewMode
          ? reservation?.reservation_group_id ?? reservation_group_id
          : undefined,
        exclude_reservation_draft_id: !isViewMode
          ? reservation?.id
          : undefined,
      },
    });

    const roomOptions = {
      availableRooms: Array.isArray(data?.available) ? data.available : [],
      occupiedRooms: Array.isArray(data?.occupied) ? data.occupied : [],
    };

    if (isViewMode) return roomOptions;

    return reconcileDraftGroupRoomOptions({
      ...roomOptions,
      reservations,
      currentReservationId: reservation?.id,
      checkInDate,
      checkOutDate,
    });
  };

  const openRoomNumberPicker = async (reservation, options = {}) => {
    if (roomNumberPickerSavingRef.current || !canManageOperationalReservation(reservation)) return;

    if (!reservation?.room_category_id) {
      setReservationActionFeedbackState({
        title: t("Room number"),
        message: t("Select a room category before assigning a room number."),
        tone: "warning",
      });
      return;
    }

    if (!reservation?.check_in_date || !reservation?.check_out_date) {
      setReservationActionFeedbackState({
        title: t("Room number"),
        message: t("Reservation dates are required before assigning a room number."),
        tone: "warning",
      });
      return;
    }

    const sourceIsCheckedIn = isCheckedInReservation(reservation);
    const pickerMode = options?.mode === "room_move" ? "room_move" : "assignment";
    const definitiveAssignment =
      pickerMode === "assignment" && options?.definitiveOnly === true;
    const queryCheckInDate = sourceIsCheckedIn ? todayIso : reservation.check_in_date;
    const queryCheckOutDate = reservation.check_out_date;
    const currentRoomId = pickerMode === "room_move" ? getRoomMoveCurrentRoomId(reservation) : null;
    const currentRoomLabel = pickerMode === "room_move" ? getRoomMoveCurrentRoomLabel(reservation) : "";

    if (sourceIsCheckedIn && (!queryCheckOutDate || queryCheckInDate >= queryCheckOutDate)) {
      setReservationActionFeedbackState({
        title: t("Room Move"),
        message: t("There are no future stay nights available for a room move."),
        tone: "warning",
      });
      return;
    }

    setRoomNumberPicker({
      show: true,
      mode: pickerMode,
      reservation,
      availableRooms: [],
      occupiedRooms: [],
      loading: true,
      error: "",
      savingRoomId: null,
      effectiveStayDate: sourceIsCheckedIn ? todayIso : "",
      effectiveEndDate: sourceIsCheckedIn ? queryCheckOutDate : "",
      currentRoomId,
      currentRoomLabel,
      allowOccupiedSelection: !sourceIsCheckedIn && !definitiveAssignment,
      title:
        pickerMode === "room_move"
          ? t("Room Move")
          : definitiveAssignment
            ? t("Assign Room Number")
            : t("Select Suggested Room"),
      subtitle:
        pickerMode === "room_move" || sourceIsCheckedIn
          ? t("Select a definitive room from {p0} to {p1}.", { p0: queryCheckInDate, p1: queryCheckOutDate })
          : definitiveAssignment
            ? t("Select an available room in the assigned room category.")
          : t("Review available and occupied rooms before applying the suggestion."),
    });

    try {
      const roomOptions = await loadRoomNumberPickerOptions({
        reservation,
        checkInDate: queryCheckInDate,
        checkOutDate: queryCheckOutDate,
      });
      const filteredRoomOptions = prepareRoomNumberPickerOptions({
        roomOptions,
        reservation,
        mode: pickerMode,
      });

      setRoomNumberPicker((prev) => ({
        ...prev,
        availableRooms: filteredRoomOptions.availableRooms,
        occupiedRooms: filteredRoomOptions.occupiedRooms,
        loading: false,
        error: "",
      }));
    } catch (err) {
      console.error("[ROOM_NUMBER_PICKER][LOAD][ERR]", err);
      setRoomNumberPicker((prev) => ({
        ...prev,
        loading: false,
        error: err?.response?.data?.error || t("Error fetching available rooms."),
      }));
    }
  };

  const updateRoomMoveDateRange = async (updates) => {
    const reservation = roomNumberPicker.reservation;
    if (!reservation?.id) return;

    const nextStart = toDateInputValue(updates.effectiveStayDate ?? roomNumberPicker.effectiveStayDate);
    const nextEnd = toDateInputValue(updates.effectiveEndDate ?? roomNumberPicker.effectiveEndDate);
    const reservationCheckOut = toDateInputValue(reservation.check_out_date);
    const minStart = todayIso;

    setRoomNumberPicker((prev) => ({
      ...prev,
      ...updates,
      effectiveStayDate: nextStart,
      effectiveEndDate: nextEnd,
      subtitle:
        nextStart && nextEnd
          ? t("Select a definitive room from {p0} to {p1}.", { p0: nextStart, p1: nextEnd })
          : prev.subtitle,
      availableRooms: [],
      occupiedRooms: [],
      loading: true,
      error: "",
    }));

    if (!nextStart || !nextEnd || nextStart < minStart || nextEnd <= nextStart || nextEnd > reservationCheckOut) {
      setRoomNumberPicker((prev) => ({
        ...prev,
        loading: false,
        availableRooms: [],
        occupiedRooms: [],
        error: t("Select a valid room move window inside the remaining stay."),
      }));
      return;
    }

    try {
      const roomOptions = await loadRoomNumberPickerOptions({
        reservation,
        checkInDate: nextStart,
        checkOutDate: nextEnd,
      });
      const filteredRoomOptions = prepareRoomNumberPickerOptions({
        roomOptions,
        reservation,
        mode: roomNumberPicker.mode,
      });
      setRoomNumberPicker((prev) => ({
        ...prev,
        availableRooms: filteredRoomOptions.availableRooms,
        occupiedRooms: filteredRoomOptions.occupiedRooms,
        loading: false,
        error: "",
      }));
    } catch (err) {
      console.error("[ROOM_NUMBER_PICKER][RANGE_LOAD][ERR]", err);
      setRoomNumberPicker((prev) => ({
        ...prev,
        loading: false,
        availableRooms: [],
        occupiedRooms: [],
        error: err?.response?.data?.error || t("Error fetching available rooms."),
      }));
    }
  };

  const applyRoomNumberSelection = async (room, selectionMode = "available") => {
    const reservation = roomNumberPicker.reservation;
    const isUnassigning = selectionMode === "unassigned";
    const roomId = isUnassigning ? null : Number(room?.id);
    const isOccupiedSuggestion = selectionMode === "occupied";

    if (roomNumberPickerSavingRef.current || !canManageOperationalReservation(reservation)) return;
    if (!reservation?.id || (!isUnassigning && !roomId)) return;
    if (isUnassigning && (roomNumberPicker.mode === "room_move" || isCheckedInReservation(reservation))) return;
    if (
      roomNumberPicker.mode === "room_move" &&
      roomNumberPicker.currentRoomId &&
      Number(roomNumberPicker.currentRoomId) === Number(roomId)
    ) {
      setRoomNumberPicker((prev) => ({
        ...prev,
        error: t("Select a different room for the room move."),
      }));
      return;
    }
    if (isOccupiedSuggestion && !roomNumberPicker.allowOccupiedSelection) {
      setRoomNumberPicker((prev) => ({
        ...prev,
        error: t("Checked-in reservations need a definitive available room, not a suggestion."),
      }));
      return;
    }

    try {
      roomNumberPickerSavingRef.current = true;
      setRoomNumberPicker((prev) => ({ ...prev, savingRoomId: isUnassigning ? "unassign" : roomId, error: "" }));

      if (isViewMode) {
        if (isUnassigning) {
          await axios.patch(`/api/reservations/${reservation.id}/room-assignment`, {
            room_id: null,
            effective_stay_date: toDateInputValue(reservation.check_in_date) > todayIso
              ? toDateInputValue(reservation.check_in_date)
              : todayIso,
            effective_end_date: toDateInputValue(reservation.check_out_date),
            updated_by: "ReservationGroupViewRoomPicker",
          });
        } else if (roomNumberPicker.mode === "room_move") {
          const moveStart = toDateInputValue(roomNumberPicker.effectiveStayDate || todayIso);
          const moveEnd = toDateInputValue(roomNumberPicker.effectiveEndDate || reservation.check_out_date);
          const reservationCheckOut = toDateInputValue(reservation.check_out_date);
          if (
            !moveStart ||
            !moveEnd ||
            moveStart < todayIso ||
            moveEnd <= moveStart ||
            (reservationCheckOut && moveEnd > reservationCheckOut)
          ) {
            setRoomNumberPicker((prev) => ({
              ...prev,
              savingRoomId: null,
              error: t("Select a valid room move window before choosing a room."),
            }));
            return;
          }

          await axios.post(`/api/reservations/${reservation.id}/room-move`, {
            to_room_id: roomId,
            effective_stay_date: moveStart,
            effective_end_date: moveEnd,
            updated_by: "ReservationGroupViewRoomMove",
          });
        } else if (isOccupiedSuggestion) {
          const isCheckedIn = normalizeReservationStatus(reservation.status) === "checked in";

          await axios.put(`/api/reservations/${reservation.id}`, {
            ...(isCheckedIn ? {} : { room_id: null }),
            suggested_room_id: roomId,
            updated_by: "ReservationGroupViewRoomSuggestion",
          });
        } else {
          await axios.patch(`/api/reservations/${reservation.id}/room-assignment`, {
            room_id: roomId,
            effective_stay_date:
              isCheckedInReservation(reservation) ? todayIso : undefined,
            updated_by: "ReservationGroupViewRoomPicker",
          });
        }
      } else {
        await axios.patch(`${process.env.REACT_APP_API_URL}/api/reservation-drafts/${reservation.id}`, {
          room_id: roomId,
          updated_by: "SelectRoom",
        });
      }

      await loadGroup();
      closeRoomNumberPicker();
    } catch (err) {
      console.error("[ROOM_NUMBER_PICKER][SAVE][ERR]", err);
      setRoomNumberPicker((prev) => ({
        ...prev,
        savingRoomId: null,
        error: err?.response?.data?.error || t("Error saving room."),
      }));
    } finally {
      roomNumberPickerSavingRef.current = false;
    }
  };

  const confirmationEmailHistory = Array.isArray(group?.confirmation_email_history)
    ? group.confirmation_email_history
    : [];

  const normalizeConfirmationHistoryEmail = (value) => String(value || "").trim().toLowerCase();

  const formatConfirmationHistoryTimestamp = (value) => {
    if (!value) return t("Unknown date");

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return String(value);

    return parsed.toLocaleString(reservationLocale, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatConfirmationLanguageSet = (languages, label) => {
    if (!Array.isArray(languages) || !languages.length) return null;
    return `${label} ${languages.map((lang) => String(lang || "").toUpperCase()).join(" + ")}`;
  };

  const findLatestConfirmationEntryForEmail = (email) => {
    const normalizedEmail = normalizeConfirmationHistoryEmail(email);
    if (!normalizedEmail) return null;

    return (
      confirmationEmailHistory.find(
        (entry) =>
          Array.isArray(entry?.sent_to) &&
          entry.sent_to.some(
            (recipient) => normalizeConfirmationHistoryEmail(recipient) === normalizedEmail
          )
      ) || null
    );
  };

  const sumNightRows = (rows) =>
    (Array.isArray(rows) ? rows : []).reduce((sum, row) => sum + Number(row?.price || 0), 0);

  const getEarlyCheckOutTotals = (dialog) => {
    const pendingNights = Array.isArray(dialog?.pendingNights) ? dialog.pendingNights : [];
    const pendingValue = sumNightRows(pendingNights);
    const forgivenValue = sumNightRows(pendingNights.filter((night) => night.decision === "forgive"));
    const retainedValue = Math.max(0, pendingValue - forgivenValue);

    return {
      pendingValue,
      forgivenValue,
      retainedValue,
      consumedValue: sumNightRows(dialog?.consumedNights),
    };
  };

  const updateEarlyCheckOutNightDecision = (nightDate, decision) => {
    setEarlyCheckOutDialog((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        pendingNights: (prev.pendingNights || []).map((night) =>
          night.date === nightDate ? { ...night, decision } : night
        ),
        error: "",
      };
    });
  };

  const setAllEarlyCheckOutNightDecisions = (decision) => {
    setEarlyCheckOutDialog((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        pendingNights: (prev.pendingNights || []).map((night) => ({ ...night, decision })),
        error: "",
      };
    });
  };

  const closeEarlyCheckOutDialog = () => {
    if (earlyCheckOutDialog?.loading || earlyCheckOutDialog?.saving) return;
    setEarlyCheckOutDialog(null);
  };

  const buildEarlyCheckOutRoomReleaseOverridePayload = (draftNightlies, releasedDates) => {
    const releasedDateSet = new Set(releasedDates);
    const draftDates = new Set((draftNightlies || []).map((night) => night.date));
    const missingDates = [...releasedDateSet].filter((date) => !draftDates.has(date));

    if (missingDates.length) {
      throw new Error(t("Could not find draft nightly prices for {p0}.", { p0: missingDates.join(", ") }));
    }

    return (Array.isArray(draftNightlies) ? draftNightlies : [])
      .map((night) => {
        if (!night?.id) return null;

        if (releasedDateSet.has(night.date)) {
          return {
            nightly_price_id: Number(night.id),
            override_type: "free_night",
            operation: null,
            override_amount: null,
            reason: "Early check-out room price release",
          };
        }

        if (night.hasOverride) {
          return {
            nightly_price_id: Number(night.id),
            override_type: "absolute",
            operation: "set",
            override_amount: Number(night.price || 0),
            reason: night.overrideReason || "Preserved existing override",
          };
        }

        return null;
      })
      .filter(Boolean);
  };

  const applyEarlyCheckOutRoomReleaseDraft = async (reservation, releasedDates) => {
    if (!Array.isArray(releasedDates) || releasedDates.length === 0) return;

    const sourceSnapshot = getModifyStaySourceSnapshot(reservation);
    if (sourceSnapshot.groupRateId) {
      throw new Error(t("Early check-out charge conversion is not available for reservations using Group Rate."));
    }

    let draftGroupId = null;
    let commitCompleted = false;

    try {
      const { data: editData } = await axios.post(
        `/api/reservation-draft-groups/from-group/${reservation_group_id}/start-edit`,
        { modification_actions: ["dates", "rate_prices"] }
      );
      draftGroupId = Number(editData?.draft_group_id);

      if (!draftGroupId) {
        throw new Error(t("Could not create the early check-out draft."));
      }

      const { data: draftRows } = await axios.get(
        `/api/reservation-drafts/draft-groups/${draftGroupId}/reservations`
      );
      const targetDraft = findTargetDraftForReservation(draftRows, reservation.id);
      const draftId = Number(targetDraft?.id);

      if (!draftId) {
        throw new Error(t("Could not find this reservation inside the early check-out draft."));
      }

      const { data: draftNightlyData } = await axios.get(`/api/reservation-drafts/${draftId}/nightly-prices`);
      const draftNightlies = normalizeNightlyRows(draftNightlyData);
      const overrides = buildEarlyCheckOutRoomReleaseOverridePayload(draftNightlies, releasedDates);

      if (!overrides.length) {
        throw new Error(t("No nightly price changes were prepared for this early check-out."));
      }

      await axios.post(`/api/reservation-drafts/${draftId}/overrides`, { overrides }, { timeout: 45000 });
      await axios.post(`/api/reservation-draft-groups/${draftGroupId}/commit`);
      commitCompleted = true;
    } finally {
      if (draftGroupId && !commitCompleted) {
        await cleanupModifyStayDraftGroup(draftGroupId);
      }
    }
  };

  const openCheckOutFlow = async (reservation) => {
    const reservationId = Number(reservation?.id);
    if (!reservationId || !isViewMode) return;

    if (!canCheckOutReservation(reservation)) {
      await handleCheckOutReservation(reservation);
      return;
    }

    const { arrivalDate: departureDate, arrivalTime: departureTime } = getLocalArrivalParts();
    const originalCheckOutDate = toDateInputValue(reservation?.check_out_date);

    if (!originalCheckOutDate || originalCheckOutDate <= departureDate) {
      setPendingCheckOutReservation(reservation);
      return;
    }

    try {
      const { data } = await axios.get(`/api/reservations/${reservationId}/nightly-prices`);
      const normalized = normalizeNightlyRows(data);
      const pendingNights = normalized
        .filter((night) => night.date >= departureDate && night.date < originalCheckOutDate)
        .map((night) => ({ ...night, decision: "charge" }));
      const consumedNights = normalized.filter((night) => night.date < departureDate);

      if (!pendingNights.length) {
        setPendingCheckOutReservation(reservation);
        return;
      }

      const sourceSnapshot = getModifyStaySourceSnapshot(reservation);
      if (sourceSnapshot.groupRateId) {
        setCheckInDialogState({
          title: t("Early check-out not available"),
          message: t("Early check-out charge conversion is not available for Group Rate reservations yet."),
          tone: "warning",
        });
        return;
      }

      await openReservationChargeWorkflow(reservation, "early_checkout", {
        departureDate,
        departureTime,
        originalCheckOutDate,
        consumedNights,
        pendingNights,
        releasedDates: pendingNights.map((night) => night.date).filter(Boolean),
        unitAmount: sumNightRows(pendingNights),
        serviceDate: departureDate,
        description: t("Early departure charge for {p0}", { p0: departureDate }),
      });
    } catch (err) {
      console.error("[GROUP][EARLY_CHECK_OUT][OPEN][ERR]", { reservationId, err });
      setCheckInDialogState({
        title: t("Could not prepare early check-out"),
        message: buildModifyStayErrorMessage(err, t("Could not prepare the early check-out charge.")),
        tone: "warning",
      });
    }
  };

  const openPreviousNightsOverrideFromEarlyCheckOut = () => {
    const reservation = earlyCheckOutDialog?.reservation;
    if (!reservation) return;

    setEarlyCheckOutDialog(null);
    void openRatesPricesDialog(reservation, "price_adjustment");
  };

  const confirmEarlyCheckOut = async () => {
    const dialog = earlyCheckOutDialog;
    const reservation = dialog?.reservation;
    const reservationId = Number(reservation?.id);

    if (!dialog || !reservationId || !isViewMode) return;

    const sourceSnapshot = getModifyStaySourceSnapshot(reservation);
    const pendingNights = Array.isArray(dialog.pendingNights) ? dialog.pendingNights : [];
    const releasedDates = pendingNights.map((night) => night.date).filter(Boolean);
    const forgivenDates = pendingNights
      .filter((night) => night.decision === "forgive")
      .map((night) => night.date);
    const chargedNights = pendingNights.filter(
      (night) => night.decision !== "forgive" && Number(night.price || 0) > 0
    );
    const totals = getEarlyCheckOutTotals(dialog);

    if (sourceSnapshot.groupRateId && releasedDates.length > 0) {
      setEarlyCheckOutDialog((prev) => ({
        ...prev,
        error: t("Early check-out charge conversion is not available for Group Rate reservations yet."),
      }));
      return;
    }

    try {
      setEarlyCheckOutDialog((prev) => (prev ? { ...prev, saving: true, error: "" } : prev));
      setArrivalActionLoadingByReservationId((prev) => ({ ...prev, [reservationId]: true }));

      if (releasedDates.length > 0) {
        await applyEarlyCheckOutRoomReleaseDraft(reservation, releasedDates);
      }

      if (releasedDates.length > 0) {
        await axios.post(`/api/reservation-charges/reservations/${reservationId}/early-checkout`, {
          departure_date: dialog.departureDate,
          original_check_out_date: dialog.originalCheckOutDate,
          charges: chargedNights.map((night) => ({
            stay_date: night.date,
            service_date: dialog.departureDate,
            amount: Number(night.price || 0),
            description: `Early departure charge for ${night.date}`,
            metadata: {
              nightly_price_id: night.id || null,
            },
          })),
        });
      }

      await axios.put(`/api/reservations/${reservationId}`, {
        status: "Checked Out",
        departure_date: dialog.departureDate,
        departure_time: dialog.departureTime,
        updated_by: "ReservationGroupViewEarlyCheckOut",
      });

      await loadGroup();
      setEarlyCheckOutDialog(null);

      const locator = reservation?.locator || t("Reservation #{p0}", { p0: reservationId });
      const decisionSummary = [
        chargedNights.length
          ? t("{p0} posted as early departure charges", { p0: formatMoney(totals.retainedValue) })
          : null,
        forgivenDates.length ? t("{p0} waived", { p0: formatMoney(totals.forgivenValue) }) : null,
        t("unconsumed room nights were removed from the room price"),
      ].filter(Boolean).join("; ");

      setCheckInDialogState({
        title: t("Check-out completed"),
        message: t("{p0} has been checked out early. {p1}", { p0: locator, p1: decisionSummary }),
        tone: "primary",
      });
    } catch (err) {
      console.error("[GROUP][EARLY_CHECK_OUT][CONFIRM][ERR]", { reservationId, err });
      setEarlyCheckOutDialog((prev) => ({
        ...prev,
        saving: false,
        error: buildModifyStayErrorMessage(err, t("Could not complete this early check-out.")),
      }));
    } finally {
      setArrivalActionLoadingByReservationId((prev) => ({ ...prev, [reservationId]: false }));
    }
  };

  const handleToggleRoomExpand = (reservationId) => {
    const id = Number(reservationId);
    if (!id) return;

    const currentlyExpanded = !!expandedRoomRows[id];
    setExpandedRoomRows((prev) => {
      const next = { ...prev };

      if (currentlyExpanded) {
        delete next[id];
      } else {
        next[id] = true;
      }

      writeExpandedRoomRows(reservation_group_id, mode, next);
      return next;
    });

  };

  const closeSendConfirmationPopup = (force = false) => {
    if (isSendingConfirmation && !force) return;
    setShowSendConfirmationPopup(false);
    setConfirmationEmailFields([""]);
  };

  const handleOpenSendConfirmationPopup = () => {
    if (!hasActiveReservationsInView) {
      alert(formatGroupError(t("This reservation group has no active rooms to confirm.")));
      return;
    }

    const bookerEmail = String(bookerProfile?.email || bookerProfile?.billing_email || "").trim();
    setConfirmationEmailFields(bookerEmail ? [bookerEmail] : [""]);
    setShowSendConfirmationPopup(true);
  };

  const handleGroupCancelReservations = () => {
    const cancellationGate = getGroupCancellationGate({
      reservations,
      canUpdateReservations,
    });
    if (!cancellationGate.allowed) {
      setReservationActionFeedbackState({
        title: t("Group cancellation unavailable"),
        message: cancellationGate.reason,
        tone: "warning",
      });
      return;
    }

    const blocked = (reservations || []).find((reservation) =>
      getManualModificationBlock(reservation, ["cancellation"])
    );
    if (
      blocked &&
      showManualModificationBlockedMsg(blocked, ["cancellation"])
    ) {
      return;
    }

    const cancellableReservations = (reservations || []).filter((reservation) =>
      canCancelReservationAction(reservation)
    );
    if (cancellableReservations.length === 1) {
      requestReservationCancellation(cancellableReservations[0]);
      return;
    }

    setCancelReason("");
    setShowCancelPopup(true);
  };

  const addRoomsToDraftGroup = async ({
    targetGroupId = reservation_group_id,
    increment = normalizedRoomsToAdd,
  } = {}) => {
    if (!canAddRoomsToGroup) {
      throw new Error(t("You do not have permission to add rooms to this group."));
    }
    const parsedIncrement = normalizeRoomsToAdd(increment);
    const currentTotal = Array.isArray(reservations) ? reservations.length : 0;
    if (Number.isNaN(parsedIncrement) || parsedIncrement <= 0) return null;

    const businessUnitIdForPayload = effectiveBusinessUnitId ?? null;

    if (!businessUnitIdForPayload) {
      console.error("[GROUP][ADD_ROOMS][ABORT] business_unit_id missing", {
        group,
        reservations0: reservations?.[0],
      });
      throw new Error("Missing business_unit_id (group not hydrated).");
    }

    const newTotal = currentTotal + parsedIncrement;

    console.log("[GROUP][ADD_ROOMS][CLICK]", {
      reservation_group_id: targetGroupId,
      current_total: currentTotal,
      increment: parsedIncrement,
      new_total: newTotal,
      business_unit_id: businessUnitIdForPayload,
    });

    const { data } = await axios.post(
      `/api/reservation-draft-groups/${targetGroupId}/add-rooms`,
      buildDraftGroupAddRoomsPayload({
        reservationGroupId: targetGroupId,
        newTotal,
        businessUnitId: businessUnitIdForPayload,
      })
    );

    console.log("[GROUP][ADD_ROOMS][OK]", data);
    return data;
  };

  const handleAddRoomsToGroup = async () => {
    try {
      const data = await addRoomsToDraftGroup();
      if (!data) return;

      await loadGroup();
      setRoomsToAdd(1);
    } catch (err) {
      console.error("[GROUP][ADD_ROOMS][ERR]", err);
      alert(formatGroupError(getDraftGroupApiErrorMessage(err, t("Error adding rooms to group."))));
    }
  };

  const handleGroupAddRoomAction = async () => {
    setGroupActionsDialogOpen(false);

    if (!canAddRoomsToGroup) {
      setReservationActionFeedbackState({
        title: t("Add rooms"),
        message: t("You do not have permission to add rooms to this group."),
        tone: "warning",
      });
      return;
    }

    try {
      if (!isViewMode) {
        await handleAddRoomsToGroup();
        return;
      }

      const { data } = await axios.post(
        `/api/reservation-draft-groups/from-group/${reservation_group_id}/start-add-rooms`,
        { count: normalizedRoomsToAdd }
      );
      const draftGroupId = data?.draft_group_id;

      if (!draftGroupId) {
        throw new Error(t("Could not create the reservation update draft."));
      }

      navigate(
        `/reservation-group/${draftGroupId}?mode=edit_draft&business_unit_id=${businessUnitId}`
      );
    } catch (err) {
      console.error("[GROUP_ACTIONS][ADD_ROOM][ERR]", err);
      alert(formatGroupError(getDraftGroupApiErrorMessage(err, t("Error adding room to group."))));
    }
  };

  const startBackgroundGroupDraft = async (action) => {
    let draftGroupId = null;

    try {
      const { data } = await axios.post(
        `/api/reservation-draft-groups/from-group/${reservation_group_id}/start-edit`,
        {
          modification_actions:
            action === "special_rate" ? ["rate_prices"] : [],
        }
      );
      draftGroupId = Number(data?.draft_group_id);

      if (!draftGroupId) {
        throw new Error(t("Could not create the reservation update draft."));
      }

      const { data: draftRowsData } = await axios.get(
        `/api/reservation-drafts/draft-groups/${draftGroupId}/reservations`
      );
      const draftRows = Array.isArray(draftRowsData) ? draftRowsData : [];

      setBackgroundGroupDraftState({
        action,
        draftGroupId,
        reservations: draftRows,
      });

      return { draftGroupId, reservations: draftRows };
    } catch (err) {
      if (draftGroupId) await cleanupModifyStayDraftGroup(draftGroupId);
      throw err;
    }
  };

  const cleanupBackgroundGroupDraft = async (expectedAction = null) => {
    const draft = backgroundGroupDraftRef.current;
    if (!draft?.draftGroupId) return;
    if (expectedAction && draft.action !== expectedAction) return;

    setBackgroundGroupDraftState(null);
    await cleanupModifyStayDraftGroup(draft.draftGroupId);
  };

  const handleCloseGroupExtrasModal = () => {
    setShowGroupExtras(false);
  };

  const handleCloseSpecialRateModal = () => {
    setShowSpecialRate(false);
    if (backgroundGroupDraftRef.current?.action === "special_rate") {
      void cleanupBackgroundGroupDraft("special_rate");
    }
  };

  const openBackgroundGroupExtras = async () => {
    setGroupActionsDialogOpen(false);

    if (!canManageGroupExtras) {
      setReservationActionFeedbackState({
        title: t("Group extras"),
        message: t("You do not have permission to manage reservation extras."),
        tone: "warning",
      });
      return;
    }

    // Live extras are additive, date-scoped operations, never a full-group draft.
    setShowGroupExtras(true);
  };

  const openBackgroundGroupSpecialRate = async () => {
    setGroupActionsDialogOpen(false);

    if (groupRateLocked || isAddRoomsDraft) {
      setReservationActionFeedbackState({
        title: t("Group special rate"),
        message: t(groupRateLocked
          ? "The group rate is locked from the first check-in."
          : "Set the rate of each new room without changing the existing group rate."),
        tone: "warning",
      });
      return;
    }

    if (!canApplyGroupSpecialRate) {
      setReservationActionFeedbackState({
        title: t("Group special rate"),
        message: t("You do not have permission to override reservation rates."),
        tone: "warning",
      });
      return;
    }

    if (!isViewMode) {
      setShowSpecialRate(true);
      return;
    }

    let draftGroupId = null;

    try {
      setBackgroundGroupActionLoading(true);
      const draft = await startBackgroundGroupDraft("special_rate");
      draftGroupId = draft.draftGroupId;
      setShowSpecialRate(true);
    } catch (err) {
      if (draftGroupId) await cleanupModifyStayDraftGroup(draftGroupId);
      console.error("[GROUP_ACTIONS][SPECIAL_RATE][OPEN][ERR]", err?.response?.data || err);
      alert(formatGroupError(err?.response?.data?.error || err?.message || t("Could not open group special rate.")));
    } finally {
      setBackgroundGroupActionLoading(false);
    }
  };

  const commitBackgroundGroupDraft = async (
    expectedAction,
    successTitle,
    successMessage,
    reason = null
  ) => {
    const draft = backgroundGroupDraftRef.current;
    const draftGroupId = Number(draft?.draftGroupId);

    if (!draftGroupId || draft.action !== expectedAction) return false;

    let commitCompleted = false;

    try {
      await axios.post(`/api/reservation-draft-groups/${draftGroupId}/commit`, {
        reason: String(reason || "").trim() || undefined,
      });
      commitCompleted = true;
      setBackgroundGroupDraftState(null);
      await loadGroup();
      setReservationActionFeedbackState({
        title: successTitle,
        message: successMessage,
        tone: "primary",
      });
      return true;
    } catch (err) {
      console.error("[GROUP_ACTIONS][BACKGROUND_COMMIT][ERR]", err?.response?.data || err);
      if (!commitCompleted) {
        await cleanupModifyStayDraftGroup(draftGroupId);
        setBackgroundGroupDraftState(null);
      }
      throw new Error(err?.response?.data?.error || err?.message || t("Could not save the group update."));
    }
  };

  const handleGroupExtrasSaved = async (response = {}) => {
    const feedback = buildGroupExtraAssignmentFeedback(response, languageId);
    const decision = getGroupExtraAssignmentPersistenceDecision(response);
    const { hasRejected } = decision;

    if (hasRejected) {
      setReservationActionFeedbackState(feedback);
      return { closeModal: false };
    }

    setShowGroupExtras(false);
    await loadGroup();
    return { closeModal: true };
  };

  const applyGroupReservationModificationPolicy = (items) =>
    (items || []).map((action) => {
      if (demoReadOnlyActive) {
        return { ...action, disabled: true, hint: DEMO_ACTION_HINT, policyBlocked: false };
      }
      const policyActions =
        action.key === "cancel_reservations"
          ? ["cancellation"]
          : action.key === "group_special_rate"
            ? ["rate_prices"]
            : [];
      if (!policyActions.length) return action;

      const blockedReservation = (reservations || []).find((reservation) =>
        getManualModificationBlock(reservation, policyActions)
      );
      if (!blockedReservation) return action;

      const block = getManualModificationBlock(
        blockedReservation,
        policyActions
      );
      return {
        ...action,
        disabled: true,
        hint: block?.message || t("This manual reservation change is disabled."),
        policyBlocked: true,
      };
    });

  const getGroupActionItems = () => {
    const items = [];
    const cancellationGate = getGroupCancellationGate({
      reservations,
      canUpdateReservations,
    });

    if (!isViewMode) {
      items.push({
        key: "add_room",
        title: t("Add rooms"),
        icon: FiPlus,
        tone: "success",
        disabled: !canAddRoomsToGroup,
        hint: canAddRoomsToGroup
          ? t("Add rooms to this reservation group")
          : t("No permission to add rooms to reservation groups"),
      });

      items.push({
        key: "copy_info",
        title: t("Copy Info"),
        icon: FiRefreshCcw,
        tone: "warning",
        disabled: !canUseCopyInfo,
        hint: canUseCopyInfo
          ? t("Copy setup from one room to another")
          : t("Needs at least two rooms with room rate"),
      });

      items.push({
        key: "group_extras",
        title: t("Manage Group Extras"),
        icon: FiPackage,
        disabled: !canManageGroupExtras,
        hint: canManageGroupExtras
          ? t("Manage extras for every room in the group")
          : t("No permission to manage reservation extras"),
      });

      items.push({
        key: "group_special_rate",
        title: t("Apply Group Special Rate"),
        icon: FiDollarSign,
        tone: "warning",
        disabled: !canApplyGroupSpecialRate || groupRateLocked || isAddRoomsDraft,
        hint: groupRateLocked
          ? t("The group rate is locked from the first check-in.")
          : isAddRoomsDraft
          ? t("Set the rate of each new room without changing the existing group rate.")
          : canApplyGroupSpecialRate
          ? t("Apply a negotiated price to the group")
          : t("No permission to override reservation rates"),
      });

      return applyGroupReservationModificationPolicy(items);
    }

    if (hasActiveReservationsInView && !isGroupCancelledInView) {
      items.push({
        key: "add_room",
        title: t("Add rooms"),
        icon: FiPlus,
        tone: "success",
        disabled: !canAddRoomsToGroup,
        hint: canAddRoomsToGroup
          ? t("Add rooms to this reservation group")
          : t("No permission to add rooms to reservation groups"),
      });

      items.push({
        key: "group_extras",
        title: t("Add Group Extras"),
        icon: FiPackage,
        disabled: !canManageGroupExtras,
        hint: !canManageGroupExtras
          ? t("No permission to manage reservation extras")
          : t("Add extras to selected reservations and pending nights only"),
      });

      if ((reservations?.length || 0) > 1) {
        items.push({
          key: "group_special_rate",
          title: backgroundGroupActionLoading ? t("Preparing...") : t("Apply Group Special Rate"),
          icon: FiDollarSign,
          tone: "warning",
          disabled: backgroundGroupActionLoading || !canApplyGroupSpecialRate || groupRateLocked,
          hint: groupRateLocked
            ? t("The group rate is locked from the first check-in.")
            : !canApplyGroupSpecialRate
            ? t("No permission to override reservation rates")
            : undefined,
        });
      }
    }

    items.push({
      key: "payments",
      title: t("Manage Payments"),
      icon: FiCreditCard,
      hint: !canViewReservationPayments
        ? t("No payment access")
        : paymentMethodsLoading
          ? t("Checking saved cards")
          : hasSavedCard
            ? t("Saved card on file")
            : t("No saved card on file"),
      disabled: !canViewReservationPayments,
    });

    if (hasCheckedInReservationsInView) {
      items.push({
        key: "registration_form",
        title: t("Fill Registration Form"),
        icon: FiFileText,
        disabled: true,
      });
    } else if (hasActiveReservationsInView) {
      items.push({
        key: "send_confirmation",
        title: isSendingConfirmation ? t("Sending...") : t("Send Confirmation"),
        icon: FiSend,
        tone: "success",
        disabled: isSendingConfirmation,
      });
    }

    if (!isGroupCancelledInView && !hasCheckedInReservationsInView) {
      items.push({
        key: "cancel_reservations",
        title: t("Cancel Reservations"),
        icon: FiX,
        tone: "danger",
        disabled: !cancellationGate.allowed,
        hint: cancellationGate.reason || undefined,
      });
    }

    return applyGroupReservationModificationPolicy(items);
  };

  const handleGroupActionClick = (action) => {
    if (demoReadOnlyActive) return;
    if (action?.disabled) {
      setReservationActionFeedbackState({
        title: t("Manual change not allowed"),
        message: action?.hint || t("This reservation action is disabled."),
        tone: "warning",
      });
      return;
    }

    setGroupActionsDialogOpen(false);

    if (action?.key === "add_room") {
      void handleGroupAddRoomAction();
      return;
    }

    if (action?.key === "group_extras") {
      void openBackgroundGroupExtras();
      return;
    }

    if (action?.key === "group_special_rate") {
      void openBackgroundGroupSpecialRate();
      return;
    }

    if (action?.key === "copy_info") {
      openCopyInfoModal();
      return;
    }

    if (action?.key === "payments") {
      if (!canViewReservationPayments) return;
      console.log("[PAYMENTS][OPEN_POPUP]", { reservation_group_id, businessUnitId });
      setPaymentTargetReservation(null);
      setShowManagePaymentsPopup(true);
      return;
    }

    if (action?.key === "send_confirmation") {
      handleOpenSendConfirmationPopup();
      return;
    }

    if (action?.key === "cancel_reservations") {
      handleGroupCancelReservations();
    }
  };

  const handleConfirmationEmailFieldChange = (index, value) => {
    setConfirmationEmailFields((prev) =>
      prev.map((email, currentIndex) => (currentIndex === index ? value : email))
    );
  };

  const handleAddConfirmationEmailField = () => {
    setConfirmationEmailFields((prev) => [...prev, ""]);
  };

  const handleRemoveConfirmationEmailField = (index) => {
    setConfirmationEmailFields((prev) => {
      if (prev.length <= 1) return [""];
      return prev.filter((_, currentIndex) => currentIndex !== index);
    });
  };

  const confirmationHistoryMatches = confirmationEmailFields.reduce((acc, email) => {
    const trimmedEmail = String(email || "").trim();
    const normalizedEmail = normalizeConfirmationHistoryEmail(trimmedEmail);
    if (!normalizedEmail) return acc;
    if (acc.some((item) => normalizeConfirmationHistoryEmail(item.email) === normalizedEmail)) {
      return acc;
    }

    const latestEntry = findLatestConfirmationEntryForEmail(trimmedEmail);
    if (!latestEntry) return acc;

    acc.push({
      email: trimmedEmail,
      entry: latestEntry,
    });
    return acc;
  }, []);

  const handleSendConfirmationEmail = async () => {
    if (isSendingConfirmation) return;

    const recipients = confirmationEmailFields
      .map((email) => String(email || "").trim())
      .filter(Boolean);

    if (!recipients.length) {
      alert(formatGroupError(t("At least one email is required to send the booking confirmation.")));
      return;
    }

      try {
        setIsSendingConfirmation(true);

        const payload = {
          emails: recipients,
        };

        const { data } = await axios.post(
          `/api/reservation-groups/${reservation_group_id}/send-confirmation-email`,
          payload
        );

        if (data?.email_log_entry) {
          setGroup((prev) => {
            if (!prev) return prev;

            const previousHistory = Array.isArray(prev.confirmation_email_history)
              ? prev.confirmation_email_history
              : [];
            const nextHistory = [
              data.email_log_entry,
              ...previousHistory.filter((entry) => entry?.id !== data.email_log_entry?.id),
            ];

            return {
              ...prev,
              confirmation_email_history: nextHistory,
            };
          });
        }

        closeSendConfirmationPopup(true);
        const sentLanguages = Array.isArray(data?.sent_languages)
          ? data.sent_languages.map((lang) => String(lang || "").toUpperCase()).join(" + ")
          : "";
        const pdfLanguages = Array.isArray(data?.pdf_languages)
          ? data.pdf_languages.map((lang) => String(lang || "").toUpperCase()).join(" + ")
          : "";
        alert(
          formatGroupError(t("Reservation confirmation sent to {p0}{p1}.", { p0: data?.sent_to || recipients.join(", "), p1: sentLanguages || pdfLanguages
              ? ` (${[
                  sentLanguages ? t("Email {p0}", { p0: sentLanguages }) : "",
                  pdfLanguages ? `PDF ${pdfLanguages}` : "",
                ]
                  .filter(Boolean)
                  .join(" | ")})`
              : "" }))
        );
      } catch (err) {
      console.error("[SEND_CONFIRMATION_EMAIL][ERROR]", err);
      alert(formatGroupError(err?.response?.data?.error || t("Failed to send reservation confirmation.")));
    } finally {
      setIsSendingConfirmation(false);
    }
  };

  const sendConfirmationOverlayStyle = {
    position: "fixed",
    inset: 0,
    background: "rgba(0, 0, 0, 0.85)",
    zIndex: 22000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "14px 12px",
  };

  const sendConfirmationShellStyle = {
    width: "100%",
    maxWidth: "860px",
    borderRadius: "22px",
    padding: "28px",
    background:
      "linear-gradient(180deg, rgba(10, 10, 18, 0.95), rgba(8, 13, 25, 0.95)) padding-box, " +
      "linear-gradient(170deg, rgba(14, 165, 233, 0.35), rgba(59, 130, 246, 0.25)) border-box",
    border: "1px solid rgba(148, 163, 184, 0.25)",
    boxShadow: "0 30px 60px rgba(2, 6, 23, 0.65)",
    display: "flex",
    flexDirection: "column",
    maxHeight: "calc(100vh - 40px)",
  };

  const sendConfirmationContentStyle = {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    maxHeight: "calc(100vh - 150px)",
  };

  const sendConfirmationSectionStyle = {
    marginTop: "18px",
    borderRadius: "16px",
    padding: "18px",
    background: "rgba(15, 23, 42, 0.85)",
    border: "1px solid rgba(148, 163, 184, 0.25)",
  };

  const sendConfirmationInputStyle = {
    width: "100%",
    borderRadius: "10px",
    padding: "10px 12px",
    background: "rgba(15, 23, 42, 0.9)",
    border: "1px solid rgba(148, 163, 184, 0.45)",
    color: "#e2e8f0",
    fontSize: "0.95rem",
  };

  const sendConfirmationLabelStyle = {
    fontSize: "0.85rem",
    fontWeight: 600,
    color: "#e2e8f0",
    marginBottom: "6px",
    display: "block",
  };

  const sendConfirmationSecondaryBtnStyle = {
    borderRadius: "12px",
    padding: "12px 18px",
    fontWeight: 700,
    letterSpacing: "0.03em",
    cursor: "pointer",
    border: "1px solid rgba(148, 163, 184, 0.5)",
    background: "transparent",
    color: "#e2e8f0",
  };

  const sendConfirmationPrimaryBtnStyle = {
    borderRadius: "12px",
    padding: "12px 18px",
    fontWeight: 700,
    letterSpacing: "0.03em",
    cursor: "pointer",
    border: "1px solid rgba(56, 189, 248, 0.8)",
    background: "linear-gradient(135deg, rgba(14, 165, 233, 0.8), rgba(59, 130, 246, 0.9))",
    color: "#eff6ff",
  };

  const sendConfirmationGhostBtnStyle = {
    borderRadius: "999px",
    border: "1px solid rgba(56, 189, 248, 0.45)",
    background: "rgba(14, 165, 233, 0.08)",
    color: "#bae6fd",
    padding: "8px 14px",
    fontWeight: 700,
    fontSize: "0.82rem",
    cursor: "pointer",
  };

  const sendConfirmationRemoveBtnStyle = {
    borderRadius: "999px",
    border: "1px solid rgba(248, 113, 113, 0.5)",
    background: "rgba(248, 113, 113, 0.08)",
    color: "#fecaca",
    padding: "8px 14px",
    fontWeight: 700,
    fontSize: "0.82rem",
    cursor: "pointer",
    whiteSpace: "nowrap",
  };

  const sendConfirmationHistoryCardStyle = {
    padding: "14px",
    borderRadius: "14px",
    background: "rgba(2, 6, 23, 0.48)",
    border: "1px solid rgba(148, 163, 184, 0.22)",
  };

  const sendConfirmationHistoryBadgeStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    borderRadius: "999px",
    padding: "6px 12px",
    border: "1px solid rgba(148, 163, 184, 0.28)",
    background: "rgba(255, 255, 255, 0.04)",
    color: "#cbd5e1",
    fontSize: "0.75rem",
    fontWeight: 700,
  };

  const sendConfirmationHistoryChipStyle = {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: "999px",
    padding: "6px 10px",
    background: "rgba(30, 41, 59, 0.9)",
    border: "1px solid rgba(148, 163, 184, 0.24)",
    color: "#e2e8f0",
    fontSize: "0.77rem",
  };

  const sendConfirmationWarningStyle = {
    marginTop: "16px",
    padding: "12px 14px",
    borderRadius: "14px",
    border: "1px solid rgba(251, 191, 36, 0.35)",
    background: "rgba(251, 191, 36, 0.08)",
    color: "#fde68a",
  };




  // ✅ Si ya se usó una vez en el grupo (booker == main_guest en alguna room), no lo mostramos más
  const sameAsBookerAlreadyUsed =
    !!bookerId && (reservations || []).some((r) => Number(r?.main_guest?.id) === Number(bookerId));

  const reservationChangeHistoryById = (reservationChangeHistory.reservations || []).reduce(
    (result, entry) => {
      const reservationId = Number(entry?.reservation_id);
      if (Number.isFinite(reservationId) && reservationId > 0) {
        result[reservationId] = entry;
      }
      return result;
    },
    {}
  );

  const handleApplySpecialRate = async (payload) => {
    try {
      if (groupRateLocked || isAddRoomsDraft) {
        throw new Error(t("The group rate is locked from the first check-in."));
      }
      const rateCandidateReservations =
        backgroundGroupDraftRef.current?.action === "special_rate"
          ? backgroundGroupDraftRef.current.reservations || []
          : reservations || [];
      const rateTargetReservations = getGroupRateTargetReservations({
        reservations: rateCandidateReservations,
        scope: payload?.scope,
        selectedReservationIds: payload?.selectedReservationIds,
      });
      const appliesToOtaReservation = rateTargetReservations.some(
        isOtaReservation
      );
      const specialRateReason = String(
        payload?.reason || payload?.notes || ""
      ).trim();
      if (appliesToOtaReservation && !specialRateReason) {
        alert(
          formatGroupError(t("Enter a note explaining why the OTA reservation price is being changed."))
        );
        return;
      }

      console.log("[DraftSpecialRate][POST][IN]", {
        reservation_group_id,
        backgroundDraft: backgroundGroupDraftRef.current,
        payload,

      });

      // ✅ endpoint draft (cuando lo tengas montado en backend)
      const { data } = await axios.post(
        "/api/reservation-draft-group-rates/apply",
        payload
      );

      console.log("[DraftSpecialRate][POST][OK]", data);

      if (backgroundGroupDraftRef.current?.action === "special_rate") {
        await commitBackgroundGroupDraft(
          "special_rate",
          t("Group special rate applied"),
          t("The special rate was applied and saved to the reservation group."),
          specialRateReason || null
        );
        setShowSpecialRate(false);
        return;
      }

      setShowSpecialRate(false);
      await loadGroup(); // refresca precios/totales y lo que devuelva el backend
    } catch (err) {
      console.error("[DraftSpecialRate][POST][ERR]", err?.response?.data || err);
      alert(formatGroupError(err?.response?.data?.message || err?.response?.data?.error || err?.message || t("Error applying special rate (draft).")));
    }
  };



  // Keep the existing reservation controller mounted off-canvas in actions-only
  // mode so Front Desk reuses every workflow dialog instead of duplicating them.
  return (
  <div
    className={`rgv-page${isLightTheme ? " rgv-page--light" : " text-light"}${
      actionsOnly ? " rgv-page--actionsOnly" : ""
    }`}
    style={
      actionsOnly
        ? {
            position: "fixed",
            top: 0,
            left: "-200vw",
            width: "100vw",
            height: "100vh",
          }
        : undefined
    }
  >
    <div className={`rgv-shell${isLightTheme ? " rgv-shell--light" : ""}`}>

    {/* ───────────────── HEADER ───────────────── */}
    <div className="rgv-header">

      {/* IZQUIERDA: TÍTULO + BOOKER + SUMMARY */}
      <div className="rgv-headerMain">
        <div className="rgv-titleRow">
          <div className="rgv-titleLeft">
            <div className="rgv-titleBlock">
              <h4 className="rgv-title"> {t("Reservation Group")} {displayGroupLocator || reservation_group_id}
              </h4>
              <div className="rgv-subtitle">
                {isAddRoomsDraft ? t("New rooms only · Existing reservations remain unchanged.") : <>{groupDetailsLabel} {t("| Review room setup, guests and pricing.")}</>} </div>
            </div>

            <div className="rgv-statsRow">
              <div className="rgv-stat">
                <div className="rgv-statLabel">{t(isAddRoomsDraft ? "New rooms" : "Active Rooms")}</div>
                <div className="rgv-statValue">{groupStats.rooms}</div>
              </div>
              <div className="rgv-stat">
                <div className="rgv-statLabel">{t("Cancelled")}</div>
                <div className="rgv-statValue">{groupStats.cancelled}</div>
              </div>
              <div className="rgv-stat">
                <div className="rgv-statLabel">{t("Adults")}</div>
                <div className="rgv-statValue">{groupStats.adults}</div>
              </div>
              <div className="rgv-stat">
                <div className="rgv-statLabel">{t("Children")}</div>
                <div className="rgv-statValue">{groupStats.children}</div>
              </div>
              <button
                type="button"
                className="btn rgv-groupActionsBtn"
                onClick={() => setGroupActionsDialogOpen(true)}
              >
                <FiMoreHorizontal size={16} aria-hidden="true" /> {t("Group Actions")} </button>
              {isViewMode && canViewReservationPayments && (
                <button
                  type="button"
                  className="btn rgv-groupActionsBtn"
                  onClick={goToGroupAccount}
                  disabled={demoReadOnlyActive}
                  title={demoReadOnlyActive ? DEMO_DATA_UNAVAILABLE : undefined}
                >
                  <FiCreditCard size={16} aria-hidden="true" /> {t("Group Account")} </button>
              )}
            </div>
          </div>

          <div className="rgv-titleActions">
            {isViewMode ? (
              <div className="rgv-statActionCluster">
                <button
                  type="button"
                  className="btn rgv-statActionBtn rgv-statActionBtn--close"
                  onClick={goToReservationsList}
                > {t("Close")} </button>
              </div>
            ) : (
              <div className="rgv-statActionCluster">
                <button
                  type="button"
                  className="btn rgv-statActionBtn rgv-statActionBtn--save"
                  disabled={isSaving}
                  onClick={() => {
                    setConfirmGroupError(null);
                    setShowConfirmGroupDialog(true);
                  }}
                > {t("Save")} </button>

                <button
                  type="button"
                  className="btn rgv-statActionBtn rgv-statActionBtn--discard"
                  disabled={isSaving}
                  onClick={() => {
                    setDiscardGroupError(null);
                    setShowDeleteGroupDialog(true);
                  }}
                > {t("Discard")} </button>
              </div>
            )}
          </div>
        </div>

        {isAddRoomsDraft && (
          <div className="rgv-addRoomsNotice" role="note">
            <FiLock aria-hidden="true" />
            <span>{t("You are adding rooms to an existing group. Its current reservations, agreed prices and payments will not be changed.")}</span>
          </div>
        )}

        <div className="rgv-overviewGrid">

          {/* BOOKER */}
          <div
            className="rgv-panel"
          >
            <div className="rgv-panelTitle"> {t("Booker")} </div>
            {bookerProfile ? (
              <div className="d-flex justify-content-between align-items-start gap-3">
                <div>
                  <div className="rgv-bookerName">
                    {getBookerDisplayName(group)}
                  </div>

                  <div className="rgv-panelMuted">
                    {bookerProfile.email || "N/A"}
                  </div>

                  <div className="rgv-panelMuted">
                    {bookerProfile.phone || "N/A"}
                  </div>
                  {companyBooker && (
                    <div className="rgv-panelMuted"> {t("Company · Contact:")} {group.booker_contact_name || t("Not provided")}
                    </div>
                  )}
                </div>

                <div className="d-flex flex-column gap-2">
                  {canShowGroupBookerControls && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-info"
                    disabled={!canManageGroupBooker}
                    onClick={async () => {
                      if (!canManageGroupBooker) return;
                      console.log("[GROUP][BOOKER][EDIT][OPEN]", { booker: group?.booker });
                      if (!bookerProfile?.id) {
                        alert(formatGroupError(t("No booker assigned.")));
                        return;
                      }

                      const fullBooker = companyBooker ? null : await loadFullCustomer(group.booker.id);

                      setBookerEditorMode("edit");
                      setBookerEditorInitialData(companyBooker ? null : fullBooker || group.booker);
                      setIsAddBookerOpen(true);
                    }}
                  > {t("Edit")} </button>
                  )}

                  {canShowGroupBookerControls && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-warning"
                    disabled={!canManageGroupBooker}
                    onClick={async () => {
                      if (!canManageGroupBooker) return;
                      try {
                        console.log("[GROUP][BOOKER][REMOVE][PATCH] start", { reservation_group_id });
                        await patchGroupBooker(null, "UserRemoveBooker");
                        await loadGroup();
                        console.log("[GROUP][BOOKER][REMOVE][OK]");
                      } catch (err) {
                        console.error("[GROUP][BOOKER][REMOVE][ERR]", err);
                        alert(formatGroupError(t("Error removing booker.")));
                      }
                    }}
                  > {t("Remove")} </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="d-flex justify-content-between align-items-start gap-3">
                <div className="d-flex align-items-center" style={{ minHeight: "58px" }}>
                  <span className="rgv-panelMuted"> {t("No booker assigned")} </span>
                </div>

                <div className="d-flex flex-column gap-2">
                  {canShowGroupBookerControls && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-info"
                    disabled={!canManageGroupBooker}
                    onClick={() => {
                      if (!canManageGroupBooker) return;
                      console.log("[GROUP][BOOKER][SELECT][OPEN]");
                      const anyDraftId = reservations?.[0]?.id || null;
                      if (!anyDraftId) {
                        alert(formatGroupError(t("No reservation found in this group.")));
                        return;
                      }
                      setBookerSelectorDraftId(anyDraftId);
                      setIsBookerSelectorOpen(true);
                    }}
                  > {t("Select")} </button>
                  )}

                  {canShowGroupBookerControls && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    disabled={!canManageGroupBooker}
                    onClick={() => {
                      if (!canManageGroupBooker) return;
                      console.log("[GROUP][BOOKER][ADD][OPEN]");
                      setBookerEditorMode("create");
                      setBookerEditorInitialData(null);
                      setIsAddBookerOpen(true);
                    }}
                  > {t("Add Booker")} </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* SUMMARY */}
          <div
            className="rgv-panel"
          >
            <div className="rgv-panelTitle"> {t("Summary")} </div>

            <div className="rgv-summaryFinancialGrid">
              <div className="rgv-summarySalesColumn">
                <div className="rgv-summaryColumnTitle">{t("Sales")}</div>

                <div className="rgv-summaryLine">
                  <span>{t("Room")}</span>
                  <strong>{formatMoney(totals.room)}</strong>
                </div>

                <div className="rgv-summaryLine">
                  <span>{t("Extras")}</span>
                  <strong>{formatMoney(totals.extras)}</strong>
                </div>

                <div className="rgv-summaryLine">
                  <span>{t("Charges")}</span>
                  <strong title={demoReadOnlyActive ? DEMO_DATA_UNAVAILABLE : undefined}>{demoReadOnlyActive ? "—" : formatMoney(totals.charges)}</strong>
                </div>
              </div>

              <div className="rgv-summaryTotalsColumn">
                <div className="rgv-summaryColumnTitle">{t("Totals")}</div>

                <div className="rgv-summaryLine rgv-summaryLine--total">
                  <span>{t("Total")}</span>
                  <strong>{demoReadOnlyActive ? "—" : formatMoney(summaryFinancials.sales)}</strong>
                </div>

                <div className="rgv-summaryLine rgv-summaryLine--paid">
                  <span>{t("Paid")}</span>
                  <strong>{formatSummaryPaymentMoney(summaryFinancials.paid)}</strong>
                </div>

                <div className="rgv-summaryLine rgv-summaryLine--balance">
                  <span>{t("Balance")}</span>
                  <strong>{formatSummaryPaymentMoney(summaryFinancials.balance)}</strong>
                </div>
              </div>
            </div>
            {demoReadOnlyActive && <div className="rgv-panelMuted">Saldo global y cargos: {DEMO_DATA_UNAVAILABLE}</div>}
          </div>

           {/* SPECIAL REQUESTS */}
          <div
            className="rgv-panel"
            style={{ cursor: !demoReadOnlyActive && specialRequestRooms.length ? "pointer" : "default" }}
            onClick={() => {
              if (demoReadOnlyActive) return;
              if (specialRequestRooms.length) setShowSpecialRequestsPopup(true);
            }}
          >
            <div className="rgv-panelTitle" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span>{t("Special requests")}</span>
              {specialRequestRooms.length > 0 && (
                <span style={{ fontSize: "0.78rem", fontWeight: 700, color: "rgba(230,230,247,0.55)", background: "rgba(255,255,255,0.08)", borderRadius: "20px", padding: "1px 8px" }}>
                  {specialRequestRooms.length}
                </span>
              )}
            </div>

            {specialRequestPreviewItems.length ? (
              <div>
                <div className="rgv-specialRequestsPreview">
                  {specialRequestPreviewItems.slice(0, 3).map((item, index, previewItems) => {
                    const previousItem = previewItems[index - 1];
                    const showRoomLabel = !previousItem || previousItem.id !== item.id;

                    return (
                      <div
                        key={`${item.id || index}-${item.isImportant ? "guest" : "channel"}`}
                        className={`rgv-specialRequestsPreviewLine ${
                          item.isImportant ? "rgv-specialRequestsPreviewLine--important" : ""
                        }`}
                      >
                        {showRoomLabel && (
                          <span className="rgv-specialRequestsPreviewRoom">{item.label}: </span>
                        )}
                        {item.text}
                      </div>
                    );
                  })}
                </div>
                {specialRequestPreviewItems.length > 3 && (
                  <div className="rgv-specialRequestsMore">
                    +{specialRequestPreviewItems.length - 3} {t("more")} </div>
                )}
              </div>
            ) : (
              <div className="rgv-panelMuted">
                {demoReadOnlyActive ? DEMO_DATA_UNAVAILABLE : t("No special requests for this group.")}
              </div>
            )}
          </div>

          {/* COMMENTS */}
          {(() => {
            const latest = commentsData.find((c) => c.is_important) ?? commentsData[0];
            const fmtDate = (v) => {
              if (!v) return "";
              try { return new Date(v).toLocaleString(reservationLocale, { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
              catch { return ""; }
            };
            const latestScope = (() => {
              if (!latest) return null;
              const latestRoomId = isViewMode ? latest.reservation_id : latest.reservation_draft_id;
              if (!latestRoomId) {
                const name = getBookerDisplayName(group);
                return name ? t("Whole group · {p0}", { p0: name }) : t("Whole group");
              }
              const res = reservations.find((r) => Number(r.id) === Number(latestRoomId));
              if (res) {
                const roomMeta = Number.isFinite(Number(res?.room_id)) ? roomCatalogById[Number(res.room_id)] : null;
                const roomNum = res?.room_number || res?.room?.room_number || roomMeta?.room_number || roomMeta?.name || null;
                const guestName = res.main_guest
                  ? `${res.main_guest.first_name || ""} ${res.main_guest.last_name || ""}`.trim() || null
                  : null;
                if (roomNum && guestName) return t("Room {p0} · {p1}", { p0: roomNum, p1: guestName });
                if (roomNum) return t("Room {p0}", { p0: roomNum });
                if (guestName) return guestName;
                return res.locator ? `Loc. ${res.locator}` : t("Res. #{p0}", { p0: latestRoomId });
              }
              return t("Res. #{p0}", { p0: latestRoomId });
            })();
            const previewText = latest
              ? (latest.body.length > 80 ? latest.body.slice(0, 80) + "…" : latest.body)
              : null;
            return (
              <div className="rgv-panel" aria-disabled={demoReadOnlyActive || undefined} style={{ cursor: demoReadOnlyActive ? "default" : "pointer" }} onClick={() => { if (demoReadOnlyActive) return; setCommentsInitialResId(null); setShowCommentsPopup(true); }}>
                <div className="rgv-panelTitle" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span>{t("Comments")}</span>
                  <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                    {commentsCount > 0 && (
                      <span className="rgv-commentsCount" style={{ fontSize: "0.78rem", fontWeight: 700, color: "rgba(230,230,247,0.55)", background: "rgba(255,255,255,0.08)", borderRadius: "20px", padding: "1px 8px" }}>
                        {commentsCount}
                      </span>
                    )}
                    {!demoReadOnlyActive && <span className="rgv-commentsAdd" title={t("Add / view comments")} style={{ fontSize: "1rem", lineHeight: 1, color: "rgba(48,216,255,0.7)", fontWeight: 400 }}>＋</span>}
                  </div>
                </div>
                {previewText ? (
                  <div>
                    <div className="rgv-commentsMeta" style={{ fontSize: "0.72rem", color: "rgba(230,230,247,0.38)", marginBottom: 3 }}>
                      {latest.created_by && <span>{latest.created_by}</span>}{latest.created_by && " · "}{fmtDate(latest.createdAt || latest.created_at)}
                    </div>
                    <div className={`rgv-panelMuted${latest.is_important ? " is-important" : ""}`} style={latest.is_important ? { color: "#9b3445" } : undefined}>
                      {latest.is_important && "🚩 "}
                      {latestScope && <span className="rgv-commentsScope" style={{ color: latest.is_important ? "#9b3445" : "#223a7a", fontWeight: 600 }}>{t("Guest")} {latestScope}: </span>}
                      {previewText}
                    </div>
                    {commentsCount > 1 && (
                      <div className="rgv-panelMuted" style={{ marginTop: 4, fontSize: "0.78rem" }}>
                        +{commentsCount - 1} {t("more")} {commentsData.some((c, i) => i > 0 && c.is_important) && " · 🚩"}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="rgv-panelMuted">{demoReadOnlyActive ? DEMO_DATA_UNAVAILABLE : t("No comments yet. Click to add one.")}</div>
                )}
              </div>
            );
          })()}

        </div>
      </div>

        {!demoReadOnlyActive && <>
        <PmsConfirmDialog
          isOpen={showConfirmGroupDialog}
          eyebrow={t("Reservation Group")}
          title={isAddRoomsDraft ? t("Add these rooms to the group?") : reservations.length === 1 ? t("Confirm this reservation?") : t("Confirm these reservations?")}
          message={confirmGroupError || (isAddRoomsDraft
            ? t("Only the {p0} new reservations will be added. Existing reservations will remain unchanged.", { p0: reservations.length })
            : reservations.length === 1
            ? t("This will confirm the reservation and make it active.")
            : t("This will confirm all {p0} reservations in this group.", { p0: reservations.length }))}
          confirmLabel={isSaving ? t("Saving...") : t("Confirm")}
          cancelLabel={t("Cancel")}
          onClose={() => { setShowConfirmGroupDialog(false); setConfirmGroupError(null); }}
          onConfirm={async () => {
            setIsSaving(true);
            try {
              await axios.post(`/api/reservation-draft-groups/${reservation_group_id}/commit`);
              setShowConfirmGroupDialog(false);
              if (isAddRoomsDraft) goToReservationGroupView(group.original_group_id);
              else goToReservationsList();
            } catch (err) {
              const status = err?.response?.status;
              const data = err?.response?.data;
              console.error("[DraftCommit][ERROR]", { status, data, err });
              if (status === 409 && data?.code === "MISSING_ROOM_RATE") {
                setConfirmGroupError(data?.error || t("Cannot confirm: some rooms have no room rate. Delete them or assign a room rate."));
              } else if (status === 409 && data?.code === "MISSING_BOOKER") {
                setConfirmGroupError(data?.error || t("Cannot confirm: no booker is assigned. Assign a booker before confirming."));
              } else {
                setConfirmGroupError(t("Failed to confirm reservation group."));
              }
            } finally {
              setIsSaving(false);
            }
          }}
          isBusy={isSaving}
          tone="primary"
        />

        <PmsConfirmDialog
          isOpen={showDeleteGroupDialog}
          eyebrow={isUpdateDraft ? t("Reservation Update") : t("Reservation Draft")}
          title={isUpdateDraft ? t("Discard this update draft?") : t("Discard this draft reservation group?")}
          message={
            discardGroupError || (isUpdateDraft
              ? t("This will discard the pending update only. The original reservation group will remain unchanged.")
              : t("This will discard the draft reservation group. No confirmed reservation will be deleted."))
          }
          confirmLabel={isSaving ? t("Discarding...") : discardDraftLabel}
          cancelLabel={t("Cancel")}
          onClose={() => {
            setShowDeleteGroupDialog(false);
            setDiscardGroupError(null);
          }}
          onConfirm={async () => {
            const originalGroupId = group?.original_group_id;
            setShowDeleteGroupDialog(false);
            setIsSaving(true);
            try {
              await axios.delete(`/api/reservation-draft-groups/${reservation_group_id}`);
              goToReservationGroupView(originalGroupId);
            } catch (err) {
              console.error("[DraftGroup][DISCARD_UPDATE][ERROR]", err);
              setDiscardGroupError(err?.response?.data?.error || t("Failed to discard update draft."));
              setShowDeleteGroupDialog(true);
            } finally {
              setIsSaving(false);
            }
          }}
          isBusy={isSaving}
          tone="warning"
        />

        <ManagePaymentsPopup
          key={paymentTargetReservation?.id || "group"}
          isOpen={showManagePaymentsPopup}
          onClose={() => {
            setShowManagePaymentsPopup(false);
            void loadPaymentMethods();
            void loadSummaryPaymentBalance();
          }}
          reservationGroupId={reservation_group_id}
          reservationId={paymentTargetReservation ? (paymentTargetReservation.id ?? 0) : null}
          reservationLocator={paymentTargetReservation?.locator || ""}
          businessUnitId={businessUnitId}
          currentUserId={currentUser?.id}
          canViewPayments={canViewReservationPayments}
          canEditNonStripePayments={canEditNonStripePayments}
          canEditStripePayments={canEditStripePayments}
        />

        <ReservationGroupCommentsPopup
          isOpen={showCommentsPopup}
          onClose={() => setShowCommentsPopup(false)}
          reservationGroupId={reservation_group_id}
          reservations={reservations}
          group={group}
          initialReservationId={commentsInitialResId}
          isDraftGroup={!isViewMode}
          currentUser={currentUser}
          onCommentsChange={(list) => setCommentsData(list)}
        />
        </>}


    </div>


{/* ───────────────── ADD ROOMS + COPY INFO ───────────────── */}
    {false && !isViewMode && (
    <div className="rgv-toolbarCard">
      <div className="rgv-toolbarTop">

        <label className="form-label">{t("Rooms to Add:")}</label>

        <input
          type="number"
          min="1"
          value={roomsToAdd}
          onChange={handleRoomsToAddChange}
          className="form-control form-control-sm"
          style={{ width: "80px" }}
        />

        {/* Add Room */}
        <button
          type="button"
          className="btn btn-sm btn-outline-primary fw-bold px-3"
          style={{ minWidth: "150px" }}
          disabled={!canAddRoomsToGroup}
          onClick={() => {
            if (canAddRoomsToGroup) void handleAddRoomsToGroup();
          }}
          title={
            canAddRoomsToGroup
              ? t("Add rooms to this reservation group")
              : t("No permission to add rooms to reservation groups")
          }
        > {t("Add Room")} </button>

        {(() => {
          // ✅ Copy SOLO se oculta; los otros 2 SIEMPRE visibles
          const showCopy = (reservations?.length || 0) > 1 && hasAnyRoomRate;

          const visibleCopyToCount =
            (copyToOptions || []).filter(opt => (reservations || []).some(r => Number(r.id) === Number(opt.id))).length;

          return (
            <>
              {/* Copy Info (junto a Add Room) */}
              {showCopy ? (
                <div className="position-relative">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-warning fw-bold px-3"
                    style={{ minWidth: "150px" }}
                    onClick={() => setShowCopyPanel(prev => !prev)}
                  >{t("Copy Info")}</button>


                  {showCopyPanel && (
                    <div
                      className={`rgv-copyPanel border border-warning p-3 rounded shadow${isLightTheme ? " bg-white text-dark" : " bg-dark text-light"}`}
                      style={{
                        position: "absolute",
                        top: "110%",
                        left: 0,
                        width: "320px",
                        zIndex: 1000,
                        fontSize: "0.75rem"
                      }}
                    >
                      <div className="mb-2">
                        <strong>{t("Copy From:")}</strong>
                        <select
                          className="form-select form-select-sm mt-1"
                          value={copyFrom}
                          onChange={(e) => setCopyFrom(e.target.value)}
                        >
                          <option value="">{t("Select reservation")}</option>
                          {(copyFromOptions || []).map(opt => (
                            <option key={opt.id} value={String(opt.id)}>
                              {opt.label || `#${opt.id}`}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="mb-2">
                        <strong>{t("Copy To:")}</strong>
                        <select
                          className="form-select form-select-sm mt-1"
                          value={copyTo}
                          onChange={(e) => setCopyTo(e.target.value)}
                        >
                          <option value="">{t("Select reservation")}</option>
                          {(copyToOptions || []).map(opt => (
                            <option key={opt.id} value={String(opt.id)}>
                              {opt.label || `#${opt.id}`}
                            </option>
                          ))}
                          {visibleCopyToCount > 1 && (
                            <option value="all">{t("All")}</option>
                          )}
                        </select>
                      </div>

                      <div className="d-flex justify-content-end gap-2">
                        <button
                          className="btn btn-sm btn-warning fw-bold px-3"
                          style={{ minWidth: "110px" }}
                          disabled={!copyFrom || !copyTo || isCopying}
                          onClick={async () => {
                            try {
                              setIsCopying(true);

                              console.log("[GROUP][COPY_INFO][CLICK]", {
                                reservation_group_id,
                                copyFrom,
                                copyTo
                              });

                              const { data } = await axios.post(
                                `/api/reservation-draft-groups/${reservation_group_id}/copy/${copyFrom}/to/${copyTo}`
                              );

                              const copiedTo = Array.isArray(data?.copied_to) ? data.copied_to : [];
                              const skipped = Array.isArray(data?.skipped) ? data.skipped : [];

                              const labelByDraftId = (id) => {
                                const opt = (copyToOptions || []).find(o => Number(o.id) === Number(id));
                                if (opt?.label) return opt.label;
                                const r = (reservations || []).find(x => Number(x.id) === Number(id));
                                return r?.locator || `#${id}`;
                              };

                              const copiedCount = copiedTo.length;
                              const skippedCount = skipped.length;

                              console.log("[GROUP][COPY_INFO][RESULT]", {
                                copiedCount,
                                skippedCount,
                                copiedTo,
                                skipped
                              });

                              if (copiedCount === 0) {
                                const details = skipped
                                  .slice(0, 5)
                                  .map(s => t("{p0}: no availability", { p0: labelByDraftId(s.reservation_draft_id) }))
                                  .join("\n");

                                alert(
                                  formatGroupError(t("No rooms were copied.\n") +
                                  t("Skipped: {p0}\n", { p0: skippedCount }) +
                                  (details ? t("\nDetails:\n{p0}", { p0: details }) : ""))
                                );
                                return; // ✅ no cerramos
                              }

                              if (skippedCount > 0) {
                                const details = skipped
                                  .slice(0, 5)
                                  .map(s => t("{p0}: no availability", { p0: labelByDraftId(s.reservation_draft_id) }))
                                  .join("\n");

                                alert(
                                  formatGroupError(t("Copied: {p0}\n", { p0: copiedCount }) +
                                  t("Skipped: {p0}\n", { p0: skippedCount }) +
                                  (details ? t("\nDetails:\n{p0}", { p0: details }) : ""))
                                );
                              } else {
                                alert(formatGroupError(t("Copied: {p0}", { p0: copiedCount })));
                              }

                              setShowCopyPanel(false);
                              setCopyFrom("");
                              setCopyTo("");
                              await loadGroup();
                            } catch (err) {
                              const backendError = err?.response?.data;
                              console.error("[GROUP][COPY_INFO][ERR]", {
                                status: err?.response?.status,
                                backendError,
                                err
                              });

                              const copiedTo = Array.isArray(backendError?.copied_to) ? backendError.copied_to : [];
                              const skipped = Array.isArray(backendError?.skipped) ? backendError.skipped : [];

                              if (copiedTo.length === 0 && skipped.length > 0) {
                                alert(formatGroupError(t("No rooms were copied.\nSkipped: {p0}", { p0: skipped.length })));
                                return;
                              }

                              alert(formatGroupError(backendError?.error || t("Error copying info.")));
                            } finally {
                              setIsCopying(false);
                            }
                          }}
                        >
                          {isCopying ? t("Copying...") : t("Copy")}
                        </button>

                        <button
                          className="btn btn-sm btn-secondary fw-bold px-3"
                          style={{ minWidth: "110px" }}
                          onClick={() => {
                            setCopyFrom("");
                            setCopyTo("");
                            setShowCopyPanel(false);
                          }}
                        > {t("Cancel")} </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : null}

              {/* Derecha: botones visibles solo si hay más de una reserva */}
              {reservations.length > 1 && !isUpdateDraft && (
                <div className="rgv-toolbarRight">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-success fw-bold px-3"
                    style={{ minWidth: "150px" }}
                    disabled={!canManageGroupExtras}
                    onClick={() => {
                      if (canManageGroupExtras) setShowGroupExtras(true);
                    }}
                    title={
                      canManageGroupExtras
                        ? t("Manage group extras")
                        : t("No permission to manage reservation extras")
                    }
                  > {t("Manage Group Extras")} </button>

                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary fw-bold px-3"
                    style={{ minWidth: "150px" }}
                    disabled={!canApplyGroupSpecialRate}
                    onClick={() => {
                      if (!canApplyGroupSpecialRate) return;
                      console.log("[ReservationGroupView][UI] Click Apply special rate");
                      setShowSpecialRate(true);
                    }}
                    title={
                      canApplyGroupSpecialRate
                        ? t("Apply special group rate (draft)")
                        : t("No permission to override reservation rates")
                    }
                  > {t("Apply Group Special Rate")} </button>
                </div>
              )}
            </>
          );
        })()}

      </div>
    </div>
    )}


    {/* ───────────────── RESERVATIONS GRID ───────────────── */}
    <div className="rgv-reservationList">
      {reservations.map((res) => {
        const isRoomCancelled =
          res?.is_cancelled === true ||
          res?.is_cancelled === 1 ||
          String(res?.status || "").toLowerCase() === "cancelled";

        const canEditRoom = !isViewMode && !isRoomCancelled;
        const canManageOperationalData = canManageOperationalReservation(res);
        const checkInTimeSaving = Boolean(
          reservationCheckTimeSavingByKey[
            reservationCheckTimeKey(res.id, "check_in_time")
          ]
        );
        const checkOutTimeSaving = Boolean(
          reservationCheckTimeSavingByKey[
            reservationCheckTimeKey(res.id, "check_out_time")
          ]
        );
        const checkTimeError = reservationCheckTimeErrorById[res.id] || "";
        const isExpanded = !!expandedRoomRows[res.id];
        const nights = getRoomNights(res);
        const roomAmount = Number(res.total_price || 0);
        const extrasAmount = Number((demoReadOnlyActive ? res.reservation_extras_total : res.total_extras) || 0);
        const chargesAmount = Number(res.total_charges || 0);
        const finalAmount = demoReadOnlyActive
          ? Number(res.total_price_with_extras ?? (roomAmount + extrasAmount))
          : roomAmount + extrasAmount + chargesAmount;
        const mainGuestName = res?.main_guest
          ? `${res.main_guest.first_name || ""} ${res.main_guest.last_name || ""}`.trim()
          : "";
        const isMainGuestPending = !mainGuestName;
        const roomMeta = Number.isFinite(Number(res?.room_id))
          ? roomCatalogById[Number(res.room_id)]
          : null;
        const suggestedRoomMeta = Number.isFinite(Number(res?.suggested_room_id))
          ? roomCatalogById[Number(res.suggested_room_id)]
          : null;
        const assignedRoomNumberLabel = firstNonEmptyText(
          res?.room_number,
          res?.room?.room_number,
          roomMeta?.room_number,
          res?.room_name,
          roomMeta?.name
        );
        const suggestedRoomNumberLabel = firstNonEmptyText(
          res?.suggested_room_number,
          res?.suggested_room?.room_number,
          suggestedRoomMeta?.room_number,
          suggestedRoomMeta?.name
        );
        const roomNumberLabel = assignedRoomNumberLabel || suggestedRoomNumberLabel || "-";
        const effectiveRoomNumberLabel = buildRoomMoveNumberLabel(res, roomNumberLabel);
        const isSuggestedRoomOnly = !assignedRoomNumberLabel && Boolean(suggestedRoomNumberLabel);
        const activeRoom = isSuggestedRoomOnly ? res?.suggested_room : res?.room;
        const activeRoomMeta = isSuggestedRoomOnly ? suggestedRoomMeta : roomMeta;
        const roomFloorLabel = firstNonEmptyText(
          isSuggestedRoomOnly ? res?.suggested_room_floor : res?.room_floor,
          activeRoom?.floor,
          activeRoomMeta?.floor
        );
        const roomBedTypeLabel = firstNonEmptyText(
          isSuggestedRoomOnly ? res?.suggested_room_bed_type : res?.room_bed_type,
          activeRoom?.bed_type,
          activeRoomMeta?.bed_type
        );
        const roomViewLabel = firstNonEmptyText(
          isSuggestedRoomOnly ? res?.suggested_room_view : res?.room_view,
          activeRoom?.view,
          activeRoomMeta?.view
        );
        const roomMetaCards = buildRoomMetaCarouselCards(res, {
          roomNumber: roomNumberLabel,
          floor: roomFloorLabel,
          bedType: roomBedTypeLabel,
          view: roomViewLabel,
        });
        const roomMetaIndex = Math.max(
          0,
          Math.min(
            roomMetaCards.length - 1,
            Number(roomMetaIndexByReservationId[res.id] || 0)
          )
        );
        const visibleRoomMeta = roomMetaCards[roomMetaIndex] || {
          roomNumber: roomNumberLabel,
          floor: roomFloorLabel,
          bedType: roomBedTypeLabel,
          view: roomViewLabel,
        };
        const hasRoomMetaPages = roomMetaCards.length > 1;
        const roomStatus = isRoomCancelled ? "Cancelled" : (res.status || (isViewMode ? "Confirmed" : "Draft"));
        const normalizedRoomStatus = String(roomStatus || "").trim().toLowerCase();
        const statusBadgeText = translateReservationStatus(roomStatus, languageId) || t("Unknown");
        const statusBadgeToneClass = normalizedRoomStatus.includes("cancel")
          ? "rgv-statusBubble--cancelled"
          : normalizedRoomStatus.includes("no show") || normalizedRoomStatus.includes("noshow")
            ? "rgv-statusBubble--noshow"
          : normalizedRoomStatus.includes("check") && normalizedRoomStatus.includes("out")
            ? "rgv-statusBubble--checkedin"
          : normalizedRoomStatus.includes("check") && normalizedRoomStatus.includes("in")
            ? "rgv-statusBubble--checkedin"
            : normalizedRoomStatus.includes("confirm")
              ? "rgv-statusBubble--confirmed"
              : normalizedRoomStatus.includes("pending")
                ? "rgv-statusBubble--pending"
                : "rgv-statusBubble--draft";
        const reservationActionMode = getReservationActionMode(res);
        const reservationActionItems = isViewMode
          ? getReservationActionItems(reservationActionMode, res)
          : [];
        const canShowReservationActions = reservationActionItems.length > 0;
        const arrivalActionLoading = !!arrivalActionLoadingByReservationId[res.id];
        const cancelActionLoading = roomCancellationLoadingId === res.id;
        const reinstateActionLoading = reinstateLoadingId === res.id;
        const showSummaryActionRail = canShowReservationActions;
        const showReservationAccountAction = isViewMode && canViewReservationPayments;
        const nightlyRows = nightlyRowsByReservationId[res.id] || [];
        const nightlyLoading = !!nightlyLoadingByReservationId[res.id];
        const nightlyError = nightlyErrorByReservationId[res.id] || null;
        const nightlyDisplayRows = buildNightlyDisplayRows(res, nightlyRows);
        const reservationStayNights = Array.isArray(res?.stay_nights)
          ? res.stay_nights
          : [];
        const assignedCategoryNames = [
          ...new Set(
            reservationStayNights
              .map((night) =>
                firstNonEmptyText(
                  night?.room_category?.name,
                  night?.room_category_id
                    ? t("Category #{p0}", { p0: night.room_category_id })
                    : null
                )
              )
              .filter(Boolean)
          ),
        ];
        const bookedCategoryNames = [
          ...new Set(
            reservationStayNights
              .map((night) =>
                firstNonEmptyText(
                  night?.booked_room_category?.name,
                  night?.room_category?.name,
                  night?.booked_room_category_id
                    ? t("Category #{p0}", { p0: night.booked_room_category_id })
                    : null
                )
              )
              .filter(Boolean)
          ),
        ];
        const assignedCategorySummary = assignedCategoryNames.length
          ? assignedCategoryNames.join(" → ")
          : res.room_category_name || "-";
        const bookedCategorySummary = bookedCategoryNames.length
          ? bookedCategoryNames.join(" → ")
          : res.room_category_name || "-";
        const nightlyTotal = nightlyRows.reduce((sum, row) => sum + Number(row?.price || 0), 0);
        const showReservationHistoryAction = isViewMode;

        return (
          <div key={res.id} className={`rgv-roomCard ${isRoomCancelled ? "rgv-roomCard--cancelled" : ""}`}>
            <div className="rgv-summaryRow">
              <button
                type="button"
                className="rgv-expandBtn"
                onClick={() => {
                  void handleToggleRoomExpand(res.id);
                }}
                aria-label={isExpanded ? t("Hide reservation details") : t("Show reservation details")}
                title={isExpanded ? t("Hide reservation details") : t("Show reservation details")}
              >
                {isExpanded ? "v" : ">"}
              </button>

              <div className="rgv-summaryGrid">
                <div className="rgv-summaryCell">
                  <div className="rgv-summaryGuestWrap">
                    <div className="rgv-summaryAvatar">
                      {res?.main_guest?.photo_url ? (
                        <img
                          src={res.main_guest.photo_url}
                          alt={t("Main guest")}
                          onError={(e) => {
                            if (e.target.src !== new URL(defaultAvatarUrl, window.location.origin).href) {
                              e.target.src = defaultAvatarUrl;
                            }
                          }}
                        />
                      ) : (
                        <svg
                          className="rgv-summaryAvatarIcon"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          aria-hidden="true"
                        >
                          <path d="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z" fill="currentColor" />
                          <path d="M4 20a8 8 0 0 1 16 0v1H4v-1Z" fill="currentColor" />
                        </svg>
                      )}
                    </div>

                    <div className="rgv-summaryGuestInfo">
                      <div className="rgv-summaryLabel">{t("Main guest")}</div>
                      <div
                        className={`rgv-summaryValue${isMainGuestPending ? " rgv-summaryValue--guestPending" : ""}`}
                        aria-label={isMainGuestPending ? t("Main guest pending assignment") : undefined}
                      >
                        {mainGuestName || t("Main guest pending")}
                      </div>
                      <div className="rgv-summaryLocatorRow">
                        <div
                          className={`rgv-summaryLocator ${canEditRoom ? "" : "rgv-summaryLocator--disabled"}`}
                          onClick={() => {
                            if (!canEditRoom) return;
                            openReservationStayEditor(res.id);
                          }}
                          title={canEditRoom ? t("Edit reservation stay data") : undefined}
                        >
                          {res.locator || t("Room {p0}", { p0: res.group_index + 1 })}
                        </div>
                      </div>

                      <div className="rgv-summaryStatusRow">
                        <div
                          className={`rgv-statusBubble ${statusBadgeToneClass}`}
                          title={t("Status: {p0}", { p0: statusBadgeText })}
                          aria-label={t("Status {p0}", { p0: statusBadgeText })}
                        >
                          {statusBadgeText}
                        </div>
                      </div>
                    </div>

                    {(showSummaryActionRail || showReservationAccountAction || showReservationHistoryAction) && (
                      <div className="rgv-summaryActionRail">
                        {showSummaryActionRail && (
                          <button
                            type="button"
                            className="rgv-summaryActionBtn rgv-summaryActionBtn--actions"
                            onClick={() => openReservationActionsDialog(res)}
                            title={getReservationActionTitle(reservationActionMode)}
                            disabled={arrivalActionLoading || cancelActionLoading || reinstateActionLoading}
                          >
                            <span className="rgv-summaryActionBtnInner">
                              <FiMoreHorizontal size={14} aria-hidden="true" /> {t("Actions")} </span>
                          </button>
                        )}
                        {showReservationAccountAction && (
                          <button
                            type="button"
                            className="rgv-summaryActionBtn rgv-summaryActionBtn--account"
                            onClick={() => goToReservationAccount(res.id)}
                            title={demoReadOnlyActive ? DEMO_DATA_UNAVAILABLE : t("Open reservation account")}
                            disabled={demoReadOnlyActive}
                          >
                            <span className="rgv-summaryActionBtnInner">
                              <FiCreditCard size={14} aria-hidden="true" /> {t("Account")} </span>
                          </button>
                        )}
                        {showReservationHistoryAction && (
                          <button
                            type="button"
                            className="rgv-summaryActionBtn rgv-summaryActionBtn--history"
                            onClick={() => {
                              if (demoReadOnlyActive) return;
                              setReservationHistoryActiveTab("overview");
                              setReservationHistoryDialogId(Number(res.id));
                            }}
                            title={demoReadOnlyActive ? DEMO_DATA_UNAVAILABLE : t("View original state and change history")}
                            disabled={demoReadOnlyActive || reservationChangeHistoryLoading}
                          >
                            <span className="rgv-summaryActionBtnInner">
                              <FiFileText size={14} aria-hidden="true" /> {t("History")} </span>
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="rgv-summaryCell">
                  <div className="rgv-summaryLabel">{t("Stay dates")}</div>
                  <div className="rgv-summaryValue rgv-summaryValue--xl">{res.check_in_date || "-"}</div>
                  <div className="rgv-summaryValue rgv-summaryValue--xl">{res.check_out_date || "-"}</div>
                </div>

                <div className="rgv-summaryCell">
                  <div className="rgv-summaryLabel">{t("Stay")}</div>
                  <div className="rgv-summaryValue">{nights || 0} {t("nights")}</div>
                  <div className="rgv-summarySubtle">{res.adults ?? "-"} {t("adults ·")} {res.children ?? 0} {t("children")}</div>
                </div>

                <div className="rgv-summaryCell">
                  <div className="rgv-summaryLabel"> {t("Assigned category")} {res.is_split_stay ? t(" · Split stay") : ""}
                  </div>
                  <div className="rgv-summaryValue">
                    {assignedCategorySummary}
                  </div>
                  <div className="rgv-summarySubtle"> {t("Booked:")} {bookedCategorySummary} ·{" "}
                    {res.rate_name ||
                      (res.rate_id ? t("Rate #{p0}", { p0: res.rate_id }) : "-")}
                  </div>
                </div>

                <div
                  className={`rgv-summaryCell rgv-summaryCell--roomMeta ${canManageOperationalData ? "rgv-summaryCell--clickable" : ""}`}
                  role={canManageOperationalData ? "button" : undefined}
                  tabIndex={canManageOperationalData ? 0 : undefined}
                  title={canManageOperationalData ? t("Change room number") : undefined}
                  onClick={() => {
                    if (!canManageOperationalData) return;
                    openRoomNumberPicker(res);
                  }}
                  onKeyDown={(event) => {
                    if (!canManageOperationalData) return;
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      openRoomNumberPicker(res);
                    }
                  }}
                >
                  <div className="rgv-summaryLabel">{t("Room number")}</div>
                  <div className="rgv-summaryValue rgv-summaryValue--roomNumber">{effectiveRoomNumberLabel}</div>
                  <div className="rgv-summarySubtle rgv-roomMetaList">
                    <div className="rgv-roomMetaNavRow">
                      {hasRoomMetaPages && roomMetaIndex > 0 && (
                        <button
                          type="button"
                          className="rgv-roomMetaNavBtn"
                          aria-label={t("Show previous room details")}
                          title={t("Previous room")}
                          onClick={(event) => {
                            event.stopPropagation();
                            changeRoomMetaIndex(res.id, -1, roomMetaCards.length);
                          }}
                          onKeyDown={(event) => event.stopPropagation()}
                        >
                          {"<"}
                        </button>
                      )}
                      <div className="rgv-roomMetaRoomNumber"> {t("Room")} {visibleRoomMeta.roomNumber || "-"}
                      </div>
                      {hasRoomMetaPages && roomMetaIndex < roomMetaCards.length - 1 && (
                        <button
                          type="button"
                          className="rgv-roomMetaNavBtn"
                          aria-label={t("Show next room details")}
                          title={t("Next room")}
                          onClick={(event) => {
                            event.stopPropagation();
                            changeRoomMetaIndex(res.id, 1, roomMetaCards.length);
                          }}
                          onKeyDown={(event) => event.stopPropagation()}
                        >
                          {">"}
                        </button>
                      )}
                    </div>
                    <div className="rgv-roomMetaLine">
                      <span className="rgv-roomMetaLabel">{t("Floor:")}</span> {visibleRoomMeta.floor || "-"}
                    </div>
                    <div className="rgv-roomMetaLine">
                      <span className="rgv-roomMetaLabel">{t("Bed type:")}</span> {visibleRoomMeta.bedType || "-"}
                    </div>
                    <div className="rgv-roomMetaLine">
                      <span className="rgv-roomMetaLabel">{t("View:")}</span> {visibleRoomMeta.view || "-"}
                    </div>
                  </div>
                </div>

                <div className="rgv-summaryCell">
                  <div className="rgv-summaryLabel">{t("Amount")}</div>
                  <div className="rgv-summaryValue">{formatMoney(finalAmount)}</div>
                  <div className="rgv-summarySubtle"> {t("Room")} {formatMoney(roomAmount)} {t("· Extras")} {formatMoney(extrasAmount)} {t("· Charges")} {demoReadOnlyActive ? "—" : formatMoney(chargesAmount)}
                  </div>
                  {demoReadOnlyActive && <div className="rgv-summarySubtle">Pagado asignado: {formatMoney(Number(res.paid_amount || 0))} · Saldo de reserva: {formatMoney(Number(res.balance_due || 0))}</div>}
                </div>

              </div>
            </div>

            {isExpanded && (
            <div className="rgv-expandedWrap">

            {/* HEADER ROW: locator (izq) + Guests (der) */}
            <div className="row align-items-start mb-1 rgv-roomHeader">
              <div className="col-md-6">
                <div className="d-flex align-items-center gap-2">
                  <div
                    className="rgv-roomLocator"
                    style={{
                      cursor: canEditRoom ? "pointer" : "default",
                      opacity: isRoomCancelled ? 0.6 : 1,
                    }}
                    onClick={() => {
                      if (!canEditRoom) return;
                      openReservationStayEditor(res.id);
                    }}
                  >


                    {res.locator || t("Room {p0}", { p0: res.group_index + 1 })}


                  </div>

                  {!isViewMode && (() => {
                    const isFromOriginal = res?.original_reservation_id != null;
                    const isCancelled = !!res?.is_cancelled;
                    const isRoomLocked = isFromOriginal && isCancelled;
                    const isRemovingCurrentRoom = roomCancellationLoadingId === res.id;
                    const cancellationBlock = isFromOriginal
                      ? getManualModificationBlock(res, ["cancellation"])
                      : null;

                    if (isFromOriginal) {
                      return (
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-info"
                            style={{ padding: "0px 8px", lineHeight: "1.0", fontSize: "0.75rem" }}
                            disabled={isRoomLocked}
                            title={isCancelled ? t("Removed reservations cannot be edited") : t("Edit this room reservation")}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (isRoomLocked) return;
                              openReservationStayEditor(res.id);
                            }}
                          > {t("Edit")} </button>

                          <button
                            type="button"
                            className={`btn btn-sm ${isCancelled ? "btn-outline-secondary" : "btn-outline-warning"}`}
                            style={{ padding: "0px 8px", lineHeight: "1.0", fontSize: "0.75rem" }}
                            disabled={
                              isRoomLocked ||
                              isRemovingCurrentRoom ||
                              Boolean(cancellationBlock)
                            }
                            title={
                              isCancelled
                                ? t("Room already removed from this group draft")
                                : cancellationBlock?.message ||
                                  t("Remove this room from the group")
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                              if (
                                isRoomLocked ||
                                isRemovingCurrentRoom ||
                                cancellationBlock
                              ) {
                                return;
                              }
                              requestDraftRoomRemoval(res);
                            }}
                          >
                            {isCancelled ? t("Removed") : isRemovingCurrentRoom ? t("Removing...") : t("Remove")}
                          </button>
                        </div>
                      );
                    }

                    return (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        style={{ padding: "0px 8px", lineHeight: "1.0", fontSize: "0.75rem" }}
                        disabled={deletingDraftId === res.id || (reservations?.length || 0) <= 1}
                        title={
                          (reservations?.length || 0) <= 1
                            ? t("You cannot delete the last room")
                            : t("Remove this room reservation")
                        }
                        onClick={async (e) => {
                          e.stopPropagation();

                          if ((reservations?.length || 0) <= 1) return;
                          const ok = window.confirm(t("Delete this room reservation?"));
                          if (!ok) return;

                          try {
                            setDeletingDraftId(res.id);

                            await axios.delete(`/api/reservation-draft-groups/${reservation_group_id}/rooms/${res.id}`);

                            const business_unit_id =
                              res?.business_unit_id ??
                              effectiveBusinessUnitId ??
                              null;

                            if (business_unit_id) {
                              await axios.post(`/api/reservation-drafts/draft-availability/sync-group`, {
                                reservation_group_id: Number(reservation_group_id),
                                business_unit_id: Number(business_unit_id),
                              });
                            }

                            await loadGroup();
                          } catch (err) {
                            console.error("[GROUP][ROOM][DELETE][ERR]", err);
                            alert(formatGroupError(err?.response?.data?.error || t("Error deleting room reservation.")));
                          } finally {
                            setDeletingDraftId(null);
                          }
                        }}
                        >
                          {deletingDraftId === res.id ? t("Removing...") : t("Remove")}
                        </button>
                    );
                  })()}

                </div>

              </div>

              <div className="col-md-6">
                <div className="rgv-roomSubtle">{t("Guest assignment and status")}</div>
              </div>
            </div>




            {/* 3 COLUMN LAYOUT */}
             <div className="row g-2 rgv-roomMain rgv-expandedGrid">

            <div
              className="col-xl-5 col-lg-12"
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
                alignItems: "stretch",
              }}
            >
              <div className="rgv-guestPanel">
              {(() => {
                const adultsLoaded = res.adults !== null && res.adults !== "" && !Number.isNaN(Number(res.adults));
                const adultsCount = adultsLoaded ? Number(res.adults) : 0;

                const childrenLoaded = res.children !== null && res.children !== "" && !Number.isNaN(Number(res.children));
                const childrenCount = childrenLoaded ? Number(res.children) : 0;

                // 🔒 Si no hay adults cargados: solo mensaje, sin botones
                if (!adultsLoaded) {
                  return (
                    <div style={{ fontSize: "0.9rem", opacity: 0.85 }}> {t("Please, select data for reservation first")} </div>
                  );
                }

                const showAcc = adultsCount > 1;
                const showChildren = childrenCount > 0;

                return (
                  <div className="rgv-guestColumns rgv-guestColumns--stacked">
                    {/* MAIN GUEST */}
                    <div className="rgv-guestBlock">
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <div className="small text-info fw-bold">{t("Main Guest")}</div>

                        {!res.main_guest ? (
                          <>
                            {canManageOperationalData && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-info"
                              style={{ padding: "0px 8px", lineHeight: "1.1" }}
                              onClick={() => {
                                console.log("[Guests][UI][MainGuest][Select][OPEN]", { draftId: res.id });
                                setMainGuestSelectorDraftId(res.id);
                                setIsMainGuestSelectorOpen(true);
                              }}
                              title={t("Select main guest")}
                            > {t("Select")} </button>
                            )}

                            {canManageOperationalData && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-success"
                              style={{ padding: "0px 8px", lineHeight: "1.1" }}
                              onClick={() => {
                                console.log("[Guests][UI][MainGuest][Add][OPEN_POPUP]", { draftId: res.id });

                                setMainGuestEditorMode("create");
                                setMainGuestEditorInitialData(null);
                                setMainGuestEditorDraftId(res.id);

                                setIsAddMainGuestOpen(true);
                              }}
                              title={t("Add main guest")}
                            >
                              +
                            </button>
                            )}
                          </>
                        ) : null}
                      </div>


                      {res.main_guest ? (
                        <div className="d-flex align-items-start gap-2" style={{ fontSize: "0.9rem", opacity: 0.95 }}>
                          <img
                            src={res.main_guest.photo_url || defaultAvatarUrl}
                            alt={t("Main Guest")}
                            style={{
                              width: "48px",
                              height: "48px",
                              borderRadius: "50%",
                              objectFit: "cover",
                              border: "2px solid #888",
                              marginTop: "2px",
                            }}
                            onError={(e) => {
                              if (e.target.src !== new URL(defaultAvatarUrl, window.location.origin).href) {
                                e.target.src = defaultAvatarUrl;
                              }
                            }}
                          />

                          <div style={{ lineHeight: "1.2" }}>
                            <div className="d-flex align-items-center gap-2" style={{ lineHeight: "1.2" }}>
                              <div
                                className="fw-bold"
                                style={{ cursor: canManageOperationalData ? "pointer" : "default" }}
                                title={canManageOperationalData ? t("Edit main guest") : undefined}
                                onClick={() => {
                                  if (!canManageOperationalData) return;

                                  console.log("[Guests][UI][MainGuest][EDIT][OPEN_POPUP]", {
                                    draftId: res.id,
                                    customerId: res.main_guest?.id,
                                  });

                                  setMainGuestEditorMode("edit");
                                  setMainGuestEditorInitialData(res.main_guest);
                                  setMainGuestEditorDraftId(res.id);
                                  setIsAddMainGuestOpen(true);
                                }}
                              >
                                {res.main_guest.first_name} {res.main_guest.last_name}
                              </div>


                              {canManageOperationalData && (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                style={{ padding: "0px 6px", lineHeight: "1.0", fontSize: "0.75rem" }}
                                title={t("Remove main guest")}
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  try {
                                    console.log("[Guests][UI][MainGuest][DELETE] start", { draftId: res.id });

                                    await deleteGuestFromReservationTarget({
                                      targetId: res.id,
                                      role: "main_guest",
                                    });

                                    await refreshOperationalGuestsFor(res.id);
                                    console.log("[Guests][UI][MainGuest][DELETE] ok", { draftId: res.id });
                                  } catch (err) {
                                    console.error("[Guests][UI][MainGuest][DELETE] err", err);
                                    alert(formatGroupError(t("Error removing main guest.")));
                                  }
                                }}
                              >
                                ×
                              </button>
                              )}
                            </div>

                            <div style={{ fontSize: "0.85rem", opacity: 0.9 }}>{res.main_guest.email || "N/A"}</div>
                            <div style={{ fontSize: "0.85rem", opacity: 0.9 }}>{res.main_guest.phone || "N/A"}</div>
                          </div>
                        </div>
                        ) : (
                        <div>
                          <span style={{ fontSize: "0.9rem", opacity: 0.85 }}> {t("Main guest pending to select")} </span>

                          {!!bookerId && !sameAsBookerAlreadyUsed && canManageOperationalData ? (
                            <button
                              type="button"
                              className="rgv-sameAsBookerAction"
                              onClick={async () => {
                                console.log("[MAIN_GUEST][SAME_AS_BOOKER][CLICK]", {
                                  draftId: res.id,
                                  bookerId,
                                });

                                try {
                                  await assignGuestToReservationTarget({
                                    targetId: res.id,
                                    customerId: bookerId,
                                    isMainGuest: true,
                                    isChild: false,
                                    guestIndex: null,
                                    updatedBy: "UserSameAsBookerMainGuest",
                                  });
                                } catch (assignmentError) {
                                  const status = assignmentError?.response?.status;
                                  const data = assignmentError?.response?.data || {};
                                  const backendError = data?.error;
                                  const conflictIds = Array.isArray(data?.conflicting_reservations)
                                    ? data.conflicting_reservations.filter(Boolean)
                                    : [];

                                  console.error(
                                    "[MAIN_GUEST][SAME_AS_BOOKER][ASSIGN_ERR]",
                                    assignmentError
                                  );

                                  if (status === 409) {
                                    setReservationActionFeedbackState({
                                      title: t("The booker cannot be assigned to this stay"),
                                      tone: "warning",
                                      message: (
                                        <div className="rgv-feedbackDetails">
                                          <div className="rgv-feedbackDetail">
                                            <strong>{t("Exact reason")}</strong>
                                            <span>
                                              {backendError ||
                                                t("This customer is already assigned to another active reservation with overlapping dates.")}
                                              {conflictIds.length
                                                ? t(" Conflicting reservation{p0}: {p1}.", { p0: conflictIds.length === 1 ? "" : "s", p1: conflictIds.map((id) => `#${id}`).join(", ") })
                                                : ""}
                                            </span>
                                          </div>
                                          <div className="rgv-feedbackDetail">
                                            <strong>{t("How to solve it")}</strong>
                                            <span> {t("Review the conflicting stay. Remove this customer from that reservation, correct either stay's dates, or close/cancel the conflicting reservation if that is operationally correct. Then try again.")} </span>
                                          </div>
                                        </div>
                                      ),
                                    });
                                    return;
                                  }

                                  setReservationActionFeedbackState({
                                    title: t("The booker could not be assigned"),
                                    tone: "warning",
                                    message: (
                                      <div className="rgv-feedbackDetails">
                                        <div className="rgv-feedbackDetail">
                                          <strong>{t("Exact reason")}</strong>
                                          <span>
                                            {backendError ||
                                              assignmentError?.message ||
                                              t("The assignment request failed{p0}.", { p0: status ? t(" with HTTP {p0}", { p0: status }) : "" })}
                                          </span>
                                        </div>
                                        <div className="rgv-feedbackDetail">
                                          <strong>{t("How to solve it")}</strong>
                                          <span> {t("Check that the reservation and booker still exist, reload this group, and retry. If it persists, report reservation #")}{res.id} {t("and the reason shown above.")} </span>
                                        </div>
                                      </div>
                                    ),
                                  });
                                  return;
                                }

                                try {
                                  await refreshOperationalGuestsFor(res.id);
                                } catch (refreshError) {
                                  console.error(
                                    "[MAIN_GUEST][SAME_AS_BOOKER][REFRESH_ERR]",
                                    refreshError
                                  );
                                  setReservationActionFeedbackState({
                                    title: t("Booker assigned — refresh required"),
                                    tone: "primary",
                                    message: (
                                      <div className="rgv-feedbackDetails">
                                        <div className="rgv-feedbackDetail">
                                          <strong>{t("What happened")}</strong>
                                          <span> {t("The booker was assigned successfully, but Pilcore could not refresh the guest panel:")} {formatGroupError(refreshError?.response?.data?.error || refreshError?.message, "unknown refresh error")}.
                                          </span>
                                        </div>
                                        <div className="rgv-feedbackDetail">
                                          <strong>{t("How to solve it")}</strong>
                                          <span>{t("Reload this reservation group. Do not assign the booker again.")}</span>
                                        </div>
                                      </div>
                                    ),
                                  });
                                  return;
                                }

                                try {
                                  // ✅ required fields check (main_guest) → auto open edit popup
                                  const bu =
                                    res?.business_unit_id ??
                                    effectiveBusinessUnitId;

                                  const resSettings = await axios.get(
                                    `${process.env.REACT_APP_API_URL}/api/hotel-settings`,
                                    { params: { business_unit_id: bu } }
                                  );

                                  const raw = resSettings.data?.required_customer_fields_reservation ?? null;
                                  const parsed = parseRequiredCustomerFields(raw);
                                  const required = getRequiredFieldsForRole(parsed, "main_guest");

                                  const fullBooker = await loadFullCustomer(bookerId);
                                  if (!fullBooker) {
                                    console.warn("[MAIN_GUEST][SAME_AS_BOOKER][REQUIRED_CHECK] full booker not available, skipping check", {
                                      bookerId,
                                    });
                                    return;
                                  }

                                  const missing = getMissingRequiredFields(required, fullBooker);

                                  console.log("[MAIN_GUEST][SAME_AS_BOOKER][REQUIRED_CHECK]", {
                                    required,
                                    missing,
                                    hasFullBooker: !!fullBooker?.id,
                                  });

                                  if (missing.length > 0) {
                                    console.warn("[MAIN_GUEST][SAME_AS_BOOKER][AUTO_EDIT_OPEN]", { missing });

                                    setMainGuestEditorMode("edit");
                                    setMainGuestEditorInitialData(fullBooker);
                                    setMainGuestEditorDraftId(res.id);
                                    setIsAddMainGuestOpen(true);

                                  }
                                } catch (verificationError) {
                                  const data = verificationError?.response?.data || {};
                                  console.error(
                                    "[MAIN_GUEST][SAME_AS_BOOKER][REQUIRED_CHECK_ERR]",
                                    verificationError
                                  );
                                  setReservationActionFeedbackState({
                                    title: t("Booker assigned — required fields not verified"),
                                    tone: "primary",
                                    message: (
                                      <div className="rgv-feedbackDetails">
                                        <div className="rgv-feedbackDetail">
                                          <strong>{t("Exact reason")}</strong>
                                          <span>
                                            {data?.reason ||
                                              data?.error ||
                                              verificationError?.message ||
                                              t("Hotel settings are temporarily unavailable.")}
                                          </span>
                                        </div>
                                        <div className="rgv-feedbackDetail">
                                          <strong>{t("How to solve it")}</strong>
                                          <span>
                                            {data?.resolution ||
                                              t("Reload the reservation and review the main guest's required fields manually. The booker assignment itself has already been saved.")}
                                          </span>
                                        </div>
                                      </div>
                                    ),
                                  });
                                }
                              }}
                              title={t("Assign booker as main guest")}
                            > {t("Click here if it is same as booker")} </button>
                          ) : null}
                        </div>
                      )}

                    </div>

                    {/* ACCOMPANYING: solo si adults > 1 */}
                    {showAcc ? (
                      <div className="rgv-guestBlock">
                        {(() => {
                          const maxAcc = Math.max(0, adultsCount - 1);
                          const accList = Array.isArray(res.accompanying_guests) ? res.accompanying_guests : [];
                          const canAddMore = accList.length < maxAcc;

                          return (
                            <>
                              <div className="d-flex align-items-center gap-2 mb-1">
                                <div className="small text-info fw-bold">{t("Accompanying")}</div>

                                {canAddMore ? (
                                  <>
                                    {canManageOperationalData && (
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-info"
                                        style={{ padding: "0px 8px", lineHeight: "1.1" }}
                                        onClick={() => {
                                          console.log("[Guests][UI][Accompanying][Select][OPEN]", { draftId: res.id });
                                          setAccompanyingSelectorDraftId(res.id);
                                          setIsAccompanyingSelectorOpen(true);
                                        }}
                                        title={t("Select accompanying guest")}
                                      > {t("Select")} </button>
                                    )}

                                    {canManageOperationalData && (
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-success"
                                        style={{ padding: "0px 8px", lineHeight: "1.1" }}
                                        onClick={() => {
                                          const maxAcc = Math.max(0, adultsCount - 1);
                                          const accList = Array.isArray(res.accompanying_guests) ? res.accompanying_guests : [];

                                          if (accList.length >= maxAcc) {
                                            console.log("[Guests][UI][Accompanying][Add][BLOCKED_MAX]", { draftId: res.id, current: accList.length, maxAcc });
                                            return;
                                          }

                                          const used = new Set(
                                            accList.map(g => Number(g?.guest_index)).filter(v => Number.isFinite(v))
                                          );

                                          let nextIndex = null;
                                          for (let i = 0; i < maxAcc; i++) {
                                            if (!used.has(i)) { nextIndex = i; break; }
                                          }

                                          if (nextIndex == null) {
                                            console.log("[Guests][UI][Accompanying][Add][BLOCKED_NO_SLOT]", { draftId: res.id, used: Array.from(used), maxAcc });
                                            return;
                                          }

                                          console.log("[Guests][UI][Accompanying][Add][OPEN_POPUP]", { draftId: res.id, guest_index: nextIndex });

                                          setAccompanyingEditorMode("create");
                                          setAccompanyingEditorInitialData(null);
                                          setAccompanyingEditorDraftId(res.id);
                                          setAccompanyingEditorGuestIndex(nextIndex);

                                          setIsAddAccompanyingOpen(true);
                                        }}
                                        title={t("Add accompanying guest")}
                                      >
                                        +
                                      </button>
                                    )}
                                  </>
                                ) : null}

                              </div>

                              {accList.length > 0 ? (
                                <div className="d-flex flex-column gap-1" style={{ fontSize: "0.9rem", opacity: 0.95 }}>
                                  {accList.map((g, idx) => {
                                    const c = g?.customer;
                                    if (!c) return null;

                                    const key = g?.guest_index != null ? `acc-${g.guest_index}` : `acc-${idx}`;

                                    return (
                                      <div key={key} className="d-flex align-items-center">
                                        <div
                                          className="fw-bold"
                                          style={{ cursor: canManageOperationalData ? "pointer" : "default" }}
                                          title={canManageOperationalData ? t("Edit accompanying guest") : undefined}
                                          onClick={() => {
                                            if (!canManageOperationalData) return;

                                            console.log("[Guests][UI][Accompanying][EDIT][OPEN_POPUP]", {
                                              draftId: res.id,
                                              guest_index: g?.guest_index ?? null,
                                              customerId: c?.id,
                                            });

                                            setAccompanyingEditorMode("edit");
                                            setAccompanyingEditorInitialData(c);
                                            setAccompanyingEditorDraftId(res.id);
                                            setAccompanyingEditorGuestIndex(g?.guest_index ?? null);

                                            setIsAddAccompanyingOpen(true);
                                          }}
                                        >
                                          {c.first_name} {c.last_name}
                                        </div>

                                        {canManageOperationalData && (
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-outline-danger"
                                          style={{ padding: "0px 6px", lineHeight: "1.0", fontSize: "0.75rem", marginLeft: "6px" }}
                                          title={t("Remove accompanying guest")}
                                          onClick={async (e) => {
                                            e.stopPropagation();
                                            try {
                                              console.log("[Guests][UI][Accompanying][DELETE] start", {
                                                draftId: res.id,
                                                guest_index: g?.guest_index ?? null,
                                                customerId: c?.id,
                                              });

                                              await deleteGuestFromReservationTarget({
                                                targetId: res.id,
                                                customerId: c.id,
                                                role: "accompanying",
                                                guestIndex: g?.guest_index ?? null,
                                              });

                                              await refreshOperationalGuestsFor(res.id);
                                              console.log("[Guests][UI][Accompanying][DELETE] ok", { draftId: res.id });
                                            } catch (err) {
                                              console.error("[Guests][UI][Accompanying][DELETE] err", err);
                                              alert(formatGroupError(t("Error removing accompanying guest.")));
                                            }
                                          }}
                                        >
                                          ×
                                        </button>
                                      )}


                                      </div>

                                    );

                                  })}
                                </div>
                              ) : (
                                <span style={{ fontSize: "0.9rem", opacity: 0.85 }}> {t("Accompanying guest pending to select")} </span>
                              )}

                              <div style={{ fontSize: "0.8rem", marginTop: "2px", color: "#fff", fontStyle: "italic", fontWeight: 400 }}>
                                {accList.length}/{maxAcc}
                              </div>

                            </>
                          );
                        })()}
                      </div>
                    ) : null}

                    {/* CHILDREN: solo si children > 0 */}
                    {showChildren ? (
                    <div className="rgv-guestBlock">
                      {(() => {
                        const maxChildren = Math.max(0, childrenCount);
                        const childList = Array.isArray(res.children_guests) ? res.children_guests : [];
                        const canAddMore = childList.length < maxChildren;

                        const used = new Set(
                          childList.map(g => Number(g?.guest_index)).filter(v => Number.isFinite(v))
                        );

                        let nextIndex = null;
                        for (let i = 0; i < maxChildren; i++) {
                          if (!used.has(i)) { nextIndex = i; break; }
                        }

                        return (
                          <>
                            <div className="d-flex align-items-center gap-2 mb-1">
                              <div className="small text-info fw-bold">{t("Children")}</div>

                              {canAddMore ? (
                                <>
                                  {canManageOperationalData && (
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-info"
                                      style={{ padding: "0px 8px", lineHeight: "1.1" }}
                                      onClick={() => {
                                        if (nextIndex == null) return;

                                        console.log("[Guests][UI][Children][Select][OPEN]", { draftId: res.id, guest_index: nextIndex });

                                        setChildSelectorDraftId(res.id);
                                        setChildSelectorGuestIndex(nextIndex);
                                        setIsChildSelectorOpen(true);
                                      }}
                                      title={t("Select child")}
                                    > {t("Select")} </button>
                                  )}

                                  {canManageOperationalData && (
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-success"
                                      style={{ padding: "0px 8px", lineHeight: "1.1" }}
                                      onClick={() => {
                                        if (nextIndex == null) return;

                                        console.log("[Guests][UI][Children][Add][OPEN_POPUP]", { draftId: res.id, guest_index: nextIndex });

                                        setChildEditorMode("create");
                                        setChildEditorInitialData(null);
                                        setChildEditorDraftId(res.id);
                                        setChildEditorGuestIndex(nextIndex);
                                        setIsAddChildOpen(true);
                                      }}
                                      title={t("Add child")}
                                    >
                                      +
                                    </button>
                                  )}
                                </>
                              ) : null}

                            </div>

                            {childList.length > 0 ? (
                              <div className="d-flex flex-column gap-1" style={{ fontSize: "0.9rem", opacity: 0.95 }}>
                                {childList.map((g, idx) => {
                                  const c = g?.customer;
                                  if (!c) return null;

                                  const key = g?.guest_index != null ? `child-${g.guest_index}` : `child-${idx}`;

                                  return (
                                    <div key={key} className="d-flex align-items-center gap-2">
                                      <div
                                        className="fw-bold"
                                        style={{ cursor: canManageOperationalData ? "pointer" : "default" }}
                                        title={canManageOperationalData ? t("Edit child") : undefined}
                                        onClick={() => {
                                          if (!canManageOperationalData) return;

                                          console.log("[Guests][UI][Children][EDIT][OPEN_POPUP]", {
                                            draftId: res.id,
                                            guest_index: g?.guest_index ?? null,
                                            customerId: c?.id,
                                          });

                                          setChildEditorMode("edit");
                                          setChildEditorInitialData(c);
                                          setChildEditorDraftId(res.id);
                                          setChildEditorGuestIndex(g?.guest_index ?? null);
                                          setIsAddChildOpen(true);
                                        }}
                                      >
                                        {c.first_name} {c.last_name}
                                      </div>


                                      {/* X pegada al nombre */}
                                      {canManageOperationalData && (
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-danger"
                                        style={{ padding: "0px 6px", lineHeight: "1.0", fontSize: "0.75rem" }}
                                        title={t("Remove child")}
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          try {
                                            console.log("[Guests][UI][Children][DELETE] start", {
                                              draftId: res.id,
                                              guest_index: g?.guest_index ?? null,
                                              customerId: c?.id,
                                            });

                                            await deleteGuestFromReservationTarget({
                                              targetId: res.id,
                                              customerId: c.id,
                                              role: "child",
                                              guestIndex: g?.guest_index ?? null,
                                            });

                                            await refreshOperationalGuestsFor(res.id);
                                            console.log("[Guests][UI][Children][DELETE] ok", { draftId: res.id });
                                          } catch (err) {
                                            console.error("[Guests][UI][Children][DELETE] err", err);
                                            alert(formatGroupError(t("Error removing child.")));
                                          }
                                        }}
                                      >
                                        ×
                                      </button>
                                      )}

                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <span style={{ fontSize: "0.9rem", opacity: 0.85 }}> {t("Children guest pending to select")} </span>
                            )}

                            {/* contador visible (blanco, sin bold, cursiva) */}
                            <div style={{ fontSize: "0.8rem", marginTop: "2px", color: "#fff", fontStyle: "italic", fontWeight: "normal" }}>
                              {childList.length}/{maxChildren}
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  ) : null}


                  </div>
                );
              })()}
              </div>
            </div>
            <div className="col-xl-3 col-lg-6">
              <div className="rgv-sidePanel">
                <div className="rgv-sidePanelSection">
                  <div className="rgv-panelTitle">{t("Check-in / Check-out")}</div>
                  <div className="rgv-checkTimesGrid">
                    <label className="rgv-checkTimeField">
                      <span className="rgv-checkTimeLabel">Entrada</span>
                      {canManageOperationalData ? (
                        <input
                          key={`${res.id}-check-in-${res.check_in_time || "empty"}`}
                          className="rgv-checkTimeInput"
                          type="time"
                          step="60"
                          defaultValue={toCheckTimeInputValue(res.check_in_time)}
                          disabled={checkInTimeSaving || checkOutTimeSaving}
                          title={t("Click to change the check-in time")}
                          aria-label={t("Check-in time")}
                          onBlur={(event) => {
                            void handleReservationCheckTimeBlur(
                              res,
                              "check_in_time",
                              event.currentTarget
                            );
                          }}
                          onKeyDown={(event) => {
                            if (event.key === "Enter") event.currentTarget.blur();
                            if (event.key === "Escape") {
                              event.currentTarget.value = toCheckTimeInputValue(
                                res.check_in_time
                              );
                              event.currentTarget.blur();
                            }
                          }}
                        />
                      ) : (
                        <span className="rgv-checkTimeValue">
                          {formatCheckTime(res.check_in_time)}
                        </span>
                      )}
                    </label>

                    <label className="rgv-checkTimeField">
                      <span className="rgv-checkTimeLabel">Salida</span>
                      {canManageOperationalData ? (
                        <input
                          key={`${res.id}-check-out-${res.check_out_time || "empty"}`}
                          className="rgv-checkTimeInput"
                          type="time"
                          step="60"
                          defaultValue={toCheckTimeInputValue(res.check_out_time)}
                          disabled={checkInTimeSaving || checkOutTimeSaving}
                          title={t("Click to change the check-out time")}
                          aria-label={t("Check-out time")}
                          onBlur={(event) => {
                            void handleReservationCheckTimeBlur(
                              res,
                              "check_out_time",
                              event.currentTarget
                            );
                          }}
                          onKeyDown={(event) => {
                            if (event.key === "Enter") event.currentTarget.blur();
                            if (event.key === "Escape") {
                              event.currentTarget.value = toCheckTimeInputValue(
                                res.check_out_time
                              );
                              event.currentTarget.blur();
                            }
                          }}
                        />
                      ) : (
                        <span className="rgv-checkTimeValue">
                          {formatCheckTime(res.check_out_time)}
                        </span>
                      )}
                    </label>
                  </div>

                  {checkInTimeSaving || checkOutTimeSaving ? (
                    <div className="rgv-checkTimeFeedback" role="status"> {t("Saving time...")} </div>
                  ) : checkTimeError ? (
                    <div
                      className="rgv-checkTimeFeedback rgv-checkTimeFeedback--error"
                      role="alert"
                    >
                      {formatGroupError(checkTimeError)}
                    </div>
                  ) : canManageOperationalData ? (
                    <div className="rgv-checkTimeFeedback"> {t("Select a time; it will be saved when you leave the field.")} </div>
                  ) : null}
                </div>

                <div className="rgv-sidePanelSection">
                  <div className="rgv-panelTitle">{t("Special requests")}</div>
                  {demoReadOnlyActive ? <div className="rgv-panelMuted">{DEMO_DATA_UNAVAILABLE}</div> : <SpecialRequestsPanel value={res?.special_requests} />}
                </div>

                <div className="rgv-sidePanelSection">
                  <div className="rgv-panelTitle" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span>{t("Comments")}</span>
                    {!demoReadOnlyActive && <span
                      title={t("Add / view comments for this room")}
                      style={{ cursor: "pointer", fontSize: "1rem", lineHeight: 1, color: "rgba(48,216,255,0.7)", fontWeight: 400 }}
                      onClick={() => { setCommentsInitialResId(res.id); setShowCommentsPopup(true); }}
                    >＋</span>}
                  </div>
                  {(() => {
                    const resSpecific = commentsData.filter((c) => {
                      const commentRoomId = isViewMode ? c.reservation_id : c.reservation_draft_id;
                      return Number(commentRoomId) === Number(res.id);
                    });
                    const latest = resSpecific.find((c) => c.is_important) ?? resSpecific[0];
                    const previewText = latest
                      ? (latest.body.length > 70 ? latest.body.slice(0, 70) + "…" : latest.body)
                      : null;
                    const fmtDate = (v) => {
                      if (!v) return "";
                      try { return new Date(v).toLocaleString(reservationLocale, { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
                      catch { return ""; }
                    };
                    return (
                      <div
                        style={{ cursor: demoReadOnlyActive ? "default" : "pointer" }}
                        onClick={() => { if (demoReadOnlyActive) return; setCommentsInitialResId(res.id); setShowCommentsPopup(true); }}
                      >
                        {previewText ? (
                          <>
                            <div style={{ fontSize: "0.72rem", color: "rgba(230,230,247,0.38)", marginBottom: 2 }}>
                              {latest.created_by && <span>{latest.created_by}</span>}{latest.created_by && " · "}{fmtDate(latest.createdAt || latest.created_at)}
                            </div>
                            <div className="rgv-panelMuted" style={latest.is_important ? { color: "#9b3445" } : undefined}>
                              {latest.is_important && "🚩 "}{previewText}
                            </div>
                            {resSpecific.length > 1 && (
                              <div className="rgv-panelMuted" style={{ marginTop: 3, fontSize: "0.76rem" }}>
                                +{resSpecific.length - 1} {t("more")} {resSpecific.some((c, i) => i > 0 && c.is_important) && " · 🚩"}
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="rgv-panelMuted" style={{ fontSize: "0.78rem" }}>
                            {demoReadOnlyActive ? DEMO_DATA_UNAVAILABLE : t("No comments for this room.")}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>

            <div className="col-xl-4 col-lg-6">
              <div className="rgv-nightlyPanel">
                <div className="rgv-panelTitle mb-2">{t("Nightly Breakdown")}</div>

                {nightlyLoading ? (
                  <div className="rgv-panelMuted">{t("Loading nightly prices...")}</div>
                ) : nightlyRows.length > 0 ? (
                  <>
                    <div className="rgv-nightlyRows">
                      {nightlyDisplayRows.map((nightRow) => (
                        <div key={`${res.id}-${nightRow.id ?? nightRow.date}`} className="rgv-nightlyRow">
                          <div className="rgv-nightlyInfo">
                            <span className="rgv-nightlyDate">{nightRow.date}</span>
                            <span className="rgv-nightlyMeta"> {t("Booked:")}{" "}
                              {nightRow.bookedRoomCategoryName ||
                                t("Category pending")}{" "} {t("· Assigned:")}{" "}
                              {nightRow.roomCategoryName ||
                                t("Category pending")}{" "} {t("· Room")} {nightRow.roomNumber || "-"}
                              {nightRow.roomRateName
                                ? ` · ${nightRow.roomRateName}`
                                : ""}
                            </span>
                          </div>
                          <strong className="rgv-nightlyPrice">{formatMoney(nightRow.price)}</strong>
                        </div>
                      ))}
                    </div>

                    <div className="rgv-nightlyTotal">
                      <span>{t("Room total from nights")}</span>
                      <span>{formatMoney(nightlyTotal)}</span>
                    </div>
                  </>
                ) : (
                  <div className="rgv-panelMuted">
                    {formatGroupError(nightlyError, "No nightly breakdown available for this reservation.")}
                  </div>
                )}
              </div>
            </div>
            </div>
            </div>
            )}

        </div>
      )})}

      {reservations.length === 0 && (
        <div className="rgv-emptyState"> {t("No room reservations yet.")} </div>
      )}
    </div>

    {!demoReadOnlyActive && <>
    {roomNumberPicker.show && (
      <div
        className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center rdp-pickerOverlay"
      >
        <div className="rdp-pickerCard rdp-pickerCard--wide">
          <div className="rdp-pickerHeader">
            <div className="rdp-pickerHeaderCopy">
              <p className="rdp-pickerEyebrow">{t("Accommodation")}</p>
              <h5 className="rdp-pickerTitle">{roomNumberPicker.title || t("Select Suggested Room")}</h5>
              <p className="rdp-pickerSubtitle">
                {roomNumberPicker.subtitle || t("Review available and occupied rooms before applying the suggestion.")}
              </p>
            </div>
            <button
              type="button"
              className="rdp-pickerClose"
              onClick={closeRoomNumberPicker}
              disabled={Boolean(roomNumberPicker.savingRoomId)}
              aria-label={t("Close")}
            >
              &times;
            </button>
          </div>

          {roomNumberPicker.mode === "room_move" ? (
            <>
              <div className="rdp-currentRoomNotice">
                <span>{t("Current room")}</span>
                <strong>{roomNumberPicker.currentRoomLabel || t("Room not assigned")}</strong>
              </div>

              <div className="rgv-modifyStayGrid" style={{ margin: "0 0 12px" }}>
                <label className="rgv-modifyStayField">
                  <span>{t("Move from")}</span>
                  <input
                    type="date"
                    value={roomNumberPicker.effectiveStayDate || ""}
                    min={todayIso}
                    max={addDaysToDateInput(roomNumberPicker.effectiveEndDate, -1) || undefined}
                    disabled={Boolean(roomNumberPicker.savingRoomId)}
                    onChange={(event) => {
                      void updateRoomMoveDateRange({ effectiveStayDate: event.target.value });
                    }}
                  />
                </label>

                <label className="rgv-modifyStayField">
                  <span>{t("Move until")}</span>
                  <input
                    type="date"
                    value={roomNumberPicker.effectiveEndDate || ""}
                    min={addDaysToDateInput(roomNumberPicker.effectiveStayDate, 1) || undefined}
                    max={toDateInputValue(roomNumberPicker.reservation?.check_out_date) || undefined}
                    disabled={Boolean(roomNumberPicker.savingRoomId)}
                    onChange={(event) => {
                      void updateRoomMoveDateRange({ effectiveEndDate: event.target.value });
                    }}
                  />
                </label>
              </div>
            </>
          ) : null}

          {roomNumberPicker.mode === "assignment" && !isCheckedInReservation(roomNumberPicker.reservation) && (
            <RoomUnassignOption
              t={t}
              busy={roomNumberPicker.savingRoomId === "unassign"}
              disabled={Boolean(roomNumberPicker.savingRoomId) || !normalizeNumericId(
                getRoomMoveCurrentRoomId(roomNumberPicker.reservation),
                roomNumberPicker.reservation?.suggested_room_id
              )}
              onClick={() => applyRoomNumberSelection(null, "unassigned")}
            />
          )}

          {roomNumberPicker.loading ? (
            <div className="rdp-pickerEmpty">{t("Loading rooms...")}</div>
          ) : roomNumberPicker.error ? (
            <div className="rdp-pickerEmpty">{formatGroupError(roomNumberPicker.error)}</div>
          ) : roomNumberPicker.availableRooms.length === 0 && roomNumberPicker.occupiedRooms.length === 0 ? (
            <div className="rdp-pickerEmpty">{t("No rooms found.")}</div>
          ) : (
            <>
              {roomNumberPicker.availableRooms.length > 0 && (
                <div className="rdp-pickerGroupLabel rdp-pickerGroupLabel--available">{t("Available")}</div>
              )}
              {roomNumberPicker.availableRooms.length > 0 && (
                <div className="rdp-pickerOptions">
                  {[...roomNumberPicker.availableRooms]
                    .sort((a, b) => {
                      const numA = parseInt(a.room_number, 10);
                      const numB = parseInt(b.room_number, 10);
                      if (!Number.isNaN(numA) && !Number.isNaN(numB)) return numA - numB;
                      return String(a.room_number || "").localeCompare(String(b.room_number || ""));
                    })
                    .map((room) => {
                      const isSavingThisRoom = Number(roomNumberPicker.savingRoomId) === Number(room.id);

                      return (
                        <button
                          key={`available-${room.id}`}
                          type="button"
                          className="rdp-pickerOption"
                          disabled={Boolean(roomNumberPicker.savingRoomId)}
                          onClick={() => applyRoomNumberSelection(room, "available")}
                        >
                          <span className="rdp-pickerOptionTitle">
                            {isSavingThisRoom ? t("Saving...") : t("Room {p0}", { p0: room.room_number || "-" })}
                          </span>
                          <span className="rdp-pickerOptionMeta rdp-pickerRoomMeta">
                            {getRoomMetadataLines(room).map((line) => (
                              <span key={line.label} className="rdp-pickerRoomMetaLine">
                                <span className="rdp-pickerRoomMetaLabel">{t(line.label)}:</span>{" "}
                                {line.value}
                              </span>
                            ))}
                          </span>
                        </button>
                      );
                    })}
                </div>
              )}

              {roomNumberPicker.occupiedRooms.length > 0 && (
                <div className="rdp-pickerGroupLabel rdp-pickerGroupLabel--occupied">{t("Occupied / Blocked")}</div>
              )}
              {roomNumberPicker.occupiedRooms.length > 0 && (
                <div className="rdp-pickerOptions">
                  {[...roomNumberPicker.occupiedRooms]
                    .sort((a, b) => {
                      const numA = parseInt(a.room_number, 10);
                      const numB = parseInt(b.room_number, 10);
                      if (!Number.isNaN(numA) && !Number.isNaN(numB)) return numA - numB;
                      return String(a.room_number || "").localeCompare(String(b.room_number || ""));
                    })
                    .map((room) => {
                      const isSavingThisRoom = Number(roomNumberPicker.savingRoomId) === Number(room.id);
                      const occupiedSelectionDisabled = !roomNumberPicker.allowOccupiedSelection;

                      return (
                        <button
                          key={`occupied-${room.id}`}
                          type="button"
                          className="rdp-pickerOption rdp-pickerOption--occupied"
                          disabled={Boolean(roomNumberPicker.savingRoomId) || occupiedSelectionDisabled}
                          onClick={() => applyRoomNumberSelection(room, "occupied")}
                        >
                          <span className="rdp-pickerOptionTitle">
                            {isSavingThisRoom ? t("Saving...") : t("Room {p0}", { p0: room.room_number || "-" })}
                          </span>
                          <span className="rdp-pickerOptionMeta rdp-pickerRoomMeta">
                            {getRoomMetadataLines(room).map((line) => (
                              <span key={line.label} className="rdp-pickerRoomMetaLine">
                                <span className="rdp-pickerRoomMetaLabel">{t(line.label)}:</span>{" "}
                                {line.value}
                              </span>
                            ))}
                          </span>
                        </button>
                      );
                    })}
                </div>
              )}
            </>
          )}

          <div className="rdp-pickerActions">
            <button
              type="button"
              className="rdp-actionBtn rdp-actionBtn--secondary"
              onClick={closeRoomNumberPicker}
              disabled={Boolean(roomNumberPicker.savingRoomId)}
            > {t("Cancel")} </button>
          </div>
        </div>
      </div>
    )}

    {isDraftPopupOpen && (
      <ReservationDraftPopup
        isOpen={isDraftPopupOpen}
        reservationDraftId={activeDraftId}
        reservationModificationPolicy={reservationModificationPolicy}
        onPricingChanged={() => loadGroup()}
        onClose={async () => {
          skipNextAutoOpenRef.current = true; // ✅ permite cerrar SIEMPRE sin re-autoopen inmediato
          setIsDraftPopupOpen(false);
          setActiveDraftId(null);
          await loadGroup();
        }}
      />
    )}

    <BookerSelectorPopup
      show={isBookerSelectorOpen}
      onClose={() => setIsBookerSelectorOpen(false)}
      businessUnitId={effectiveBusinessUnitId}
      onBookerSelected={async ({ type, customer, company, contactName }) => {
        if (!canManageGroupBooker) throw new Error(t("You cannot change the booker for this group."));
        if (type === "company") {
          if (!company?.id) throw new Error(t("Select a company."));
          const endpoint = isViewMode
            ? `/api/reservation-groups/${reservation_group_id}`
            : `/api/reservation-draft-groups/${reservation_group_id}/booker`;
          await axios.patch(endpoint, {
            booker_id: null,
            booker_company_id: company.id,
            booker_contact_name: String(contactName || "").trim() || null,
            updated_by: "UserSelectCompanyBooker",
          });
          setGroup((current) => current ? {
            ...current, booker: null, booker_id: null,
            booker_company: company, booker_company_id: company.id,
            booker_contact_name: String(contactName || "").trim() || null,
          } : current);
          return;
        }

        if (!customer?.id) throw new Error(t("Select a person."));
        await patchGroupBooker(customer.id, "UserSelectBooker");
        setGroup((current) => current ? {
          ...current, booker: customer, booker_id: customer.id,
          booker_company: null, booker_company_id: null, booker_contact_name: null,
        } : current);
        try {
          const { data: settings } = await axios.get("/api/hotel-settings", {
            params: { business_unit_id: effectiveBusinessUnitId },
          });
          const required = getRequiredFieldsForRole(
            parseRequiredCustomerFields(settings?.required_customer_fields_reservation), "booker"
          );
          if (getMissingRequiredFields(required, customer).length) {
            setBookerEditorMode("edit");
            setBookerEditorInitialData(customer);
            setIsAddBookerOpen(true);
          }
        } catch (error) {
          console.warn("[GROUP][BOOKER][REQUIRED_CHECK] Booker assigned, settings could not be checked", error);
        }
      }}
    />


    <BookerCustomerCreatePopup
      show={isAddBookerOpen}
      mode={bookerEditorMode}
      initialData={bookerEditorInitialData}
      initialType={bookerEditorMode === "edit" && companyBooker ? "company" : "person"}
      initialCompany={bookerEditorMode === "edit" ? group.booker_company || null : null}
      initialContactName={bookerEditorMode === "edit" ? group.booker_contact_name || "" : ""}
      businessUnitId={effectiveBusinessUnitId}
      reservationGroupId={group.id}
      isRealGroup={isViewMode}
      onClose={() => setIsAddBookerOpen(false)}
      onSaved={async () => {
        console.log("[GROUP][BOOKER][POPUP][SAVED] reloading group");
        setIsAddBookerOpen(false);
        await loadGroup();
      }}
    />

    <GuestSelectorPopup
      show={isMainGuestSelectorOpen}
      onClose={() => {
        setIsMainGuestSelectorOpen(false);
        setMainGuestSelectorDraftId(null);
      }}
      reservationId={null} // ✅ evita que el popup pueda asignar booker
      businessUnitId={effectiveBusinessUnitId}
      onCustomerSelected={async (customer) => {
        setIsMainGuestSelectorOpen(false);

        const customer_id = customer?.id;
        const reservation_draft_id = mainGuestSelectorDraftId;

        if (!customer_id || !reservation_draft_id) {
          console.warn("[MAIN_GUEST][SELECT] missing ids", { customer_id, reservation_draft_id });
          return;
        }

        try {
          console.log("[MAIN_GUEST][SELECT][POST] start", { reservation_draft_id, customer_id });

          await assignGuestToReservationTarget({
            targetId: reservation_draft_id,
            customerId: customer_id,
            isMainGuest: true,
            isChild: false,
            guestIndex: null,
            updatedBy: "UserSelectMainGuest",
          });

          await refreshOperationalGuestsFor(reservation_draft_id);

          // ✅ REQUIRED FIELDS CHECK (main_guest) → auto open edit popup
          const bu = effectiveBusinessUnitId;

          const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/hotel-settings`, {
            params: { business_unit_id: bu },
          });

          const raw = res.data?.required_customer_fields_reservation ?? null;

          let parsed = null;
          if (typeof raw === "string") {
            try { parsed = JSON.parse(raw); }
            catch (e) { console.warn("[MAIN_GUEST][REQUIRED] JSON parse failed", { raw }); }
          } else {
            parsed = raw;
          }

          const required =
            parsed && typeof parsed === "object" && !Array.isArray(parsed)
              ? (Array.isArray(parsed.main_guest) ? parsed.main_guest : [])
              : (Array.isArray(parsed) ? parsed : []);

          const missing = required.filter((f) => {
            const v = customer?.[f];
            return v == null || String(v).trim() === "";
          });

          console.log("[MAIN_GUEST][REQUIRED_CHECK]", { required, missing });

          if (missing.length > 0) {
            console.warn("[MAIN_GUEST][AUTO_EDIT_OPEN] missing required fields", { missing });

            setMainGuestEditorMode("edit");
            setMainGuestEditorInitialData(customer);       // ← el customer seleccionado
            setMainGuestEditorDraftId(reservation_draft_id);
            setIsAddMainGuestOpen(true);
          }
        } catch (err) {
          console.error("[MAIN_GUEST][SELECT][ERR]", err);
          alert(formatGroupError(t("Failed to assign main guest.")));
        }
      }}

    />

    <MainGuestCustomerCreatePopup
      show={isAddMainGuestOpen}
      mode={mainGuestEditorMode}
      initialData={mainGuestEditorInitialData}
      businessUnitId={effectiveBusinessUnitId}
      reservationId={isViewMode ? mainGuestEditorDraftId : null}
      reservationDraftId={isViewMode ? null : mainGuestEditorDraftId}
      onClose={() => {
        setIsAddMainGuestOpen(false);
        setMainGuestEditorDraftId(null);
      }}
      onSaved={async () => {
        console.log("[GROUP][MAIN_GUEST][POPUP][SAVED] refresh guests", { targetId: mainGuestEditorDraftId });
        const targetId = mainGuestEditorDraftId;

        setIsAddMainGuestOpen(false);
        setMainGuestEditorDraftId(null);

        if (targetId) {
          await refreshOperationalGuestsFor(targetId);
        } else {
          await loadGroup();
        }
      }}
    />

    <GuestSelectorPopup
      show={isAccompanyingSelectorOpen}
      onClose={() => {
        setIsAccompanyingSelectorOpen(false);
        setAccompanyingSelectorDraftId(null);
      }}
      reservationId={null}
      businessUnitId={effectiveBusinessUnitId}
      onCustomerSelected={async (customer) => {
        setIsAccompanyingSelectorOpen(false);

        const customer_id = customer?.id;
        const reservation_draft_id = accompanyingSelectorDraftId;

        if (!customer_id || !reservation_draft_id) {
          console.warn("[ACCOMPANYING][SELECT] missing ids", { customer_id, reservation_draft_id });
          return;
        }

        const resLocal = reservations.find(r => r.id === reservation_draft_id);
        const adultsCount = Number(resLocal?.adults || 0);
        const maxAcc = Math.max(0, adultsCount - 1);

        const currentList = Array.isArray(resLocal?.accompanying_guests) ? resLocal.accompanying_guests : [];
        if (currentList.length >= maxAcc) {
          console.warn("[ACCOMPANYING][SELECT][BLOCKED_MAX]", { reservation_draft_id, current: currentList.length, maxAcc });
          return;
        }

        const used = new Set(
          currentList.map(g => Number(g?.guest_index)).filter(v => Number.isFinite(v))
        );

        let nextIndex = null;
        for (let i = 0; i < maxAcc; i++) {
          if (!used.has(i)) {
            nextIndex = i;
            break;
          }
        }

        if (nextIndex == null) {
          console.warn("[ACCOMPANYING][SELECT][BLOCKED_NO_SLOT]", { reservation_draft_id, used: Array.from(used), maxAcc });
          return;
        }

        console.log("[ACCOMPANYING][SELECT][POST] start", { reservation_draft_id, customer_id, guest_index: nextIndex });

        await assignGuestToReservationTarget({
          targetId: reservation_draft_id,
          customerId: customer_id,
          isMainGuest: false,
          isChild: false,
          guestIndex: nextIndex,
          updatedBy: "UserSelectAccompanying",
        });

        await refreshOperationalGuestsFor(reservation_draft_id);

        // ✅ REQUIRED FIELDS CHECK (accompanying) → auto open edit popup
        try {
          const bu = effectiveBusinessUnitId;

          const resSettings = await axios.get(`${process.env.REACT_APP_API_URL}/api/hotel-settings`, {
            params: { business_unit_id: bu },
          });

          const raw = resSettings.data?.required_customer_fields_reservation ?? null;

          let parsed = null;
          if (typeof raw === "string") {
            try { parsed = JSON.parse(raw); }
            catch (e) { console.warn("[ACCOMPANYING][REQUIRED] JSON parse failed", { raw }); }
          } else {
            parsed = raw;
          }

          const required =
            parsed && typeof parsed === "object" && !Array.isArray(parsed)
              ? (
                  Array.isArray(parsed.accompanying) ? parsed.accompanying :
                  Array.isArray(parsed.accompanying_guest) ? parsed.accompanying_guest :
                  Array.isArray(parsed.accompanying_guests) ? parsed.accompanying_guests :
                  []
                )
              : (Array.isArray(parsed) ? parsed : []);

          const missing = required.filter((f) => {
            const v = customer?.[f];
            return v == null || String(v).trim() === "";
          });

          console.log("[ACCOMPANYING][REQUIRED_CHECK]", { required, missing, guest_index: nextIndex });

          if (missing.length > 0) {
            console.warn("[ACCOMPANYING][AUTO_EDIT_OPEN] missing required fields", { missing, guest_index: nextIndex });

            setAccompanyingEditorMode("edit");
            setAccompanyingEditorInitialData(customer);
            setAccompanyingEditorDraftId(reservation_draft_id);
            setAccompanyingEditorGuestIndex(nextIndex);

            setIsAddAccompanyingOpen(true);
          }
        } catch (err) {
          console.error("[ACCOMPANYING][REQUIRED_CHECK][ERR]", err);
        }

      }}
    />

    <AccompanyingCustomerCreatePopup
      show={isAddAccompanyingOpen}
      mode={accompanyingEditorMode}
      initialData={accompanyingEditorInitialData}
      businessUnitId={effectiveBusinessUnitId}
      reservationId={isViewMode ? accompanyingEditorDraftId : null}
      reservationDraftId={isViewMode ? null : accompanyingEditorDraftId}
      guestIndex={accompanyingEditorGuestIndex}
      onClose={() => {
        setIsAddAccompanyingOpen(false);
        setAccompanyingEditorDraftId(null);
        setAccompanyingEditorGuestIndex(null);
      }}
      onSaved={async () => {
        const targetId = accompanyingEditorDraftId;

        setIsAddAccompanyingOpen(false);
        setAccompanyingEditorDraftId(null);
        setAccompanyingEditorGuestIndex(null);

        if (targetId) {
          await refreshOperationalGuestsFor(targetId);
        } else {
          await loadGroup();
        }
      }}
    />

        <GuestSelectorPopup
      show={isChildSelectorOpen}
      onClose={() => {
        setIsChildSelectorOpen(false);
        setChildSelectorDraftId(null);
        setChildSelectorGuestIndex(null);
      }}
      reservationId={null}
      businessUnitId={effectiveBusinessUnitId}
      onCustomerSelected={async (customer) => {
        setIsChildSelectorOpen(false);

        const customer_id = customer?.id;
        const reservation_draft_id = childSelectorDraftId;
        const slotIndex = childSelectorGuestIndex;

        if (!customer_id || !reservation_draft_id || slotIndex == null) {
          console.warn("[CHILD][SELECT] missing ids", { customer_id, reservation_draft_id, slotIndex });
          return;
        }

        try {
          console.log("[CHILD][SELECT][POST] start", { reservation_draft_id, customer_id, guest_index: slotIndex });

          await assignGuestToReservationTarget({
            targetId: reservation_draft_id,
            customerId: customer_id,
            isMainGuest: false,
            isChild: true,
            guestIndex: slotIndex,
            updatedBy: "UserSelectChild",
          });

          await refreshOperationalGuestsFor(reservation_draft_id);

          // ✅ REQUIRED FIELDS CHECK (child) → auto open edit popup
          const bu = effectiveBusinessUnitId;

          const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/hotel-settings`, {
            params: { business_unit_id: bu },
          });

          const raw = res.data?.required_customer_fields_reservation ?? null;

          let parsed = null;
          if (typeof raw === "string") {
            try { parsed = JSON.parse(raw); }
            catch (e) { console.warn("[CHILD][REQUIRED] JSON parse failed", { raw }); }
          } else {
            parsed = raw;
          }

          const required =
            parsed && typeof parsed === "object" && !Array.isArray(parsed)
              ? (
                  Array.isArray(parsed.child) ? parsed.child :
                  Array.isArray(parsed.children) ? parsed.children :
                  []
                )
              : (Array.isArray(parsed) ? parsed : []);

          const missing = required.filter((f) => {
            const v = customer?.[f];
            return v == null || String(v).trim() === "";
          });

          console.log("[CHILD][REQUIRED_CHECK]", { required, missing });

          if (missing.length > 0) {
            console.warn("[CHILD][AUTO_EDIT_OPEN] missing required fields", { missing });

            setChildEditorMode("edit");
            setChildEditorInitialData(customer);
            setChildEditorDraftId(reservation_draft_id);
            setChildEditorGuestIndex(slotIndex);
            setIsAddChildOpen(true);
          }
        } catch (err) {
          console.error("[CHILD][SELECT][ERR]", err);
          alert(formatGroupError(t("Failed to assign child.")));
        } finally {
          setChildSelectorDraftId(null);
          setChildSelectorGuestIndex(null);
        }
      }}
    />


    <ChildCustomerCreatePopup
      show={isAddChildOpen}
      mode={childEditorMode}
      initialData={childEditorInitialData}
      businessUnitId={effectiveBusinessUnitId}
      reservationId={isViewMode ? childEditorDraftId : null}
      reservationDraftId={isViewMode ? null : childEditorDraftId}
      guestIndex={childEditorGuestIndex}
      onClose={() => {
        setIsAddChildOpen(false);
        setChildEditorDraftId(null);
        setChildEditorGuestIndex(null);
      }}
      onSaved={async () => {
        const targetId = childEditorDraftId;

        setIsAddChildOpen(false);
        setChildEditorDraftId(null);
        setChildEditorGuestIndex(null);

        if (targetId) {
          await refreshOperationalGuestsFor(targetId);
        } else {
          await loadGroup();
        }
      }}
    />

    <GroupPendingExtrasModal
      show={isViewMode && showGroupExtras}
      onClose={handleCloseGroupExtrasModal}
      groupId={reservation_group_id}
      businessUnitId={effectiveBusinessUnitId}
      onSaved={async () => { await loadGroup(); }}
    />

    <GroupDraftExtrasModal
      show={!isViewMode && showGroupExtras}
      onClose={handleCloseGroupExtrasModal}
      groupId={isViewMode ? null : reservation_group_id}
      businessUnitId={effectiveBusinessUnitId}
      onSaved={handleGroupExtrasSaved}
    />

    <GroupSpecialRate
      show={showSpecialRate}
      onClose={handleCloseSpecialRateModal}
      onApply={handleApplySpecialRate}
      groupReservations={
        backgroundGroupDraft?.action === "special_rate"
          ? backgroundGroupDraft.reservations
          : reservations
      }
    />
    </>}

    {groupActionsDialogOpen && (() => {
      const actionItems = getGroupActionItems();

      return (
        <div
          className="rgv-actionModalOverlay"
          onClick={() => setGroupActionsDialogOpen(false)}
        >
          <div
            className="rgv-actionModal"
            role="dialog"
            aria-modal="true"
            aria-label={t("Group Actions")}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="rgv-actionModalHeader">
              <div>
                <div className="rgv-actionModalEyebrow">{t("Reservation Group")}</div>
                <h2 className="rgv-actionModalTitle">{t("Group Actions")}</h2>
                <div className="rgv-actionModalRoom">
                  {displayGroupLocator || t("Group #{p0}", { p0: reservation_group_id })}
                </div>
              </div>

              <button
                type="button"
                className="rgv-actionModalClose"
                onClick={() => setGroupActionsDialogOpen(false)}
                aria-label={t("Close group actions")}
              >
                <FiX size={18} aria-hidden="true" />
              </button>
            </div>

            {demoReadOnlyActive && <p role="note" style={{ color: isLightTheme ? "#334155" : "#cbd5e1", margin: "0 0 16px" }}>Demo de solo consulta: las acciones no se pueden ejecutar.</p>}
            <div className="rgv-actionCards">
              {actionItems.map((action) => {
                const Icon = action.icon;

                if (action.key === "add_room") {
                  return (
                    <div
                      key={action.key}
                      className="rgv-addRoomAction"
                    >
                      <button
                        type="button"
                        className="rgv-actionCard rgv-actionCard--success rgv-addRoomActionCard"
                        disabled={action.disabled}
                        title={action.hint}
                        onClick={() => handleGroupActionClick(action)}
                      >
                        <span className="rgv-actionCardIcon">
                          <Icon size={19} aria-hidden="true" />
                        </span>
                        <span className="rgv-actionCardTitle">{t("Add rooms")}</span>
                      </button>

                      <div className="rgv-addRoomQuantity" role="group" aria-label={t("Rooms to add")}>
                        <input
                          type="number"
                          min="1"
                          step="1"
                          inputMode="numeric"
                          aria-label={t("Rooms to add")}
                          className="rgv-addRoomCountInput"
                          value={roomsToAdd}
                          disabled={action.disabled}
                          onChange={handleRoomsToAddChange}
                          onBlur={() => setRoomsToAdd((value) => normalizeRoomsToAdd(value))}
                          onKeyDown={(event) => {
                            if (event.key === "Enter") event.preventDefault();
                          }}
                        />
                        <button
                          type="button"
                          className="rgv-addRoomStepBtn"
                          aria-label={t("Increase room count")}
                          title={t("Increase room count")}
                          disabled={action.disabled}
                          onClick={() => setRoomsToAdd((value) => normalizeRoomsToAdd(value) + 1)}
                        >
                          <FiPlus size={15} aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          className="rgv-addRoomStepBtn"
                          aria-label={t("Decrease room count")}
                          title={t("Decrease room count")}
                          disabled={action.disabled || normalizedRoomsToAdd <= 1}
                          onClick={() => setRoomsToAdd((value) => Math.max(1, normalizeRoomsToAdd(value) - 1))}
                        >
                          <FiMinus size={15} aria-hidden="true" />
                        </button>
                      </div>
                    </div>
                  );
                }

                return (
                  <button
                    key={action.key}
                    type="button"
                    className={`rgv-actionCard ${action.tone ? `rgv-actionCard--${action.tone}` : ""} ${
                      action.disabled ? "rgv-actionCard--disabled" : ""
                    }`}
                    disabled={action.disabled}
                    title={action.disabled ? action.hint || t("This action is not available right now.") : undefined}
                    onClick={() => handleGroupActionClick(action)}
                  >
                    <span className="rgv-actionCardIcon">
                      <Icon size={19} aria-hidden="true" />
                    </span>
                    <span className="rgv-actionCardTitle">{t(action.title)}</span>
                    {action.policyBlocked ? (
                      <span className="rgv-actionCardPolicyMark" aria-hidden="true">
                        <FiLock size={38} />
                      </span>
                    ) : action.hint && !demoReadOnlyActive ? (
                      <span className="rgv-actionCardHint">{action.hint}</span>
                    ) : null}
                  </button>
                );
              })}
            </div>
            {actionItems.some((action) => action.policyBlocked) ? (
              <div className="rgv-actionPolicyLegend">
                <FiLock size={14} aria-hidden="true" />
                <span> {t("This icon means the action is disabled by the manual modification permissions in Settings → Reservation Settings.")} </span>
              </div>
            ) : null}
          </div>
        </div>
      );
    })()}

    {!demoReadOnlyActive && <>
    {showCopyPanel && (() => {
      const visibleCopyToCount =
        (copyToOptions || []).filter((option) =>
          (reservations || []).some((reservation) => Number(reservation.id) === Number(option.id))
        ).length;

      return (
        <div
          className="rgv-actionModalOverlay"
          onClick={isCopying ? undefined : closeCopyInfoModal}
        >
          <div
            className="rgv-actionModal rgv-copyInfoModal"
            role="dialog"
            aria-modal="true"
            aria-label={t("Copy room information")}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="rgv-actionModalHeader">
              <div>
                <div className="rgv-actionModalEyebrow">{t("Reservation Group")}</div>
                <h2 className="rgv-actionModalTitle">{t("Copy Info")}</h2>
                <div className="rgv-actionModalRoom">
                  {displayGroupLocator || t("Group #{p0}", { p0: reservation_group_id })}
                </div>
              </div>

              <button
                type="button"
                className="rgv-actionModalClose"
                onClick={closeCopyInfoModal}
                aria-label={t("Close copy info")}
                disabled={isCopying}
              >
                <FiX size={18} aria-hidden="true" />
              </button>
            </div>

            <form
              className="rgv-copyInfoForm"
              onSubmit={(event) => {
                event.preventDefault();
                void handleCopyInfoSubmit();
              }}
            >
              <div className="rgv-copyInfoSummary">
                <span className="rgv-copyInfoSummaryIcon">
                  <FiRefreshCcw size={18} aria-hidden="true" />
                </span>
                <div>
                  <strong>{t("Copy room setup")}</strong>
                  <span>{t("Choose one source room and copy its setup into another room or the whole group.")}</span>
                </div>
              </div>

              <div className="rgv-copyInfoFields">
                <label className="rgv-copyInfoField">
                  <span>{t("Copy From")}</span>
                  <select
                    className="rgv-copyInfoSelect"
                    value={copyFrom}
                    onChange={(event) => setCopyFrom(event.target.value)}
                    disabled={isCopying}
                  >
                    <option value="">{t("Select reservation")}</option>
                    {(copyFromOptions || []).map((option) => (
                      <option key={option.id} value={String(option.id)}>
                        {option.label || `#${option.id}`}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="rgv-copyInfoField">
                  <span>{t("Copy To")}</span>
                  <select
                    className="rgv-copyInfoSelect"
                    value={copyTo}
                    onChange={(event) => setCopyTo(event.target.value)}
                    disabled={isCopying}
                  >
                    <option value="">{t("Select reservation")}</option>
                    {(copyToOptions || []).map((option) => (
                      <option key={option.id} value={String(option.id)}>
                        {option.label || `#${option.id}`}
                      </option>
                    ))}
                    {visibleCopyToCount > 1 && (
                      <option value="all">{t("All")}</option>
                    )}
                  </select>
                </label>
              </div>

              <div className="rgv-copyInfoActions">
                <button
                  type="submit"
                  className="rgv-copyInfoBtn rgv-copyInfoBtn--copy"
                  disabled={!copyFrom || !copyTo || isCopying}
                >
                  {isCopying ? t("Copying...") : t("Copy")}
                </button>

                <button
                  type="button"
                  className="rgv-copyInfoBtn rgv-copyInfoBtn--cancel"
                  onClick={closeCopyInfoModal}
                  disabled={isCopying}
                > {t("Cancel")} </button>
              </div>
            </form>
          </div>
        </div>
      );
    })()}

    {reservationHistoryDialogId ? (() => {
      const reservationId = Number(reservationHistoryDialogId);
      const reservation = (reservations || []).find(
        (item) => Number(item?.id) === reservationId
      );
      const history = reservationChangeHistoryById[reservationId] || null;
      const initialVersion = history?.initial_version || null;
      const currentVersion = history?.current_version || null;
      const comparisonRows = RESERVATION_VERSION_COMPARISON_FIELDS.map((field) => {
        const before = firstReservationVersionValue(initialVersion, field.paths);
        const after = firstReservationVersionValue(currentVersion, field.paths);
        return {
          ...field,
          before,
          after,
          changed:
            Boolean(currentVersion) &&
            formatReservationHistoryValue(before, t) !==
            formatReservationHistoryValue(after, t),
        };
      });
      const events = [...(Array.isArray(history?.events) ? history.events : [])].sort(
        (left, right) =>
          new Date(right?.occurred_at || 0).getTime() -
          new Date(left?.occurred_at || 0).getTime()
      );
      const reservationLabel =
        reservation?.locator ||
        firstReservationVersionValue(currentVersion, ["locator"]) ||
        firstReservationVersionValue(initialVersion, ["locator"]) ||
        t("Reservation #{p0}", { p0: reservationId });
      const historyListRows = buildReservationHistoryListRows(
        history?.events,
        reservationLabel
      , t);
      const initialNights = Array.isArray(initialVersion?.nights)
        ? initialVersion.nights
        : [];
      const currentNights = Array.isArray(currentVersion?.nights)
        ? currentVersion.nights
        : [];
      const initialNightByDate = new Map(
        initialNights.map((night) => [String(night?.stay_date || ""), night])
      );
      const currentNightByDate = new Map(
        currentNights.map((night) => [String(night?.stay_date || ""), night])
      );
      const versionNightDates = [
        ...new Set([...initialNightByDate.keys(), ...currentNightByDate.keys()]),
      ]
        .filter(Boolean)
        .sort();
      const versionNightRows = versionNightDates.map((stayDate) => {
        const beforeLabel = formatReservationVersionNight(initialNightByDate.get(stayDate) || null, t);
        const afterLabel = formatReservationVersionNight(currentNightByDate.get(stayDate) || null, t);
        return { stayDate, beforeLabel, afterLabel, changed: Boolean(currentVersion) && beforeLabel !== afterLabel };
      });
      const initialGuests = Array.isArray(initialVersion?.guests)
        ? initialVersion.guests
        : [];
      const currentGuests = Array.isArray(currentVersion?.guests)
        ? currentVersion.guests
        : [];
      const versionGuestRows = Array.from({
        length: Math.max(initialGuests.length, currentGuests.length),
      }).map((_, index) => {
        const beforeLabel = formatReservationVersionGuest(initialGuests[index] || null, t);
        const afterLabel = formatReservationVersionGuest(currentGuests[index] || null, t);
        return { index, beforeLabel, afterLabel, changed: Boolean(currentVersion) && beforeLabel !== afterLabel };
      });

      return (
        <div
          className="rgv-actionModalOverlay"
          onClick={() => setReservationHistoryDialogId(null)}
        >
          <div
            className="rgv-actionModal rgv-historyModal"
            role="dialog"
            aria-modal="true"
            aria-label={t("Reservation change history")}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="rgv-actionModalHeader">
              <div>
                <div className="rgv-actionModalEyebrow">{t("Reservation audit")}</div>
                <h2 className="rgv-actionModalTitle">{t("Original state and change history")}</h2>
                <div className="rgv-actionModalRoom">
                  {reservationLabel}
                  {history?.change_count
                    ? t(" · {p0} change{p1}", { p0: history.change_count, p1: Number(history.change_count) === 1 ? "" : "s" })
                    : ""}
                </div>
              </div>

              <button
                type="button"
                className="rgv-actionModalClose"
                onClick={() => setReservationHistoryDialogId(null)}
                aria-label={t("Close reservation history")}
              >
                <FiX size={18} aria-hidden="true" />
              </button>
            </div>

            <div
              className="rgv-historyTabs"
              role="tablist"
              aria-label={t("Reservation audit views")}
            >
              <button
                type="button"
                id="rgv-history-overview-tab"
                className={`rgv-historyTab ${
                  reservationHistoryActiveTab === "overview"
                    ? "rgv-historyTab--active"
                    : ""
                }`}
                role="tab"
                aria-selected={reservationHistoryActiveTab === "overview"}
                aria-controls="rgv-history-overview-panel"
                onClick={() => setReservationHistoryActiveTab("overview")}
              > {t("Audit detail")} </button>
              <button
                type="button"
                id="rgv-history-list-tab"
                className={`rgv-historyTab ${
                  reservationHistoryActiveTab === "list"
                    ? "rgv-historyTab--active"
                    : ""
                }`}
                role="tab"
                aria-selected={reservationHistoryActiveTab === "list"}
                aria-controls="rgv-history-list-panel"
                onClick={() => setReservationHistoryActiveTab("list")}
              > {t("Change list")} {historyListRows.length ? ` (${historyListRows.length})` : ""}
              </button>
            </div>

            {reservationChangeHistoryLoading ? (
              <div className="rgv-historyEmpty">{t("Loading reservation history...")}</div>
            ) : reservationChangeHistoryError ? (
              <div className="rgv-historyEmpty rgv-historyEmpty--error">
                <span>{formatGroupError(reservationChangeHistoryError)}</span>
                <button
                  type="button"
                  className="rgv-modifyStayBtn rgv-modifyStayBtn--secondary"
                  onClick={() => {
                    void loadReservationChangeHistory();
                  }}
                > {t("Try again")} </button>
              </div>
            ) : !history?.initial_version ? (
              <div className="rgv-historyEmpty"> {t("No version history is available for this reservation yet.")} </div>
            ) : reservationHistoryActiveTab === "list" ? (
              <section
                className="rgv-historyListSection"
                id="rgv-history-list-panel"
                role="tabpanel"
                aria-labelledby="rgv-history-list-tab"
              >
                <div className="rgv-historySectionTitle">
                  <span>{t("Field changes")}</span>
                  <small>{t("Oldest change first")}</small>
                </div>

                {historyListRows.length === 0 ? (
                  <div className="rgv-historyEmpty"> {t("This reservation has an initial version but no later changes.")} </div>
                ) : (
                  <div className="rgv-historyListWrap">
                    <table className="rgv-historyList">
                      <thead>
                        <tr>
                          <th scope="col">{t("Date ↑")}</th>
                          <th scope="col">{t("Reservation")}</th>
                          <th scope="col">{t("Field")}</th>
                          <th scope="col">{t("Old value")}</th>
                          <th scope="col">{t("New value")}</th>
                          <th scope="col">{t("Changed by")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {historyListRows.map((row) => (
                          <tr key={row.key}>
                            <td>{formatReservationHistoryDate(row.occurredAt, t, reservationLocale)}</td>
                            <td className="rgv-historyListReservation">
                              {formatReservationHistoryValue(row.reservationLabel, t)}
                            </td>
                            <td className="rgv-historyListField">{row.fieldLabel}</td>
                            <td>
                              {row.field === "status" ? translateReservationStatus(row.before, languageId) : formatReservationHistoryListValue(row.before, row.field, t)}
                            </td>
                            <td className="rgv-historyValue--changed">
                              {row.field === "status" ? translateReservationStatus(row.after, languageId) : formatReservationHistoryListValue(row.after, row.field, t)}
                            </td>
                            <td className="rgv-historyListActor">
                              {formatReservationHistoryValue(row.actor, t)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            ) : (
              <div
                className="rgv-historyContent"
                id="rgv-history-overview-panel"
                role="tabpanel"
                aria-labelledby="rgv-history-overview-tab"
              >
                <section className="rgv-historySection">
                  <div className="rgv-historySectionTitle">
                    <span>{t("Original vs current")}</span>
                    {history?.last_changed_at ? (
                      <small> {t("Last changed")} {formatReservationHistoryDate(history.last_changed_at, t, reservationLocale)}
                        {history?.last_actor
                          ? ` · ${formatReservationHistoryValue(history.last_actor)}`
                          : ""}
                      </small>
                    ) : null}
                  </div>

                  {currentVersion && comparisonRows.every((row) => !row.changed) ? (
                    <div className="rgv-historyUnchangedNotice" role="status">
                      <FiCheck aria-hidden="true" />
                      <span>{t("No changes in these fields.")}</span>
                    </div>
                  ) : null}

                  <div className="rgv-historyComparison">
                    <div className="rgv-historyComparisonHeader">{t("Field")}</div>
                    <div className="rgv-historyComparisonHeader">{t("Original")}</div>
                    <div className="rgv-historyComparisonHeader">{t("Current")}</div>
                    {comparisonRows.map((row) => (
                      <div className="rgv-historyComparisonRow" key={row.label}>
                        <div className="rgv-historyComparisonLabel">{t(row.label)}</div>
                        <div>{row.paths.includes("status") ? translateReservationStatus(row.before, languageId) : formatReservationHistoryValue(row.before, t)}</div>
                        <div className={row.changed ? "rgv-historyValue--changed" : currentVersion ? "rgv-historyValue--unchanged" : ""}>
                          {currentVersion && !row.changed
                            ? t("No changes")
                            : row.paths.includes("status") ? translateReservationStatus(row.after, languageId) : formatReservationHistoryValue(row.after, t)}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>

                {versionGuestRows.length > 0 ? (
                  <section className="rgv-historySection">
                    <div className="rgv-historySectionTitle">
                      <span>{t("Original vs current guests")}</span>
                      <small>{t("Identity, role and contact snapshot")}</small>
                    </div>

                    {currentVersion && versionGuestRows.every((row) => !row.changed) ? (
                      <div className="rgv-historyUnchangedNotice" role="status">
                        <FiCheck aria-hidden="true" />
                        <span>{t("No changes in guests.")}</span>
                      </div>
                    ) : null}

                    <div className="rgv-historyComparison">
                      <div className="rgv-historyComparisonHeader">{t("Guest")}</div>
                      <div className="rgv-historyComparisonHeader">{t("Original")}</div>
                      <div className="rgv-historyComparisonHeader">{t("Current")}</div>
                      {versionGuestRows.map(({ index, beforeLabel, afterLabel, changed }) => (
                          <div
                            className="rgv-historyComparisonRow"
                            key={`guest-version-${index}`}
                          >
                            <div className="rgv-historyComparisonLabel"> {t("Guest")} {index + 1}
                            </div>
                            <div>{beforeLabel}</div>
                            <div
                              className={
                                changed
                                  ? "rgv-historyValue--changed"
                                  : currentVersion ? "rgv-historyValue--unchanged" : ""
                              }
                            >
                              {currentVersion && !changed ? t("No changes") : afterLabel}
                            </div>
                          </div>
                      ))}
                    </div>
                  </section>
                ) : null}

                {versionNightRows.length > 0 ? (
                  <section className="rgv-historySection">
                    <div className="rgv-historySectionTitle">
                      <span>{t("Original vs current nightly terms")}</span>
                      <small>{t("Category, rate, effective price and rate price link")}</small>
                    </div>

                    {currentVersion && versionNightRows.every((row) => !row.changed) ? (
                      <div className="rgv-historyUnchangedNotice" role="status">
                        <FiCheck aria-hidden="true" />
                        <span>{t("No changes in nightly terms.")}</span>
                      </div>
                    ) : null}

                    <div className="rgv-historyComparison">
                      <div className="rgv-historyComparisonHeader">{t("Night")}</div>
                      <div className="rgv-historyComparisonHeader">{t("Original")}</div>
                      <div className="rgv-historyComparisonHeader">{t("Current")}</div>
                      {versionNightRows.map(({ stayDate, beforeLabel, afterLabel, changed }) => (
                          <div className="rgv-historyComparisonRow" key={stayDate}>
                            <div className="rgv-historyComparisonLabel">{stayDate}</div>
                            <div>{beforeLabel}</div>
                            <div
                              className={
                                changed
                                  ? "rgv-historyValue--changed"
                                  : currentVersion ? "rgv-historyValue--unchanged" : ""
                              }
                            >
                              {currentVersion && !changed ? t("No changes") : afterLabel}
                            </div>
                          </div>
                      ))}
                    </div>
                  </section>
                ) : null}

                <section className="rgv-historySection">
                  <div className="rgv-historySectionTitle">
                    <span>{t("Timeline")}</span>
                    <small>{t("Newest change first")}</small>
                  </div>

                  {events.length === 0 ? (
                    <div className="rgv-historyEmpty"> {t("This reservation has an initial version but no later changes.")} </div>
                  ) : (
                    <div className="rgv-historyTimeline">
                      {events.map((event, eventIndex) => {
                        const changes = Array.isArray(event?.changes) ? event.changes : [];
                        const nights = Array.isArray(event?.nights) ? event.nights : [];
                        const actor =
                          event?.actor_name ||
                          event?.actor?.name ||
                          event?.actor?.email ||
                          t("System");
                        const origin = humanizeReservationHistoryField(
                          event?.origin || event?.source || "system"
                        , t);

                        return (
                          <article
                            className="rgv-historyEvent"
                            key={event?.id || `${event?.occurred_at || "event"}-${eventIndex}`}
                          >
                            <div className="rgv-historyEventHeader">
                              <div>
                                <strong>{formatReservationHistoryDate(event?.occurred_at, t, reservationLocale)}</strong>
                                <span>{formatReservationHistoryValue(actor, t)} · {origin}</span>
                              </div>
                              <span className="rgv-historyOriginBadge">{origin}</span>
                            </div>

                            <div className="rgv-historyReason">
                              <span>{t("Reason")}</span>
                              <strong>{event?.reason || t("No reason recorded.")}</strong>
                            </div>

                            {changes.length > 0 ? (
                              <div className="rgv-historyChanges">
                                {changes.map((change, changeIndex) => (
                                  <div
                                    className="rgv-historyChange"
                                    key={change?.field || `${eventIndex}-${changeIndex}`}
                                  >
                                    <strong>
                                      {change?.label ||
                                        humanizeReservationHistoryField(change?.field, t)}
                                    </strong>
                                    <span>{formatReservationHistoryValue(
                                      change?.before ?? change?.before_value
                                    , t)}</span>
                                    <span className="rgv-historyArrow" aria-hidden="true">→</span>
                                    <span className="rgv-historyValue--changed">
                                      {formatReservationHistoryValue(
                                        change?.after ?? change?.after_value
                                      , t)}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            ) : null}

                            {nights.length > 0 ? (
                              <details className="rgv-historyNights">
                                <summary> {t("Nightly detail ·")} {nights.length} {t("night")}{nights.length === 1 ? "" : "s"}
                                </summary>
                                <div className="rgv-historyNightList">
                                  {nights.map((night, nightIndex) => {
                                    const nightChanges = Array.isArray(night?.changes)
                                      ? night.changes
                                      : [];
                                    const before =
                                      night?.before ??
                                      night?.old_price ??
                                      night?.previous_price;
                                    const after =
                                      night?.after ??
                                      night?.new_price ??
                                      night?.price;

                                    return (
                                      <div
                                        className="rgv-historyNight"
                                        key={night?.id || night?.stay_date || night?.date || nightIndex}
                                      >
                                        <strong>
                                          {night?.stay_date || night?.date || t("Night {p0}", { p0: nightIndex + 1 })}
                                        </strong>
                                        {nightChanges.length > 0 ? (
                                          nightChanges.map((change, changeIndex) => (
                                            <span key={change?.field || changeIndex}>
                                              {change?.label ||
                                                humanizeReservationHistoryField(change?.field, t)}
                                              : {formatReservationHistoryValue(
                                                change?.before ?? change?.before_value
                                              , t)} → {formatReservationHistoryValue(
                                                change?.after ?? change?.after_value
                                              , t)}
                                            </span>
                                          ))
                                        ) : (
                                          <span>
                                            {formatReservationHistoryValue(before, t)} →{" "}
                                            {formatReservationHistoryValue(after, t)}
                                          </span>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </details>
                            ) : null}
                          </article>
                        );
                      })}
                    </div>
                  )}
                </section>
              </div>
            )}
          </div>
        </div>
      );
    })() : null}

    </>}
    {reservationActionsDialog && (() => {
      const actionMode = reservationActionsDialog.mode || "confirmed";
      const reservation = reservationActionsDialog.reservation;
      const actionItems = getReservationActionItems(actionMode, reservation);
      const actionTitle = getReservationActionTitle(actionMode);
      const roomLabel = getReservationRoomLabel(reservation);

      return (
        <div
          className="rgv-actionModalOverlay"
          onClick={closeReservationActionsDialog}
        >
          <div
            className="rgv-actionModal"
            role="dialog"
            aria-modal="true"
            aria-label={actionTitle}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="rgv-actionModalHeader">
              <div>
                <div className="rgv-actionModalEyebrow">{t("Reservation Studio")}</div>
                <h2 className="rgv-actionModalTitle">{actionTitle}</h2>
                <div className="rgv-actionModalRoom">{roomLabel}</div>
              </div>

              <button
                type="button"
                className="rgv-actionModalClose"
                onClick={closeReservationActionsDialog}
                aria-label={t("Close actions")}
              >
                <FiX size={18} aria-hidden="true" />
              </button>
            </div>

            {demoReadOnlyActive && <p role="note" style={{ color: isLightTheme ? "#334155" : "#cbd5e1", margin: "0 0 16px" }}>Demo de solo consulta: las acciones no se pueden ejecutar.</p>}
            <div className="rgv-actionCards">
              {actionItems.map((action) => {
                const Icon = action.icon;

                return (
                  <button
                    key={action.key}
                    type="button"
                    className={`rgv-actionCard ${action.tone ? `rgv-actionCard--${action.tone}` : ""} ${
                      action.disabled ? "rgv-actionCard--disabled" : ""
                    }`}
                    disabled={Boolean(action.disabled)}
                    title={action.disabled ? action.hint : undefined}
                    onClick={() => handleReservationActionClick(action, reservation)}
                  >
                    <span className="rgv-actionCardIcon">
                      <Icon size={19} aria-hidden="true" />
                    </span>
                    <span className="rgv-actionCardTitle">{t(action.title)}</span>
                    {action.policyBlocked ? (
                      <span className="rgv-actionCardPolicyMark" aria-hidden="true">
                        <FiLock size={38} />
                      </span>
                    ) : action.hint && !demoReadOnlyActive ? (
                      <span className="rgv-actionCardHint">{action.hint}</span>
                    ) : null}
                  </button>
                );
              })}
            </div>
            {actionItems.some((action) => action.policyBlocked) ? (
              <div className="rgv-actionPolicyLegend">
                <FiLock size={14} aria-hidden="true" />
                <span> {t("This icon means the action is disabled by the manual modification permissions in Settings → Reservation Settings.")} </span>
              </div>
            ) : null}
          </div>
        </div>
      );
    })()}

    {!demoReadOnlyActive && <>
    {roomCategoryDialog && (() => {
      const dialog = roomCategoryDialog;
      const reservation = dialog.reservation;
      const roomLabel = getReservationRoomLabel(reservation);
      const isBusy = Boolean(dialog.loading);
      const isNightlyCategoryChange =
        dialog.mode === "nightly_category_change";
      const selectedChangeType = dialog.selectedChangeType || null;
      const isUpgrade = selectedChangeType === "upgrade";
      const requiresOtaReason = isOtaReservation(reservation);
      const priceReview = dialog.priceReview || null;
      const difference = Number(priceReview?.difference || 0);
      const priceTone =
        difference > 0.005 ? "increase" : difference < -0.005 ? "decrease" : "same";
      const currentCategoryLabel =
        reservation?.room_category_name ||
        reservation?.room_category?.name ||
        t("No category");
      const currentRateLabel =
        reservation?.rate_name ||
        reservation?.rate?.name ||
        reservation?.group_rate_name ||
        t("No rate");
      const currentRoomLabel =
        reservation?.room_number ||
        reservation?.room?.room_number ||
        reservation?.suggested_room_number ||
        reservation?.suggested_room?.room_number ||
        "";
      const selectedCategoryId = Number(dialog.selectedCategory?.id || 0);
      const selectedRateId = Number(dialog.selectedRate?.id || 0);
      const selectedRoomId = Number(dialog.selectedRoom?.id || 0);
      const roomOptionsCount =
        Number(dialog.availableRooms?.length || 0) + Number(dialog.occupiedRooms?.length || 0);

      return (
        <div
          className={`rgv-actionModalOverlay ${roomCategoryOverridePopupOpen ? "rgv-actionModalOverlay--belowOverride" : ""}`}
          onClick={closeRoomCategoryDialog}
        >
          <div
            className="rgv-actionModal rgv-roomCategoryModal"
            role="dialog"
            aria-modal="true"
            aria-label={t("Room and Category")}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="rgv-actionModalHeader">
              <div>
                <div className="rgv-actionModalEyebrow">{t("Reservation Studio")}</div>
                <h2 className="rgv-actionModalTitle">
                  {priceReview ? t("Review Room & Category") : t("Room & Category")}
                </h2>
                <div className="rgv-actionModalRoom">{roomLabel}</div>
              </div>

              <button
                type="button"
                className="rgv-actionModalClose"
                onClick={closeRoomCategoryDialog}
                aria-label={t("Close room and category")}
                disabled={isBusy}
              >
                <FiX size={18} aria-hidden="true" />
              </button>
            </div>

            <div className="rgv-modifyStaySummary">
              <span> {t("Selected nights:")} {dialog.checkInDate || "-"} {t("to")} {dialog.checkOutDate || "-"} · {dialog.nights || 0} {t("night")}{Number(dialog.nights || 0) === 1 ? "" : "s"}
              </span>
              <span> {t("Current:")} {currentCategoryLabel} · {currentRateLabel}
              </span>
              <span>
                {currentRoomLabel ? t("Room {p0}", { p0: currentRoomLabel }) : t("No room")}
              </span>
            </div>

            <div className="rgv-categoryChangeDates">
              <label>
                <span>{t("First affected night")}</span>
                <input
                  type="date"
                  value={dialog.checkInDate || ""}
                  min={
                    (dialog.reservationCheckInDate || "") > todayIso
                      ? dialog.reservationCheckInDate
                      : todayIso
                  }
                  max={
                    addDaysToDateInput(
                      dialog.reservationCheckOutDate,
                      -1
                    ) || undefined
                  }
                  disabled={isBusy}
                  onChange={(event) => {
                    void changeRoomCategoryDateRange(
                      "checkInDate",
                      event.target.value
                    );
                  }}
                />
              </label>
              <label>
                <span>{t("End of change")}</span>
                <input
                  type="date"
                  value={dialog.checkOutDate || ""}
                  min={
                    addDaysToDateInput(dialog.checkInDate, 1) ||
                    undefined
                  }
                  max={dialog.reservationCheckOutDate || undefined}
                  disabled={isBusy}
                  onChange={(event) => {
                    void changeRoomCategoryDateRange(
                      "checkOutDate",
                      event.target.value
                    );
                  }}
                />
              </label>
            </div>

            {isBusy && dialog.categories.length === 0 ? (
              <div className="rdp-pickerEmpty">{t("Loading accommodation options...")}</div>
            ) : (
              <div className="rgv-roomCategorySections">
                <section className="rgv-roomCategorySection">
                  <div className="rgv-roomCategorySectionHeader">
                    <span>{t("Room Category")}</span>
                    {dialog.selectedCategory ? <strong>{dialog.selectedCategory.name}</strong> : null}
                  </div>

                  {dialog.categories.length === 0 ? (
                    <div className="rdp-pickerEmpty">{t("No categories available.")}</div>
                  ) : (
                    <div className="rdp-pickerOptions">
                      {[...dialog.categories]
                        .sort((a, b) => String(a?.name || "").localeCompare(String(b?.name || "")))
                        .map((category) => {
                          const isSelected = selectedCategoryId === Number(category.id);

                          return (
                            <button
                              key={category.id}
                              type="button"
                              className={`rdp-pickerOption ${isSelected ? "rgv-pickerOption--selected" : ""}`}
                              aria-pressed={isSelected}
                              disabled={isBusy}
                              onClick={() => selectRoomCategoryDialogCategory(category)}
                            >
                              <span className="rdp-pickerOptionTitle">{category.name}</span>
                              <span className="rdp-pickerOptionMeta"> {t("Adults")} {category.max_adults ?? "-"} {t("· Children")} {category.max_children ?? "-"}
                                {category.max_occupancy ? t(" · Max {p0}", { p0: category.max_occupancy }) : ""}
                              </span>
                            </button>
                          );
                        })}
                    </div>
                  )}
                </section>

                <section
                  ref={roomCategoryRateSectionRef}
                  className="rgv-roomCategorySection"
                >
                  <div className="rgv-roomCategorySectionHeader">
                    <span>{t("Room Rate")}</span>
                    {isUpgrade ? (
                      <strong>{currentRateLabel}</strong>
                    ) : dialog.selectedRate ? (
                      <strong>{dialog.selectedRate.name}</strong>
                    ) : null}
                  </div>

                  {!dialog.selectedCategory ? (
                    <div className="rdp-pickerEmpty">{t("Select a room category first.")}</div>
                  ) : isUpgrade ? (
                    <div className="rdp-pickerOptions">
                      <button
                        type="button"
                        className="rdp-pickerOption rgv-pickerOption--selected"
                        aria-pressed="true"
                        disabled
                      >
                        <span className="rdp-pickerOptionTitle"> {t("Keep booked room rate")} </span>
                        <span className="rdp-pickerOptionMeta">
                          {currentRateLabel}
                        </span>
                      </button>
                    </div>
                  ) : dialog.rates.length === 0 ? (
                    <div className="rdp-pickerEmpty">
                      {isBusy ? t("Loading room rates...") : t("No room rates available.")}
                    </div>
                  ) : (
                    <div className="rdp-pickerOptions">
                      {dialog.rates.map((rate) => {
                        const isSelected = selectedRateId === Number(rate.id);

                        return (
                          <button
                            key={rate.id}
                            type="button"
                            className={`rdp-pickerOption ${isSelected ? "rgv-pickerOption--selected" : ""}`}
                            aria-pressed={isSelected}
                            disabled={isBusy}
                            onClick={() => selectRoomCategoryDialogRate(rate)}
                          >
                            <span className="rdp-pickerOptionTitle">{rate.name}</span>
                            {rate.client?.name ? (
                              <span className="rdp-pickerOptionMeta">{rate.client.name}</span>
                            ) : null}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </section>

                <section
                  ref={roomCategoryRoomSectionRef}
                  className="rgv-roomCategorySection"
                >
                  <div className="rgv-roomCategorySectionHeader">
                    <span> {t("Room Number")} {dialog.requiresRoom ? "" : t(" (optional)")}
                    </span>
                    {dialog.selectedRoom ? (
                      <strong>{t("Room")} {dialog.selectedRoom.room_number || "-"}</strong>
                    ) : null}
                  </div>

                  {!dialog.selectedCategory ? (
                    <div className="rdp-pickerEmpty">{t("Select a room category first.")}</div>
                  ) : roomOptionsCount === 0 ? (
                    <div className="rdp-pickerEmpty">
                      {dialog.requiresRoom
                        ? t("No available room was found for every affected night.")
                        : t("No room selected. It can be assigned later.")}
                    </div>
                  ) : (
                    <>
                      {dialog.availableRooms.length > 0 && (
                        <div className="rdp-pickerGroupLabel rdp-pickerGroupLabel--available">{t("Available")}</div>
                      )}
                      {dialog.availableRooms.length > 0 && (
                        <div className="rdp-pickerOptions">
                          {sortRoomsByNumber(dialog.availableRooms).map((room) => {
                            const isSavingThisRoom = Number(dialog.savingRoomId) === Number(room.id);
                            const isSelected = selectedRoomId === Number(room.id);

                            return (
                              <button
                                key={`available-${room.id}`}
                                type="button"
                                className={`rdp-pickerOption ${isSelected ? "rgv-pickerOption--selected" : ""}`}
                                aria-pressed={isSelected}
                                disabled={isBusy || Boolean(dialog.savingRoomId)}
                                onClick={() => selectRoomCategoryDialogRoom(room, "available")}
                              >
                                <span className="rdp-pickerOptionTitle">
                                  {isSavingThisRoom ? t("Saving...") : t("Room {p0}", { p0: room.room_number || "-" })}
                                </span>
                                <span className="rdp-pickerOptionMeta rdp-pickerRoomMeta">
                                  {getRoomMetadataLines(room).map((line) => (
                                    <span key={line.label} className="rdp-pickerRoomMetaLine">
                                      <span className="rdp-pickerRoomMetaLabel">{t(line.label)}:</span>{" "}
                                      {line.value}
                                    </span>
                                  ))}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      )}

                      {dialog.occupiedRooms.length > 0 && (
                        <div className="rdp-pickerGroupLabel rdp-pickerGroupLabel--occupied">{t("Occupied / Blocked")}</div>
                      )}
                      {dialog.occupiedRooms.length > 0 && (
                        <div className="rdp-pickerOptions">
                          {sortRoomsByNumber(dialog.occupiedRooms).map((room) => {
                            const isSavingThisRoom = Number(dialog.savingRoomId) === Number(room.id);
                            const isSelected = selectedRoomId === Number(room.id);
                            const isOccupiedDisabled =
                              isNightlyCategoryChange;

                            return (
                              <button
                                key={`occupied-${room.id}`}
                                type="button"
                                className={`rdp-pickerOption rdp-pickerOption--occupied ${isSelected ? "rgv-pickerOption--selected" : ""}`}
                                aria-pressed={isSelected}
                                disabled={isBusy || Boolean(dialog.savingRoomId) || isOccupiedDisabled}
                                onClick={() => selectRoomCategoryDialogRoom(room, "occupied")}
                              >
                                <span className="rdp-pickerOptionTitle">
                                  {isSavingThisRoom ? t("Saving...") : t("Room {p0}", { p0: room.room_number || "-" })}
                                </span>
                                <span className="rdp-pickerOptionMeta rdp-pickerRoomMeta">
                                  {getRoomMetadataLines(room).map((line) => (
                                    <span key={line.label} className="rdp-pickerRoomMetaLine">
                                      <span className="rdp-pickerRoomMetaLabel">{t(line.label)}:</span>{" "}
                                      {line.value}
                                    </span>
                                  ))}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      )}

                      {dialog.selectedRoom ? (
                        <div className="rgv-roomCategoryInlineActions">
                          <button
                            type="button"
                            className="rgv-modifyStayBtn rgv-modifyStayBtn--secondary"
                            disabled={isBusy || Boolean(dialog.savingRoomId)}
                            onClick={clearRoomCategoryDialogRoom}
                          > {t("Clear room")} </button>
                        </div>
                      ) : null}
                    </>
                  )}
                </section>

                {priceReview ? (
                  <section
                    ref={roomCategoryReviewSectionRef}
                    className="rgv-roomCategorySection rgv-roomCategorySection--review"
                  >
                    <div className="rgv-roomCategorySectionHeader">
                      <span>{t("Price Review")}</span>
                    </div>
                    <div className="rgv-modifyStayPriceReview">
                      <div className="rgv-modifyStayPriceRow">
                        <span>{t("Current room price")}</span>
                        <strong>
                          {formatCategoryMoveMoney(
                            priceReview.currentRoomPrice,
                            priceReview.baseCurrencyCode ||
                              dialog.baseCurrencyCode
                          )}
                        </strong>
                      </div>
                      <div className="rgv-modifyStayPriceRow">
                        <span>{t("New room price")}</span>
                        <div className="rgv-priceInlineActions">
                          {isUpgrade ? (
                            <button
                              type="button"
                              className="rgv-priceInlineOverrideBtn rgv-priceInlineOverrideBtn--removeUpgrade"
                              disabled={isBusy}
                              onClick={() => {
                                void toggleRoomCategoryUpgradeRate(false);
                              }}
                            > {t("Remove upgrade rate")} </button>
                          ) : priceReview.hasOverride ? (
                            <button
                              type="button"
                              className="rgv-priceInlineOverrideBtn rgv-priceInlineOverrideBtn--removeOverride"
                              disabled={isBusy}
                              onClick={() => {
                                void removeInHouseRoomCategoryOverride();
                              }}
                            > {t("Remove override")} </button>
                          ) : (
                            <>
                              <button
                                type="button"
                                className="rgv-priceInlineOverrideBtn"
                                disabled={isBusy || !dialog.selectedRate}
                                onClick={() =>
                                  setRoomCategoryOverridePopupOpen(true)
                                }
                              > {t("Override")} </button>
                              {dialog.selectedCategory?.upgrade_available ? (
                                <button
                                  type="button"
                                  className="rgv-priceInlineOverrideBtn rgv-priceInlineOverrideBtn--upgrade"
                                  disabled={isBusy}
                                  onClick={() => {
                                    void toggleRoomCategoryUpgradeRate(true);
                                  }}
                                > {t("Apply upgrade rate")} </button>
                              ) : null}
                            </>
                          )}
                        </div>
                        <strong>
                          {formatCategoryMoveMoney(
                            priceReview.newRoomPrice,
                            priceReview.baseCurrencyCode ||
                              dialog.baseCurrencyCode
                          )}
                        </strong>
                      </div>
                      {isUpgrade ? (
                        <div className="rgv-modifyStayPriceRow">
                          <span>{t("Upgrade charge")}</span>
                          <strong>
                            {formatCategoryMoveMoney(
                              priceReview.upgradeChargeAmount || 0,
                              priceReview.baseCurrencyCode ||
                                dialog.baseCurrencyCode
                            )}
                          </strong>
                        </div>
                      ) : null}
                      <div className={`rgv-modifyStayPriceRow rgv-modifyStayPriceRow--${priceTone}`}>
                        <span>{t("Total difference")}</span>
                        <strong>
                          {difference > 0.005 ? "+" : ""}
                          {formatCategoryMoveMoney(
                            difference,
                            priceReview.baseCurrencyCode ||
                              dialog.baseCurrencyCode
                          )}
                        </strong>
                      </div>
                    </div>
                  </section>
                ) : null}
              </div>
            )}

            {requiresOtaReason ? (
              <label className="rgv-changeReasonField">
                <span>{t("Reason for changing this OTA reservation")} <strong>{t("Required")}</strong></span>
                <textarea
                  value={dialog.reason || ""}
                  onChange={(event) => {
                    updateRoomCategoryDialog({ reason: event.target.value, error: "" });
                  }}
                  placeholder={t("For example: Upgrade requested by the guest")}
                  rows={3}
                  maxLength={1000}
                  disabled={isBusy}
                />
                <small> {t("This note will be saved in the reservation history with your user and the change date.")} </small>
              </label>
            ) : null}

            {dialog.error ? (
              <div className="rgv-modifyStayError">{formatGroupError(dialog.error)}</div>
            ) : priceReview ? (
              <div className="rgv-modifyStayHint">
                {isUpgrade
                  ? t("Upgrade rate applied. The configured upgrade charge will be added separately when you confirm.")
                  : t("The selected nights will use the new booked and assigned category, room rate and reviewed room price. Past nights remain locked.")}
              </div>
            ) : (
              <div className="rgv-modifyStayHint"> {t("Select a category and room rate to review the new room price.")} </div>
            )}

            <div className="rgv-modifyStayActions">
              <button
                type="button"
                className="rgv-modifyStayBtn rgv-modifyStayBtn--secondary"
                onClick={closeRoomCategoryDialog}
                disabled={isBusy}
              > {t("Cancel")} </button>
              <button
                type="button"
                className="rgv-modifyStayBtn rgv-modifyStayBtn--primary"
                onClick={() => {
                  void confirmRoomCategoryCommit();
                }}
                disabled={
                  isBusy ||
                  !priceReview ||
                  (dialog.requiresRoom && !selectedRoomId) ||
                  (requiresOtaReason && !String(dialog.reason || "").trim())
                }
              >
                {isBusy ? (
                  t("Working...")
                ) : (
                  <>
                    <FiCheck size={16} aria-hidden="true" /> {t("Confirm Room & Category")} </>
                )}
              </button>
            </div>
          </div>
        </div>
      );
    })()}

    <OverridePopup
      reservationId={
        roomCategoryDialog?.mode === "nightly_category_change"
          ? null
          : roomCategoryDialog?.draftId
      }
      nightlyPrices={
        roomCategoryDialog?.mode === "nightly_category_change"
          ? roomCategoryDialog?.priceReview?.nightlyPrices || []
          : null
      }
      previousCategoryPrice={
        roomCategoryDialog?.priceReview?.currentRoomPrice ?? null
      }
      baseCurrency={
        resolveCategoryMoveBaseCurrency(
          roomCategoryDialog?.priceReview?.baseCurrencyCode,
          roomCategoryDialog?.baseCurrencyCode,
          baseCurrency
        ) || "EUR"
      }
      isOpen={Boolean(
        roomCategoryOverridePopupOpen &&
          roomCategoryDialog?.priceReview &&
          roomCategoryDialog?.selectedRate &&
          roomCategoryDialog?.selectedChangeType === "normal"
      )}
      onApplyOverrides={
        roomCategoryDialog?.mode === "nightly_category_change"
          ? applyInHouseRoomCategoryOverride
          : null
      }
      onClose={() => {
        setRoomCategoryOverridePopupOpen(false);
        if (
          roomCategoryDialog?.mode !== "nightly_category_change" &&
          roomCategoryDialog?.draftId
        ) {
          void refreshRoomCategoryDialogFromDraft().catch((err) => {
            console.warn(
              "[GROUP][ROOM_CATEGORY][OVERRIDE_REFRESH][WARN]",
              err?.response?.data || err
            );
          });
        }
      }}
      onApplied={
        roomCategoryDialog?.mode === "nightly_category_change"
          ? undefined
          : refreshRoomCategoryDialogFromDraft
      }
    />

    {ratesPricesDialog && (() => {
      const dialog = ratesPricesDialog;
      const reservation = dialog.reservation;
      const roomLabel = getReservationRoomLabel(reservation);
      const isBusy = Boolean(dialog.loading);
      const requiresOtaReason = isOtaReservation(reservation);
      const priceReview = dialog.priceReview || null;
      const difference = Number(priceReview?.difference || 0);
      const priceTone =
        difference > 0.005 ? "increase" : difference < -0.005 ? "decrease" : "same";
      const rateDifference = Number(priceReview?.rateDifference || 0);
      const overrideDifference = Number(priceReview?.overrideDifference || 0);
      const rateTone =
        rateDifference > 0.005 ? "increase" : rateDifference < -0.005 ? "decrease" : "same";
      const overrideTone =
        overrideDifference > 0.005 ? "increase" : overrideDifference < -0.005 ? "decrease" : "same";
      const selectedRateId = Number(dialog.selectedRate?.id || 0);
      const currentRateLabel = getReservationRateLabel(reservation) || t("No rate");
      const hasOverride = Boolean(dialog.hasOverride);
      const canOpenOverrides = Boolean(dialog.draftId && dialog.selectedRate);

      return (
        <div
          className={`rgv-actionModalOverlay ${ratesOverridePopupOpen ? "rgv-actionModalOverlay--belowOverride" : ""}`}
          onClick={closeRatesPricesDialog}
        >
          <div
            className="rgv-actionModal rgv-ratesPricesModal"
            role="dialog"
            aria-modal="true"
            aria-label={dialog.title || t("Rates and Prices")}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="rgv-actionModalHeader">
              <div>
                <div className="rgv-actionModalEyebrow">{t("Reservation Studio")}</div>
                <h2 className="rgv-actionModalTitle">{dialog.title || t("Rates & Prices")}</h2>
                <div className="rgv-actionModalRoom">{roomLabel}</div>
              </div>

              <button
                type="button"
                className="rgv-actionModalClose"
                onClick={closeRatesPricesDialog}
                aria-label={t("Close rates and prices")}
                disabled={isBusy}
              >
                <FiX size={18} aria-hidden="true" />
              </button>
            </div>

            <div className="rgv-modifyStaySummary">
              <span> {t("Stay:")} {dialog.checkInDate || "-"} {t("to")} {dialog.checkOutDate || "-"}
              </span>
              <span>{t("Current:")} {currentRateLabel}</span>
              <span>
                {dialog.nights > 0
                  ? t("{p0} night{p1}", { p0: dialog.nights, p1: Number(dialog.nights) === 1 ? "" : "s" })
                  : t("Invalid dates")}
              </span>
            </div>

            {!dialog.draftId && isBusy ? (
              <div className="rdp-pickerEmpty">{t("Loading rates and prices...")}</div>
            ) : (
              <div className="rgv-roomCategorySections">
                {dialog.allowRateChange ? (
                  <section className="rgv-roomCategorySection">
                    <div className="rgv-roomCategorySectionHeader">
                      <span>{t("Room Rate")}</span>
                      {dialog.selectedRate ? <strong>{getRateOptionLabel(dialog.selectedRate)}</strong> : null}
                    </div>

                    {dialog.rates.length === 0 ? (
                      <div className="rdp-pickerEmpty">{t("No room rates available.")}</div>
                    ) : (
                      <div className="rdp-pickerOptions">
                        {dialog.rates.map((rate) => {
                          const isSelected = selectedRateId === Number(rate.id);

                          return (
                            <button
                              key={rate.id}
                              type="button"
                              className={`rdp-pickerOption ${isSelected ? "rgv-pickerOption--selected" : ""}`}
                              disabled={isBusy}
                              onClick={() => selectRatesPricesDialogRate(rate)}
                            >
                              <span className="rdp-pickerOptionTitle">{getRateOptionLabel(rate)}</span>
                              {rate.client?.name ? (
                                <span className="rdp-pickerOptionMeta">{rate.client.name}</span>
                              ) : null}
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {dialog.ratesMessage && dialog.rates.length <= 1 ? (
                      <div className="rgv-modifyStayHint">{dialog.ratesMessage}</div>
                    ) : null}
                  </section>
                ) : null}

                <section className="rgv-roomCategorySection">
                  <div className="rgv-roomCategorySectionHeader">
                    <span>{t("Overrides")}</span>
                    {hasOverride ? <strong>{t("Applied")}</strong> : <strong>{t("None")}</strong>}
                  </div>

                  <div className="rgv-ratesPricesOverridePanel">
                    <div>
                      <strong>{hasOverride ? t("Override active") : t("Base nightly prices")}</strong>
                      <span>
                        {hasOverride
                          ? t("The draft price includes manual nightly override rules.")
                          : t("Use the existing override options when this reservation needs a manual price adjustment.")}
                      </span>
                    </div>
                    <div className="rgv-ratesPricesOverrideActions">
                      <button
                        type="button"
                        className="rgv-modifyStayBtn rgv-modifyStayBtn--primary"
                        disabled={isBusy || !canOpenOverrides}
                        onClick={() => setRatesOverridePopupOpen(true)}
                      > {t("Override")} </button>
                      {hasOverride ? (
                        <button
                          type="button"
                          className="rgv-modifyStayBtn rgv-modifyStayBtn--secondary"
                          disabled={isBusy}
                          onClick={() => {
                            void removeRatesPricesOverrides();
                          }}
                        > {t("Remove override")} </button>
                      ) : null}
                    </div>
                  </div>
                </section>

                {priceReview ? (
                  <section className="rgv-roomCategorySection">
                    <div className="rgv-roomCategorySectionHeader">
                      <span>{t("Price Review")}</span>
                    </div>
                    <div className="rgv-modifyStayPriceReview">
                      <div className="rgv-modifyStayPriceRow">
                        <span>{t("Current room price")}</span>
                        <strong>{formatMoney(priceReview.currentRoomPrice)}</strong>
                      </div>
                      <div className={`rgv-modifyStayPriceRow rgv-modifyStayPriceRow--${rateTone}`}>
                        <span>{t("Selected rate base")}</span>
                        <strong>
                          {formatMoney(priceReview.selectedRateBasePrice)}
                          <small className="rgv-priceBreakdownDelta">
                            {rateDifference > 0.005 ? "+" : ""}
                            {formatMoney(rateDifference)}
                          </small>
                        </strong>
                      </div>
                      {hasOverride ? (
                        <div className={`rgv-modifyStayPriceRow rgv-modifyStayPriceRow--${overrideTone}`}>
                          <span>{t("Override impact")}</span>
                          <strong>
                            {overrideDifference > 0.005 ? "+" : ""}
                            {formatMoney(overrideDifference)}
                          </strong>
                        </div>
                      ) : null}
                      <div className="rgv-modifyStayPriceRow">
                        <span>{t("Final room price")}</span>
                        <strong>{formatMoney(priceReview.newRoomPrice)}</strong>
                      </div>
                      <div className={`rgv-modifyStayPriceRow rgv-modifyStayPriceRow--${priceTone}`}>
                        <span>{t("Difference")}</span>
                        <strong>
                          {difference > 0.005 ? "+" : ""}
                          {formatMoney(difference)}
                        </strong>
                      </div>
                    </div>
                  </section>
                ) : null}
              </div>
            )}

            {requiresOtaReason ? (
              <label className="rgv-changeReasonField">
                <span>{t("Reason for changing this OTA reservation")} <strong>{t("Required")}</strong></span>
                <textarea
                  value={dialog.reason || ""}
                  onChange={(event) => {
                    updateRatesPricesDialog({ reason: event.target.value, error: "" });
                  }}
                  placeholder={t("For example: Price adjustment agreed with the guest")}
                  rows={3}
                  maxLength={1000}
                  disabled={isBusy}
                />
                <small> {t("This note will be saved in the reservation history with your user and the change date.")} </small>
              </label>
            ) : null}

            {dialog.error ? (
              <div className="rgv-modifyStayError">{formatGroupError(dialog.error)}</div>
            ) : priceReview?.hasAnyChange ? (
              <div className="rgv-modifyStayHint"> {t("Confirming will update the reservation room rate, overrides and room price.")} </div>
            ) : (
              <div className="rgv-modifyStayHint"> {t("Select a different room rate or apply an override before confirming.")} </div>
            )}

            <div className="rgv-modifyStayActions">
              <button
                type="button"
                className="rgv-modifyStayBtn rgv-modifyStayBtn--secondary"
                onClick={closeRatesPricesDialog}
                disabled={isBusy}
              > {t("Cancel")} </button>
              <button
                type="button"
                className="rgv-modifyStayBtn rgv-modifyStayBtn--primary"
                onClick={() => {
                  void confirmRatesPricesCommit();
                }}
                disabled={
                  isBusy ||
                  !priceReview?.hasAnyChange ||
                  (requiresOtaReason && !String(dialog.reason || "").trim())
                }
              >
                {isBusy ? (
                  t("Working...")
                ) : (
                  <>
                    <FiCheck size={16} aria-hidden="true" /> {t("Confirm")} {dialog.title || t("Rates & Prices")}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      );
    })()}

    <OverridePopup
      reservationId={ratesPricesDialog?.draftId}
      isOpen={Boolean(ratesOverridePopupOpen && ratesPricesDialog?.draftId)}
      onClose={() => {
        setRatesOverridePopupOpen(false);
        if (ratesPricesDialog?.draftId) {
          void refreshRatesPricesDialogFromDraft().catch((err) => {
            console.warn("[GROUP][RATES_PRICES][OVERRIDE_REFRESH][WARN]", err?.response?.data || err);
          });
        }
      }}
      onApplied={async () => {
        await refreshRatesPricesDialogFromDraft();
      }}
    />

    {reservationExtrasDialog && !reservationExtrasDialog.draftId ? (() => {
      const dialog = reservationExtrasDialog;
      const reservation = dialog.reservation;
      const roomLabel = getReservationRoomLabel(reservation);
      const isBusy = Boolean(dialog.loading || dialog.saving);

      return (
        <div
          className="rgv-actionModalOverlay"
          onClick={isBusy ? undefined : closeReservationExtrasDialog}
        >
          <div
            className="rgv-actionModal rgv-modifyStayModal"
            role="dialog"
            aria-modal="true"
            aria-label={t("Manage Extras")}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="rgv-actionModalHeader">
              <div>
                <div className="rgv-actionModalEyebrow">{t("Reservation Studio")}</div>
                <h2 className="rgv-actionModalTitle">{t("Manage Extras")}</h2>
                <div className="rgv-actionModalRoom">{roomLabel}</div>
              </div>

              <button
                type="button"
                className="rgv-actionModalClose"
                onClick={closeReservationExtrasDialog}
                aria-label={t("Close manage extras")}
                disabled={isBusy}
              >
                <FiX size={18} aria-hidden="true" />
              </button>
            </div>

            {dialog.error ? (
              <div className="rgv-modifyStayError">{formatGroupError(dialog.error)}</div>
            ) : (
              <div className="rdp-pickerEmpty">{t("Preparing extras...")}</div>
            )}

            {dialog.error ? (
              <div className="rgv-modifyStayActions">
                <button
                  type="button"
                  className="rgv-modifyStayBtn rgv-modifyStayBtn--secondary"
                  onClick={closeReservationExtrasDialog}
                > {t("Close")} </button>
              </div>
            ) : null}
          </div>
        </div>
      );
    })() : null}

    <ReservationExtrasModal
      show={Boolean(reservationExtrasDialog?.draftId)}
      onClose={closeReservationExtrasDialog}
      reservationId={reservationExtrasDialog?.draftId}
      businessUnitId={reservationExtrasDialog?.businessUnitId || effectiveBusinessUnitId}
      onSaved={handleReservationExtrasSaved}
    />

    {reservationChargesDialog && (() => {
      const dialog = reservationChargesDialog;
      const reservation = dialog.reservation;
      const roomLabel = getReservationRoomLabel(reservation);
      const draft = dialog.draft || {};
      const rows = Array.isArray(dialog.rows) ? dialog.rows : [];
      const activeTypes = (dialog.types || []).filter((type) => type?.is_active);
      const manuallySelectableTypes = activeTypes.filter(
        (type) =>
          !isWorkflowOnlyReservationChargeType(type) ||
          (draft.editingChargeId &&
            String(type.id) === String(draft.charge_type_id))
      );
      const selectedType = (dialog.types || []).find(
        (type) => String(type.id) === String(draft.charge_type_id)
      );
      const workflow = dialog.workflow || null;
      const isWorkflow = Boolean(workflow);
      const chargeTypeOptions =
        dialog.lockedChargeCode && selectedType
          ? [selectedType]
          : manuallySelectableTypes;
      const stayDateOptions = buildStayDateOptions(
        toDateInputValue(reservation?.check_in_date),
        toDateInputValue(reservation?.check_out_date)
      );
      const quantity = Number(draft.quantity || 0);
      const unitAmount = Number(draft.unit_amount || 0);
      const chargeCurrency = String(selectedType?.currency || baseCurrency || "").trim().toUpperCase();
      const previewAmount = Number.isFinite(quantity * unitAmount)
        ? quantizeBaseAmount(quantity * unitAmount, chargeCurrency)
        : 0;
      const isEditing = Boolean(draft.editingChargeId);
      const isBusy = Boolean(dialog.loading || dialog.saving || dialog.voidingChargeId);
      const requiresCancellationReason = workflow?.kind === "cancel";
      const hasCancellationReason = !requiresCancellationReason || Boolean(String(draft.cancellation_reason || "").trim());

      return (
        <div
          className="rgv-actionModalOverlay"
          onClick={isBusy ? undefined : closeReservationChargesDialog}
        >
          <div
            className="rgv-actionModal rgv-chargeModal"
            role="dialog"
            aria-modal="true"
            aria-label={workflow?.title || t("Manage Charges")}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="rgv-actionModalHeader">
              <div>
                <div className="rgv-actionModalEyebrow">{t("Reservation Studio")}</div>
                <h2 className="rgv-actionModalTitle">{workflow?.title || t("Manage Charges")}</h2>
                <div className="rgv-actionModalRoom">{roomLabel}</div>
              </div>

              <button
                type="button"
                className="rgv-actionModalClose"
                onClick={closeReservationChargesDialog}
                aria-label={t("Close manage charges")}
                disabled={isBusy}
              >
                <FiX size={18} aria-hidden="true" />
              </button>
            </div>

            {dialog.loading ? (
              <div className="rdp-pickerEmpty">{t("Loading reservation charges...")}</div>
            ) : (
              <>
                <div className="rgv-modifyStaySummary">
                  <span>{t("Posted charges:")} {dialog.totals?.posted_count || 0}</span>
                  <span>{t("Voided:")} {dialog.totals?.voided_count || 0}</span>
                  <span>{t("Total charges:")} {formatMoney(dialog.totals?.charges || 0)}</span>
                </div>

                <section className="rgv-chargeEditor">
                  <div className="rgv-roomCategorySectionHeader">
                    <span>
                      {isEditing ? t("Edit manual charge") : workflow?.editorTitle || t("Add manual charge")}
                    </span>
                    <strong>{formatMoney(previewAmount)}</strong>
                  </div>

                  <div className="rgv-chargeGrid">
                    {requiresCancellationReason ? (
                      <label className="rgv-chargeField rgv-chargeField--full">
                        <span>{workflow?.reasonLabel || t("Reason for cancellation")}</span>
                        <textarea
                          className="rgv-chargeInput rgv-chargeTextarea"
                          rows={3}
                          value={draft.cancellation_reason || ""}
                          onChange={(event) =>
                            updateReservationChargeDraft("cancellation_reason", event.target.value)
                          }
                          disabled={isBusy}
                          placeholder={t("Why is this reservation being cancelled?")}
                        />
                      </label>
                    ) : null}

                    <label className="rgv-chargeField">
                      <span>{t("Charge type")}</span>
                      <select
                        className="rgv-chargeInput"
                        value={draft.charge_type_id || ""}
                        onChange={(event) => updateReservationChargeDraft("charge_type_id", event.target.value)}
                        disabled={isBusy || isEditing || Boolean(dialog.lockedChargeCode)}
                      >
                        {!dialog.lockedChargeCode ? <option value="">{t("Choose charge type")}</option> : null}
                        {dialog.lockedChargeCode && !selectedType ? (
                          <option value="">{t("Charge type unavailable")}</option>
                        ) : null}
                        {chargeTypeOptions.map((type) => (
                          <option key={type.id} value={type.id}>
                            {type.name}
                          </option>
                        ))}
                      </select>
                    </label>

                    <label className="rgv-chargeField">
                      <span>{t("Service date")}</span>
                      <input
                        className="rgv-chargeInput"
                        type="date"
                        value={draft.service_date || ""}
                        onChange={(event) => updateReservationChargeDraft("service_date", event.target.value)}
                        disabled={isBusy}
                      />
                    </label>

                    <label className="rgv-chargeField">
                      <span>{t("Stay date")}</span>
                      <select
                        className="rgv-chargeInput"
                        value={draft.stay_date || ""}
                        onChange={(event) => updateReservationChargeDraft("stay_date", event.target.value)}
                        disabled={isBusy || !stayDateOptions.length}
                      >
                        <option value="">{t("Not linked to a stay date")}</option>
                        {stayDateOptions.map((date) => (
                          <option key={date} value={date}>
                            {date}
                          </option>
                        ))}
                      </select>
                    </label>

                    <label className="rgv-chargeField">
                      <span>{t("Quantity")}</span>
                      <input
                        className="rgv-chargeInput"
                        type="number"
                        min="0"
                        step="1"
                        value={draft.quantity || ""}
                        onChange={(event) => updateReservationChargeDraft("quantity", event.target.value)}
                        disabled={isBusy}
                      />
                    </label>

                    <label className="rgv-chargeField">
                      <span>{t("Unit amount")}</span>
                      <input
                        className="rgv-chargeInput"
                        type="number"
                        min="0"
                        step={currencyAmountStep(chargeCurrency, currencyCatalog)}
                        inputMode={getCurrencyMinorUnits(chargeCurrency, currencyCatalog) === 0 ? "numeric" : "decimal"}
                        value={draft.unit_amount || ""}
                        onChange={(event) => updateReservationChargeDraft("unit_amount", event.target.value)}
                        disabled={isBusy}
                      />
                    </label>

                    <label className="rgv-chargeField rgv-chargeField--wide">
                      <span>{workflow?.descriptionLabel || t("Description")}</span>
                      <input
                        className="rgv-chargeInput"
                        type="text"
                        value={draft.description || ""}
                        onChange={(event) => updateReservationChargeDraft("description", event.target.value)}
                        disabled={isBusy}
                        placeholder={
                          workflow?.descriptionPlaceholder ||
                          selectedType?.description ||
                          t("Internal charge note")
                        }
                      />
                    </label>
                  </div>

                  <div className="rgv-chargeEditorActions">
                    {isEditing ? (
                      <button
                        type="button"
                        className="rgv-modifyStayBtn rgv-modifyStayBtn--secondary"
                        onClick={resetReservationChargeDraft}
                        disabled={isBusy}
                      > {t("Cancel edit")} </button>
                    ) : null}
                    {isWorkflow && !isEditing ? (
                      <button
                        type="button"
                        className="rgv-modifyStayBtn rgv-modifyStayBtn--secondary"
                        onClick={() => {
                          void completeReservationChargeWorkflow({ waive: true });
                        }}
                        disabled={isBusy || !hasCancellationReason}
                      > {t("Waive Charge")} </button>
                    ) : null}
                    <button
                      type="button"
                      className="rgv-modifyStayBtn rgv-modifyStayBtn--primary"
                      onClick={() => {
                        void saveReservationCharge();
                      }}
                      disabled={isBusy || !selectedType || !hasCancellationReason}
                    >
                      {dialog.saving
                        ? isWorkflow
                          ? t("Processing...")
                          : t("Saving...")
                        : isEditing
                          ? t("Save Charge")
                          : t("Add Charge")}
                    </button>
                  </div>
                </section>

                <section className="rgv-chargeList">
                  <div className="rgv-roomCategorySectionHeader">
                    <span>{t("Reservation charges")}</span>
                    <strong>{formatMoney(dialog.totals?.charges || 0)}</strong>
                  </div>

                  {rows.length ? (
                    rows.map((charge) => {
                      const isVoided = String(charge.status || "") === "voided";
                      const canEditCharge = !isVoided && Boolean(charge.is_editable);
                      const isVoidableProtectedCharge = isVoidableProtectedReservationCharge(charge);
                      const canVoidCharge = !isVoided && (Boolean(charge.is_editable) || isVoidableProtectedCharge);
                      const serviceDate = toDateInputValue(charge.service_date) || "-";
                      const stayDate = toDateInputValue(charge.stay_date);

                      return (
                        <div
                          key={charge.id}
                          className={`rgv-chargeRow ${isVoided ? "rgv-chargeRow--voided" : ""}`}
                        >
                          <div className="rgv-chargeRowMain">
                            <strong>{charge.charge_name || t("Reservation charge")}</strong>
                            <span>
                              {serviceDate}
                              {stayDate ? t(" · Stay {p0}", { p0: stayDate }) : ""}
                              {charge.description ? ` · ${charge.description}` : ""}
                            </span>
                            <span>
                              {charge.source === "early_checkout" ? t("Early check-out") : t(charge.source || "manual")}
                              {charge.is_system_generated ? t(" · system") : ""}
                              {!charge.is_editable ? t(" · protected") : ""}
                              {isVoidableProtectedCharge && !isVoided ? t(" · voidable") : ""}
                              {isVoided ? t(" · voided") : ""}
                            </span>
                          </div>
                          <div className="rgv-chargeRowAmount">
                            <strong>{formatMoney(charge.amount)}</strong>
                            <span>
                              {Number(charge.quantity || 0)} x {formatMoney(charge.unit_amount)}
                            </span>
                          </div>
                          <div className="rgv-chargeRowActions">
                            <button
                              type="button"
                              className="rgv-chargeMiniBtn"
                              onClick={() => startEditReservationCharge(charge)}
                              disabled={isBusy || !canEditCharge}
                            > {t("Edit")} </button>
                            <button
                              type="button"
                              className="rgv-chargeMiniBtn rgv-chargeMiniBtn--danger"
                              onClick={() => {
                                void voidReservationCharge(charge);
                              }}
                              disabled={isBusy || !canVoidCharge}
                            >
                              {dialog.voidingChargeId === charge.id ? t("Voiding...") : t("Void")}
                            </button>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="rdp-pickerEmpty">{t("No reservation charges yet.")}</div>
                  )}
                </section>
              </>
            )}

            {dialog.error ? (
              <div className="rgv-modifyStayError">{formatGroupError(dialog.error)}</div>
            ) : null}

            <div className="rgv-modifyStayActions">
              <button
                type="button"
                className="rgv-modifyStayBtn rgv-modifyStayBtn--secondary"
                onClick={closeReservationChargesDialog}
                disabled={isBusy}
              > {t("Close")} </button>
            </div>
          </div>
        </div>
      );
    })()}

    {modifyStayDialog && (() => {
      const reservation = modifyStayDialog.reservation;
      const roomLabel = getReservationRoomLabel(reservation);
      const nights = countDateInputNights(
        modifyStayDialog.checkInDate,
        modifyStayDialog.checkOutDate
      );
      const isBusy = Boolean(modifyStayDialog.loading);
      const isInHouseStay = Boolean(modifyStayDialog.isInHouseStay);
      const priceReview = modifyStayDialog.priceReview || null;
      const difference = Number(priceReview?.difference || 0);
      const priceTone =
        difference > 0.005 ? "increase" : difference < -0.005 ? "decrease" : "same";

      return (
        <div
          className="rgv-actionModalOverlay"
          onClick={closeModifyStayDialog}
        >
          <div
            className="rgv-actionModal rgv-modifyStayModal"
            role="dialog"
            aria-modal="true"
            aria-label={t("Modify Stay")}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="rgv-actionModalHeader">
              <div>
                <div className="rgv-actionModalEyebrow">{t("Reservation Studio")}</div>
                <h2 className="rgv-actionModalTitle">
                  {priceReview ? t("Review Price Change") : t("Modify Stay")}
                </h2>
                <div className="rgv-actionModalRoom">{roomLabel}</div>
              </div>

              <button
                type="button"
                className="rgv-actionModalClose"
                onClick={closeModifyStayDialog}
                aria-label={t("Close modify stay")}
                disabled={isBusy}
              >
                <FiX size={18} aria-hidden="true" />
              </button>
            </div>

            <div className="rgv-modifyStaySummary">
              <span> {t("Current stay:")} {modifyStayDialog.originalCheckInDate || "-"} {t("to")}{" "}
                {modifyStayDialog.originalCheckOutDate || "-"}
              </span>
              <span> {t("New stay:")} {modifyStayDialog.checkInDate || "-"} {t("to")}{" "}
                {modifyStayDialog.checkOutDate || "-"}
              </span>
              <span>
                {nights > 0 ? t("{p0} night{p1}", { p0: nights, p1: nights === 1 ? "" : "s" }) : t("Invalid dates")}
              </span>
            </div>

            {priceReview ? (
              <div className="rgv-modifyStayPriceReview">
                <div className="rgv-modifyStayPriceRow">
                  <span>{t("Current room price")}</span>
                  <strong>{formatMoney(priceReview.currentRoomPrice)}</strong>
                </div>
                <div className="rgv-modifyStayPriceRow">
                  <span>{t("New room price")}</span>
                  <strong>{formatMoney(priceReview.newRoomPrice)}</strong>
                </div>
                <div className={`rgv-modifyStayPriceRow rgv-modifyStayPriceRow--${priceTone}`}>
                  <span>{t("Difference")}</span>
                  <strong>
                    {difference > 0.005 ? "+" : ""}
                    {formatMoney(difference)}
                  </strong>
                </div>
              </div>
            ) : (
              <div className="rgv-modifyStayGrid">
                <label className="rgv-modifyStayField">
                  <span>{t("Check-in")}</span>
                  <input
                    type="date"
                    value={modifyStayDialog.checkInDate || ""}
                    disabled={isBusy || isInHouseStay}
                    onChange={(event) =>
                      updateModifyStayDialog({ checkInDate: event.target.value, error: "" })
                    }
                  />
                </label>

                <label className="rgv-modifyStayField">
                  <span>{t("Check-out")}</span>
                  <input
                    type="date"
                    value={modifyStayDialog.checkOutDate || ""}
                    min={modifyStayDialog.checkInDate || undefined}
                    disabled={isBusy}
                    onChange={(event) =>
                      updateModifyStayDialog({ checkOutDate: event.target.value, error: "" })
                    }
                  />
                </label>
              </div>
            )}

            {priceReview && isOtaReservation(reservation) ? (
              <label className="rgv-changeReasonField">
                <span> {t("Change reason")} <strong>{t("Required for OTA reservations")}</strong>
                </span>
                <textarea
                  rows="3"
                  value={modifyStayDialog.reason || ""}
                  disabled={isBusy}
                  onChange={(event) =>
                    updateModifyStayDialog({
                      reason: event.target.value,
                      error: "",
                    })
                  }
                  placeholder={t("Why is the PMS stay being changed?")}
                />
              </label>
            ) : null}

            {modifyStayDialog.error ? (
              <div className="rgv-modifyStayError">{formatGroupError(modifyStayDialog.error)}</div>
            ) : priceReview ? (
              <div className="rgv-modifyStayHint">
                {isInHouseStay
                  ? t("Confirming will update the reservation check-out and the recalculated room price.")
                  : t("Confirming will update the reservation with the new stay dates and the recalculated room price.")}
              </div>
            ) : (
              <div className="rgv-modifyStayHint">
                {isInHouseStay
                  ? t("Check-in is locked because the guest is already in house. Pilcore will keep the existing occupancy, category, room rate and room number when they are still valid. Prices will be recalculated.")
                  : t("Pilcore will keep the existing occupancy, category, room rate and room number when they are still valid. Prices will be recalculated.")}
              </div>
            )}

            <div className="rgv-modifyStayActions">
              <button
                type="button"
                className="rgv-modifyStayBtn rgv-modifyStayBtn--secondary"
                onClick={() => {
                  if (priceReview) {
                    void resetModifyStayPreparedDraft();
                    return;
                  }
                  closeModifyStayDialog();
                }}
                disabled={isBusy}
              >
                {priceReview ? t("Back") : t("Cancel")}
              </button>
              <button
                type="button"
                className="rgv-modifyStayBtn rgv-modifyStayBtn--primary"
                onClick={() => {
                  if (priceReview) {
                    void confirmModifyStayCommit();
                    return;
                  }
                  void runModifyStayWorkflow();
                }}
                disabled={isBusy}
              >
                {isBusy ? (
                  priceReview ? t("Confirming...") : t("Applying...")
                ) : priceReview ? (
                  <>
                    <FiCheck size={16} aria-hidden="true" /> {t("Confirm Modify Stay")} </>
                ) : (
                  <>
                    <FiCheck size={16} aria-hidden="true" /> {t("Apply Modify Stay")} </>
                )}
              </button>
            </div>
          </div>
        </div>
      );
    })()}

    {guestOccupancyDialog && (() => {
      const reservation = guestOccupancyDialog.reservation;
      const sourceSnapshot = getModifyStaySourceSnapshot(reservation);
      const roomLabel = getReservationRoomLabel(reservation);
      const nights = countDateInputNights(
        guestOccupancyDialog.checkInDate,
        guestOccupancyDialog.checkOutDate
      );
      const isBusy = Boolean(guestOccupancyDialog.loading);
      const priceReview = guestOccupancyDialog.priceReview || null;
      const difference = Number(priceReview?.difference || 0);
      const priceTone =
        difference > 0.005 ? "increase" : difference < -0.005 ? "decrease" : "same";
      const currentAdultsValue = Number(guestOccupancyDialog.adults || 0);
      const currentChildrenValue = Number(guestOccupancyDialog.children || 0);
      const adultsMaxOptions = [
        sourceSnapshot.categoryMaxAdults,
        sourceSnapshot.categoryMaxOccupancy != null && Number(sourceSnapshot.categoryMaxOccupancy) > 0
          ? Math.max(1, Number(sourceSnapshot.categoryMaxOccupancy) - currentChildrenValue)
          : null,
      ].filter((value) => value != null && Number.isFinite(Number(value)));
      const childrenMaxOptions = [
        sourceSnapshot.categoryMaxChildren,
        sourceSnapshot.categoryMaxOccupancy != null && Number(sourceSnapshot.categoryMaxOccupancy) > 0
          ? Math.max(0, Number(sourceSnapshot.categoryMaxOccupancy) - currentAdultsValue)
          : null,
      ].filter((value) => value != null && Number.isFinite(Number(value)));
      const adultsInputMax = adultsMaxOptions.length ? Math.min(...adultsMaxOptions.map(Number)) : null;
      const childrenInputMax = childrenMaxOptions.length ? Math.min(...childrenMaxOptions.map(Number)) : null;

      return (
        <div
          className="rgv-actionModalOverlay"
          onClick={closeGuestOccupancyDialog}
        >
          <div
            className="rgv-actionModal rgv-modifyStayModal"
            role="dialog"
            aria-modal="true"
            aria-label={t("Guests and Occupancy")}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="rgv-actionModalHeader">
              <div>
                <div className="rgv-actionModalEyebrow">{t("Reservation Studio")}</div>
                <h2 className="rgv-actionModalTitle">
                  {priceReview ? t("Review Price Change") : t("Guests & Occupancy")}
                </h2>
                <div className="rgv-actionModalRoom">{roomLabel}</div>
              </div>

              <button
                type="button"
                className="rgv-actionModalClose"
                onClick={closeGuestOccupancyDialog}
                aria-label={t("Close guests and occupancy")}
                disabled={isBusy}
              >
                <FiX size={18} aria-hidden="true" />
              </button>
            </div>

            <div className="rgv-modifyStaySummary">
              <span> {t("Stay:")} {guestOccupancyDialog.checkInDate || "-"} {t("to")}{" "}
                {guestOccupancyDialog.checkOutDate || "-"}
              </span>
              <span> {t("Current:")} {guestOccupancyDialog.originalAdults} {t(Number(guestOccupancyDialog.originalAdults) === 1 ? "adult" : "adults")} ·{" "}
                {guestOccupancyDialog.originalChildren} {t(Number(guestOccupancyDialog.originalChildren) === 1 ? "child" : "children")}
              </span>
              <span>
                {nights > 0 ? t("{p0} night{p1}", { p0: nights, p1: nights === 1 ? "" : "s" }) : t("Invalid dates")}
              </span>
            </div>

            {priceReview ? (
              <>
                <div className="rgv-modifyStaySummary">
                  <span> {t("New:")} {priceReview.adults} {t(Number(priceReview.adults) === 1 ? "adult" : "adults")} · {priceReview.children} {t(Number(priceReview.children) === 1 ? "child" : "children")}
                  </span>
                </div>
                <div className="rgv-modifyStayPriceReview">
                  <div className="rgv-modifyStayPriceRow">
                    <span>{t("Current room price")}</span>
                    <strong>{formatMoney(priceReview.currentRoomPrice)}</strong>
                  </div>
                  <div className="rgv-modifyStayPriceRow">
                    <span>{t("New room price")}</span>
                    <strong>{formatMoney(priceReview.newRoomPrice)}</strong>
                  </div>
                  <div className={`rgv-modifyStayPriceRow rgv-modifyStayPriceRow--${priceTone}`}>
                    <span>{t("Difference")}</span>
                    <strong>
                      {difference > 0.005 ? "+" : ""}
                      {formatMoney(difference)}
                    </strong>
                  </div>
                </div>
              </>
            ) : (
              <div className="rgv-modifyStayGrid">
                <label className="rgv-modifyStayField">
                  <span>{t("Adults")}</span>
                  <input
                    type="number"
                    min="1"
                    max={adultsInputMax ?? undefined}
                    step="1"
                    value={guestOccupancyDialog.adults ?? ""}
                    disabled={isBusy}
                    onChange={(event) =>
                      updateGuestOccupancyDialog({
                        adults: event.target.value === "" ? "" : Number(event.target.value),
                        error: "",
                      })
                    }
                  />
                </label>

                <label className="rgv-modifyStayField">
                  <span>{t("Children")}</span>
                  <input
                    type="number"
                    min="0"
                    max={childrenInputMax ?? undefined}
                    step="1"
                    value={guestOccupancyDialog.children ?? ""}
                    disabled={isBusy}
                    onChange={(event) =>
                      updateGuestOccupancyDialog({
                        children: event.target.value === "" ? "" : Number(event.target.value),
                        error: "",
                      })
                    }
                  />
                </label>
              </div>
            )}

            {priceReview && isOtaReservation(reservation) ? (
              <label className="rgv-changeReasonField">
                <span> {t("Change reason")} <strong>{t("Required for OTA reservations")}</strong>
                </span>
                <textarea
                  rows="3"
                  value={guestOccupancyDialog.reason || ""}
                  disabled={isBusy}
                  onChange={(event) =>
                    updateGuestOccupancyDialog({
                      reason: event.target.value,
                      error: "",
                    })
                  }
                  placeholder={t("Why is the OTA reservation occupancy being changed?")}
                />
              </label>
            ) : null}

            {guestOccupancyDialog.error ? (
              <div className="rgv-modifyStayError">{formatGroupError(guestOccupancyDialog.error)}</div>
            ) : priceReview ? (
              <div className="rgv-modifyStayHint"> {t("Confirming will update the reservation occupancy and the recalculated room price.")} </div>
            ) : (
              <div className="rgv-modifyStayHint"> {t("Pilcore will keep the existing dates, category, room rate and room number when they are still valid. Prices will be recalculated.")} </div>
            )}

            <div className="rgv-modifyStayActions">
              <button
                type="button"
                className="rgv-modifyStayBtn rgv-modifyStayBtn--secondary"
                onClick={() => {
                  if (priceReview) {
                    void resetGuestOccupancyPreparedDraft();
                    return;
                  }
                  closeGuestOccupancyDialog();
                }}
                disabled={isBusy}
              >
                {priceReview ? t("Back") : t("Cancel")}
              </button>
              <button
                type="button"
                className="rgv-modifyStayBtn rgv-modifyStayBtn--primary"
                onClick={() => {
                  if (priceReview) {
                    void confirmGuestOccupancyCommit();
                    return;
                  }
                  void runGuestOccupancyWorkflow();
                }}
                disabled={isBusy}
              >
                {isBusy ? (
                  priceReview ? t("Confirming...") : t("Applying...")
                ) : priceReview ? (
                  <>
                    <FiCheck size={16} aria-hidden="true" /> {t("Confirm Guests & Occupancy")} </>
                ) : (
                  <>
                    <FiCheck size={16} aria-hidden="true" /> {t("Apply Guests & Occupancy")} </>
                )}
              </button>
            </div>
          </div>
        </div>
      );
    })()}

    {earlyCheckOutDialog && (() => {
      const dialog = earlyCheckOutDialog;
      const reservation = dialog.reservation;
      const roomLabel = getReservationRoomLabel(reservation);
      const isBusy = Boolean(dialog.loading || dialog.saving);
      const totals = getEarlyCheckOutTotals(dialog);
      const sourceSnapshot = getModifyStaySourceSnapshot(reservation);
      const canManagePendingNightCharges = !sourceSnapshot.groupRateId;
      const pendingCount = dialog.pendingNights?.length || 0;
      const forgivenCount = (dialog.pendingNights || []).filter((night) => night.decision === "forgive").length;
      const retainedCount = pendingCount - forgivenCount;

      return (
        <div
          className="rgv-actionModalOverlay"
          onClick={isBusy ? undefined : closeEarlyCheckOutDialog}
        >
          <div
            className="rgv-actionModal rgv-earlyCheckOutModal"
            role="dialog"
            aria-modal="true"
            aria-label={t("Early check-out review")}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="rgv-actionModalHeader">
              <div>
                <div className="rgv-actionModalEyebrow">{t("Reservation Studio")}</div>
                <h2 className="rgv-actionModalTitle">{t("Early Check-Out")}</h2>
                <div className="rgv-actionModalRoom">{roomLabel}</div>
              </div>

              <button
                type="button"
                className="rgv-actionModalClose"
                onClick={closeEarlyCheckOutDialog}
                aria-label={t("Close early check-out")}
                disabled={isBusy}
              >
                <FiX size={18} aria-hidden="true" />
              </button>
            </div>

            <div className="rgv-modifyStaySummary">
              <span>{t("Departure:")} {dialog.departureDate || "-"}</span>
              <span>{t("Original check-out:")} {dialog.originalCheckOutDate || "-"}</span>
              <span>{pendingCount} {t(pendingCount === 1 ? "pending night" : "pending nights")}</span>
            </div>

            {dialog.loading ? (
              <div className="rdp-pickerEmpty">{t("Loading pending nights...")}</div>
            ) : (
              <div className="rgv-roomCategorySections">
                <section className="rgv-roomCategorySection">
                  <div className="rgv-roomCategorySectionHeader">
                    <span>{t("Early departure value")}</span>
                    <strong>{formatMoney(totals.pendingValue)}</strong>
                  </div>
                  <div className="rgv-modifyStayPriceReview">
                    <div className="rgv-modifyStayPriceRow rgv-modifyStayPriceRow--increase">
                      <span>{t("Unconsumed room value")}</span>
                      <strong>{formatMoney(totals.pendingValue)}</strong>
                    </div>
                    <div className="rgv-modifyStayPriceRow rgv-modifyStayPriceRow--decrease">
                      <span>{t("Waived amount")}</span>
                      <strong>{formatMoney(totals.forgivenValue)}</strong>
                    </div>
                    <div className="rgv-modifyStayPriceRow">
                      <span>{t("Kept as charge")}</span>
                      <strong>{formatMoney(totals.retainedValue)}</strong>
                    </div>
                  </div>
                </section>

                <section className="rgv-roomCategorySection">
                  <div className="rgv-roomCategorySectionHeader">
                    <span>{t("Pending nights")}</span>
                    <strong>
                      {retainedCount} {t("charge ·")} {forgivenCount} {t("waived")} </strong>
                  </div>

                  <div className="rgv-earlyCheckOutBulkActions">
                    <button
                      type="button"
                      className="rgv-modifyStayBtn rgv-modifyStayBtn--secondary"
                      onClick={() => setAllEarlyCheckOutNightDecisions("charge")}
                      disabled={isBusy || !canManagePendingNightCharges}
                    > {t("Charge all")} </button>
                    <button
                      type="button"
                      className="rgv-modifyStayBtn rgv-modifyStayBtn--secondary"
                      onClick={() => setAllEarlyCheckOutNightDecisions("forgive")}
                      disabled={isBusy || !canManagePendingNightCharges}
                    > {t("Waive all")} </button>
                  </div>

                  <div className="rgv-earlyCheckOutNightList">
                    {(dialog.pendingNights || []).map((night) => {
                      const isForgiven = night.decision === "forgive";

                      return (
                        <div
                          key={night.date}
                          className={`rgv-earlyCheckOutNight ${isForgiven ? "rgv-earlyCheckOutNight--forgiven" : ""}`}
                        >
                          <div className="rgv-earlyCheckOutNightInfo">
                            <strong>{night.date}</strong>
                            <span>
                              {formatMoney(night.price)}
                              {night.hasOverride ? t(" · override included") : ""}
                            </span>
                          </div>
                          <div className="rgv-earlyCheckOutDecision">
                            <button
                              type="button"
                              className={`rgv-earlyCheckOutDecisionBtn ${!isForgiven ? "rgv-earlyCheckOutDecisionBtn--selected" : ""}`}
                              onClick={() => updateEarlyCheckOutNightDecision(night.date, "charge")}
                              disabled={isBusy || !canManagePendingNightCharges}
                            > {t("Charge")} </button>
                            <button
                              type="button"
                              className={`rgv-earlyCheckOutDecisionBtn ${isForgiven ? "rgv-earlyCheckOutDecisionBtn--selected" : ""}`}
                              onClick={() => updateEarlyCheckOutNightDecision(night.date, "forgive")}
                              disabled={isBusy || !canManagePendingNightCharges}
                            > {t("Waive")} </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>

                <section className="rgv-roomCategorySection">
                  <div className="rgv-roomCategorySectionHeader">
                    <span>{t("Consumed nights")}</span>
                    <strong>{formatMoney(totals.consumedValue)}</strong>
                  </div>

                  <div className="rgv-ratesPricesOverridePanel">
                    <div>
                      <strong>{t("Adjust previous nights")}</strong>
                      <span> {t("Open the existing override flow if already-consumed nights need a manual price adjustment.")} </span>
                    </div>
                    <div className="rgv-ratesPricesOverrideActions">
                      <button
                        type="button"
                        className="rgv-modifyStayBtn rgv-modifyStayBtn--primary"
                        onClick={openPreviousNightsOverrideFromEarlyCheckOut}
                        disabled={isBusy}
                      > {t("Override")} </button>
                    </div>
                  </div>
                </section>
              </div>
            )}

            {!canManagePendingNightCharges ? (
              <div className="rgv-modifyStayHint"> {t("This reservation uses Group Rate, so early check-out charge conversion is blocked for now.")} </div>
            ) : null}

            {dialog.error ? (
              <div className="rgv-modifyStayError">{formatGroupError(dialog.error)}</div>
            ) : (
              <div className="rgv-modifyStayHint"> {t("Completing check-out removes pending nights from room price. Charged nights are posted as protected Reservation Charges.")} </div>
            )}

            <div className="rgv-modifyStayActions">
              <button
                type="button"
                className="rgv-modifyStayBtn rgv-modifyStayBtn--secondary"
                onClick={closeEarlyCheckOutDialog}
                disabled={isBusy}
              > {t("Back")} </button>
              <button
                type="button"
                className="rgv-modifyStayBtn rgv-modifyStayBtn--primary"
                onClick={() => {
                  void confirmEarlyCheckOut();
                }}
                disabled={isBusy || dialog.loading || !canManagePendingNightCharges}
              >
                {dialog.saving ? (
                  t("Checking out...")
                ) : (
                  <>
                    <FiCheck size={16} aria-hidden="true" /> {t("Complete Check-Out")} </>
                )}
              </button>
            </div>
          </div>
        </div>
      );
    })()}

    <PmsConfirmDialog
      isOpen={Boolean(checkInDialogState)}
      eyebrow={t("Reservation Studio")}
      title={t(checkInDialogState?.title || "Check-in")}
      message={checkInDialogState?.message || ""}
      confirmLabel={t("Understood")}
      showCancel={false}
      onClose={() => setCheckInDialogState(null)}
      onConfirm={() => setCheckInDialogState(null)}
      tone={checkInDialogState?.tone || "warning"}
      closeOnOverlayClick={false}
      closeOnEscape={false}
    />

    <PmsConfirmDialog
      isOpen={Boolean(reservationActionFeedbackState)}
      eyebrow={t("Reservation Studio")}
      title={t(reservationActionFeedbackState?.title || "Reservation action")}
      message={reservationActionFeedbackState?.message || ""}
      confirmLabel={t("Understood")}
      showCancel={false}
      onClose={() => setReservationActionFeedbackState(null)}
      onConfirm={() => setReservationActionFeedbackState(null)}
      tone={reservationActionFeedbackState?.tone || "primary"}
    />

    <PmsConfirmDialog
      isOpen={Boolean(pendingNoShowReservation)}
      eyebrow={t("Reservation Studio")}
      title={t("Mark reservation as No Show?")}
      message={
        pendingNoShowReservation
          ? t("This will update {p0} to No Show and remove it from active check-in workflows. No no-show charge will be applied.", { p0: pendingNoShowReservation.locator || t("reservation #{p0}", { p0: pendingNoShowReservation.id }) })
          : ""
      }
      confirmLabel={
        pendingNoShowReservation &&
        arrivalActionLoadingByReservationId[Number(pendingNoShowReservation.id)]
          ? t("Saving...")
          : t("No Show")
      }
      cancelLabel={t("Back")}
      onClose={() => setPendingNoShowReservation(null)}
      onConfirm={() => {
        if (pendingNoShowReservation) {
          void handleMarkReservationNoShow(pendingNoShowReservation);
        }
      }}
      isBusy={Boolean(
        pendingNoShowReservation &&
          arrivalActionLoadingByReservationId[Number(pendingNoShowReservation.id)]
      )}
      tone="warning"
    />

    <PmsConfirmDialog
      isOpen={Boolean(pendingReverseCheckInReservation)}
      eyebrow={t("Reservation Studio")}
      title={t("Reverse check-in?")}
      message={
        pendingReverseCheckInReservation
          ? t("This will move {p0} back to Confirmed and clear the arrival timestamp. This is only available on the check-in date.", { p0: pendingReverseCheckInReservation.locator || t("reservation #{p0}", { p0: pendingReverseCheckInReservation.id }) })
          : ""
      }
      confirmLabel={
        pendingReverseCheckInReservation &&
        arrivalActionLoadingByReservationId[Number(pendingReverseCheckInReservation.id)]
          ? t("Reversing...")
          : t("Reverse Check-In")
      }
      cancelLabel={t("Back")}
      onClose={() => setPendingReverseCheckInReservation(null)}
      onConfirm={() => {
        if (pendingReverseCheckInReservation) {
          void handleReverseCheckInReservation(pendingReverseCheckInReservation);
        }
      }}
      isBusy={Boolean(
        pendingReverseCheckInReservation &&
          arrivalActionLoadingByReservationId[Number(pendingReverseCheckInReservation.id)]
      )}
      tone="warning"
    />

    <PmsConfirmDialog
      isOpen={Boolean(pendingCheckOutReservation)}
      eyebrow={t("Reservation Studio")}
      title={t("Complete check-out?")}
      message={
        pendingCheckOutReservation
          ? t("This will move {p0} to Checked Out and record the departure time.", { p0: pendingCheckOutReservation.locator || t("reservation #{p0}", { p0: pendingCheckOutReservation.id }) })
          : ""
      }
      confirmLabel={
        pendingCheckOutReservation &&
        arrivalActionLoadingByReservationId[Number(pendingCheckOutReservation.id)]
          ? t("Checking out...")
          : t("Check-Out")
      }
      cancelLabel={t("Back")}
      onClose={() => setPendingCheckOutReservation(null)}
      onConfirm={() => {
        if (pendingCheckOutReservation) {
          void handleCheckOutReservation(pendingCheckOutReservation);
        }
      }}
      isBusy={Boolean(
        pendingCheckOutReservation &&
          arrivalActionLoadingByReservationId[Number(pendingCheckOutReservation.id)]
      )}
      tone="primary"
    />

    <PmsConfirmDialog
      isOpen={Boolean(pendingReinstateReservation)}
      eyebrow={t("Reservation Studio")}
      title={t("Reinstate reservation?")}
      message={
        pendingReinstateReservation
          ? t("This will restore {p0} and consume availability again if the stay dates are still free.", { p0: pendingReinstateReservation.locator || t("reservation #{p0}", { p0: pendingReinstateReservation.id }) })
          : ""
      }
      confirmLabel={
        pendingReinstateReservation && reinstateLoadingId === Number(pendingReinstateReservation.id)
          ? t("Restoring...")
          : t("Reinstate")
      }
      cancelLabel={t("Back")}
      onClose={() => setPendingReinstateReservation(null)}
      onConfirm={() => {
        if (pendingReinstateReservation) {
          void handleReinstateReservation(pendingReinstateReservation);
        }
      }}
      isBusy={Boolean(
        pendingReinstateReservation &&
          reinstateLoadingId === Number(pendingReinstateReservation.id)
      )}
      tone="primary"
    />

    <PmsConfirmDialog
      isOpen={Boolean(pendingRevertNoShowReservation)}
      eyebrow={t("Reservation Studio")}
      title={t("Revert No Show?")}
      message={
        pendingRevertNoShowReservation
          ? t("This will move {p0} back to Confirmed so it returns to the active check-in workflow.", { p0: pendingRevertNoShowReservation.locator || t("reservation #{p0}", { p0: pendingRevertNoShowReservation.id }) })
          : ""
      }
      confirmLabel={
        pendingRevertNoShowReservation &&
        arrivalActionLoadingByReservationId[Number(pendingRevertNoShowReservation.id)]
          ? t("Saving...")
          : t("Revert No Show")
      }
      cancelLabel={t("Back")}
      onClose={() => setPendingRevertNoShowReservation(null)}
      onConfirm={() => {
        if (pendingRevertNoShowReservation) {
          void handleRevertReservationNoShow(pendingRevertNoShowReservation);
        }
      }}
      isBusy={Boolean(
        pendingRevertNoShowReservation &&
          arrivalActionLoadingByReservationId[Number(pendingRevertNoShowReservation.id)]
      )}
      tone="primary"
    />

    <PmsConfirmDialog
      isOpen={Boolean(reinstateDialogState)}
      eyebrow={t("Reservation Studio")}
      title={reinstateDialogState?.title || t("Reservation update")}
      message={reinstateDialogState?.message || ""}
      confirmLabel={t("Understood")}
      onClose={closeReinstateDialog}
      onConfirm={closeReinstateDialog}
      tone={reinstateDialogState?.tone || "primary"}
      showCancel={false}
    />

    {isSaving && (
      <div
        className="rgv-savingOverlay"
        style={{
          position: "fixed",
          inset: 0,
          backgroundColor: "rgba(0,0,0,0.85)",
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "1.5rem",
          fontWeight: "bold",
          zIndex: 999999,
          cursor: "wait",
        }}
      > {t("Saving reservation…")} </div>
    )}

    {showCancelPopup && (
      <>
        <div
          className="rgv-legacyModalOverlay"
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.6)",
            zIndex: 900,
          }}
        />

        <div
          className={`rgv-legacyModal p-4 rounded${isLightTheme ? " border" : " bg-dark text-light"}`}
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "420px",
            zIndex: 1000,
            ...(isLightTheme ? { background: "#ffffff", color: "#26354f", borderColor: "#d7e2ec" } : {}),
          }}
        >
          <h5 className="mb-3">{t("Cancel reservation group")}</h5>

          <div className="mb-3">
            <label className="form-label">{t("Reason for cancellation")}</label>
            <textarea
              className="form-control"
              rows={4}
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
            />
          </div>

          <div className="d-flex justify-content-end gap-2">
            <button
              className="btn btn-secondary"
              onClick={() => {
                setCancelReason("");
                setShowCancelPopup(false);
              }}
            > {t("Back")} </button>

            <button
              className="btn btn-danger fw-bold"
              disabled={!cancelReason.trim()}
              onClick={async () => {
                try {
                  // ✅ GROUP cancel (real reservation group)
                  await axios.post(`/api/reservation-groups/${reservation_group_id}/cancel`, {
                    cancellation_reason: cancelReason.trim(),
                    updated_by: "UserCancelGroup",
                  });

                  goToReservationsList();
                } catch (err) {
                  console.error("[CANCEL][ERR]", err);
                  alert(formatGroupError(err?.response?.data?.error || t("Failed to cancel reservation group.")));
                }
              }}
            > {t("Confirm cancellation")} </button>


          </div>
        </div>
      </>
    )}

    {showSendConfirmationPopup && (
      <div className="rgv-sendConfirmationOverlay" style={sendConfirmationOverlayStyle} onClick={closeSendConfirmationPopup}>
        <div
          className="rgv-sendConfirmationModal"
          style={sendConfirmationShellStyle}
          onClick={(event) => event.stopPropagation()}
        >
          <div className="rgv-sendConfirmationHeader" style={{ textAlign: "center", marginBottom: "18px" }}>
            <p
              style={{
                letterSpacing: "0.35em",
                fontSize: "0.8rem",
                color: "rgba(248, 250, 252, 0.65)",
                marginBottom: "6px",
                textTransform: "uppercase",
              }}
            > {t("Reservation Studio")} </p>
            <h2 style={{ margin: 0, fontSize: "2rem", color: "#f8fafc" }}> {t("Send Reservation Confirmation")} </h2>
            <p style={{ marginTop: "6px", color: "rgba(226, 232, 240, 0.75)" }}> {t("Review the recipient list before sending the PDF confirmation to the guest.")} </p>
          </div>

          <div className="rgv-sendConfirmationContent" style={sendConfirmationContentStyle}>
            <section className="rgv-sendConfirmationSection" style={sendConfirmationSectionStyle}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "12px",
                  alignItems: "flex-start",
                  flexWrap: "wrap",
                  marginBottom: "14px",
                }}
              >
                <div>
                  <h3 style={{ margin: 0, fontSize: "1.05rem", fontWeight: 800, color: "#f8fafc" }}> {t("Recipients")} </h3>
                  <p style={{ margin: "4px 0 0", fontSize: "0.78rem", color: "rgba(226, 232, 240, 0.6)" }}> {t("The booker email is prefilled when available. You can edit it or add more recipients.")} </p>
                </div>

                <div
                  style={{
                    borderRadius: "999px",
                    padding: "6px 12px",
                    border: "1px solid rgba(148, 163, 184, 0.35)",
                    background: "rgba(255, 255, 255, 0.03)",
                    fontSize: "0.78rem",
                    color: "#cbd5e1",
                  }}
                >
                  {displayGroupLocator || t("Group {p0}", { p0: reservation_group_id })}
                </div>
              </div>

              {confirmationHistoryMatches.length > 0 && (
                <div style={sendConfirmationWarningStyle}>
                  <div style={{ fontWeight: 700, marginBottom: "6px" }}> {t("Some recipients already received a confirmation for this group.")} </div>
                  <div style={{ fontSize: "0.8rem", lineHeight: 1.55 }}>
                    {confirmationHistoryMatches.map(({ email, entry }) => {
                      const senderLabel =
                        entry?.sent_by_name || entry?.sent_by_email || t("a Palantir user");
                      return (
                        <div key={`confirmation-history-match-${email}`}>
                          {email} {t("last received it on")}{" "}
                          {formatConfirmationHistoryTimestamp(entry?.created_at)} {t("by")} {senderLabel}.
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {confirmationEmailFields.map((email, index) => {
                  const latestConfirmationForEmail = findLatestConfirmationEntryForEmail(email);

                  return (
                    <div
                      key={`confirmation-email-${index}`}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "minmax(0, 1fr) auto",
                        gap: "12px",
                        alignItems: "end",
                        padding: "12px",
                        borderRadius: "12px",
                        background: "rgba(2, 6, 23, 0.55)",
                        border: "1px solid rgba(148, 163, 184, 0.3)",
                      }}
                    >
                      <div>
                        <label style={sendConfirmationLabelStyle}>
                          {index === 0 ? t("Primary recipient") : t("Additional recipient {p0}", { p0: index })}
                        </label>
                        <input
                          type="email"
                          className="form-control"
                          style={sendConfirmationInputStyle}
                          value={email}
                          onChange={(e) => handleConfirmationEmailFieldChange(index, e.target.value)}
                          placeholder="guest@example.com"
                          autoFocus={index === 0}
                        />
                        {latestConfirmationForEmail && (
                          <div
                            style={{
                              marginTop: "6px",
                              fontSize: "0.76rem",
                              color: "#fde68a",
                              lineHeight: 1.45,
                            }}
                          > {t("Last sent on")}{" "}
                            {formatConfirmationHistoryTimestamp(
                              latestConfirmationForEmail?.created_at
                            )}{" "} {t("to this address.")} </div>
                        )}
                      </div>

                      <button
                        type="button"
                        style={{
                          ...sendConfirmationRemoveBtnStyle,
                          opacity:
                            confirmationEmailFields.length <= 1 || isSendingConfirmation ? 0.5 : 1,
                          cursor:
                            confirmationEmailFields.length <= 1 || isSendingConfirmation
                              ? "not-allowed"
                              : "pointer",
                        }}
                        disabled={confirmationEmailFields.length <= 1 || isSendingConfirmation}
                        onClick={() => handleRemoveConfirmationEmailField(index)}
                        title={t("Remove this email")}
                      > {t("Remove")} </button>
                    </div>
                  );
                })}
              </div>

              <div style={{ marginTop: "16px" }}>
                <button
                  type="button"
                  style={{
                    ...sendConfirmationGhostBtnStyle,
                    opacity: isSendingConfirmation ? 0.5 : 1,
                    cursor: isSendingConfirmation ? "not-allowed" : "pointer",
                  }}
                  disabled={isSendingConfirmation}
                  onClick={handleAddConfirmationEmailField}
                > {t("Add another email")} </button>
              </div>
            </section>

            <section className="rgv-sendConfirmationSection" style={sendConfirmationSectionStyle}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "12px",
                  alignItems: "flex-start",
                  flexWrap: "wrap",
                  marginBottom: "14px",
                }}
              >
                <div>
                  <h3 style={{ margin: 0, fontSize: "1.05rem", fontWeight: 800, color: "#f8fafc" }}> {t("Sent history")} </h3>
                  <p style={{ margin: "4px 0 0", fontSize: "0.78rem", color: "rgba(226, 232, 240, 0.6)" }}> {t("Check previous confirmation emails and the recipient list before sending again.")} </p>
                </div>

                <div style={sendConfirmationHistoryBadgeStyle}>
                  {confirmationEmailHistory.length}{" "}
                  {t(confirmationEmailHistory.length === 1 ? "delivery" : "deliveries")}
                </div>
              </div>

              {confirmationEmailHistory.length ? (
                <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                  {confirmationEmailHistory.map((entry, index) => {
                    const senderLabel =
                      entry?.sent_by_name || entry?.sent_by_email || t("Palantir user");
                    const emailLanguages = formatConfirmationLanguageSet(
                      entry?.email_languages,
                      t("Email")
                    );
                    const pdfLanguages = formatConfirmationLanguageSet(entry?.pdf_languages, "PDF");

                    return (
                      <div
                        key={`confirmation-history-entry-${entry?.id || entry?.created_at || index}`}
                        style={sendConfirmationHistoryCardStyle}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: "12px",
                            alignItems: "flex-start",
                            flexWrap: "wrap",
                            marginBottom: "12px",
                          }}
                        >
                          <div>
                            <div style={{ color: "#f8fafc", fontWeight: 700 }}>
                              {formatConfirmationHistoryTimestamp(entry?.created_at)}
                            </div>
                            <div style={{ marginTop: "4px", fontSize: "0.8rem", color: "rgba(226, 232, 240, 0.68)" }}> {t("Sent by")} {senderLabel}
                            </div>
                          </div>

                          <div style={{ ...sendConfirmationHistoryBadgeStyle, fontSize: "0.72rem" }}>
                            {entry?.used_email_override ? t("Manual recipients") : t("Booker email")}
                          </div>
                        </div>

                        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                          {(Array.isArray(entry?.sent_to) ? entry.sent_to : []).map((recipient, recipientIndex) => (
                            <span
                              key={`confirmation-history-recipient-${entry?.id || index}-${recipientIndex}`}
                              style={sendConfirmationHistoryChipStyle}
                            >
                              {recipient}
                            </span>
                          ))}
                        </div>

                        {(emailLanguages || pdfLanguages || Number(entry?.active_rooms || 0) > 0) && (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginTop: "12px" }}>
                            {emailLanguages && (
                              <span style={sendConfirmationHistoryChipStyle}>{emailLanguages}</span>
                            )}
                            {pdfLanguages && (
                              <span style={sendConfirmationHistoryChipStyle}>{pdfLanguages}</span>
                            )}
                            {Number(entry?.active_rooms || 0) > 0 && (
                              <span style={sendConfirmationHistoryChipStyle}>
                                {entry.active_rooms} {t(Number(entry.active_rooms) === 1 ? "active room" : "active rooms")}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div
                  style={{
                    ...sendConfirmationHistoryCardStyle,
                    color: "rgba(226, 232, 240, 0.7)",
                    fontSize: "0.85rem",
                  }}
                > {t("No confirmation emails have been sent yet for this reservation group.")} </div>
              )}
            </section>

            <div
              style={{
                marginTop: "22px",
                display: "flex",
                gap: "12px",
                flexWrap: "wrap",
              }}
            >
              <button
                type="button"
                style={{
                  ...sendConfirmationPrimaryBtnStyle,
                  flex: "1 1 220px",
                  opacity:
                    isSendingConfirmation ||
                    !confirmationEmailFields.some((email) => String(email || "").trim() !== "")
                      ? 0.55
                      : 1,
                  cursor:
                    isSendingConfirmation ||
                    !confirmationEmailFields.some((email) => String(email || "").trim() !== "")
                      ? "not-allowed"
                      : "pointer",
                }}
                disabled={
                  isSendingConfirmation ||
                  !confirmationEmailFields.some((email) => String(email || "").trim() !== "")
                }
                onClick={handleSendConfirmationEmail}
              >
                {isSendingConfirmation ? t("Sending...") : t("Send confirmation")}
              </button>

              <button
                type="button"
                style={{
                  ...sendConfirmationSecondaryBtnStyle,
                  flex: "1 1 180px",
                  opacity: isSendingConfirmation ? 0.5 : 1,
                  cursor: isSendingConfirmation ? "not-allowed" : "pointer",
                }}
                disabled={isSendingConfirmation}
                onClick={closeSendConfirmationPopup}
              > {t("Cancel")} </button>
            </div>
          </div>
        </div>
      </div>
    )}

    {showRoomCancelPopup && roomCancelTarget?.kind !== "reservation" && (
      <>
        <div
          className="rgv-legacyModalOverlay"
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.6)",
            zIndex: 900,
          }}
          onClick={resetRoomCancelFlow}
        />

        <div
          className={`rgv-legacyModal p-4 rounded${isLightTheme ? " border" : " bg-dark text-light"}`}
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "420px",
            zIndex: 1000,
            ...(isLightTheme ? { background: "#ffffff", color: "#26354f", borderColor: "#d7e2ec" } : {}),
          }}
        >
          <h5 className="mb-3">
            {roomCancelTarget?.kind === "reservation" ? t("Cancel reservation") : t("Cancel room reservation")}
          </h5>

          {roomCancelTarget?.locator ? (
            <div className="rgv-legacyModalMeta mb-3" style={{ fontSize: "0.82rem", color: "rgba(230,230,247,0.72)" }}>
              {roomCancelTarget.locator}
            </div>
          ) : null}

          <div className="mb-3">
            <label className="form-label">{t("Reason for cancellation")}</label>
            <textarea
              className="form-control"
              rows={4}
              value={roomCancelReason}
              onChange={(e) => setRoomCancelReason(e.target.value)}
            />
          </div>

          <div className="d-flex justify-content-end gap-2">
            <button
              className="btn btn-secondary"
              onClick={resetRoomCancelFlow}
              disabled={roomCancellationLoadingId === roomCancelTarget?.id}
            > {t("Back")} </button>

            <button
              className="btn btn-warning fw-bold"
              disabled={!roomCancelReason.trim() || !roomCancelTarget?.id || roomCancellationLoadingId === roomCancelTarget?.id}
              onClick={() => {
                void handleConfirmRoomCancellation();
              }}
            >
              {roomCancellationLoadingId === roomCancelTarget?.id ? t("Cancelling...") : t("Confirm cancellation")}
            </button>
          </div>
        </div>
      </>
    )}









    </>}
  </div>
    {!demoReadOnlyActive && <SpecialRequestsPopup
      isOpen={showSpecialRequestsPopup}
      onClose={() => setShowSpecialRequestsPopup(false)}
      rooms={specialRequestRooms}
    />}
    {!demoReadOnlyActive && attendanceDialog && <ReservationAttendanceDialog
      {...attendanceDialog}
      languageId={languageId}
      validateArrival={async (values) => {
        const reservation = reservations.find((row) => Number(row.id) === Number(attendanceDialog.reservationId));
        const missing = await loadCheckInMissingItems({ ...reservation, room_id: values.room_id });
        return missing.length ? t("Please fill the required check-in data before completing check-in: {p0}.", { p0: missing.map((item) => item.label).join(", ") }) : "";
      }}
      onClose={() => {
        setAttendanceDialog(null);
        if (actionsOnly) onActionsClose?.();
      }}
      onCompleted={() => loadGroup()}
    />}
  </div>
  
);


};

export default ReservationGroupView;
